import { jsx, jsxs, Fragment as Fragment$1 } from 'react/jsx-runtime';
import * as React from 'react';
import React__default, { createContext, useMemo, createElement, useContext, useCallback, forwardRef, Children, isValidElement, cloneElement, Fragment, useRef, useEffect, useState, useLayoutEffect, useReducer } from 'react';
import require$$3, { flushSync } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import require$$0 from 'path';
import require$$1 from 'util';
import { library, config } from '@fortawesome/fontawesome-svg-core';

function _extends() {
  _extends = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends.apply(this, arguments);
}

/* -------------------------------------------------------------------------------------------------
 * createContextScope
 * -----------------------------------------------------------------------------------------------*/ function $c512c27ab02ef895$export$50c7b4e9d9f19c1(scopeName, createContextScopeDeps = []) {
    let defaultContexts = [];
    /* -----------------------------------------------------------------------------------------------
   * createContext
   * ---------------------------------------------------------------------------------------------*/ function $c512c27ab02ef895$export$fd42f52fd3ae1109(rootComponentName, defaultContext) {
        const BaseContext = /*#__PURE__*/ createContext(defaultContext);
        const index = defaultContexts.length;
        defaultContexts = [
            ...defaultContexts,
            defaultContext
        ];
        function Provider(props) {
            const { scope: scope , children: children , ...context } = props;
            const Context = (scope === null || scope === void 0 ? void 0 : scope[scopeName][index]) || BaseContext; // Only re-memoize when prop values change
            // eslint-disable-next-line react-hooks/exhaustive-deps
            const value = useMemo(()=>context
            , Object.values(context));
            return /*#__PURE__*/ createElement(Context.Provider, {
                value: value
            }, children);
        }
        function useContext$1(consumerName, scope) {
            const Context = (scope === null || scope === void 0 ? void 0 : scope[scopeName][index]) || BaseContext;
            const context = useContext(Context);
            if (context) return context;
            if (defaultContext !== undefined) return defaultContext; // if a defaultContext wasn't specified, it's a required context.
            throw new Error(`\`${consumerName}\` must be used within \`${rootComponentName}\``);
        }
        Provider.displayName = rootComponentName + 'Provider';
        return [
            Provider,
            useContext$1
        ];
    }
    /* -----------------------------------------------------------------------------------------------
   * createScope
   * ---------------------------------------------------------------------------------------------*/ const createScope = ()=>{
        const scopeContexts = defaultContexts.map((defaultContext)=>{
            return /*#__PURE__*/ createContext(defaultContext);
        });
        return function useScope(scope) {
            const contexts = (scope === null || scope === void 0 ? void 0 : scope[scopeName]) || scopeContexts;
            return useMemo(()=>({
                    [`__scope${scopeName}`]: {
                        ...scope,
                        [scopeName]: contexts
                    }
                })
            , [
                scope,
                contexts
            ]);
        };
    };
    createScope.scopeName = scopeName;
    return [
        $c512c27ab02ef895$export$fd42f52fd3ae1109,
        $c512c27ab02ef895$var$composeContextScopes(createScope, ...createContextScopeDeps)
    ];
}
/* -------------------------------------------------------------------------------------------------
 * composeContextScopes
 * -----------------------------------------------------------------------------------------------*/ function $c512c27ab02ef895$var$composeContextScopes(...scopes) {
    const baseScope = scopes[0];
    if (scopes.length === 1) return baseScope;
    const createScope1 = ()=>{
        const scopeHooks = scopes.map((createScope)=>({
                useScope: createScope(),
                scopeName: createScope.scopeName
            })
        );
        return function useComposedScopes(overrideScopes) {
            const nextScopes1 = scopeHooks.reduce((nextScopes, { useScope: useScope , scopeName: scopeName  })=>{
                // We are calling a hook inside a callback which React warns against to avoid inconsistent
                // renders, however, scoping doesn't have render side effects so we ignore the rule.
                // eslint-disable-next-line react-hooks/rules-of-hooks
                const scopeProps = useScope(overrideScopes);
                const currentScope = scopeProps[`__scope${scopeName}`];
                return {
                    ...nextScopes,
                    ...currentScope
                };
            }, {});
            return useMemo(()=>({
                    [`__scope${baseScope.scopeName}`]: nextScopes1
                })
            , [
                nextScopes1
            ]);
        };
    };
    createScope1.scopeName = baseScope.scopeName;
    return createScope1;
}

/**
 * Set a given ref to a given value
 * This utility takes care of different types of refs: callback refs and RefObject(s)
 */ function $6ed0406888f73fc4$var$setRef(ref, value) {
    if (typeof ref === 'function') ref(value);
    else if (ref !== null && ref !== undefined) ref.current = value;
}
/**
 * A utility to compose multiple refs together
 * Accepts callback refs and RefObject(s)
 */ function $6ed0406888f73fc4$export$43e446d32b3d21af(...refs) {
    return (node)=>refs.forEach((ref)=>$6ed0406888f73fc4$var$setRef(ref, node)
        )
    ;
}
/**
 * A custom hook that composes multiple refs
 * Accepts callback refs and RefObject(s)
 */ function $6ed0406888f73fc4$export$c7b2cbe3552a0d05(...refs) {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    return useCallback($6ed0406888f73fc4$export$43e446d32b3d21af(...refs), refs);
}

/* -------------------------------------------------------------------------------------------------
 * Slot
 * -----------------------------------------------------------------------------------------------*/ const $5e63c961fc1ce211$export$8c6ed5c666ac1360 = /*#__PURE__*/ forwardRef((props, forwardedRef)=>{
    const { children: children , ...slotProps } = props;
    const childrenArray = Children.toArray(children);
    const slottable = childrenArray.find($5e63c961fc1ce211$var$isSlottable);
    if (slottable) {
        // the new element to render is the one passed as a child of `Slottable`
        const newElement = slottable.props.children;
        const newChildren = childrenArray.map((child)=>{
            if (child === slottable) {
                // because the new element will be the one rendered, we are only interested
                // in grabbing its children (`newElement.props.children`)
                if (Children.count(newElement) > 1) return Children.only(null);
                return /*#__PURE__*/ isValidElement(newElement) ? newElement.props.children : null;
            } else return child;
        });
        return /*#__PURE__*/ createElement($5e63c961fc1ce211$var$SlotClone, _extends({}, slotProps, {
            ref: forwardedRef
        }), /*#__PURE__*/ isValidElement(newElement) ? /*#__PURE__*/ cloneElement(newElement, undefined, newChildren) : null);
    }
    return /*#__PURE__*/ createElement($5e63c961fc1ce211$var$SlotClone, _extends({}, slotProps, {
        ref: forwardedRef
    }), children);
});
$5e63c961fc1ce211$export$8c6ed5c666ac1360.displayName = 'Slot';
/* -------------------------------------------------------------------------------------------------
 * SlotClone
 * -----------------------------------------------------------------------------------------------*/ const $5e63c961fc1ce211$var$SlotClone = /*#__PURE__*/ forwardRef((props, forwardedRef)=>{
    const { children: children , ...slotProps } = props;
    if (/*#__PURE__*/ isValidElement(children)) return /*#__PURE__*/ cloneElement(children, {
        ...$5e63c961fc1ce211$var$mergeProps(slotProps, children.props),
        ref: $6ed0406888f73fc4$export$43e446d32b3d21af(forwardedRef, children.ref)
    });
    return Children.count(children) > 1 ? Children.only(null) : null;
});
$5e63c961fc1ce211$var$SlotClone.displayName = 'SlotClone';
/* -------------------------------------------------------------------------------------------------
 * Slottable
 * -----------------------------------------------------------------------------------------------*/ const $5e63c961fc1ce211$export$d9f1ccf0bdb05d45 = ({ children: children  })=>{
    return /*#__PURE__*/ createElement(Fragment, null, children);
};
/* ---------------------------------------------------------------------------------------------- */ function $5e63c961fc1ce211$var$isSlottable(child) {
    return /*#__PURE__*/ isValidElement(child) && child.type === $5e63c961fc1ce211$export$d9f1ccf0bdb05d45;
}
function $5e63c961fc1ce211$var$mergeProps(slotProps, childProps) {
    // all child props should override
    const overrideProps = {
        ...childProps
    };
    for(const propName in childProps){
        const slotPropValue = slotProps[propName];
        const childPropValue = childProps[propName];
        const isHandler = /^on[A-Z]/.test(propName);
        if (isHandler) {
            // if the handler exists on both, we compose them
            if (slotPropValue && childPropValue) overrideProps[propName] = (...args)=>{
                childPropValue(...args);
                slotPropValue(...args);
            };
            else if (slotPropValue) overrideProps[propName] = slotPropValue;
        } else if (propName === 'style') overrideProps[propName] = {
            ...slotPropValue,
            ...childPropValue
        };
        else if (propName === 'className') overrideProps[propName] = [
            slotPropValue,
            childPropValue
        ].filter(Boolean).join(' ');
    }
    return {
        ...slotProps,
        ...overrideProps
    };
}

// We have resorted to returning slots directly rather than exposing primitives that can then
// be slotted like `<CollectionItem as={Slot}>…</CollectionItem>`.
// This is because we encountered issues with generic types that cannot be statically analysed
// due to creating them dynamically via createCollection.
function $e02a7d9cb1dc128c$export$c74125a8e3af6bb2(name) {
    /* -----------------------------------------------------------------------------------------------
   * CollectionProvider
   * ---------------------------------------------------------------------------------------------*/ const PROVIDER_NAME = name + 'CollectionProvider';
    const [createCollectionContext, createCollectionScope] = $c512c27ab02ef895$export$50c7b4e9d9f19c1(PROVIDER_NAME);
    const [CollectionProviderImpl, useCollectionContext] = createCollectionContext(PROVIDER_NAME, {
        collectionRef: {
            current: null
        },
        itemMap: new Map()
    });
    const CollectionProvider = (props)=>{
        const { scope: scope , children: children  } = props;
        const ref = React__default.useRef(null);
        const itemMap = React__default.useRef(new Map()).current;
        return /*#__PURE__*/ React__default.createElement(CollectionProviderImpl, {
            scope: scope,
            itemMap: itemMap,
            collectionRef: ref
        }, children);
    };
    /* -----------------------------------------------------------------------------------------------
   * CollectionSlot
   * ---------------------------------------------------------------------------------------------*/ const COLLECTION_SLOT_NAME = name + 'CollectionSlot';
    const CollectionSlot = /*#__PURE__*/ React__default.forwardRef((props, forwardedRef)=>{
        const { scope: scope , children: children  } = props;
        const context = useCollectionContext(COLLECTION_SLOT_NAME, scope);
        const composedRefs = $6ed0406888f73fc4$export$c7b2cbe3552a0d05(forwardedRef, context.collectionRef);
        return /*#__PURE__*/ React__default.createElement($5e63c961fc1ce211$export$8c6ed5c666ac1360, {
            ref: composedRefs
        }, children);
    });
    /* -----------------------------------------------------------------------------------------------
   * CollectionItem
   * ---------------------------------------------------------------------------------------------*/ const ITEM_SLOT_NAME = name + 'CollectionItemSlot';
    const ITEM_DATA_ATTR = 'data-radix-collection-item';
    const CollectionItemSlot = /*#__PURE__*/ React__default.forwardRef((props, forwardedRef)=>{
        const { scope: scope , children: children , ...itemData } = props;
        const ref = React__default.useRef(null);
        const composedRefs = $6ed0406888f73fc4$export$c7b2cbe3552a0d05(forwardedRef, ref);
        const context = useCollectionContext(ITEM_SLOT_NAME, scope);
        React__default.useEffect(()=>{
            context.itemMap.set(ref, {
                ref: ref,
                ...itemData
            });
            return ()=>void context.itemMap.delete(ref)
            ;
        });
        return /*#__PURE__*/ React__default.createElement($5e63c961fc1ce211$export$8c6ed5c666ac1360, {
            [ITEM_DATA_ATTR]: '',
            ref: composedRefs
        }, children);
    });
    /* -----------------------------------------------------------------------------------------------
   * useCollection
   * ---------------------------------------------------------------------------------------------*/ function useCollection(scope) {
        const context = useCollectionContext(name + 'CollectionConsumer', scope);
        const getItems = React__default.useCallback(()=>{
            const collectionNode = context.collectionRef.current;
            if (!collectionNode) return [];
            const orderedNodes = Array.from(collectionNode.querySelectorAll(`[${ITEM_DATA_ATTR}]`));
            const items = Array.from(context.itemMap.values());
            const orderedItems = items.sort((a, b)=>orderedNodes.indexOf(a.ref.current) - orderedNodes.indexOf(b.ref.current)
            );
            return orderedItems;
        }, [
            context.collectionRef,
            context.itemMap
        ]);
        return getItems;
    }
    return [
        {
            Provider: CollectionProvider,
            Slot: CollectionSlot,
            ItemSlot: CollectionItemSlot
        },
        useCollection,
        createCollectionScope
    ];
}

function $e42e1063c40fb3ef$export$b9ecd428b558ff10(originalEventHandler, ourEventHandler, { checkForDefaultPrevented: checkForDefaultPrevented = true  } = {}) {
    return function handleEvent(event) {
        originalEventHandler === null || originalEventHandler === void 0 || originalEventHandler(event);
        if (checkForDefaultPrevented === false || !event.defaultPrevented) return ourEventHandler === null || ourEventHandler === void 0 ? void 0 : ourEventHandler(event);
    };
}

/**
 * A custom hook that converts a callback to a ref to avoid triggering re-renders when passed as a
 * prop or avoid re-executing effects when passed as a dependency
 */ function $b1b2314f5f9a1d84$export$25bec8c6f54ee79a(callback) {
    const callbackRef = useRef(callback);
    useEffect(()=>{
        callbackRef.current = callback;
    }); // https://github.com/facebook/react/issues/19240
    return useMemo(()=>(...args)=>{
            var _callbackRef$current;
            return (_callbackRef$current = callbackRef.current) === null || _callbackRef$current === void 0 ? void 0 : _callbackRef$current.call(callbackRef, ...args);
        }
    , []);
}

function $71cd76cc60e0454e$export$6f32135080cb4c3({ prop: prop , defaultProp: defaultProp , onChange: onChange = ()=>{}  }) {
    const [uncontrolledProp, setUncontrolledProp] = $71cd76cc60e0454e$var$useUncontrolledState({
        defaultProp: defaultProp,
        onChange: onChange
    });
    const isControlled = prop !== undefined;
    const value1 = isControlled ? prop : uncontrolledProp;
    const handleChange = $b1b2314f5f9a1d84$export$25bec8c6f54ee79a(onChange);
    const setValue = useCallback((nextValue)=>{
        if (isControlled) {
            const setter = nextValue;
            const value = typeof nextValue === 'function' ? setter(prop) : nextValue;
            if (value !== prop) handleChange(value);
        } else setUncontrolledProp(nextValue);
    }, [
        isControlled,
        prop,
        setUncontrolledProp,
        handleChange
    ]);
    return [
        value1,
        setValue
    ];
}
function $71cd76cc60e0454e$var$useUncontrolledState({ defaultProp: defaultProp , onChange: onChange  }) {
    const uncontrolledState = useState(defaultProp);
    const [value] = uncontrolledState;
    const prevValueRef = useRef(value);
    const handleChange = $b1b2314f5f9a1d84$export$25bec8c6f54ee79a(onChange);
    useEffect(()=>{
        if (prevValueRef.current !== value) {
            handleChange(value);
            prevValueRef.current = value;
        }
    }, [
        value,
        prevValueRef,
        handleChange
    ]);
    return uncontrolledState;
}

const $8927f6f2acc4f386$var$NODES = [
    'a',
    'button',
    'div',
    'form',
    'h2',
    'h3',
    'img',
    'input',
    'label',
    'li',
    'nav',
    'ol',
    'p',
    'span',
    'svg',
    'ul'
]; // Temporary while we await merge of this fix:
// https://github.com/DefinitelyTyped/DefinitelyTyped/pull/55396
// prettier-ignore
/* -------------------------------------------------------------------------------------------------
 * Primitive
 * -----------------------------------------------------------------------------------------------*/ const $8927f6f2acc4f386$export$250ffa63cdc0d034 = $8927f6f2acc4f386$var$NODES.reduce((primitive, node)=>{
    const Node = /*#__PURE__*/ forwardRef((props, forwardedRef)=>{
        const { asChild: asChild , ...primitiveProps } = props;
        const Comp = asChild ? $5e63c961fc1ce211$export$8c6ed5c666ac1360 : node;
        useEffect(()=>{
            window[Symbol.for('radix-ui')] = true;
        }, []);
        return /*#__PURE__*/ createElement(Comp, _extends({}, primitiveProps, {
            ref: forwardedRef
        }));
    });
    Node.displayName = `Primitive.${node}`;
    return {
        ...primitive,
        [node]: Node
    };
}, {});
/* -------------------------------------------------------------------------------------------------
 * Utils
 * -----------------------------------------------------------------------------------------------*/ /**
 * Flush custom event dispatch
 * https://github.com/radix-ui/primitives/pull/1378
 *
 * React batches *all* event handlers since version 18, this introduces certain considerations when using custom event types.
 *
 * Internally, React prioritises events in the following order:
 *  - discrete
 *  - continuous
 *  - default
 *
 * https://github.com/facebook/react/blob/a8a4742f1c54493df00da648a3f9d26e3db9c8b5/packages/react-dom/src/events/ReactDOMEventListener.js#L294-L350
 *
 * `discrete` is an  important distinction as updates within these events are applied immediately.
 * React however, is not able to infer the priority of custom event types due to how they are detected internally.
 * Because of this, it's possible for updates from custom events to be unexpectedly batched when
 * dispatched by another `discrete` event.
 *
 * In order to ensure that updates from custom events are applied predictably, we need to manually flush the batch.
 * This utility should be used when dispatching a custom event from within another `discrete` event, this utility
 * is not nessesary when dispatching known event types, or if dispatching a custom type inside a non-discrete event.
 * For example:
 *
 * dispatching a known click 👎
 * target.dispatchEvent(new Event(‘click’))
 *
 * dispatching a custom type within a non-discrete event 👎
 * onScroll={(event) => event.target.dispatchEvent(new CustomEvent(‘customType’))}
 *
 * dispatching a custom type within a `discrete` event 👍
 * onPointerDown={(event) => dispatchDiscreteCustomEvent(event.target, new CustomEvent(‘customType’))}
 *
 * Note: though React classifies `focus`, `focusin` and `focusout` events as `discrete`, it's  not recommended to use
 * this utility with them. This is because it's possible for those handlers to be called implicitly during render
 * e.g. when focus is within a component as it is unmounted, or when managing focus on mount.
 */ function $8927f6f2acc4f386$export$6d1a0317bde7de7f(target, event) {
    if (target) flushSync(()=>target.dispatchEvent(event)
    );
}

/**
 * On the server, React emits a warning when calling `useLayoutEffect`.
 * This is because neither `useLayoutEffect` nor `useEffect` run on the server.
 * We use this safe version which suppresses the warning by replacing it with a noop on the server.
 *
 * See: https://reactjs.org/docs/hooks-reference.html#uselayouteffect
 */ const $9f79659886946c16$export$e5c5a5f917a5871c = Boolean(globalThis === null || globalThis === void 0 ? void 0 : globalThis.document) ? useLayoutEffect : ()=>{};

function $fe963b355347cc68$export$3e6543de14f8614f(initialState, machine) {
    return useReducer((state, event)=>{
        const nextState = machine[state][event];
        return nextState !== null && nextState !== void 0 ? nextState : state;
    }, initialState);
}


const $921a889cee6df7e8$export$99c2b779aa4e8b8b = (props)=>{
    const { present: present , children: children  } = props;
    const presence = $921a889cee6df7e8$var$usePresence(present);
    const child = typeof children === 'function' ? children({
        present: presence.isPresent
    }) : Children.only(children);
    const ref = $6ed0406888f73fc4$export$c7b2cbe3552a0d05(presence.ref, child.ref);
    const forceMount = typeof children === 'function';
    return forceMount || presence.isPresent ? /*#__PURE__*/ cloneElement(child, {
        ref: ref
    }) : null;
};
$921a889cee6df7e8$export$99c2b779aa4e8b8b.displayName = 'Presence';
/* -------------------------------------------------------------------------------------------------
 * usePresence
 * -----------------------------------------------------------------------------------------------*/ function $921a889cee6df7e8$var$usePresence(present) {
    const [node1, setNode] = useState();
    const stylesRef = useRef({});
    const prevPresentRef = useRef(present);
    const prevAnimationNameRef = useRef('none');
    const initialState = present ? 'mounted' : 'unmounted';
    const [state, send] = $fe963b355347cc68$export$3e6543de14f8614f(initialState, {
        mounted: {
            UNMOUNT: 'unmounted',
            ANIMATION_OUT: 'unmountSuspended'
        },
        unmountSuspended: {
            MOUNT: 'mounted',
            ANIMATION_END: 'unmounted'
        },
        unmounted: {
            MOUNT: 'mounted'
        }
    });
    useEffect(()=>{
        const currentAnimationName = $921a889cee6df7e8$var$getAnimationName(stylesRef.current);
        prevAnimationNameRef.current = state === 'mounted' ? currentAnimationName : 'none';
    }, [
        state
    ]);
    $9f79659886946c16$export$e5c5a5f917a5871c(()=>{
        const styles = stylesRef.current;
        const wasPresent = prevPresentRef.current;
        const hasPresentChanged = wasPresent !== present;
        if (hasPresentChanged) {
            const prevAnimationName = prevAnimationNameRef.current;
            const currentAnimationName = $921a889cee6df7e8$var$getAnimationName(styles);
            if (present) send('MOUNT');
            else if (currentAnimationName === 'none' || (styles === null || styles === void 0 ? void 0 : styles.display) === 'none') // If there is no exit animation or the element is hidden, animations won't run
            // so we unmount instantly
            send('UNMOUNT');
            else {
                /**
         * When `present` changes to `false`, we check changes to animation-name to
         * determine whether an animation has started. We chose this approach (reading
         * computed styles) because there is no `animationrun` event and `animationstart`
         * fires after `animation-delay` has expired which would be too late.
         */ const isAnimating = prevAnimationName !== currentAnimationName;
                if (wasPresent && isAnimating) send('ANIMATION_OUT');
                else send('UNMOUNT');
            }
            prevPresentRef.current = present;
        }
    }, [
        present,
        send
    ]);
    $9f79659886946c16$export$e5c5a5f917a5871c(()=>{
        if (node1) {
            /**
       * Triggering an ANIMATION_OUT during an ANIMATION_IN will fire an `animationcancel`
       * event for ANIMATION_IN after we have entered `unmountSuspended` state. So, we
       * make sure we only trigger ANIMATION_END for the currently active animation.
       */ const handleAnimationEnd = (event)=>{
                const currentAnimationName = $921a889cee6df7e8$var$getAnimationName(stylesRef.current);
                const isCurrentAnimation = currentAnimationName.includes(event.animationName);
                if (event.target === node1 && isCurrentAnimation) // With React 18 concurrency this update is applied
                // a frame after the animation ends, creating a flash of visible content.
                // By manually flushing we ensure they sync within a frame, removing the flash.
                flushSync(()=>send('ANIMATION_END')
                );
            };
            const handleAnimationStart = (event)=>{
                if (event.target === node1) // if animation occurred, store its name as the previous animation.
                prevAnimationNameRef.current = $921a889cee6df7e8$var$getAnimationName(stylesRef.current);
            };
            node1.addEventListener('animationstart', handleAnimationStart);
            node1.addEventListener('animationcancel', handleAnimationEnd);
            node1.addEventListener('animationend', handleAnimationEnd);
            return ()=>{
                node1.removeEventListener('animationstart', handleAnimationStart);
                node1.removeEventListener('animationcancel', handleAnimationEnd);
                node1.removeEventListener('animationend', handleAnimationEnd);
            };
        } else // Transition to the unmounted state if the node is removed prematurely.
        // We avoid doing so during cleanup as the node may change but still exist.
        send('ANIMATION_END');
    }, [
        node1,
        send
    ]);
    return {
        isPresent: [
            'mounted',
            'unmountSuspended'
        ].includes(state),
        ref: useCallback((node)=>{
            if (node) stylesRef.current = getComputedStyle(node);
            setNode(node);
        }, [])
    };
}
/* -----------------------------------------------------------------------------------------------*/ function $921a889cee6df7e8$var$getAnimationName(styles) {
    return (styles === null || styles === void 0 ? void 0 : styles.animationName) || 'none';
}

const $1746a345f3d73bb7$var$useReactId = React['useId'.toString()] || (()=>undefined
);
let $1746a345f3d73bb7$var$count = 0;
function $1746a345f3d73bb7$export$f680877a34711e37(deterministicId) {
    const [id, setId] = React.useState($1746a345f3d73bb7$var$useReactId()); // React versions older than 18 will have client-side ids only.
    $9f79659886946c16$export$e5c5a5f917a5871c(()=>{
        if (!deterministicId) setId((reactId)=>reactId !== null && reactId !== void 0 ? reactId : String($1746a345f3d73bb7$var$count++)
        );
    }, [
        deterministicId
    ]);
    return deterministicId || (id ? `radix-${id}` : '');
}

/* -------------------------------------------------------------------------------------------------
 * Collapsible
 * -----------------------------------------------------------------------------------------------*/ const $409067139f391064$var$COLLAPSIBLE_NAME = 'Collapsible';
const [$409067139f391064$var$createCollapsibleContext, $409067139f391064$export$952b32dcbe73087a] = $c512c27ab02ef895$export$50c7b4e9d9f19c1($409067139f391064$var$COLLAPSIBLE_NAME);
const [$409067139f391064$var$CollapsibleProvider, $409067139f391064$var$useCollapsibleContext] = $409067139f391064$var$createCollapsibleContext($409067139f391064$var$COLLAPSIBLE_NAME);
const $409067139f391064$export$6eb0f7ddcda6131f = /*#__PURE__*/ forwardRef((props, forwardedRef)=>{
    const { __scopeCollapsible: __scopeCollapsible , open: openProp , defaultOpen: defaultOpen , disabled: disabled , onOpenChange: onOpenChange , ...collapsibleProps } = props;
    const [open = false, setOpen] = $71cd76cc60e0454e$export$6f32135080cb4c3({
        prop: openProp,
        defaultProp: defaultOpen,
        onChange: onOpenChange
    });
    return /*#__PURE__*/ createElement($409067139f391064$var$CollapsibleProvider, {
        scope: __scopeCollapsible,
        disabled: disabled,
        contentId: $1746a345f3d73bb7$export$f680877a34711e37(),
        open: open,
        onOpenToggle: useCallback(()=>setOpen((prevOpen)=>!prevOpen
            )
        , [
            setOpen
        ])
    }, /*#__PURE__*/ createElement($8927f6f2acc4f386$export$250ffa63cdc0d034.div, _extends({
        "data-state": $409067139f391064$var$getState(open),
        "data-disabled": disabled ? '' : undefined
    }, collapsibleProps, {
        ref: forwardedRef
    })));
});
/* -------------------------------------------------------------------------------------------------
 * CollapsibleTrigger
 * -----------------------------------------------------------------------------------------------*/ const $409067139f391064$var$TRIGGER_NAME = 'CollapsibleTrigger';
const $409067139f391064$export$c135dce7b15bbbdc = /*#__PURE__*/ forwardRef((props, forwardedRef)=>{
    const { __scopeCollapsible: __scopeCollapsible , ...triggerProps } = props;
    const context = $409067139f391064$var$useCollapsibleContext($409067139f391064$var$TRIGGER_NAME, __scopeCollapsible);
    return /*#__PURE__*/ createElement($8927f6f2acc4f386$export$250ffa63cdc0d034.button, _extends({
        type: "button",
        "aria-controls": context.contentId,
        "aria-expanded": context.open || false,
        "data-state": $409067139f391064$var$getState(context.open),
        "data-disabled": context.disabled ? '' : undefined,
        disabled: context.disabled
    }, triggerProps, {
        ref: forwardedRef,
        onClick: $e42e1063c40fb3ef$export$b9ecd428b558ff10(props.onClick, context.onOpenToggle)
    }));
});
/* -------------------------------------------------------------------------------------------------
 * CollapsibleContent
 * -----------------------------------------------------------------------------------------------*/ const $409067139f391064$var$CONTENT_NAME = 'CollapsibleContent';
const $409067139f391064$export$aadde00976f34151 = /*#__PURE__*/ forwardRef((props, forwardedRef)=>{
    const { forceMount: forceMount , ...contentProps } = props;
    const context = $409067139f391064$var$useCollapsibleContext($409067139f391064$var$CONTENT_NAME, props.__scopeCollapsible);
    return /*#__PURE__*/ createElement($921a889cee6df7e8$export$99c2b779aa4e8b8b, {
        present: forceMount || context.open
    }, ({ present: present  })=>/*#__PURE__*/ createElement($409067139f391064$var$CollapsibleContentImpl, _extends({}, contentProps, {
            ref: forwardedRef,
            present: present
        }))
    );
});
/* -----------------------------------------------------------------------------------------------*/ const $409067139f391064$var$CollapsibleContentImpl = /*#__PURE__*/ forwardRef((props, forwardedRef)=>{
    const { __scopeCollapsible: __scopeCollapsible , present: present , children: children , ...contentProps } = props;
    const context = $409067139f391064$var$useCollapsibleContext($409067139f391064$var$CONTENT_NAME, __scopeCollapsible);
    const [isPresent, setIsPresent] = useState(present);
    const ref = useRef(null);
    const composedRefs = $6ed0406888f73fc4$export$c7b2cbe3552a0d05(forwardedRef, ref);
    const heightRef = useRef(0);
    const height = heightRef.current;
    const widthRef = useRef(0);
    const width = widthRef.current; // when opening we want it to immediately open to retrieve dimensions
    // when closing we delay `present` to retrieve dimensions before closing
    const isOpen = context.open || isPresent;
    const isMountAnimationPreventedRef = useRef(isOpen);
    const originalStylesRef = useRef();
    useEffect(()=>{
        const rAF = requestAnimationFrame(()=>isMountAnimationPreventedRef.current = false
        );
        return ()=>cancelAnimationFrame(rAF)
        ;
    }, []);
    $9f79659886946c16$export$e5c5a5f917a5871c(()=>{
        const node = ref.current;
        if (node) {
            originalStylesRef.current = originalStylesRef.current || {
                transitionDuration: node.style.transitionDuration,
                animationName: node.style.animationName
            }; // block any animations/transitions so the element renders at its full dimensions
            node.style.transitionDuration = '0s';
            node.style.animationName = 'none'; // get width and height from full dimensions
            const rect = node.getBoundingClientRect();
            heightRef.current = rect.height;
            widthRef.current = rect.width; // kick off any animations/transitions that were originally set up if it isn't the initial mount
            if (!isMountAnimationPreventedRef.current) {
                node.style.transitionDuration = originalStylesRef.current.transitionDuration;
                node.style.animationName = originalStylesRef.current.animationName;
            }
            setIsPresent(present);
        }
    /**
     * depends on `context.open` because it will change to `false`
     * when a close is triggered but `present` will be `false` on
     * animation end (so when close finishes). This allows us to
     * retrieve the dimensions *before* closing.
     */ }, [
        context.open,
        present
    ]);
    return /*#__PURE__*/ createElement($8927f6f2acc4f386$export$250ffa63cdc0d034.div, _extends({
        "data-state": $409067139f391064$var$getState(context.open),
        "data-disabled": context.disabled ? '' : undefined,
        id: context.contentId,
        hidden: !isOpen
    }, contentProps, {
        ref: composedRefs,
        style: {
            [`--radix-collapsible-content-height`]: height ? `${height}px` : undefined,
            [`--radix-collapsible-content-width`]: width ? `${width}px` : undefined,
            ...props.style
        }
    }), isOpen && children);
});
/* -----------------------------------------------------------------------------------------------*/ function $409067139f391064$var$getState(open) {
    return open ? 'open' : 'closed';
}
const $409067139f391064$export$be92b6f5f03c0fe9 = $409067139f391064$export$6eb0f7ddcda6131f;
const $409067139f391064$export$41fb9f06171c75f4 = $409067139f391064$export$c135dce7b15bbbdc;
const $409067139f391064$export$7c6e2c02157bb7d2 = $409067139f391064$export$aadde00976f34151;

const $f631663db3294ace$var$DirectionContext = /*#__PURE__*/ createContext(undefined);
/* -----------------------------------------------------------------------------------------------*/ function $f631663db3294ace$export$b39126d51d94e6f3(localDir) {
    const globalDir = useContext($f631663db3294ace$var$DirectionContext);
    return localDir || globalDir || 'ltr';
}

/* -------------------------------------------------------------------------------------------------
 * Accordion
 * -----------------------------------------------------------------------------------------------*/ const $1bf158f521e1b1b4$var$ACCORDION_NAME = 'Accordion';
const $1bf158f521e1b1b4$var$ACCORDION_KEYS = [
    'Home',
    'End',
    'ArrowDown',
    'ArrowUp',
    'ArrowLeft',
    'ArrowRight'
];
const [$1bf158f521e1b1b4$var$Collection, $1bf158f521e1b1b4$var$useCollection, $1bf158f521e1b1b4$var$createCollectionScope] = $e02a7d9cb1dc128c$export$c74125a8e3af6bb2($1bf158f521e1b1b4$var$ACCORDION_NAME);
const [$1bf158f521e1b1b4$var$createAccordionContext, $1bf158f521e1b1b4$export$9748edc328a73be1] = $c512c27ab02ef895$export$50c7b4e9d9f19c1($1bf158f521e1b1b4$var$ACCORDION_NAME, [
    $1bf158f521e1b1b4$var$createCollectionScope,
    $409067139f391064$export$952b32dcbe73087a
]);
const $1bf158f521e1b1b4$var$useCollapsibleScope = $409067139f391064$export$952b32dcbe73087a();
const $1bf158f521e1b1b4$export$a766cd26d0d69044 = /*#__PURE__*/ React__default.forwardRef((props, forwardedRef)=>{
    const { type: type , ...accordionProps } = props;
    const singleProps = accordionProps;
    const multipleProps = accordionProps;
    return /*#__PURE__*/ React__default.createElement($1bf158f521e1b1b4$var$Collection.Provider, {
        scope: props.__scopeAccordion
    }, type === 'multiple' ? /*#__PURE__*/ React__default.createElement($1bf158f521e1b1b4$var$AccordionImplMultiple, _extends({}, multipleProps, {
        ref: forwardedRef
    })) : /*#__PURE__*/ React__default.createElement($1bf158f521e1b1b4$var$AccordionImplSingle, _extends({}, singleProps, {
        ref: forwardedRef
    })));
});
$1bf158f521e1b1b4$export$a766cd26d0d69044.propTypes = {
    type (props) {
        const value = props.value || props.defaultValue;
        if (props.type && ![
            'single',
            'multiple'
        ].includes(props.type)) return new Error('Invalid prop `type` supplied to `Accordion`. Expected one of `single | multiple`.');
        if (props.type === 'multiple' && typeof value === 'string') return new Error('Invalid prop `type` supplied to `Accordion`. Expected `single` when `defaultValue` or `value` is type `string`.');
        if (props.type === 'single' && Array.isArray(value)) return new Error('Invalid prop `type` supplied to `Accordion`. Expected `multiple` when `defaultValue` or `value` is type `string[]`.');
        return null;
    }
};
/* -----------------------------------------------------------------------------------------------*/ const [$1bf158f521e1b1b4$var$AccordionValueProvider, $1bf158f521e1b1b4$var$useAccordionValueContext] = $1bf158f521e1b1b4$var$createAccordionContext($1bf158f521e1b1b4$var$ACCORDION_NAME);
const [$1bf158f521e1b1b4$var$AccordionCollapsibleProvider, $1bf158f521e1b1b4$var$useAccordionCollapsibleContext] = $1bf158f521e1b1b4$var$createAccordionContext($1bf158f521e1b1b4$var$ACCORDION_NAME, {
    collapsible: false
});
const $1bf158f521e1b1b4$var$AccordionImplSingle = /*#__PURE__*/ React__default.forwardRef((props, forwardedRef)=>{
    const { value: valueProp , defaultValue: defaultValue , onValueChange: onValueChange = ()=>{} , collapsible: collapsible = false , ...accordionSingleProps } = props;
    const [value, setValue] = $71cd76cc60e0454e$export$6f32135080cb4c3({
        prop: valueProp,
        defaultProp: defaultValue,
        onChange: onValueChange
    });
    return /*#__PURE__*/ React__default.createElement($1bf158f521e1b1b4$var$AccordionValueProvider, {
        scope: props.__scopeAccordion,
        value: value ? [
            value
        ] : [],
        onItemOpen: setValue,
        onItemClose: React__default.useCallback(()=>collapsible && setValue('')
        , [
            collapsible,
            setValue
        ])
    }, /*#__PURE__*/ React__default.createElement($1bf158f521e1b1b4$var$AccordionCollapsibleProvider, {
        scope: props.__scopeAccordion,
        collapsible: collapsible
    }, /*#__PURE__*/ React__default.createElement($1bf158f521e1b1b4$var$AccordionImpl, _extends({}, accordionSingleProps, {
        ref: forwardedRef
    }))));
});
/* -----------------------------------------------------------------------------------------------*/ const $1bf158f521e1b1b4$var$AccordionImplMultiple = /*#__PURE__*/ React__default.forwardRef((props, forwardedRef)=>{
    const { value: valueProp , defaultValue: defaultValue , onValueChange: onValueChange = ()=>{} , ...accordionMultipleProps } = props;
    const [value1 = [], setValue] = $71cd76cc60e0454e$export$6f32135080cb4c3({
        prop: valueProp,
        defaultProp: defaultValue,
        onChange: onValueChange
    });
    const handleItemOpen = React__default.useCallback((itemValue)=>setValue((prevValue = [])=>[
                ...prevValue,
                itemValue
            ]
        )
    , [
        setValue
    ]);
    const handleItemClose = React__default.useCallback((itemValue)=>setValue((prevValue = [])=>prevValue.filter((value)=>value !== itemValue
            )
        )
    , [
        setValue
    ]);
    return /*#__PURE__*/ React__default.createElement($1bf158f521e1b1b4$var$AccordionValueProvider, {
        scope: props.__scopeAccordion,
        value: value1,
        onItemOpen: handleItemOpen,
        onItemClose: handleItemClose
    }, /*#__PURE__*/ React__default.createElement($1bf158f521e1b1b4$var$AccordionCollapsibleProvider, {
        scope: props.__scopeAccordion,
        collapsible: true
    }, /*#__PURE__*/ React__default.createElement($1bf158f521e1b1b4$var$AccordionImpl, _extends({}, accordionMultipleProps, {
        ref: forwardedRef
    }))));
});
/* -----------------------------------------------------------------------------------------------*/ const [$1bf158f521e1b1b4$var$AccordionImplProvider, $1bf158f521e1b1b4$var$useAccordionContext] = $1bf158f521e1b1b4$var$createAccordionContext($1bf158f521e1b1b4$var$ACCORDION_NAME);
const $1bf158f521e1b1b4$var$AccordionImpl = /*#__PURE__*/ React__default.forwardRef((props, forwardedRef)=>{
    const { __scopeAccordion: __scopeAccordion , disabled: disabled , dir: dir , orientation: orientation = 'vertical' , ...accordionProps } = props;
    const accordionRef = React__default.useRef(null);
    const composedRefs = $6ed0406888f73fc4$export$c7b2cbe3552a0d05(accordionRef, forwardedRef);
    const getItems = $1bf158f521e1b1b4$var$useCollection(__scopeAccordion);
    const direction = $f631663db3294ace$export$b39126d51d94e6f3(dir);
    const isDirectionLTR = direction === 'ltr';
    const handleKeyDown = $e42e1063c40fb3ef$export$b9ecd428b558ff10(props.onKeyDown, (event)=>{
        var _triggerCollection$cl;
        if (!$1bf158f521e1b1b4$var$ACCORDION_KEYS.includes(event.key)) return;
        const target = event.target;
        const triggerCollection = getItems().filter((item)=>{
            var _item$ref$current;
            return !((_item$ref$current = item.ref.current) !== null && _item$ref$current !== void 0 && _item$ref$current.disabled);
        });
        const triggerIndex = triggerCollection.findIndex((item)=>item.ref.current === target
        );
        const triggerCount = triggerCollection.length;
        if (triggerIndex === -1) return; // Prevents page scroll while user is navigating
        event.preventDefault();
        let nextIndex = triggerIndex;
        const homeIndex = 0;
        const endIndex = triggerCount - 1;
        const moveNext = ()=>{
            nextIndex = triggerIndex + 1;
            if (nextIndex > endIndex) nextIndex = homeIndex;
        };
        const movePrev = ()=>{
            nextIndex = triggerIndex - 1;
            if (nextIndex < homeIndex) nextIndex = endIndex;
        };
        switch(event.key){
            case 'Home':
                nextIndex = homeIndex;
                break;
            case 'End':
                nextIndex = endIndex;
                break;
            case 'ArrowRight':
                if (orientation === 'horizontal') {
                    if (isDirectionLTR) moveNext();
                    else movePrev();
                }
                break;
            case 'ArrowDown':
                if (orientation === 'vertical') moveNext();
                break;
            case 'ArrowLeft':
                if (orientation === 'horizontal') {
                    if (isDirectionLTR) movePrev();
                    else moveNext();
                }
                break;
            case 'ArrowUp':
                if (orientation === 'vertical') movePrev();
                break;
        }
        const clampedIndex = nextIndex % triggerCount;
        (_triggerCollection$cl = triggerCollection[clampedIndex].ref.current) === null || _triggerCollection$cl === void 0 || _triggerCollection$cl.focus();
    });
    return /*#__PURE__*/ React__default.createElement($1bf158f521e1b1b4$var$AccordionImplProvider, {
        scope: __scopeAccordion,
        disabled: disabled,
        direction: dir,
        orientation: orientation
    }, /*#__PURE__*/ React__default.createElement($1bf158f521e1b1b4$var$Collection.Slot, {
        scope: __scopeAccordion
    }, /*#__PURE__*/ React__default.createElement($8927f6f2acc4f386$export$250ffa63cdc0d034.div, _extends({}, accordionProps, {
        "data-orientation": orientation,
        ref: composedRefs,
        onKeyDown: disabled ? undefined : handleKeyDown
    }))));
});
/* -------------------------------------------------------------------------------------------------
 * AccordionItem
 * -----------------------------------------------------------------------------------------------*/ const $1bf158f521e1b1b4$var$ITEM_NAME = 'AccordionItem';
const [$1bf158f521e1b1b4$var$AccordionItemProvider, $1bf158f521e1b1b4$var$useAccordionItemContext] = $1bf158f521e1b1b4$var$createAccordionContext($1bf158f521e1b1b4$var$ITEM_NAME);
/**
 * `AccordionItem` contains all of the parts of a collapsible section inside of an `Accordion`.
 */ const $1bf158f521e1b1b4$export$d99097c13d4dac9f = /*#__PURE__*/ React__default.forwardRef((props, forwardedRef)=>{
    const { __scopeAccordion: __scopeAccordion , value: value , ...accordionItemProps } = props;
    const accordionContext = $1bf158f521e1b1b4$var$useAccordionContext($1bf158f521e1b1b4$var$ITEM_NAME, __scopeAccordion);
    const valueContext = $1bf158f521e1b1b4$var$useAccordionValueContext($1bf158f521e1b1b4$var$ITEM_NAME, __scopeAccordion);
    const collapsibleScope = $1bf158f521e1b1b4$var$useCollapsibleScope(__scopeAccordion);
    const triggerId = $1746a345f3d73bb7$export$f680877a34711e37();
    const open1 = value && valueContext.value.includes(value) || false;
    const disabled = accordionContext.disabled || props.disabled;
    return /*#__PURE__*/ React__default.createElement($1bf158f521e1b1b4$var$AccordionItemProvider, {
        scope: __scopeAccordion,
        open: open1,
        disabled: disabled,
        triggerId: triggerId
    }, /*#__PURE__*/ React__default.createElement($409067139f391064$export$be92b6f5f03c0fe9, _extends({
        "data-orientation": accordionContext.orientation,
        "data-state": open1 ? 'open' : 'closed'
    }, collapsibleScope, accordionItemProps, {
        ref: forwardedRef,
        disabled: disabled,
        open: open1,
        onOpenChange: (open)=>{
            if (open) valueContext.onItemOpen(value);
            else valueContext.onItemClose(value);
        }
    })));
});
/* -------------------------------------------------------------------------------------------------
 * AccordionHeader
 * -----------------------------------------------------------------------------------------------*/ const $1bf158f521e1b1b4$var$HEADER_NAME = 'AccordionHeader';
/**
 * `AccordionHeader` contains the content for the parts of an `AccordionItem` that will be visible
 * whether or not its content is collapsed.
 */ const $1bf158f521e1b1b4$export$5e3e5deaaf81ee41 = /*#__PURE__*/ React__default.forwardRef((props, forwardedRef)=>{
    const { __scopeAccordion: __scopeAccordion , ...headerProps } = props;
    const accordionContext = $1bf158f521e1b1b4$var$useAccordionContext($1bf158f521e1b1b4$var$ACCORDION_NAME, __scopeAccordion);
    const itemContext = $1bf158f521e1b1b4$var$useAccordionItemContext($1bf158f521e1b1b4$var$HEADER_NAME, __scopeAccordion);
    return /*#__PURE__*/ React__default.createElement($8927f6f2acc4f386$export$250ffa63cdc0d034.h3, _extends({
        "data-orientation": accordionContext.orientation,
        "data-state": $1bf158f521e1b1b4$var$getState(itemContext.open),
        "data-disabled": itemContext.disabled ? '' : undefined
    }, headerProps, {
        ref: forwardedRef
    }));
});
/* -------------------------------------------------------------------------------------------------
 * AccordionTrigger
 * -----------------------------------------------------------------------------------------------*/ const $1bf158f521e1b1b4$var$TRIGGER_NAME = 'AccordionTrigger';
/**
 * `AccordionTrigger` is the trigger that toggles the collapsed state of an `AccordionItem`. It
 * should always be nested inside of an `AccordionHeader`.
 */ const $1bf158f521e1b1b4$export$94e939b1f85bdd73 = /*#__PURE__*/ React__default.forwardRef((props, forwardedRef)=>{
    const { __scopeAccordion: __scopeAccordion , ...triggerProps } = props;
    const accordionContext = $1bf158f521e1b1b4$var$useAccordionContext($1bf158f521e1b1b4$var$ACCORDION_NAME, __scopeAccordion);
    const itemContext = $1bf158f521e1b1b4$var$useAccordionItemContext($1bf158f521e1b1b4$var$TRIGGER_NAME, __scopeAccordion);
    const collapsibleContext = $1bf158f521e1b1b4$var$useAccordionCollapsibleContext($1bf158f521e1b1b4$var$TRIGGER_NAME, __scopeAccordion);
    const collapsibleScope = $1bf158f521e1b1b4$var$useCollapsibleScope(__scopeAccordion);
    return /*#__PURE__*/ React__default.createElement($1bf158f521e1b1b4$var$Collection.ItemSlot, {
        scope: __scopeAccordion
    }, /*#__PURE__*/ React__default.createElement($409067139f391064$export$41fb9f06171c75f4, _extends({
        "aria-disabled": itemContext.open && !collapsibleContext.collapsible || undefined,
        "data-orientation": accordionContext.orientation,
        id: itemContext.triggerId
    }, collapsibleScope, triggerProps, {
        ref: forwardedRef
    })));
});
/* -------------------------------------------------------------------------------------------------
 * AccordionContent
 * -----------------------------------------------------------------------------------------------*/ const $1bf158f521e1b1b4$var$CONTENT_NAME = 'AccordionContent';
/**
 * `AccordionContent` contains the collapsible content for an `AccordionItem`.
 */ const $1bf158f521e1b1b4$export$985b9a77379b54a0 = /*#__PURE__*/ React__default.forwardRef((props, forwardedRef)=>{
    const { __scopeAccordion: __scopeAccordion , ...contentProps } = props;
    const accordionContext = $1bf158f521e1b1b4$var$useAccordionContext($1bf158f521e1b1b4$var$ACCORDION_NAME, __scopeAccordion);
    const itemContext = $1bf158f521e1b1b4$var$useAccordionItemContext($1bf158f521e1b1b4$var$CONTENT_NAME, __scopeAccordion);
    const collapsibleScope = $1bf158f521e1b1b4$var$useCollapsibleScope(__scopeAccordion);
    return /*#__PURE__*/ React__default.createElement($409067139f391064$export$7c6e2c02157bb7d2, _extends({
        role: "region",
        "aria-labelledby": itemContext.triggerId,
        "data-orientation": accordionContext.orientation
    }, collapsibleScope, contentProps, {
        ref: forwardedRef,
        style: {
            ['--radix-accordion-content-height']: 'var(--radix-collapsible-content-height)',
            ['--radix-accordion-content-width']: 'var(--radix-collapsible-content-width)',
            ...props.style
        }
    }));
});
/* -----------------------------------------------------------------------------------------------*/ function $1bf158f521e1b1b4$var$getState(open) {
    return open ? 'open' : 'closed';
}
const $1bf158f521e1b1b4$export$be92b6f5f03c0fe9 = $1bf158f521e1b1b4$export$a766cd26d0d69044;
const $1bf158f521e1b1b4$export$6d08773d2e66f8f2 = $1bf158f521e1b1b4$export$d99097c13d4dac9f;
const $1bf158f521e1b1b4$export$8b251419efc915eb = $1bf158f521e1b1b4$export$5e3e5deaaf81ee41;
const $1bf158f521e1b1b4$export$41fb9f06171c75f4 = $1bf158f521e1b1b4$export$94e939b1f85bdd73;
const $1bf158f521e1b1b4$export$7c6e2c02157bb7d2 = $1bf158f521e1b1b4$export$985b9a77379b54a0;

function r(e){var t,f,n="";if("string"==typeof e||"number"==typeof e)n+=e;else if("object"==typeof e)if(Array.isArray(e))for(t=0;t<e.length;t++)e[t]&&(f=r(e[t]))&&(n&&(n+=" "),n+=f);else for(t in e)e[t]&&(n&&(n+=" "),n+=t);return n}function clsx(){for(var e,t,f=0,n="";f<arguments.length;)(e=arguments[f++])&&(t=r(e))&&(n&&(n+=" "),n+=t);return n}

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
/* global Reflect, Promise, SuppressedError, Symbol */


var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

function __spreadArray(to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
}

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

/**
 * IFE-002 Icon
 *
 * The Icon component displays an icon from the FontAwesome icon library.
 */
function Icon(props) {
    var icon = typeof props.icon === 'string' ? ['far', props.icon] : props.icon;
    return jsx(FontAwesomeIcon, __assign({}, props, { icon: icon }));
}

function styleInject(css, ref) {
  if ( ref === void 0 ) ref = {};
  var insertAt = ref.insertAt;

  if (!css || typeof document === 'undefined') { return; }

  var head = document.head || document.getElementsByTagName('head')[0];
  var style = document.createElement('style');
  style.type = 'text/css';

  if (insertAt === 'top') {
    if (head.firstChild) {
      head.insertBefore(style, head.firstChild);
    } else {
      head.appendChild(style);
    }
  } else {
    head.appendChild(style);
  }

  if (style.styleSheet) {
    style.styleSheet.cssText = css;
  } else {
    style.appendChild(document.createTextNode(css));
  }
}

var css_248z$8 = ".Typography-module_h1__Aivwz {\n  font-size: 2rem;\n}\n@media (min-width: 834px) {\n  .Typography-module_h1__Aivwz {\n    font-size: 3rem;\n  }\n}\n\n.Typography-module_h2__CV8Oo {\n  font-size: 1.75rem;\n}\n@media (min-width: 834px) {\n  .Typography-module_h2__CV8Oo {\n    font-size: 2.5rem;\n  }\n}\n\n.Typography-module_h3__lrbrK {\n  font-size: 1.5rem;\n}\n@media (min-width: 834px) {\n  .Typography-module_h3__lrbrK {\n    font-size: 2rem;\n  }\n}\n\n.Typography-module_h4__-jkC1 {\n  font-size: 1.25rem;\n}\n@media (min-width: 834px) {\n  .Typography-module_h4__-jkC1 {\n    font-size: 1.5rem;\n  }\n}\n\n.Typography-module_h5__GnQvD {\n  font-size: 1rem;\n}\n@media (min-width: 834px) {\n  .Typography-module_h5__GnQvD {\n    font-size: 1.25rem;\n  }\n}\n\n.Typography-module_h6__MKlIN {\n  font-size: 0.75rem;\n}\n@media (min-width: 834px) {\n  .Typography-module_h6__MKlIN {\n    font-size: 1rem;\n  }\n}\n\n.Typography-module_textXl__7rLd2 {\n  font-size: 1.25rem;\n}\n\n.Typography-module_textLg__cir2b {\n  font-size: 1.125rem;\n}\n\n.Typography-module_textXs__TVRWM {\n  font-size: 0.75rem;\n}\n\n.Typography-module_continuousText__Pe4dD h1,\n.Typography-module_continuousText__Pe4dD h2,\n.Typography-module_continuousText__Pe4dD h3,\n.Typography-module_continuousText__Pe4dD h4,\n.Typography-module_continuousText__Pe4dD h5,\n.Typography-module_continuousText__Pe4dD h6 {\n  max-width: 90ch;\n  margin-bottom: 0.5rem;\n}\n.Typography-module_continuousText__Pe4dD p {\n  max-width: 90ch;\n  margin-bottom: 1.5rem;\n}\n.Typography-module_continuousText__Pe4dD li {\n  max-width: 90ch;\n}";
var styles$8 = {"h1":"Typography-module_h1__Aivwz","h2":"Typography-module_h2__CV8Oo","h3":"Typography-module_h3__lrbrK","h4":"Typography-module_h4__-jkC1","h5":"Typography-module_h5__GnQvD","h6":"Typography-module_h6__MKlIN","textXl":"Typography-module_textXl__7rLd2","textLg":"Typography-module_textLg__cir2b","textXs":"Typography-module_textXs__TVRWM","continuousText":"Typography-module_continuousText__Pe4dD"};
styleInject(css_248z$8);

/**
 * IFE-001 Typography: ContinuousText Wrapper
 */
function ContinuousText(_a) {
    var children = _a.children, className = _a.className;
    return jsx("div", { className: clsx(styles$8.continuousText, className), children: children });
}

var getElementType = function (variant) {
    switch (variant) {
        case 'h1':
            return 'h1';
        case 'h2':
            return 'h2';
        case 'h3':
            return 'h3';
        case 'h4':
            return 'h4';
        case 'h5':
            return 'h5';
        case 'h6':
            return 'h6';
        case 'textXl':
        case 'textXlBold':
        case 'textLg':
        case 'textLgBold':
        case 'textMd':
        case 'textMdBold':
        case 'textSm':
        case 'textSmBold':
        case 'textXs':
        default:
            return 'p';
    }
};
var getClasses = function (variant) {
    switch (variant) {
        case 'textXl':
            return styles$8.textXl;
        case 'textXlBold':
            return clsx('fw-bold', styles$8.textXl);
        case 'textLg':
            return styles$8.textLg;
        case 'textLgBold':
            return clsx('fw-bold', styles$8.textLg);
        case 'textMdBold':
            return 'fs-6 fw-bold';
        case 'textSm':
            return 'small lh-sm';
        case 'textSmBold':
            return 'small lh-sm fw-bold';
        case 'textXs':
            return styles$8.textXs;
        case 'h1':
            return clsx('h1', styles$8.h1);
        case 'h2':
            return clsx('h2', styles$8.h2);
        case 'h3':
            return clsx('h3', styles$8.h3);
        case 'h4':
            return clsx('h4', styles$8.h4);
        case 'h5':
            return clsx('h5', styles$8.h5);
        case 'h6':
            return clsx('h6', styles$8.h6);
        case 'textMd':
        default:
            return 'fs-6';
    }
};
/**
 * IFE-001 Typography
 */
function Typography(_a) {
    var _b = _a.variant, variant = _b === void 0 ? 'textMd' : _b, Component = _a.component, children = _a.children, className = _a.className;
    var Element = Component !== null && Component !== void 0 ? Component : getElementType(variant);
    var classNames = getClasses(variant);
    return jsx(Element, { className: clsx(classNames, className), children: children });
}

var css_248z$7 = ".Accordion-module_icon__TdeJS {\n  transition: transform 0.2s ease-in-out;\n}\n.Accordion-module_trigger__sPQlj[data-state=open] .Accordion-module_icon__TdeJS {\n  transform: rotate(-180deg);\n}\n\n.Accordion-module_content__RyBzl[data-state=open] {\n  animation: Accordion-module_slideDown__Wo0So 0.35s ease;\n}\n.Accordion-module_content__RyBzl[data-state=closed] {\n  animation: Accordion-module_slideUp__ScYpq 0.35s ease;\n}\n\n@keyframes Accordion-module_slideDown__Wo0So {\n  from {\n    height: 0;\n  }\n  to {\n    height: var(--radix-accordion-content-height);\n  }\n}\n@keyframes Accordion-module_slideUp__ScYpq {\n  from {\n    height: var(--radix-accordion-content-height);\n  }\n  to {\n    height: 0;\n  }\n}";
var styles$7 = {"icon":"Accordion-module_icon__TdeJS","trigger":"Accordion-module_trigger__sPQlj","content":"Accordion-module_content__RyBzl","slideDown":"Accordion-module_slideDown__Wo0So","slideUp":"Accordion-module_slideUp__ScYpq"};
styleInject(css_248z$7);

/**
 * IFE-107 Accordion
 *
 * The Accordion component provides a vertically stacked set of interactive headings that each reveal an associated section of content.
 */
function Accordion(_a) {
    var items = _a.items, _b = _a.type, type = _b === void 0 ? 'single' : _b, className = _a.className;
    return (jsx($1bf158f521e1b1b4$export$be92b6f5f03c0fe9, { className: clsx('px-6 py-3 border rounded-3', className), type: type, collapsible: true, children: items.map(function (item, index) { return (jsxs($1bf158f521e1b1b4$export$6d08773d2e66f8f2, { value: "accordion-item-".concat(index + 1), className: clsx({ 'border-bottom': index !== items.length - 1 }), children: [jsx($1bf158f521e1b1b4$export$8b251419efc915eb, { asChild: true, children: jsxs($1bf158f521e1b1b4$export$41fb9f06171c75f4, { className: clsx(styles$7.trigger, 'btn d-flex w-100 px-0 py-4 text-start border-0'), children: [jsx(Icon, { icon: 'chevron-down', className: clsx(styles$7.icon, 'mt-1 me-4 text-primary-500') }), jsx(Typography, { variant: 'h5', component: 'span', children: item.title })] }) }), jsx($1bf158f521e1b1b4$export$7c6e2c02157bb7d2, { className: clsx(styles$7.content, 'ps-8 overflow-hidden'), children: jsx(Typography, { className: 'pb-4', component: 'div', children: item.content }) })] }, "accordion-item-".concat(index))); }) }));
}

/**
 * IFE-106 Advice
 *
 * The Advice component provides contextual information to the user.
 */
function Advice(_a) {
    var title = _a.title, content = _a.content, className = _a.className;
    return (jsxs("div", { className: clsx('ps-6 border-start', className), children: [jsxs("div", { className: "d-flex gap-2 align-items-center mb-2 text-gray-700", children: [jsx(Icon, { icon: "lightbulb", className: "flex-shrink-0" }), jsx(Typography, { variant: "textMdBold", component: "p", children: title })] }), jsx(Typography, { variant: "textSm", className: "text-gray-700", children: content })] }));
}

function getDefaultExportFromCjs (x) {
	return x && x.__esModule && Object.prototype.hasOwnProperty.call(x, 'default') ? x['default'] : x;
}

var link$1 = {exports: {}};

var _interop_require_default$1 = {};

_interop_require_default$1._ = _interop_require_default$1._interop_require_default = _interop_require_default;
function _interop_require_default(obj) {
    return obj && obj.__esModule ? obj : { default: obj };
}

var resolveHref = {exports: {}};

var querystring = {};

var hasRequiredQuerystring;

function requireQuerystring () {
	if (hasRequiredQuerystring) return querystring;
	hasRequiredQuerystring = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", {
		    value: true
		});
		function _export(target, all) {
		    for(var name in all)Object.defineProperty(target, name, {
		        enumerable: true,
		        get: all[name]
		    });
		}
		_export(exports, {
		    searchParamsToUrlQuery: function() {
		        return searchParamsToUrlQuery;
		    },
		    urlQueryToSearchParams: function() {
		        return urlQueryToSearchParams;
		    },
		    assign: function() {
		        return assign;
		    }
		});
		function searchParamsToUrlQuery(searchParams) {
		    const query = {};
		    searchParams.forEach((value, key)=>{
		        if (typeof query[key] === "undefined") {
		            query[key] = value;
		        } else if (Array.isArray(query[key])) {
		            query[key].push(value);
		        } else {
		            query[key] = [
		                query[key],
		                value
		            ];
		        }
		    });
		    return query;
		}
		function stringifyUrlQueryParam(param) {
		    if (typeof param === "string" || typeof param === "number" && !isNaN(param) || typeof param === "boolean") {
		        return String(param);
		    } else {
		        return "";
		    }
		}
		function urlQueryToSearchParams(urlQuery) {
		    const result = new URLSearchParams();
		    Object.entries(urlQuery).forEach((param)=>{
		        let [key, value] = param;
		        if (Array.isArray(value)) {
		            value.forEach((item)=>result.append(key, stringifyUrlQueryParam(item)));
		        } else {
		            result.set(key, stringifyUrlQueryParam(value));
		        }
		    });
		    return result;
		}
		function assign(target) {
		    for(var _len = arguments.length, searchParamsList = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++){
		        searchParamsList[_key - 1] = arguments[_key];
		    }
		    searchParamsList.forEach((searchParams)=>{
		        Array.from(searchParams.keys()).forEach((key)=>target.delete(key));
		        searchParams.forEach((value, key)=>target.append(key, value));
		    });
		    return target;
		}

		
	} (querystring));
	return querystring;
}

var formatUrl = {};

var _interop_require_wildcard$1 = {};

function _getRequireWildcardCache(nodeInterop) {
    if (typeof WeakMap !== "function") return null;

    var cacheBabelInterop = new WeakMap();
    var cacheNodeInterop = new WeakMap();

    return (_getRequireWildcardCache = function(nodeInterop) {
        return nodeInterop ? cacheNodeInterop : cacheBabelInterop;
    })(nodeInterop);
}
_interop_require_wildcard$1._ = _interop_require_wildcard$1._interop_require_wildcard = _interop_require_wildcard;
function _interop_require_wildcard(obj, nodeInterop) {
    if (!nodeInterop && obj && obj.__esModule) return obj;
    if (obj === null || typeof obj !== "object" && typeof obj !== "function") return { default: obj };

    var cache = _getRequireWildcardCache(nodeInterop);

    if (cache && cache.has(obj)) return cache.get(obj);

    var newObj = {};
    var hasPropertyDescriptor = Object.defineProperty && Object.getOwnPropertyDescriptor;

    for (var key in obj) {
        if (key !== "default" && Object.prototype.hasOwnProperty.call(obj, key)) {
            var desc = hasPropertyDescriptor ? Object.getOwnPropertyDescriptor(obj, key) : null;
            if (desc && (desc.get || desc.set)) Object.defineProperty(newObj, key, desc);
            else newObj[key] = obj[key];
        }
    }

    newObj.default = obj;

    if (cache) cache.set(obj, newObj);

    return newObj;
}

var hasRequiredFormatUrl;

function requireFormatUrl () {
	if (hasRequiredFormatUrl) return formatUrl;
	hasRequiredFormatUrl = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", {
		    value: true
		});
		function _export(target, all) {
		    for(var name in all)Object.defineProperty(target, name, {
		        enumerable: true,
		        get: all[name]
		    });
		}
		_export(exports, {
		    formatUrl: function() {
		        return formatUrl;
		    },
		    urlObjectKeys: function() {
		        return urlObjectKeys;
		    },
		    formatWithValidation: function() {
		        return formatWithValidation;
		    }
		});
		const _interop_require_wildcard = _interop_require_wildcard$1;
		const _querystring = /*#__PURE__*/ _interop_require_wildcard._(requireQuerystring());
		const slashedProtocols = /https?|ftp|gopher|file/;
		function formatUrl(urlObj) {
		    let { auth, hostname } = urlObj;
		    let protocol = urlObj.protocol || "";
		    let pathname = urlObj.pathname || "";
		    let hash = urlObj.hash || "";
		    let query = urlObj.query || "";
		    let host = false;
		    auth = auth ? encodeURIComponent(auth).replace(/%3A/i, ":") + "@" : "";
		    if (urlObj.host) {
		        host = auth + urlObj.host;
		    } else if (hostname) {
		        host = auth + (~hostname.indexOf(":") ? "[" + hostname + "]" : hostname);
		        if (urlObj.port) {
		            host += ":" + urlObj.port;
		        }
		    }
		    if (query && typeof query === "object") {
		        query = String(_querystring.urlQueryToSearchParams(query));
		    }
		    let search = urlObj.search || query && "?" + query || "";
		    if (protocol && !protocol.endsWith(":")) protocol += ":";
		    if (urlObj.slashes || (!protocol || slashedProtocols.test(protocol)) && host !== false) {
		        host = "//" + (host || "");
		        if (pathname && pathname[0] !== "/") pathname = "/" + pathname;
		    } else if (!host) {
		        host = "";
		    }
		    if (hash && hash[0] !== "#") hash = "#" + hash;
		    if (search && search[0] !== "?") search = "?" + search;
		    pathname = pathname.replace(/[?#]/g, encodeURIComponent);
		    search = search.replace("#", "%23");
		    return "" + protocol + host + pathname + search + hash;
		}
		const urlObjectKeys = [
		    "auth",
		    "hash",
		    "host",
		    "hostname",
		    "href",
		    "path",
		    "pathname",
		    "port",
		    "protocol",
		    "query",
		    "search",
		    "slashes"
		];
		function formatWithValidation(url) {
		    if (process.env.NODE_ENV === "development") {
		        if (url !== null && typeof url === "object") {
		            Object.keys(url).forEach((key)=>{
		                if (!urlObjectKeys.includes(key)) {
		                    console.warn("Unknown key passed via urlObject into url.format: " + key);
		                }
		            });
		        }
		    }
		    return formatUrl(url);
		}

		
	} (formatUrl));
	return formatUrl;
}

var omit = {};

var hasRequiredOmit;

function requireOmit () {
	if (hasRequiredOmit) return omit;
	hasRequiredOmit = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", {
		    value: true
		});
		Object.defineProperty(exports, "omit", {
		    enumerable: true,
		    get: function() {
		        return omit;
		    }
		});
		function omit(object, keys) {
		    const omitted = {};
		    Object.keys(object).forEach((key)=>{
		        if (!keys.includes(key)) {
		            omitted[key] = object[key];
		        }
		    });
		    return omitted;
		}

		
	} (omit));
	return omit;
}

var utils$1 = {};

var hasRequiredUtils$1;

function requireUtils$1 () {
	if (hasRequiredUtils$1) return utils$1;
	hasRequiredUtils$1 = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", {
		    value: true
		});
		function _export(target, all) {
		    for(var name in all)Object.defineProperty(target, name, {
		        enumerable: true,
		        get: all[name]
		    });
		}
		_export(exports, {
		    WEB_VITALS: function() {
		        return WEB_VITALS;
		    },
		    execOnce: function() {
		        return execOnce;
		    },
		    isAbsoluteUrl: function() {
		        return isAbsoluteUrl;
		    },
		    getLocationOrigin: function() {
		        return getLocationOrigin;
		    },
		    getURL: function() {
		        return getURL;
		    },
		    getDisplayName: function() {
		        return getDisplayName;
		    },
		    isResSent: function() {
		        return isResSent;
		    },
		    normalizeRepeatedSlashes: function() {
		        return normalizeRepeatedSlashes;
		    },
		    loadGetInitialProps: function() {
		        return loadGetInitialProps;
		    },
		    SP: function() {
		        return SP;
		    },
		    ST: function() {
		        return ST;
		    },
		    DecodeError: function() {
		        return DecodeError;
		    },
		    NormalizeError: function() {
		        return NormalizeError;
		    },
		    PageNotFoundError: function() {
		        return PageNotFoundError;
		    },
		    MissingStaticPage: function() {
		        return MissingStaticPage;
		    },
		    MiddlewareNotFoundError: function() {
		        return MiddlewareNotFoundError;
		    },
		    stringifyError: function() {
		        return stringifyError;
		    }
		});
		const WEB_VITALS = [
		    "CLS",
		    "FCP",
		    "FID",
		    "INP",
		    "LCP",
		    "TTFB"
		];
		function execOnce(fn) {
		    let used = false;
		    let result;
		    return function() {
		        for(var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++){
		            args[_key] = arguments[_key];
		        }
		        if (!used) {
		            used = true;
		            result = fn(...args);
		        }
		        return result;
		    };
		}
		// Scheme: https://tools.ietf.org/html/rfc3986#section-3.1
		// Absolute URL: https://tools.ietf.org/html/rfc3986#section-4.3
		const ABSOLUTE_URL_REGEX = /^[a-zA-Z][a-zA-Z\d+\-.]*?:/;
		const isAbsoluteUrl = (url)=>ABSOLUTE_URL_REGEX.test(url);
		function getLocationOrigin() {
		    const { protocol, hostname, port } = window.location;
		    return protocol + "//" + hostname + (port ? ":" + port : "");
		}
		function getURL() {
		    const { href } = window.location;
		    const origin = getLocationOrigin();
		    return href.substring(origin.length);
		}
		function getDisplayName(Component) {
		    return typeof Component === "string" ? Component : Component.displayName || Component.name || "Unknown";
		}
		function isResSent(res) {
		    return res.finished || res.headersSent;
		}
		function normalizeRepeatedSlashes(url) {
		    const urlParts = url.split("?");
		    const urlNoQuery = urlParts[0];
		    return urlNoQuery// first we replace any non-encoded backslashes with forward
		    // then normalize repeated forward slashes
		    .replace(/\\/g, "/").replace(/\/\/+/g, "/") + (urlParts[1] ? "?" + urlParts.slice(1).join("?") : "");
		}
		async function loadGetInitialProps(App, ctx) {
		    if (process.env.NODE_ENV !== "production") {
		        var _App_prototype;
		        if ((_App_prototype = App.prototype) == null ? void 0 : _App_prototype.getInitialProps) {
		            const message = '"' + getDisplayName(App) + '.getInitialProps()" is defined as an instance method - visit https://nextjs.org/docs/messages/get-initial-props-as-an-instance-method for more information.';
		            throw new Error(message);
		        }
		    }
		    // when called from _app `ctx` is nested in `ctx`
		    const res = ctx.res || ctx.ctx && ctx.ctx.res;
		    if (!App.getInitialProps) {
		        if (ctx.ctx && ctx.Component) {
		            // @ts-ignore pageProps default
		            return {
		                pageProps: await loadGetInitialProps(ctx.Component, ctx.ctx)
		            };
		        }
		        return {};
		    }
		    const props = await App.getInitialProps(ctx);
		    if (res && isResSent(res)) {
		        return props;
		    }
		    if (!props) {
		        const message = '"' + getDisplayName(App) + '.getInitialProps()" should resolve to an object. But found "' + props + '" instead.';
		        throw new Error(message);
		    }
		    if (process.env.NODE_ENV !== "production") {
		        if (Object.keys(props).length === 0 && !ctx.ctx) {
		            console.warn("" + getDisplayName(App) + " returned an empty object from `getInitialProps`. This de-optimizes and prevents automatic static optimization. https://nextjs.org/docs/messages/empty-object-getInitialProps");
		        }
		    }
		    return props;
		}
		const SP = typeof performance !== "undefined";
		const ST = SP && [
		    "mark",
		    "measure",
		    "getEntriesByName"
		].every((method)=>typeof performance[method] === "function");
		class DecodeError extends Error {
		}
		class NormalizeError extends Error {
		}
		class PageNotFoundError extends Error {
		    constructor(page){
		        super();
		        this.code = "ENOENT";
		        this.name = "PageNotFoundError";
		        this.message = "Cannot find module for page: " + page;
		    }
		}
		class MissingStaticPage extends Error {
		    constructor(page, message){
		        super();
		        this.message = "Failed to load static file for page: " + page + " " + message;
		    }
		}
		class MiddlewareNotFoundError extends Error {
		    constructor(){
		        super();
		        this.code = "ENOENT";
		        this.message = "Cannot find the middleware module";
		    }
		}
		function stringifyError(error) {
		    return JSON.stringify({
		        message: error.message,
		        stack: error.stack
		    });
		}

		
	} (utils$1));
	return utils$1;
}

var normalizeTrailingSlash = {exports: {}};

var removeTrailingSlash = {};

/**
 * Removes the trailing slash for a given route or page path. Preserves the
 * root page. Examples:
 *   - `/foo/bar/` -> `/foo/bar`
 *   - `/foo/bar` -> `/foo/bar`
 *   - `/` -> `/`
 */

var hasRequiredRemoveTrailingSlash;

function requireRemoveTrailingSlash () {
	if (hasRequiredRemoveTrailingSlash) return removeTrailingSlash;
	hasRequiredRemoveTrailingSlash = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", {
		    value: true
		});
		Object.defineProperty(exports, "removeTrailingSlash", {
		    enumerable: true,
		    get: function() {
		        return removeTrailingSlash;
		    }
		});
		function removeTrailingSlash(route) {
		    return route.replace(/\/$/, "") || "/";
		}

		
	} (removeTrailingSlash));
	return removeTrailingSlash;
}

var parsePath = {};

/**
 * Given a path this function will find the pathname, query and hash and return
 * them. This is useful to parse full paths on the client side.
 * @param path A path to parse e.g. /foo/bar?id=1#hash
 */

var hasRequiredParsePath;

function requireParsePath () {
	if (hasRequiredParsePath) return parsePath;
	hasRequiredParsePath = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", {
		    value: true
		});
		Object.defineProperty(exports, "parsePath", {
		    enumerable: true,
		    get: function() {
		        return parsePath;
		    }
		});
		function parsePath(path) {
		    const hashIndex = path.indexOf("#");
		    const queryIndex = path.indexOf("?");
		    const hasQuery = queryIndex > -1 && (hashIndex < 0 || queryIndex < hashIndex);
		    if (hasQuery || hashIndex > -1) {
		        return {
		            pathname: path.substring(0, hasQuery ? queryIndex : hashIndex),
		            query: hasQuery ? path.substring(queryIndex, hashIndex > -1 ? hashIndex : undefined) : "",
		            hash: hashIndex > -1 ? path.slice(hashIndex) : ""
		        };
		    }
		    return {
		        pathname: path,
		        query: "",
		        hash: ""
		    };
		}

		
	} (parsePath));
	return parsePath;
}

var hasRequiredNormalizeTrailingSlash;

function requireNormalizeTrailingSlash () {
	if (hasRequiredNormalizeTrailingSlash) return normalizeTrailingSlash.exports;
	hasRequiredNormalizeTrailingSlash = 1;
	(function (module, exports) {
		Object.defineProperty(exports, "__esModule", {
		    value: true
		});
		Object.defineProperty(exports, "normalizePathTrailingSlash", {
		    enumerable: true,
		    get: function() {
		        return normalizePathTrailingSlash;
		    }
		});
		const _removetrailingslash = requireRemoveTrailingSlash();
		const _parsepath = requireParsePath();
		const normalizePathTrailingSlash = (path)=>{
		    if (!path.startsWith("/") || process.env.__NEXT_MANUAL_TRAILING_SLASH) {
		        return path;
		    }
		    const { pathname, query, hash } = (0, _parsepath.parsePath)(path);
		    if (process.env.__NEXT_TRAILING_SLASH) {
		        if (/\.[^/]+\/?$/.test(pathname)) {
		            return "" + (0, _removetrailingslash.removeTrailingSlash)(pathname) + query + hash;
		        } else if (pathname.endsWith("/")) {
		            return "" + pathname + query + hash;
		        } else {
		            return pathname + "/" + query + hash;
		        }
		    }
		    return "" + (0, _removetrailingslash.removeTrailingSlash)(pathname) + query + hash;
		};

		if ((typeof exports.default === 'function' || (typeof exports.default === 'object' && exports.default !== null)) && typeof exports.default.__esModule === 'undefined') {
		  Object.defineProperty(exports.default, '__esModule', { value: true });
		  Object.assign(exports.default, exports);
		  module.exports = exports.default;
		}

		
	} (normalizeTrailingSlash, normalizeTrailingSlash.exports));
	return normalizeTrailingSlash.exports;
}

var isLocalUrl = {};

var hasBasePath = {exports: {}};

var pathHasPrefix = {};

var hasRequiredPathHasPrefix;

function requirePathHasPrefix () {
	if (hasRequiredPathHasPrefix) return pathHasPrefix;
	hasRequiredPathHasPrefix = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", {
		    value: true
		});
		Object.defineProperty(exports, "pathHasPrefix", {
		    enumerable: true,
		    get: function() {
		        return pathHasPrefix;
		    }
		});
		const _parsepath = requireParsePath();
		function pathHasPrefix(path, prefix) {
		    if (typeof path !== "string") {
		        return false;
		    }
		    const { pathname } = (0, _parsepath.parsePath)(path);
		    return pathname === prefix || pathname.startsWith(prefix + "/");
		}

		
	} (pathHasPrefix));
	return pathHasPrefix;
}

var hasRequiredHasBasePath;

function requireHasBasePath () {
	if (hasRequiredHasBasePath) return hasBasePath.exports;
	hasRequiredHasBasePath = 1;
	(function (module, exports) {
		Object.defineProperty(exports, "__esModule", {
		    value: true
		});
		Object.defineProperty(exports, "hasBasePath", {
		    enumerable: true,
		    get: function() {
		        return hasBasePath;
		    }
		});
		const _pathhasprefix = requirePathHasPrefix();
		const basePath = process.env.__NEXT_ROUTER_BASEPATH || "";
		function hasBasePath(path) {
		    return (0, _pathhasprefix.pathHasPrefix)(path, basePath);
		}

		if ((typeof exports.default === 'function' || (typeof exports.default === 'object' && exports.default !== null)) && typeof exports.default.__esModule === 'undefined') {
		  Object.defineProperty(exports.default, '__esModule', { value: true });
		  Object.assign(exports.default, exports);
		  module.exports = exports.default;
		}

		
	} (hasBasePath, hasBasePath.exports));
	return hasBasePath.exports;
}

var hasRequiredIsLocalUrl;

function requireIsLocalUrl () {
	if (hasRequiredIsLocalUrl) return isLocalUrl;
	hasRequiredIsLocalUrl = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", {
		    value: true
		});
		Object.defineProperty(exports, "isLocalURL", {
		    enumerable: true,
		    get: function() {
		        return isLocalURL;
		    }
		});
		const _utils = requireUtils$1();
		const _hasbasepath = requireHasBasePath();
		function isLocalURL(url) {
		    // prevent a hydration mismatch on href for url with anchor refs
		    if (!(0, _utils.isAbsoluteUrl)(url)) return true;
		    try {
		        // absolute urls can be local if they are on the same origin
		        const locationOrigin = (0, _utils.getLocationOrigin)();
		        const resolved = new URL(url, locationOrigin);
		        return resolved.origin === locationOrigin && (0, _hasbasepath.hasBasePath)(resolved.pathname);
		    } catch (_) {
		        return false;
		    }
		}

		
	} (isLocalUrl));
	return isLocalUrl;
}

var utils = {};

var sortedRoutes = {};

var hasRequiredSortedRoutes;

function requireSortedRoutes () {
	if (hasRequiredSortedRoutes) return sortedRoutes;
	hasRequiredSortedRoutes = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", {
		    value: true
		});
		Object.defineProperty(exports, "getSortedRoutes", {
		    enumerable: true,
		    get: function() {
		        return getSortedRoutes;
		    }
		});
		class UrlNode {
		    insert(urlPath) {
		        this._insert(urlPath.split("/").filter(Boolean), [], false);
		    }
		    smoosh() {
		        return this._smoosh();
		    }
		    _smoosh(prefix) {
		        if (prefix === void 0) prefix = "/";
		        const childrenPaths = [
		            ...this.children.keys()
		        ].sort();
		        if (this.slugName !== null) {
		            childrenPaths.splice(childrenPaths.indexOf("[]"), 1);
		        }
		        if (this.restSlugName !== null) {
		            childrenPaths.splice(childrenPaths.indexOf("[...]"), 1);
		        }
		        if (this.optionalRestSlugName !== null) {
		            childrenPaths.splice(childrenPaths.indexOf("[[...]]"), 1);
		        }
		        const routes = childrenPaths.map((c)=>this.children.get(c)._smoosh("" + prefix + c + "/")).reduce((prev, curr)=>[
		                ...prev,
		                ...curr
		            ], []);
		        if (this.slugName !== null) {
		            routes.push(...this.children.get("[]")._smoosh(prefix + "[" + this.slugName + "]/"));
		        }
		        if (!this.placeholder) {
		            const r = prefix === "/" ? "/" : prefix.slice(0, -1);
		            if (this.optionalRestSlugName != null) {
		                throw new Error('You cannot define a route with the same specificity as a optional catch-all route ("' + r + '" and "' + r + "[[..." + this.optionalRestSlugName + ']]").');
		            }
		            routes.unshift(r);
		        }
		        if (this.restSlugName !== null) {
		            routes.push(...this.children.get("[...]")._smoosh(prefix + "[..." + this.restSlugName + "]/"));
		        }
		        if (this.optionalRestSlugName !== null) {
		            routes.push(...this.children.get("[[...]]")._smoosh(prefix + "[[..." + this.optionalRestSlugName + "]]/"));
		        }
		        return routes;
		    }
		    _insert(urlPaths, slugNames, isCatchAll) {
		        if (urlPaths.length === 0) {
		            this.placeholder = false;
		            return;
		        }
		        if (isCatchAll) {
		            throw new Error("Catch-all must be the last part of the URL.");
		        }
		        // The next segment in the urlPaths list
		        let nextSegment = urlPaths[0];
		        // Check if the segment matches `[something]`
		        if (nextSegment.startsWith("[") && nextSegment.endsWith("]")) {
		            // Strip `[` and `]`, leaving only `something`
		            let segmentName = nextSegment.slice(1, -1);
		            let isOptional = false;
		            if (segmentName.startsWith("[") && segmentName.endsWith("]")) {
		                // Strip optional `[` and `]`, leaving only `something`
		                segmentName = segmentName.slice(1, -1);
		                isOptional = true;
		            }
		            if (segmentName.startsWith("...")) {
		                // Strip `...`, leaving only `something`
		                segmentName = segmentName.substring(3);
		                isCatchAll = true;
		            }
		            if (segmentName.startsWith("[") || segmentName.endsWith("]")) {
		                throw new Error("Segment names may not start or end with extra brackets ('" + segmentName + "').");
		            }
		            if (segmentName.startsWith(".")) {
		                throw new Error("Segment names may not start with erroneous periods ('" + segmentName + "').");
		            }
		            function handleSlug(previousSlug, nextSlug) {
		                if (previousSlug !== null) {
		                    // If the specific segment already has a slug but the slug is not `something`
		                    // This prevents collisions like:
		                    // pages/[post]/index.js
		                    // pages/[id]/index.js
		                    // Because currently multiple dynamic params on the same segment level are not supported
		                    if (previousSlug !== nextSlug) {
		                        // TODO: This error seems to be confusing for users, needs an error link, the description can be based on above comment.
		                        throw new Error("You cannot use different slug names for the same dynamic path ('" + previousSlug + "' !== '" + nextSlug + "').");
		                    }
		                }
		                slugNames.forEach((slug)=>{
		                    if (slug === nextSlug) {
		                        throw new Error('You cannot have the same slug name "' + nextSlug + '" repeat within a single dynamic path');
		                    }
		                    if (slug.replace(/\W/g, "") === nextSegment.replace(/\W/g, "")) {
		                        throw new Error('You cannot have the slug names "' + slug + '" and "' + nextSlug + '" differ only by non-word symbols within a single dynamic path');
		                    }
		                });
		                slugNames.push(nextSlug);
		            }
		            if (isCatchAll) {
		                if (isOptional) {
		                    if (this.restSlugName != null) {
		                        throw new Error('You cannot use both an required and optional catch-all route at the same level ("[...' + this.restSlugName + ']" and "' + urlPaths[0] + '" ).');
		                    }
		                    handleSlug(this.optionalRestSlugName, segmentName);
		                    // slugName is kept as it can only be one particular slugName
		                    this.optionalRestSlugName = segmentName;
		                    // nextSegment is overwritten to [[...]] so that it can later be sorted specifically
		                    nextSegment = "[[...]]";
		                } else {
		                    if (this.optionalRestSlugName != null) {
		                        throw new Error('You cannot use both an optional and required catch-all route at the same level ("[[...' + this.optionalRestSlugName + ']]" and "' + urlPaths[0] + '").');
		                    }
		                    handleSlug(this.restSlugName, segmentName);
		                    // slugName is kept as it can only be one particular slugName
		                    this.restSlugName = segmentName;
		                    // nextSegment is overwritten to [...] so that it can later be sorted specifically
		                    nextSegment = "[...]";
		                }
		            } else {
		                if (isOptional) {
		                    throw new Error('Optional route parameters are not yet supported ("' + urlPaths[0] + '").');
		                }
		                handleSlug(this.slugName, segmentName);
		                // slugName is kept as it can only be one particular slugName
		                this.slugName = segmentName;
		                // nextSegment is overwritten to [] so that it can later be sorted specifically
		                nextSegment = "[]";
		            }
		        }
		        // If this UrlNode doesn't have the nextSegment yet we create a new child UrlNode
		        if (!this.children.has(nextSegment)) {
		            this.children.set(nextSegment, new UrlNode());
		        }
		        this.children.get(nextSegment)._insert(urlPaths.slice(1), slugNames, isCatchAll);
		    }
		    constructor(){
		        this.placeholder = true;
		        this.children = new Map();
		        this.slugName = null;
		        this.restSlugName = null;
		        this.optionalRestSlugName = null;
		    }
		}
		function getSortedRoutes(normalizedPages) {
		    // First the UrlNode is created, and every UrlNode can have only 1 dynamic segment
		    // Eg you can't have pages/[post]/abc.js and pages/[hello]/something-else.js
		    // Only 1 dynamic segment per nesting level
		    // So in the case that is test/integration/dynamic-routing it'll be this:
		    // pages/[post]/comments.js
		    // pages/blog/[post]/comment/[id].js
		    // Both are fine because `pages/[post]` and `pages/blog` are on the same level
		    // So in this case `UrlNode` created here has `this.slugName === 'post'`
		    // And since your PR passed through `slugName` as an array basically it'd including it in too many possibilities
		    // Instead what has to be passed through is the upwards path's dynamic names
		    const root = new UrlNode();
		    // Here the `root` gets injected multiple paths, and insert will break them up into sublevels
		    normalizedPages.forEach((pagePath)=>root.insert(pagePath));
		    // Smoosh will then sort those sublevels up to the point where you get the correct route definition priority
		    return root.smoosh();
		}

		
	} (sortedRoutes));
	return sortedRoutes;
}

var isDynamic = {};

var interceptionRoutes = {};

var appPaths = {};

var ensureLeadingSlash = {};

/**
 * For a given page path, this function ensures that there is a leading slash.
 * If there is not a leading slash, one is added, otherwise it is noop.
 */

var hasRequiredEnsureLeadingSlash;

function requireEnsureLeadingSlash () {
	if (hasRequiredEnsureLeadingSlash) return ensureLeadingSlash;
	hasRequiredEnsureLeadingSlash = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", {
		    value: true
		});
		Object.defineProperty(exports, "ensureLeadingSlash", {
		    enumerable: true,
		    get: function() {
		        return ensureLeadingSlash;
		    }
		});
		function ensureLeadingSlash(path) {
		    return path.startsWith("/") ? path : "/" + path;
		}

		
	} (ensureLeadingSlash));
	return ensureLeadingSlash;
}

var segment = {};

var hasRequiredSegment;

function requireSegment () {
	if (hasRequiredSegment) return segment;
	hasRequiredSegment = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", {
		    value: true
		});
		Object.defineProperty(exports, "isGroupSegment", {
		    enumerable: true,
		    get: function() {
		        return isGroupSegment;
		    }
		});
		function isGroupSegment(segment) {
		    // Use array[0] for performant purpose
		    return segment[0] === "(" && segment.endsWith(")");
		}

		
	} (segment));
	return segment;
}

var hasRequiredAppPaths;

function requireAppPaths () {
	if (hasRequiredAppPaths) return appPaths;
	hasRequiredAppPaths = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", {
		    value: true
		});
		function _export(target, all) {
		    for(var name in all)Object.defineProperty(target, name, {
		        enumerable: true,
		        get: all[name]
		    });
		}
		_export(exports, {
		    normalizeAppPath: function() {
		        return normalizeAppPath;
		    },
		    normalizeRscURL: function() {
		        return normalizeRscURL;
		    }
		});
		const _ensureleadingslash = requireEnsureLeadingSlash();
		const _segment = requireSegment();
		function normalizeAppPath(route) {
		    return (0, _ensureleadingslash.ensureLeadingSlash)(route.split("/").reduce((pathname, segment, index, segments)=>{
		        // Empty segments are ignored.
		        if (!segment) {
		            return pathname;
		        }
		        // Groups are ignored.
		        if ((0, _segment.isGroupSegment)(segment)) {
		            return pathname;
		        }
		        // Parallel segments are ignored.
		        if (segment[0] === "@") {
		            return pathname;
		        }
		        // The last segment (if it's a leaf) should be ignored.
		        if ((segment === "page" || segment === "route") && index === segments.length - 1) {
		            return pathname;
		        }
		        return pathname + "/" + segment;
		    }, ""));
		}
		function normalizeRscURL(url) {
		    return url.replace(/\.rsc($|\?)/, // $1 ensures `?` is preserved
		    "$1");
		}

		
	} (appPaths));
	return appPaths;
}

var hasRequiredInterceptionRoutes;

function requireInterceptionRoutes () {
	if (hasRequiredInterceptionRoutes) return interceptionRoutes;
	hasRequiredInterceptionRoutes = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", {
		    value: true
		});
		function _export(target, all) {
		    for(var name in all)Object.defineProperty(target, name, {
		        enumerable: true,
		        get: all[name]
		    });
		}
		_export(exports, {
		    INTERCEPTION_ROUTE_MARKERS: function() {
		        return INTERCEPTION_ROUTE_MARKERS;
		    },
		    isInterceptionRouteAppPath: function() {
		        return isInterceptionRouteAppPath;
		    },
		    extractInterceptionRouteInformation: function() {
		        return extractInterceptionRouteInformation;
		    }
		});
		const _apppaths = requireAppPaths();
		const INTERCEPTION_ROUTE_MARKERS = [
		    "(..)(..)",
		    "(.)",
		    "(..)",
		    "(...)"
		];
		function isInterceptionRouteAppPath(path) {
		    // TODO-APP: add more serious validation
		    return path.split("/").find((segment)=>INTERCEPTION_ROUTE_MARKERS.find((m)=>segment.startsWith(m))) !== undefined;
		}
		function extractInterceptionRouteInformation(path) {
		    let interceptingRoute, marker, interceptedRoute;
		    for (const segment of path.split("/")){
		        marker = INTERCEPTION_ROUTE_MARKERS.find((m)=>segment.startsWith(m));
		        if (marker) {
		            [interceptingRoute, interceptedRoute] = path.split(marker, 2);
		            break;
		        }
		    }
		    if (!interceptingRoute || !marker || !interceptedRoute) {
		        throw new Error(`Invalid interception route: ${path}. Must be in the format /<intercepting route>/(..|...|..)(..)/<intercepted route>`);
		    }
		    interceptingRoute = (0, _apppaths.normalizeAppPath)(interceptingRoute) // normalize the path, e.g. /(blog)/feed -> /feed
		    ;
		    switch(marker){
		        case "(.)":
		            // (.) indicates that we should match with sibling routes, so we just need to append the intercepted route to the intercepting route
		            if (interceptingRoute === "/") {
		                interceptedRoute = `/${interceptedRoute}`;
		            } else {
		                interceptedRoute = interceptingRoute + "/" + interceptedRoute;
		            }
		            break;
		        case "(..)":
		            // (..) indicates that we should match at one level up, so we need to remove the last segment of the intercepting route
		            if (interceptingRoute === "/") {
		                throw new Error(`Invalid interception route: ${path}. Cannot use (..) marker at the root level, use (.) instead.`);
		            }
		            interceptedRoute = interceptingRoute.split("/").slice(0, -1).concat(interceptedRoute).join("/");
		            break;
		        case "(...)":
		            // (...) will match the route segment in the root directory, so we need to use the root directory to prepend the intercepted route
		            interceptedRoute = "/" + interceptedRoute;
		            break;
		        case "(..)(..)":
		            // (..)(..) indicates that we should match at two levels up, so we need to remove the last two segments of the intercepting route
		            const splitInterceptingRoute = interceptingRoute.split("/");
		            if (splitInterceptingRoute.length <= 2) {
		                throw new Error(`Invalid interception route: ${path}. Cannot use (..)(..) marker at the root level or one level up.`);
		            }
		            interceptedRoute = splitInterceptingRoute.slice(0, -2).concat(interceptedRoute).join("/");
		            break;
		        default:
		            throw new Error("Invariant: unexpected marker");
		    }
		    return {
		        interceptingRoute,
		        interceptedRoute
		    };
		}

		
	} (interceptionRoutes));
	return interceptionRoutes;
}

var hasRequiredIsDynamic;

function requireIsDynamic () {
	if (hasRequiredIsDynamic) return isDynamic;
	hasRequiredIsDynamic = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", {
		    value: true
		});
		Object.defineProperty(exports, "isDynamicRoute", {
		    enumerable: true,
		    get: function() {
		        return isDynamicRoute;
		    }
		});
		const _interceptionroutes = requireInterceptionRoutes();
		// Identify /[param]/ in route string
		const TEST_ROUTE = /\/\[[^/]+?\](?=\/|$)/;
		function isDynamicRoute(route) {
		    if ((0, _interceptionroutes.isInterceptionRouteAppPath)(route)) {
		        route = (0, _interceptionroutes.extractInterceptionRouteInformation)(route).interceptedRoute;
		    }
		    return TEST_ROUTE.test(route);
		}

		
	} (isDynamic));
	return isDynamic;
}

var hasRequiredUtils;

function requireUtils () {
	if (hasRequiredUtils) return utils;
	hasRequiredUtils = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", {
		    value: true
		});
		function _export(target, all) {
		    for(var name in all)Object.defineProperty(target, name, {
		        enumerable: true,
		        get: all[name]
		    });
		}
		_export(exports, {
		    getSortedRoutes: function() {
		        return _sortedroutes.getSortedRoutes;
		    },
		    isDynamicRoute: function() {
		        return _isdynamic.isDynamicRoute;
		    }
		});
		const _sortedroutes = requireSortedRoutes();
		const _isdynamic = requireIsDynamic();

		
	} (utils));
	return utils;
}

var interpolateAs = {};

var routeMatcher = {};

var hasRequiredRouteMatcher;

function requireRouteMatcher () {
	if (hasRequiredRouteMatcher) return routeMatcher;
	hasRequiredRouteMatcher = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", {
		    value: true
		});
		Object.defineProperty(exports, "getRouteMatcher", {
		    enumerable: true,
		    get: function() {
		        return getRouteMatcher;
		    }
		});
		const _utils = requireUtils$1();
		function getRouteMatcher(param) {
		    let { re, groups } = param;
		    return (pathname)=>{
		        const routeMatch = re.exec(pathname);
		        if (!routeMatch) {
		            return false;
		        }
		        const decode = (param)=>{
		            try {
		                return decodeURIComponent(param);
		            } catch (_) {
		                throw new _utils.DecodeError("failed to decode param");
		            }
		        };
		        const params = {};
		        Object.keys(groups).forEach((slugName)=>{
		            const g = groups[slugName];
		            const m = routeMatch[g.pos];
		            if (m !== undefined) {
		                params[slugName] = ~m.indexOf("/") ? m.split("/").map((entry)=>decode(entry)) : g.repeat ? [
		                    decode(m)
		                ] : decode(m);
		            }
		        });
		        return params;
		    };
		}

		
	} (routeMatcher));
	return routeMatcher;
}

var routeRegex = {};

var escapeRegexp = {};

var hasRequiredEscapeRegexp;

function requireEscapeRegexp () {
	if (hasRequiredEscapeRegexp) return escapeRegexp;
	hasRequiredEscapeRegexp = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", {
		    value: true
		});
		Object.defineProperty(exports, "escapeStringRegexp", {
		    enumerable: true,
		    get: function() {
		        return escapeStringRegexp;
		    }
		});
		const reHasRegExp = /[|\\{}()[\]^$+*?.-]/;
		const reReplaceRegExp = /[|\\{}()[\]^$+*?.-]/g;
		function escapeStringRegexp(str) {
		    // see also: https://github.com/lodash/lodash/blob/2da024c3b4f9947a48517639de7560457cd4ec6c/escapeRegExp.js#L23
		    if (reHasRegExp.test(str)) {
		        return str.replace(reReplaceRegExp, "\\$&");
		    }
		    return str;
		}

		
	} (escapeRegexp));
	return escapeRegexp;
}

var hasRequiredRouteRegex;

function requireRouteRegex () {
	if (hasRequiredRouteRegex) return routeRegex;
	hasRequiredRouteRegex = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", {
		    value: true
		});
		function _export(target, all) {
		    for(var name in all)Object.defineProperty(target, name, {
		        enumerable: true,
		        get: all[name]
		    });
		}
		_export(exports, {
		    getRouteRegex: function() {
		        return getRouteRegex;
		    },
		    getNamedRouteRegex: function() {
		        return getNamedRouteRegex;
		    },
		    getNamedMiddlewareRegex: function() {
		        return getNamedMiddlewareRegex;
		    }
		});
		const _interceptionroutes = requireInterceptionRoutes();
		const _escaperegexp = requireEscapeRegexp();
		const _removetrailingslash = requireRemoveTrailingSlash();
		const NEXT_QUERY_PARAM_PREFIX = "nxtP";
		const NEXT_INTERCEPTION_MARKER_PREFIX = "nxtI";
		/**
		 * Parses a given parameter from a route to a data structure that can be used
		 * to generate the parametrized route. Examples:
		 *   - `[...slug]` -> `{ key: 'slug', repeat: true, optional: true }`
		 *   - `...slug` -> `{ key: 'slug', repeat: true, optional: false }`
		 *   - `[foo]` -> `{ key: 'foo', repeat: false, optional: true }`
		 *   - `bar` -> `{ key: 'bar', repeat: false, optional: false }`
		 */ function parseParameter(param) {
		    const optional = param.startsWith("[") && param.endsWith("]");
		    if (optional) {
		        param = param.slice(1, -1);
		    }
		    const repeat = param.startsWith("...");
		    if (repeat) {
		        param = param.slice(3);
		    }
		    return {
		        key: param,
		        repeat,
		        optional
		    };
		}
		function getParametrizedRoute(route) {
		    const segments = (0, _removetrailingslash.removeTrailingSlash)(route).slice(1).split("/");
		    const groups = {};
		    let groupIndex = 1;
		    return {
		        parameterizedRoute: segments.map((segment)=>{
		            const markerMatch = _interceptionroutes.INTERCEPTION_ROUTE_MARKERS.find((m)=>segment.startsWith(m));
		            const paramMatches = segment.match(/\[((?:\[.*\])|.+)\]/) // Check for parameters
		            ;
		            if (markerMatch && paramMatches) {
		                const { key, optional, repeat } = parseParameter(paramMatches[1]);
		                groups[key] = {
		                    pos: groupIndex++,
		                    repeat,
		                    optional
		                };
		                return "/" + (0, _escaperegexp.escapeStringRegexp)(markerMatch) + "([^/]+?)";
		            } else if (paramMatches) {
		                const { key, repeat, optional } = parseParameter(paramMatches[1]);
		                groups[key] = {
		                    pos: groupIndex++,
		                    repeat,
		                    optional
		                };
		                return repeat ? optional ? "(?:/(.+?))?" : "/(.+?)" : "/([^/]+?)";
		            } else {
		                return "/" + (0, _escaperegexp.escapeStringRegexp)(segment);
		            }
		        }).join(""),
		        groups
		    };
		}
		function getRouteRegex(normalizedRoute) {
		    const { parameterizedRoute, groups } = getParametrizedRoute(normalizedRoute);
		    return {
		        re: new RegExp("^" + parameterizedRoute + "(?:/)?$"),
		        groups: groups
		    };
		}
		/**
		 * Builds a function to generate a minimal routeKey using only a-z and minimal
		 * number of characters.
		 */ function buildGetSafeRouteKey() {
		    let i = 0;
		    return ()=>{
		        let routeKey = "";
		        let j = ++i;
		        while(j > 0){
		            routeKey += String.fromCharCode(97 + (j - 1) % 26);
		            j = Math.floor((j - 1) / 26);
		        }
		        return routeKey;
		    };
		}
		function getSafeKeyFromSegment(param) {
		    let { getSafeRouteKey, segment, routeKeys, keyPrefix } = param;
		    const { key, optional, repeat } = parseParameter(segment);
		    // replace any non-word characters since they can break
		    // the named regex
		    let cleanedKey = key.replace(/\W/g, "");
		    if (keyPrefix) {
		        cleanedKey = "" + keyPrefix + cleanedKey;
		    }
		    let invalidKey = false;
		    // check if the key is still invalid and fallback to using a known
		    // safe key
		    if (cleanedKey.length === 0 || cleanedKey.length > 30) {
		        invalidKey = true;
		    }
		    if (!isNaN(parseInt(cleanedKey.slice(0, 1)))) {
		        invalidKey = true;
		    }
		    if (invalidKey) {
		        cleanedKey = getSafeRouteKey();
		    }
		    if (keyPrefix) {
		        routeKeys[cleanedKey] = "" + keyPrefix + key;
		    } else {
		        routeKeys[cleanedKey] = "" + key;
		    }
		    return repeat ? optional ? "(?:/(?<" + cleanedKey + ">.+?))?" : "/(?<" + cleanedKey + ">.+?)" : "/(?<" + cleanedKey + ">[^/]+?)";
		}
		function getNamedParametrizedRoute(route, prefixRouteKeys) {
		    const segments = (0, _removetrailingslash.removeTrailingSlash)(route).slice(1).split("/");
		    const getSafeRouteKey = buildGetSafeRouteKey();
		    const routeKeys = {};
		    return {
		        namedParameterizedRoute: segments.map((segment)=>{
		            const hasInterceptionMarker = _interceptionroutes.INTERCEPTION_ROUTE_MARKERS.some((m)=>segment.startsWith(m));
		            const paramMatches = segment.match(/\[((?:\[.*\])|.+)\]/) // Check for parameters
		            ;
		            if (hasInterceptionMarker && paramMatches) {
		                return getSafeKeyFromSegment({
		                    getSafeRouteKey,
		                    segment: paramMatches[1],
		                    routeKeys,
		                    keyPrefix: prefixRouteKeys ? NEXT_INTERCEPTION_MARKER_PREFIX : undefined
		                });
		            } else if (paramMatches) {
		                return getSafeKeyFromSegment({
		                    getSafeRouteKey,
		                    segment: paramMatches[1],
		                    routeKeys,
		                    keyPrefix: prefixRouteKeys ? NEXT_QUERY_PARAM_PREFIX : undefined
		                });
		            } else {
		                return "/" + (0, _escaperegexp.escapeStringRegexp)(segment);
		            }
		        }).join(""),
		        routeKeys
		    };
		}
		function getNamedRouteRegex(normalizedRoute, prefixRouteKey) {
		    const result = getNamedParametrizedRoute(normalizedRoute, prefixRouteKey);
		    return {
		        ...getRouteRegex(normalizedRoute),
		        namedRegex: "^" + result.namedParameterizedRoute + "(?:/)?$",
		        routeKeys: result.routeKeys
		    };
		}
		function getNamedMiddlewareRegex(normalizedRoute, options) {
		    const { parameterizedRoute } = getParametrizedRoute(normalizedRoute);
		    const { catchAll = true } = options;
		    if (parameterizedRoute === "/") {
		        let catchAllRegex = catchAll ? ".*" : "";
		        return {
		            namedRegex: "^/" + catchAllRegex + "$"
		        };
		    }
		    const { namedParameterizedRoute } = getNamedParametrizedRoute(normalizedRoute, false);
		    let catchAllGroupedRegex = catchAll ? "(?:(/.*)?)" : "";
		    return {
		        namedRegex: "^" + namedParameterizedRoute + catchAllGroupedRegex + "$"
		    };
		}

		
	} (routeRegex));
	return routeRegex;
}

var hasRequiredInterpolateAs;

function requireInterpolateAs () {
	if (hasRequiredInterpolateAs) return interpolateAs;
	hasRequiredInterpolateAs = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", {
		    value: true
		});
		Object.defineProperty(exports, "interpolateAs", {
		    enumerable: true,
		    get: function() {
		        return interpolateAs;
		    }
		});
		const _routematcher = requireRouteMatcher();
		const _routeregex = requireRouteRegex();
		function interpolateAs(route, asPathname, query) {
		    let interpolatedRoute = "";
		    const dynamicRegex = (0, _routeregex.getRouteRegex)(route);
		    const dynamicGroups = dynamicRegex.groups;
		    const dynamicMatches = // Try to match the dynamic route against the asPath
		    (asPathname !== route ? (0, _routematcher.getRouteMatcher)(dynamicRegex)(asPathname) : "") || // Fall back to reading the values from the href
		    // TODO: should this take priority; also need to change in the router.
		    query;
		    interpolatedRoute = route;
		    const params = Object.keys(dynamicGroups);
		    if (!params.every((param)=>{
		        let value = dynamicMatches[param] || "";
		        const { repeat, optional } = dynamicGroups[param];
		        // support single-level catch-all
		        // TODO: more robust handling for user-error (passing `/`)
		        let replaced = "[" + (repeat ? "..." : "") + param + "]";
		        if (optional) {
		            replaced = (!value ? "/" : "") + "[" + replaced + "]";
		        }
		        if (repeat && !Array.isArray(value)) value = [
		            value
		        ];
		        return (optional || param in dynamicMatches) && // Interpolate group into data URL if present
		        (interpolatedRoute = interpolatedRoute.replace(replaced, repeat ? value.map(// these values should be fully encoded instead of just
		        // path delimiter escaped since they are being inserted
		        // into the URL and we expect URL encoded segments
		        // when parsing dynamic route params
		        (segment)=>encodeURIComponent(segment)).join("/") : encodeURIComponent(value)) || "/");
		    })) {
		        interpolatedRoute = "" // did not satisfy all requirements
		        ;
		    // n.b. We ignore this error because we handle warning for this case in
		    // development in the `<Link>` component directly.
		    }
		    return {
		        params,
		        result: interpolatedRoute
		    };
		}

		
	} (interpolateAs));
	return interpolateAs;
}

var hasRequiredResolveHref;

function requireResolveHref () {
	if (hasRequiredResolveHref) return resolveHref.exports;
	hasRequiredResolveHref = 1;
	(function (module, exports) {
		Object.defineProperty(exports, "__esModule", {
		    value: true
		});
		Object.defineProperty(exports, "resolveHref", {
		    enumerable: true,
		    get: function() {
		        return resolveHref;
		    }
		});
		const _querystring = requireQuerystring();
		const _formaturl = requireFormatUrl();
		const _omit = requireOmit();
		const _utils = requireUtils$1();
		const _normalizetrailingslash = requireNormalizeTrailingSlash();
		const _islocalurl = requireIsLocalUrl();
		const _utils1 = requireUtils();
		const _interpolateas = requireInterpolateAs();
		function resolveHref(router, href, resolveAs) {
		    // we use a dummy base url for relative urls
		    let base;
		    let urlAsString = typeof href === "string" ? href : (0, _formaturl.formatWithValidation)(href);
		    // repeated slashes and backslashes in the URL are considered
		    // invalid and will never match a Next.js page/file
		    const urlProtoMatch = urlAsString.match(/^[a-zA-Z]{1,}:\/\//);
		    const urlAsStringNoProto = urlProtoMatch ? urlAsString.slice(urlProtoMatch[0].length) : urlAsString;
		    const urlParts = urlAsStringNoProto.split("?", 1);
		    if ((urlParts[0] || "").match(/(\/\/|\\)/)) {
		        console.error("Invalid href '" + urlAsString + "' passed to next/router in page: '" + router.pathname + "'. Repeated forward-slashes (//) or backslashes \\ are not valid in the href.");
		        const normalizedUrl = (0, _utils.normalizeRepeatedSlashes)(urlAsStringNoProto);
		        urlAsString = (urlProtoMatch ? urlProtoMatch[0] : "") + normalizedUrl;
		    }
		    // Return because it cannot be routed by the Next.js router
		    if (!(0, _islocalurl.isLocalURL)(urlAsString)) {
		        return resolveAs ? [
		            urlAsString
		        ] : urlAsString;
		    }
		    try {
		        base = new URL(urlAsString.startsWith("#") ? router.asPath : router.pathname, "http://n");
		    } catch (_) {
		        // fallback to / for invalid asPath values e.g. //
		        base = new URL("/", "http://n");
		    }
		    try {
		        const finalUrl = new URL(urlAsString, base);
		        finalUrl.pathname = (0, _normalizetrailingslash.normalizePathTrailingSlash)(finalUrl.pathname);
		        let interpolatedAs = "";
		        if ((0, _utils1.isDynamicRoute)(finalUrl.pathname) && finalUrl.searchParams && resolveAs) {
		            const query = (0, _querystring.searchParamsToUrlQuery)(finalUrl.searchParams);
		            const { result, params } = (0, _interpolateas.interpolateAs)(finalUrl.pathname, finalUrl.pathname, query);
		            if (result) {
		                interpolatedAs = (0, _formaturl.formatWithValidation)({
		                    pathname: result,
		                    hash: finalUrl.hash,
		                    query: (0, _omit.omit)(query, params)
		                });
		            }
		        }
		        // if the origin didn't change, it means we received a relative href
		        const resolvedHref = finalUrl.origin === base.origin ? finalUrl.href.slice(finalUrl.origin.length) : finalUrl.href;
		        return resolveAs ? [
		            resolvedHref,
		            interpolatedAs || resolvedHref
		        ] : resolvedHref;
		    } catch (_) {
		        return resolveAs ? [
		            urlAsString
		        ] : urlAsString;
		    }
		}

		if ((typeof exports.default === 'function' || (typeof exports.default === 'object' && exports.default !== null)) && typeof exports.default.__esModule === 'undefined') {
		  Object.defineProperty(exports.default, '__esModule', { value: true });
		  Object.assign(exports.default, exports);
		  module.exports = exports.default;
		}

		
	} (resolveHref, resolveHref.exports));
	return resolveHref.exports;
}

var addLocale$1 = {exports: {}};

var addLocale = {};

var addPathPrefix = {};

var hasRequiredAddPathPrefix;

function requireAddPathPrefix () {
	if (hasRequiredAddPathPrefix) return addPathPrefix;
	hasRequiredAddPathPrefix = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", {
		    value: true
		});
		Object.defineProperty(exports, "addPathPrefix", {
		    enumerable: true,
		    get: function() {
		        return addPathPrefix;
		    }
		});
		const _parsepath = requireParsePath();
		function addPathPrefix(path, prefix) {
		    if (!path.startsWith("/") || !prefix) {
		        return path;
		    }
		    const { pathname, query, hash } = (0, _parsepath.parsePath)(path);
		    return "" + prefix + pathname + query + hash;
		}

		
	} (addPathPrefix));
	return addPathPrefix;
}

var hasRequiredAddLocale$1;

function requireAddLocale$1 () {
	if (hasRequiredAddLocale$1) return addLocale;
	hasRequiredAddLocale$1 = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", {
		    value: true
		});
		Object.defineProperty(exports, "addLocale", {
		    enumerable: true,
		    get: function() {
		        return addLocale;
		    }
		});
		const _addpathprefix = requireAddPathPrefix();
		const _pathhasprefix = requirePathHasPrefix();
		function addLocale(path, locale, defaultLocale, ignorePrefix) {
		    // If no locale was given or the locale is the default locale, we don't need
		    // to prefix the path.
		    if (!locale || locale === defaultLocale) return path;
		    const lower = path.toLowerCase();
		    // If the path is an API path or the path already has the locale prefix, we
		    // don't need to prefix the path.
		    if (!ignorePrefix) {
		        if ((0, _pathhasprefix.pathHasPrefix)(lower, "/api")) return path;
		        if ((0, _pathhasprefix.pathHasPrefix)(lower, "/" + locale.toLowerCase())) return path;
		    }
		    // Add the locale prefix to the path.
		    return (0, _addpathprefix.addPathPrefix)(path, "/" + locale);
		}

		
	} (addLocale));
	return addLocale;
}

var hasRequiredAddLocale;

function requireAddLocale () {
	if (hasRequiredAddLocale) return addLocale$1.exports;
	hasRequiredAddLocale = 1;
	(function (module, exports) {
		Object.defineProperty(exports, "__esModule", {
		    value: true
		});
		Object.defineProperty(exports, "addLocale", {
		    enumerable: true,
		    get: function() {
		        return addLocale;
		    }
		});
		const _normalizetrailingslash = requireNormalizeTrailingSlash();
		const addLocale = function(path) {
		    for(var _len = arguments.length, args = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++){
		        args[_key - 1] = arguments[_key];
		    }
		    if (process.env.__NEXT_I18N_SUPPORT) {
		        return (0, _normalizetrailingslash.normalizePathTrailingSlash)(requireAddLocale$1().addLocale(path, ...args));
		    }
		    return path;
		};

		if ((typeof exports.default === 'function' || (typeof exports.default === 'object' && exports.default !== null)) && typeof exports.default.__esModule === 'undefined') {
		  Object.defineProperty(exports.default, '__esModule', { value: true });
		  Object.assign(exports.default, exports);
		  module.exports = exports.default;
		}

		
	} (addLocale$1, addLocale$1.exports));
	return addLocale$1.exports;
}

var routerContext_sharedRuntime = {};

var hasRequiredRouterContext_sharedRuntime;

function requireRouterContext_sharedRuntime () {
	if (hasRequiredRouterContext_sharedRuntime) return routerContext_sharedRuntime;
	hasRequiredRouterContext_sharedRuntime = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", {
		    value: true
		});
		Object.defineProperty(exports, "RouterContext", {
		    enumerable: true,
		    get: function() {
		        return RouterContext;
		    }
		});
		const _interop_require_default = _interop_require_default$1;
		const _react = /*#__PURE__*/ _interop_require_default._(React__default);
		const RouterContext = _react.default.createContext(null);
		if (process.env.NODE_ENV !== "production") {
		    RouterContext.displayName = "RouterContext";
		}

		
	} (routerContext_sharedRuntime));
	return routerContext_sharedRuntime;
}

var appRouterContext_sharedRuntime = {};

var hasRequiredAppRouterContext_sharedRuntime;

function requireAppRouterContext_sharedRuntime () {
	if (hasRequiredAppRouterContext_sharedRuntime) return appRouterContext_sharedRuntime;
	hasRequiredAppRouterContext_sharedRuntime = 1;
	(function (exports) {
		"use client";
		Object.defineProperty(exports, "__esModule", {
		    value: true
		});
		function _export(target, all) {
		    for(var name in all)Object.defineProperty(target, name, {
		        enumerable: true,
		        get: all[name]
		    });
		}
		_export(exports, {
		    CacheStates: function() {
		        return CacheStates;
		    },
		    AppRouterContext: function() {
		        return AppRouterContext;
		    },
		    LayoutRouterContext: function() {
		        return LayoutRouterContext;
		    },
		    GlobalLayoutRouterContext: function() {
		        return GlobalLayoutRouterContext;
		    },
		    TemplateContext: function() {
		        return TemplateContext;
		    }
		});
		const _interop_require_default = _interop_require_default$1;
		const _react = /*#__PURE__*/ _interop_require_default._(React__default);
		var CacheStates;
		(function(CacheStates) {
		    CacheStates["LAZY_INITIALIZED"] = "LAZYINITIALIZED";
		    CacheStates["DATA_FETCH"] = "DATAFETCH";
		    CacheStates["READY"] = "READY";
		})(CacheStates || (CacheStates = {}));
		const AppRouterContext = _react.default.createContext(null);
		const LayoutRouterContext = _react.default.createContext(null);
		const GlobalLayoutRouterContext = _react.default.createContext(null);
		const TemplateContext = _react.default.createContext(null);
		if (process.env.NODE_ENV !== "production") {
		    AppRouterContext.displayName = "AppRouterContext";
		    LayoutRouterContext.displayName = "LayoutRouterContext";
		    GlobalLayoutRouterContext.displayName = "GlobalLayoutRouterContext";
		    TemplateContext.displayName = "TemplateContext";
		}

		
	} (appRouterContext_sharedRuntime));
	return appRouterContext_sharedRuntime;
}

var useIntersection = {exports: {}};

var requestIdleCallback = {exports: {}};

var hasRequiredRequestIdleCallback;

function requireRequestIdleCallback () {
	if (hasRequiredRequestIdleCallback) return requestIdleCallback.exports;
	hasRequiredRequestIdleCallback = 1;
	(function (module, exports) {
		Object.defineProperty(exports, "__esModule", {
		    value: true
		});
		function _export(target, all) {
		    for(var name in all)Object.defineProperty(target, name, {
		        enumerable: true,
		        get: all[name]
		    });
		}
		_export(exports, {
		    requestIdleCallback: function() {
		        return requestIdleCallback;
		    },
		    cancelIdleCallback: function() {
		        return cancelIdleCallback;
		    }
		});
		const requestIdleCallback = typeof self !== "undefined" && self.requestIdleCallback && self.requestIdleCallback.bind(window) || function(cb) {
		    let start = Date.now();
		    return self.setTimeout(function() {
		        cb({
		            didTimeout: false,
		            timeRemaining: function() {
		                return Math.max(0, 50 - (Date.now() - start));
		            }
		        });
		    }, 1);
		};
		const cancelIdleCallback = typeof self !== "undefined" && self.cancelIdleCallback && self.cancelIdleCallback.bind(window) || function(id) {
		    return clearTimeout(id);
		};

		if ((typeof exports.default === 'function' || (typeof exports.default === 'object' && exports.default !== null)) && typeof exports.default.__esModule === 'undefined') {
		  Object.defineProperty(exports.default, '__esModule', { value: true });
		  Object.assign(exports.default, exports);
		  module.exports = exports.default;
		}

		
	} (requestIdleCallback, requestIdleCallback.exports));
	return requestIdleCallback.exports;
}

var hasRequiredUseIntersection;

function requireUseIntersection () {
	if (hasRequiredUseIntersection) return useIntersection.exports;
	hasRequiredUseIntersection = 1;
	(function (module, exports) {
		Object.defineProperty(exports, "__esModule", {
		    value: true
		});
		Object.defineProperty(exports, "useIntersection", {
		    enumerable: true,
		    get: function() {
		        return useIntersection;
		    }
		});
		const _react = React__default;
		const _requestidlecallback = requireRequestIdleCallback();
		const hasIntersectionObserver = typeof IntersectionObserver === "function";
		const observers = new Map();
		const idList = [];
		function createObserver(options) {
		    const id = {
		        root: options.root || null,
		        margin: options.rootMargin || ""
		    };
		    const existing = idList.find((obj)=>obj.root === id.root && obj.margin === id.margin);
		    let instance;
		    if (existing) {
		        instance = observers.get(existing);
		        if (instance) {
		            return instance;
		        }
		    }
		    const elements = new Map();
		    const observer = new IntersectionObserver((entries)=>{
		        entries.forEach((entry)=>{
		            const callback = elements.get(entry.target);
		            const isVisible = entry.isIntersecting || entry.intersectionRatio > 0;
		            if (callback && isVisible) {
		                callback(isVisible);
		            }
		        });
		    }, options);
		    instance = {
		        id,
		        observer,
		        elements
		    };
		    idList.push(id);
		    observers.set(id, instance);
		    return instance;
		}
		function observe(element, callback, options) {
		    const { id, observer, elements } = createObserver(options);
		    elements.set(element, callback);
		    observer.observe(element);
		    return function unobserve() {
		        elements.delete(element);
		        observer.unobserve(element);
		        // Destroy observer when there's nothing left to watch:
		        if (elements.size === 0) {
		            observer.disconnect();
		            observers.delete(id);
		            const index = idList.findIndex((obj)=>obj.root === id.root && obj.margin === id.margin);
		            if (index > -1) {
		                idList.splice(index, 1);
		            }
		        }
		    };
		}
		function useIntersection(param) {
		    let { rootRef, rootMargin, disabled } = param;
		    const isDisabled = disabled || !hasIntersectionObserver;
		    const [visible, setVisible] = (0, _react.useState)(false);
		    const elementRef = (0, _react.useRef)(null);
		    const setElement = (0, _react.useCallback)((element)=>{
		        elementRef.current = element;
		    }, []);
		    (0, _react.useEffect)(()=>{
		        if (hasIntersectionObserver) {
		            if (isDisabled || visible) return;
		            const element = elementRef.current;
		            if (element && element.tagName) {
		                const unobserve = observe(element, (isVisible)=>isVisible && setVisible(isVisible), {
		                    root: rootRef == null ? void 0 : rootRef.current,
		                    rootMargin
		                });
		                return unobserve;
		            }
		        } else {
		            if (!visible) {
		                const idleCallback = (0, _requestidlecallback.requestIdleCallback)(()=>setVisible(true));
		                return ()=>(0, _requestidlecallback.cancelIdleCallback)(idleCallback);
		            }
		        }
		    // eslint-disable-next-line react-hooks/exhaustive-deps
		    }, [
		        isDisabled,
		        rootMargin,
		        rootRef,
		        visible,
		        elementRef.current
		    ]);
		    const resetVisible = (0, _react.useCallback)(()=>{
		        setVisible(false);
		    }, []);
		    return [
		        setElement,
		        visible,
		        resetVisible
		    ];
		}

		if ((typeof exports.default === 'function' || (typeof exports.default === 'object' && exports.default !== null)) && typeof exports.default.__esModule === 'undefined') {
		  Object.defineProperty(exports.default, '__esModule', { value: true });
		  Object.assign(exports.default, exports);
		  module.exports = exports.default;
		}

		
	} (useIntersection, useIntersection.exports));
	return useIntersection.exports;
}

var getDomainLocale = {exports: {}};

var normalizeLocalePath$1 = {exports: {}};

var normalizeLocalePath = {};

var hasRequiredNormalizeLocalePath$1;

function requireNormalizeLocalePath$1 () {
	if (hasRequiredNormalizeLocalePath$1) return normalizeLocalePath;
	hasRequiredNormalizeLocalePath$1 = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", {
		    value: true
		});
		Object.defineProperty(exports, "normalizeLocalePath", {
		    enumerable: true,
		    get: function() {
		        return normalizeLocalePath;
		    }
		});
		function normalizeLocalePath(pathname, locales) {
		    let detectedLocale;
		    // first item will be empty string from splitting at first char
		    const pathnameParts = pathname.split("/");
		    (locales || []).some((locale)=>{
		        if (pathnameParts[1] && pathnameParts[1].toLowerCase() === locale.toLowerCase()) {
		            detectedLocale = locale;
		            pathnameParts.splice(1, 1);
		            pathname = pathnameParts.join("/") || "/";
		            return true;
		        }
		        return false;
		    });
		    return {
		        pathname,
		        detectedLocale
		    };
		}

		
	} (normalizeLocalePath));
	return normalizeLocalePath;
}

var hasRequiredNormalizeLocalePath;

function requireNormalizeLocalePath () {
	if (hasRequiredNormalizeLocalePath) return normalizeLocalePath$1.exports;
	hasRequiredNormalizeLocalePath = 1;
	(function (module, exports) {
		Object.defineProperty(exports, "__esModule", {
		    value: true
		});
		Object.defineProperty(exports, "normalizeLocalePath", {
		    enumerable: true,
		    get: function() {
		        return normalizeLocalePath;
		    }
		});
		const normalizeLocalePath = (pathname, locales)=>{
		    if (process.env.__NEXT_I18N_SUPPORT) {
		        return requireNormalizeLocalePath$1().normalizeLocalePath(pathname, locales);
		    }
		    return {
		        pathname,
		        detectedLocale: undefined
		    };
		};

		if ((typeof exports.default === 'function' || (typeof exports.default === 'object' && exports.default !== null)) && typeof exports.default.__esModule === 'undefined') {
		  Object.defineProperty(exports.default, '__esModule', { value: true });
		  Object.assign(exports.default, exports);
		  module.exports = exports.default;
		}

		
	} (normalizeLocalePath$1, normalizeLocalePath$1.exports));
	return normalizeLocalePath$1.exports;
}

var detectDomainLocale$1 = {exports: {}};

var detectDomainLocale = {};

var hasRequiredDetectDomainLocale$1;

function requireDetectDomainLocale$1 () {
	if (hasRequiredDetectDomainLocale$1) return detectDomainLocale;
	hasRequiredDetectDomainLocale$1 = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", {
		    value: true
		});
		Object.defineProperty(exports, "detectDomainLocale", {
		    enumerable: true,
		    get: function() {
		        return detectDomainLocale;
		    }
		});
		function detectDomainLocale(domainItems, hostname, detectedLocale) {
		    if (!domainItems) return;
		    if (detectedLocale) {
		        detectedLocale = detectedLocale.toLowerCase();
		    }
		    for (const item of domainItems){
		        var _item_domain, _item_locales;
		        // remove port if present
		        const domainHostname = (_item_domain = item.domain) == null ? void 0 : _item_domain.split(":", 1)[0].toLowerCase();
		        if (hostname === domainHostname || detectedLocale === item.defaultLocale.toLowerCase() || ((_item_locales = item.locales) == null ? void 0 : _item_locales.some((locale)=>locale.toLowerCase() === detectedLocale))) {
		            return item;
		        }
		    }
		}

		
	} (detectDomainLocale));
	return detectDomainLocale;
}

var hasRequiredDetectDomainLocale;

function requireDetectDomainLocale () {
	if (hasRequiredDetectDomainLocale) return detectDomainLocale$1.exports;
	hasRequiredDetectDomainLocale = 1;
	(function (module, exports) {
		Object.defineProperty(exports, "__esModule", {
		    value: true
		});
		Object.defineProperty(exports, "detectDomainLocale", {
		    enumerable: true,
		    get: function() {
		        return detectDomainLocale;
		    }
		});
		const detectDomainLocale = function() {
		    for(var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++){
		        args[_key] = arguments[_key];
		    }
		    if (process.env.__NEXT_I18N_SUPPORT) {
		        return requireDetectDomainLocale$1().detectDomainLocale(...args);
		    }
		};

		if ((typeof exports.default === 'function' || (typeof exports.default === 'object' && exports.default !== null)) && typeof exports.default.__esModule === 'undefined') {
		  Object.defineProperty(exports.default, '__esModule', { value: true });
		  Object.assign(exports.default, exports);
		  module.exports = exports.default;
		}

		
	} (detectDomainLocale$1, detectDomainLocale$1.exports));
	return detectDomainLocale$1.exports;
}

var hasRequiredGetDomainLocale;

function requireGetDomainLocale () {
	if (hasRequiredGetDomainLocale) return getDomainLocale.exports;
	hasRequiredGetDomainLocale = 1;
	(function (module, exports) {
		Object.defineProperty(exports, "__esModule", {
		    value: true
		});
		Object.defineProperty(exports, "getDomainLocale", {
		    enumerable: true,
		    get: function() {
		        return getDomainLocale;
		    }
		});
		const _normalizetrailingslash = requireNormalizeTrailingSlash();
		const basePath = process.env.__NEXT_ROUTER_BASEPATH || "";
		function getDomainLocale(path, locale, locales, domainLocales) {
		    if (process.env.__NEXT_I18N_SUPPORT) {
		        const normalizeLocalePath = requireNormalizeLocalePath().normalizeLocalePath;
		        const detectDomainLocale = requireDetectDomainLocale().detectDomainLocale;
		        const target = locale || normalizeLocalePath(path, locales).detectedLocale;
		        const domain = detectDomainLocale(domainLocales, undefined, target);
		        if (domain) {
		            const proto = "http" + (domain.http ? "" : "s") + "://";
		            const finalLocale = target === domain.defaultLocale ? "" : "/" + target;
		            return "" + proto + domain.domain + (0, _normalizetrailingslash.normalizePathTrailingSlash)("" + basePath + finalLocale + path);
		        }
		        return false;
		    } else {
		        return false;
		    }
		}

		if ((typeof exports.default === 'function' || (typeof exports.default === 'object' && exports.default !== null)) && typeof exports.default.__esModule === 'undefined') {
		  Object.defineProperty(exports.default, '__esModule', { value: true });
		  Object.assign(exports.default, exports);
		  module.exports = exports.default;
		}

		
	} (getDomainLocale, getDomainLocale.exports));
	return getDomainLocale.exports;
}

var addBasePath = {exports: {}};

var hasRequiredAddBasePath;

function requireAddBasePath () {
	if (hasRequiredAddBasePath) return addBasePath.exports;
	hasRequiredAddBasePath = 1;
	(function (module, exports) {
		Object.defineProperty(exports, "__esModule", {
		    value: true
		});
		Object.defineProperty(exports, "addBasePath", {
		    enumerable: true,
		    get: function() {
		        return addBasePath;
		    }
		});
		const _addpathprefix = requireAddPathPrefix();
		const _normalizetrailingslash = requireNormalizeTrailingSlash();
		const basePath = process.env.__NEXT_ROUTER_BASEPATH || "";
		function addBasePath(path, required) {
		    return (0, _normalizetrailingslash.normalizePathTrailingSlash)(process.env.__NEXT_MANUAL_CLIENT_BASE_PATH && !required ? path : (0, _addpathprefix.addPathPrefix)(path, basePath));
		}

		if ((typeof exports.default === 'function' || (typeof exports.default === 'object' && exports.default !== null)) && typeof exports.default.__esModule === 'undefined') {
		  Object.defineProperty(exports.default, '__esModule', { value: true });
		  Object.assign(exports.default, exports);
		  module.exports = exports.default;
		}

		
	} (addBasePath, addBasePath.exports));
	return addBasePath.exports;
}

var routerReducerTypes = {exports: {}};

var hasRequiredRouterReducerTypes;

function requireRouterReducerTypes () {
	if (hasRequiredRouterReducerTypes) return routerReducerTypes.exports;
	hasRequiredRouterReducerTypes = 1;
	(function (module, exports) {
		Object.defineProperty(exports, "__esModule", {
		    value: true
		});
		function _export(target, all) {
		    for(var name in all)Object.defineProperty(target, name, {
		        enumerable: true,
		        get: all[name]
		    });
		}
		_export(exports, {
		    PrefetchKind: function() {
		        return PrefetchKind;
		    },
		    ACTION_REFRESH: function() {
		        return ACTION_REFRESH;
		    },
		    ACTION_NAVIGATE: function() {
		        return ACTION_NAVIGATE;
		    },
		    ACTION_RESTORE: function() {
		        return ACTION_RESTORE;
		    },
		    ACTION_SERVER_PATCH: function() {
		        return ACTION_SERVER_PATCH;
		    },
		    ACTION_PREFETCH: function() {
		        return ACTION_PREFETCH;
		    },
		    ACTION_FAST_REFRESH: function() {
		        return ACTION_FAST_REFRESH;
		    },
		    ACTION_SERVER_ACTION: function() {
		        return ACTION_SERVER_ACTION;
		    },
		    isThenable: function() {
		        return isThenable;
		    }
		});
		const ACTION_REFRESH = "refresh";
		const ACTION_NAVIGATE = "navigate";
		const ACTION_RESTORE = "restore";
		const ACTION_SERVER_PATCH = "server-patch";
		const ACTION_PREFETCH = "prefetch";
		const ACTION_FAST_REFRESH = "fast-refresh";
		const ACTION_SERVER_ACTION = "server-action";
		var PrefetchKind;
		(function(PrefetchKind) {
		    PrefetchKind["AUTO"] = "auto";
		    PrefetchKind["FULL"] = "full";
		    PrefetchKind["TEMPORARY"] = "temporary";
		})(PrefetchKind || (PrefetchKind = {}));
		function isThenable(value) {
		    // TODO: We don't gain anything from this abstraction. It's unsound, and only
		    // makes sense in the specific places where we use it. So it's better to keep
		    // the type coercion inline, instead of leaking this to other places in
		    // the codebase.
		    return value && (typeof value === "object" || typeof value === "function") && typeof value.then === "function";
		}

		if ((typeof exports.default === 'function' || (typeof exports.default === 'object' && exports.default !== null)) && typeof exports.default.__esModule === 'undefined') {
		  Object.defineProperty(exports.default, '__esModule', { value: true });
		  Object.assign(exports.default, exports);
		  module.exports = exports.default;
		}

		
	} (routerReducerTypes, routerReducerTypes.exports));
	return routerReducerTypes.exports;
}

(function (module, exports) {
	"use client";
	Object.defineProperty(exports, "__esModule", {
	    value: true
	});
	Object.defineProperty(exports, "default", {
	    enumerable: true,
	    get: function() {
	        return _default;
	    }
	});
	const _interop_require_default = _interop_require_default$1;
	const _react = /*#__PURE__*/ _interop_require_default._(React__default);
	const _resolvehref = requireResolveHref();
	const _islocalurl = requireIsLocalUrl();
	const _formaturl = requireFormatUrl();
	const _utils = requireUtils$1();
	const _addlocale = requireAddLocale();
	const _routercontextsharedruntime = requireRouterContext_sharedRuntime();
	const _approutercontextsharedruntime = requireAppRouterContext_sharedRuntime();
	const _useintersection = requireUseIntersection();
	const _getdomainlocale = requireGetDomainLocale();
	const _addbasepath = requireAddBasePath();
	const _routerreducertypes = requireRouterReducerTypes();
	const prefetched = new Set();
	function prefetch(router, href, as, options, appOptions, isAppRouter) {
	    if (typeof window === "undefined") {
	        return;
	    }
	    // app-router supports external urls out of the box so it shouldn't short-circuit here as support for e.g. `replace` is added in the app-router.
	    if (!isAppRouter && !(0, _islocalurl.isLocalURL)(href)) {
	        return;
	    }
	    // We should only dedupe requests when experimental.optimisticClientCache is
	    // disabled.
	    if (!options.bypassPrefetchedCheck) {
	        const locale = // Let the link's locale prop override the default router locale.
	        typeof options.locale !== "undefined" ? options.locale : "locale" in router ? router.locale : undefined;
	        const prefetchedKey = href + "%" + as + "%" + locale;
	        // If we've already fetched the key, then don't prefetch it again!
	        if (prefetched.has(prefetchedKey)) {
	            return;
	        }
	        // Mark this URL as prefetched.
	        prefetched.add(prefetchedKey);
	    }
	    const prefetchPromise = isAppRouter ? router.prefetch(href, appOptions) : router.prefetch(href, as, options);
	    // Prefetch the JSON page if asked (only in the client)
	    // We need to handle a prefetch error here since we may be
	    // loading with priority which can reject but we don't
	    // want to force navigation since this is only a prefetch
	    Promise.resolve(prefetchPromise).catch((err)=>{
	        if (process.env.NODE_ENV !== "production") {
	            // rethrow to show invalid URL errors
	            throw err;
	        }
	    });
	}
	function isModifiedEvent(event) {
	    const eventTarget = event.currentTarget;
	    const target = eventTarget.getAttribute("target");
	    return target && target !== "_self" || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || // triggers resource download
	    event.nativeEvent && event.nativeEvent.which === 2;
	}
	function linkClicked(e, router, href, as, replace, shallow, scroll, locale, isAppRouter) {
	    const { nodeName } = e.currentTarget;
	    // anchors inside an svg have a lowercase nodeName
	    const isAnchorNodeName = nodeName.toUpperCase() === "A";
	    if (isAnchorNodeName && (isModifiedEvent(e) || // app-router supports external urls out of the box so it shouldn't short-circuit here as support for e.g. `replace` is added in the app-router.
	    !isAppRouter && !(0, _islocalurl.isLocalURL)(href))) {
	        // ignore click for browser’s default behavior
	        return;
	    }
	    e.preventDefault();
	    const navigate = ()=>{
	        // If the router is an NextRouter instance it will have `beforePopState`
	        const routerScroll = scroll != null ? scroll : true;
	        if ("beforePopState" in router) {
	            router[replace ? "replace" : "push"](href, as, {
	                shallow,
	                locale,
	                scroll: routerScroll
	            });
	        } else {
	            router[replace ? "replace" : "push"](as || href, {
	                scroll: routerScroll
	            });
	        }
	    };
	    if (isAppRouter) {
	        _react.default.startTransition(navigate);
	    } else {
	        navigate();
	    }
	}
	function formatStringOrUrl(urlObjOrString) {
	    if (typeof urlObjOrString === "string") {
	        return urlObjOrString;
	    }
	    return (0, _formaturl.formatUrl)(urlObjOrString);
	}
	/**
	 * React Component that enables client-side transitions between routes.
	 */ const Link = /*#__PURE__*/ _react.default.forwardRef(function LinkComponent(props, forwardedRef) {
	    let children;
	    const { href: hrefProp, as: asProp, children: childrenProp, prefetch: prefetchProp = null, passHref, replace, shallow, scroll, locale, onClick, onMouseEnter: onMouseEnterProp, onTouchStart: onTouchStartProp, legacyBehavior = false, ...restProps } = props;
	    children = childrenProp;
	    if (legacyBehavior && (typeof children === "string" || typeof children === "number")) {
	        children = /*#__PURE__*/ _react.default.createElement("a", null, children);
	    }
	    const pagesRouter = _react.default.useContext(_routercontextsharedruntime.RouterContext);
	    const appRouter = _react.default.useContext(_approutercontextsharedruntime.AppRouterContext);
	    const router = pagesRouter != null ? pagesRouter : appRouter;
	    // We're in the app directory if there is no pages router.
	    const isAppRouter = !pagesRouter;
	    const prefetchEnabled = prefetchProp !== false;
	    /**
	     * The possible states for prefetch are:
	     * - null: this is the default "auto" mode, where we will prefetch partially if the link is in the viewport
	     * - true: we will prefetch if the link is visible and prefetch the full page, not just partially
	     * - false: we will not prefetch if in the viewport at all
	     */ const appPrefetchKind = prefetchProp === null ? _routerreducertypes.PrefetchKind.AUTO : _routerreducertypes.PrefetchKind.FULL;
	    if (process.env.NODE_ENV !== "production") {
	        function createPropError(args) {
	            return new Error("Failed prop type: The prop `" + args.key + "` expects a " + args.expected + " in `<Link>`, but got `" + args.actual + "` instead." + (typeof window !== "undefined" ? "\nOpen your browser's console to view the Component stack trace." : ""));
	        }
	        // TypeScript trick for type-guarding:
	        const requiredPropsGuard = {
	            href: true
	        };
	        const requiredProps = Object.keys(requiredPropsGuard);
	        requiredProps.forEach((key)=>{
	            if (key === "href") {
	                if (props[key] == null || typeof props[key] !== "string" && typeof props[key] !== "object") {
	                    throw createPropError({
	                        key,
	                        expected: "`string` or `object`",
	                        actual: props[key] === null ? "null" : typeof props[key]
	                    });
	                }
	            }
	        });
	        // TypeScript trick for type-guarding:
	        const optionalPropsGuard = {
	            as: true,
	            replace: true,
	            scroll: true,
	            shallow: true,
	            passHref: true,
	            prefetch: true,
	            locale: true,
	            onClick: true,
	            onMouseEnter: true,
	            onTouchStart: true,
	            legacyBehavior: true
	        };
	        const optionalProps = Object.keys(optionalPropsGuard);
	        optionalProps.forEach((key)=>{
	            const valType = typeof props[key];
	            if (key === "as") {
	                if (props[key] && valType !== "string" && valType !== "object") {
	                    throw createPropError({
	                        key,
	                        expected: "`string` or `object`",
	                        actual: valType
	                    });
	                }
	            } else if (key === "locale") {
	                if (props[key] && valType !== "string") {
	                    throw createPropError({
	                        key,
	                        expected: "`string`",
	                        actual: valType
	                    });
	                }
	            } else if (key === "onClick" || key === "onMouseEnter" || key === "onTouchStart") {
	                if (props[key] && valType !== "function") {
	                    throw createPropError({
	                        key,
	                        expected: "`function`",
	                        actual: valType
	                    });
	                }
	            } else if (key === "replace" || key === "scroll" || key === "shallow" || key === "passHref" || key === "prefetch" || key === "legacyBehavior") {
	                if (props[key] != null && valType !== "boolean") {
	                    throw createPropError({
	                        key,
	                        expected: "`boolean`",
	                        actual: valType
	                    });
	                }
	            } else ;
	        });
	        // This hook is in a conditional but that is ok because `process.env.NODE_ENV` never changes
	        // eslint-disable-next-line react-hooks/rules-of-hooks
	        const hasWarned = _react.default.useRef(false);
	        if (props.prefetch && !hasWarned.current && !isAppRouter) {
	            hasWarned.current = true;
	            console.warn("Next.js auto-prefetches automatically based on viewport. The prefetch attribute is no longer needed. More: https://nextjs.org/docs/messages/prefetch-true-deprecated");
	        }
	    }
	    if (process.env.NODE_ENV !== "production") {
	        if (isAppRouter && !asProp) {
	            let href;
	            if (typeof hrefProp === "string") {
	                href = hrefProp;
	            } else if (typeof hrefProp === "object" && typeof hrefProp.pathname === "string") {
	                href = hrefProp.pathname;
	            }
	            if (href) {
	                const hasDynamicSegment = href.split("/").some((segment)=>segment.startsWith("[") && segment.endsWith("]"));
	                if (hasDynamicSegment) {
	                    throw new Error("Dynamic href `" + href + "` found in <Link> while using the `/app` router, this is not supported. Read more: https://nextjs.org/docs/messages/app-dir-dynamic-href");
	                }
	            }
	        }
	    }
	    const { href, as } = _react.default.useMemo(()=>{
	        if (!pagesRouter) {
	            const resolvedHref = formatStringOrUrl(hrefProp);
	            return {
	                href: resolvedHref,
	                as: asProp ? formatStringOrUrl(asProp) : resolvedHref
	            };
	        }
	        const [resolvedHref, resolvedAs] = (0, _resolvehref.resolveHref)(pagesRouter, hrefProp, true);
	        return {
	            href: resolvedHref,
	            as: asProp ? (0, _resolvehref.resolveHref)(pagesRouter, asProp) : resolvedAs || resolvedHref
	        };
	    }, [
	        pagesRouter,
	        hrefProp,
	        asProp
	    ]);
	    const previousHref = _react.default.useRef(href);
	    const previousAs = _react.default.useRef(as);
	    // This will return the first child, if multiple are provided it will throw an error
	    let child;
	    if (legacyBehavior) {
	        if (process.env.NODE_ENV === "development") {
	            if (onClick) {
	                console.warn('"onClick" was passed to <Link> with `href` of `' + hrefProp + '` but "legacyBehavior" was set. The legacy behavior requires onClick be set on the child of next/link');
	            }
	            if (onMouseEnterProp) {
	                console.warn('"onMouseEnter" was passed to <Link> with `href` of `' + hrefProp + '` but "legacyBehavior" was set. The legacy behavior requires onMouseEnter be set on the child of next/link');
	            }
	            try {
	                child = _react.default.Children.only(children);
	            } catch (err) {
	                if (!children) {
	                    throw new Error("No children were passed to <Link> with `href` of `" + hrefProp + "` but one child is required https://nextjs.org/docs/messages/link-no-children");
	                }
	                throw new Error("Multiple children were passed to <Link> with `href` of `" + hrefProp + "` but only one child is supported https://nextjs.org/docs/messages/link-multiple-children" + (typeof window !== "undefined" ? " \nOpen your browser's console to view the Component stack trace." : ""));
	            }
	        } else {
	            child = _react.default.Children.only(children);
	        }
	    } else {
	        if (process.env.NODE_ENV === "development") {
	            if ((children == null ? void 0 : children.type) === "a") {
	                throw new Error("Invalid <Link> with <a> child. Please remove <a> or use <Link legacyBehavior>.\nLearn more: https://nextjs.org/docs/messages/invalid-new-link-with-extra-anchor");
	            }
	        }
	    }
	    const childRef = legacyBehavior ? child && typeof child === "object" && child.ref : forwardedRef;
	    const [setIntersectionRef, isVisible, resetVisible] = (0, _useintersection.useIntersection)({
	        rootMargin: "200px"
	    });
	    const setRef = _react.default.useCallback((el)=>{
	        // Before the link getting observed, check if visible state need to be reset
	        if (previousAs.current !== as || previousHref.current !== href) {
	            resetVisible();
	            previousAs.current = as;
	            previousHref.current = href;
	        }
	        setIntersectionRef(el);
	        if (childRef) {
	            if (typeof childRef === "function") childRef(el);
	            else if (typeof childRef === "object") {
	                childRef.current = el;
	            }
	        }
	    }, [
	        as,
	        childRef,
	        href,
	        resetVisible,
	        setIntersectionRef
	    ]);
	    // Prefetch the URL if we haven't already and it's visible.
	    _react.default.useEffect(()=>{
	        // in dev, we only prefetch on hover to avoid wasting resources as the prefetch will trigger compiling the page.
	        if (process.env.NODE_ENV !== "production") {
	            return;
	        }
	        if (!router) {
	            return;
	        }
	        // If we don't need to prefetch the URL, don't do prefetch.
	        if (!isVisible || !prefetchEnabled) {
	            return;
	        }
	        // Prefetch the URL.
	        prefetch(router, href, as, {
	            locale
	        }, {
	            kind: appPrefetchKind
	        }, isAppRouter);
	    }, [
	        as,
	        href,
	        isVisible,
	        locale,
	        prefetchEnabled,
	        pagesRouter == null ? void 0 : pagesRouter.locale,
	        router,
	        isAppRouter,
	        appPrefetchKind
	    ]);
	    const childProps = {
	        ref: setRef,
	        onClick (e) {
	            if (process.env.NODE_ENV !== "production") {
	                if (!e) {
	                    throw new Error('Component rendered inside next/link has to pass click event to "onClick" prop.');
	                }
	            }
	            if (!legacyBehavior && typeof onClick === "function") {
	                onClick(e);
	            }
	            if (legacyBehavior && child.props && typeof child.props.onClick === "function") {
	                child.props.onClick(e);
	            }
	            if (!router) {
	                return;
	            }
	            if (e.defaultPrevented) {
	                return;
	            }
	            linkClicked(e, router, href, as, replace, shallow, scroll, locale, isAppRouter);
	        },
	        onMouseEnter (e) {
	            if (!legacyBehavior && typeof onMouseEnterProp === "function") {
	                onMouseEnterProp(e);
	            }
	            if (legacyBehavior && child.props && typeof child.props.onMouseEnter === "function") {
	                child.props.onMouseEnter(e);
	            }
	            if (!router) {
	                return;
	            }
	            if ((!prefetchEnabled || process.env.NODE_ENV === "development") && isAppRouter) {
	                return;
	            }
	            prefetch(router, href, as, {
	                locale,
	                priority: true,
	                // @see {https://github.com/vercel/next.js/discussions/40268?sort=top#discussioncomment-3572642}
	                bypassPrefetchedCheck: true
	            }, {
	                kind: appPrefetchKind
	            }, isAppRouter);
	        },
	        onTouchStart (e) {
	            if (!legacyBehavior && typeof onTouchStartProp === "function") {
	                onTouchStartProp(e);
	            }
	            if (legacyBehavior && child.props && typeof child.props.onTouchStart === "function") {
	                child.props.onTouchStart(e);
	            }
	            if (!router) {
	                return;
	            }
	            if (!prefetchEnabled && isAppRouter) {
	                return;
	            }
	            prefetch(router, href, as, {
	                locale,
	                priority: true,
	                // @see {https://github.com/vercel/next.js/discussions/40268?sort=top#discussioncomment-3572642}
	                bypassPrefetchedCheck: true
	            }, {
	                kind: appPrefetchKind
	            }, isAppRouter);
	        }
	    };
	    // If child is an <a> tag and doesn't have a href attribute, or if the 'passHref' property is
	    // defined, we specify the current 'href', so that repetition is not needed by the user.
	    // If the url is absolute, we can bypass the logic to prepend the domain and locale.
	    if ((0, _utils.isAbsoluteUrl)(as)) {
	        childProps.href = as;
	    } else if (!legacyBehavior || passHref || child.type === "a" && !("href" in child.props)) {
	        const curLocale = typeof locale !== "undefined" ? locale : pagesRouter == null ? void 0 : pagesRouter.locale;
	        // we only render domain locales if we are currently on a domain locale
	        // so that locale links are still visitable in development/preview envs
	        const localeDomain = (pagesRouter == null ? void 0 : pagesRouter.isLocaleDomain) && (0, _getdomainlocale.getDomainLocale)(as, curLocale, pagesRouter == null ? void 0 : pagesRouter.locales, pagesRouter == null ? void 0 : pagesRouter.domainLocales);
	        childProps.href = localeDomain || (0, _addbasepath.addBasePath)((0, _addlocale.addLocale)(as, curLocale, pagesRouter == null ? void 0 : pagesRouter.defaultLocale));
	    }
	    return legacyBehavior ? /*#__PURE__*/ _react.default.cloneElement(child, childProps) : /*#__PURE__*/ _react.default.createElement("a", {
	        ...restProps,
	        ...childProps
	    }, children);
	});
	const _default = Link;

	if ((typeof exports.default === 'function' || (typeof exports.default === 'object' && exports.default !== null)) && typeof exports.default.__esModule === 'undefined') {
	  Object.defineProperty(exports.default, '__esModule', { value: true });
	  Object.assign(exports.default, exports);
	  module.exports = exports.default;
	}

	
} (link$1, link$1.exports));

var linkExports = link$1.exports;

var link = linkExports;

var NextLink = /*@__PURE__*/getDefaultExportFromCjs(link);

/**
 * Link
 *
 * Link Wrapper for `next/link` that also handles external links.
 */
var Link = forwardRef(function (props, ref) {
    var href = props.href; props.scroll; props.as; props.replace; props.shallow; props.passHref; props.prefetch; props.locale; props.legacyBehavior; var children = props.children, rest = __rest(props, ["href", "scroll", "as", "replace", "shallow", "passHref", "prefetch", "locale", "legacyBehavior", "children"]);
    var isExternal = typeof href === 'string' && (/^https?:\/\//i.test(href) || /^mailto:\/\//i.test(href));
    if (!isExternal) {
        return jsx(NextLink, __assign({ ref: ref }, props));
    }
    return (jsx("a", __assign({ ref: ref, href: href }, rest, { children: children })));
});

var css_248z$6 = ".Spinner-module_spinner__SZoUP {\n  animation: Spinner-module_spinner__SZoUP 1.5s linear infinite;\n}\n.Spinner-module_spinner__SZoUP.Spinner-module_regular__xiMf6 {\n  width: 1.75rem;\n  height: 1.75rem;\n}\n.Spinner-module_spinner__SZoUP.Spinner-module_small__bQahy {\n  width: 1rem;\n  height: 1rem;\n}\n.Spinner-module_spinner__SZoUP.Spinner-module_large__Dc4gW {\n  width: 3rem;\n  height: 3rem;\n}\n.Spinner-module_spinner__SZoUP.Spinner-module_xlarge__NUnMe {\n  width: 4rem;\n  height: 4rem;\n}\n\n@keyframes Spinner-module_spinner__SZoUP {\n  100% {\n    transform: rotate(1turn);\n  }\n}\n.Spinner-module_dots__JEKUF.Spinner-module_regular__xiMf6 {\n  width: 3rem;\n  height: 3rem;\n}\n.Spinner-module_dots__JEKUF.Spinner-module_small__bQahy {\n  width: 1.75rem;\n  height: 1.75rem;\n}\n\n.Spinner-module_dot__QysMs {\n  background-color: #ff7b83;\n  border-radius: 50%;\n  animation: Spinner-module_dot__QysMs 1.5s infinite;\n}\n.Spinner-module_regular__xiMf6 .Spinner-module_dot__QysMs {\n  width: 0.75rem;\n  height: 0.75rem;\n}\n.Spinner-module_small__bQahy .Spinner-module_dot__QysMs {\n  width: 0.4375rem;\n  height: 0.4375rem;\n}\n.Spinner-module_dot__QysMs.Spinner-module_dot2__3AERe {\n  animation-delay: 0.5s;\n}\n.Spinner-module_dot__QysMs.Spinner-module_dot3__AMyMt {\n  animation-delay: 1s;\n}\n\n@keyframes Spinner-module_dot__QysMs {\n  0%, 100% {\n    background-color: #ff7b83;\n  }\n  50% {\n    background-color: #fff;\n  }\n}";
var styles$6 = {"spinner":"Spinner-module_spinner__SZoUP","regular":"Spinner-module_regular__xiMf6","small":"Spinner-module_small__bQahy","large":"Spinner-module_large__Dc4gW","xlarge":"Spinner-module_xlarge__NUnMe","dots":"Spinner-module_dots__JEKUF","dot":"Spinner-module_dot__QysMs","dot2":"Spinner-module_dot2__3AERe","dot3":"Spinner-module_dot3__AMyMt"};
styleInject(css_248z$6);

/**
 * IFE-012 Spinner
 *
 * Indicate the loading state of a component or page.
 */
function Spinner(_a) {
    var _b, _c;
    var ariaLabel = _a.ariaLabel, _d = _a.variant, variant = _d === void 0 ? 'circle' : _d, _e = _a.size, size = _e === void 0 ? 'regular' : _e, className = _a.className;
    if (variant === 'dots') {
        return (jsxs("div", { className: clsx('d-flex align-items-center justify-content-between', styles$6.dots, className, (_b = {},
                _b[styles$6.small] = size === 'small',
                _b[styles$6.regular] = size === 'regular',
                _b)), role: "status", children: [jsx("span", { className: styles$6.dot }), jsx("span", { className: clsx(styles$6.dot, styles$6.dot2) }), jsx("span", { className: clsx(styles$6.dot, styles$6.dot3) }), jsx("span", { className: "visually-hidden", children: ariaLabel })] }));
    }
    return (jsxs("div", { className: clsx('text-primary-500', className), role: "status", children: [jsx("svg", { viewBox: "0 0 64 64", className: clsx(styles$6.spinner, (_c = {},
                    _c[styles$6.regular] = size === 'regular',
                    _c[styles$6.small] = size === 'small',
                    _c[styles$6.large] = size === 'large',
                    _c[styles$6.xlarge] = size === 'xlarge',
                    _c)), children: jsx("path", { fill: "none", stroke: "currentColor", strokeWidth: "6", strokeLinecap: "round", strokeLinejoin: "round", d: "M11.4939 11.4939C14.73 8.25783 18.686 5.83358 23.0385 4.41936C27.391 3.00514 32.0164 2.64112 36.5366 3.35704C41.0568 4.07296 45.3433 5.84851 49.0458 8.53851C52.7482 11.2285 55.7615 14.7566 57.8392 18.8343C59.9169 22.912 61 27.4235 61 32C61 36.5765 59.9169 41.088 57.8392 45.1657C55.7615 49.2434 52.7482 52.7715 49.0458 55.4615C45.3433 58.1515 41.0567 59.927 36.5366 60.643" }) }), jsx("span", { className: "visually-hidden", children: ariaLabel })] }));
}

var css_248z$5 = ".Button-module_button__18Bed {\n  --bs-btn-focus-box-shadow: none;\n  --bs-btn-disabled-opacity: 1;\n}\n\n.Button-module_primary__st6yY {\n  --bs-btn-disabled-bg: var(--bs-primary-100);\n  --bs-btn-disabled-border-color: var(--bs-primary-100);\n}\n.Button-module_primary__st6yY:focus {\n  background-color: #e20613;\n}\n\n.Button-module_secondary__j-3rj {\n  --bs-btn-color: var(--bs-primary-500);\n  --bs-btn-border-color: var(--bs-primary-500);\n  --bs-btn-hover-color: var(--bs-white);\n  --bs-btn-hover-bg: var(--bs-primary-500);\n  --bs-btn-active-color: var(--bs-white);\n  --bs-btn-active-bg: var(--bs-primary-500);\n  --bs-btn-disabled-color: var(--bs-primary-100);\n  --bs-btn-disabled-border-color: var(--bs-primary-100);\n}\n.Button-module_secondary__j-3rj:focus {\n  color: #b4050f;\n  background-color: transparent;\n  border-color: #b4050f;\n}\n\n.Button-module_tertiary__Nd7xM {\n  --bs-btn-bg: var(--bs-white);\n  --bs-btn-border-color: var(--bs-gray-600);\n  --bs-btn-hover-bg: var(--bs-gray-200);\n  --bs-btn-hover-border-color: var(--bs-gray-200);\n  --bs-btn-active-bg: var(--bs-gray-400);\n  --bs-btn-active-border-color: var(--bs-gray-400);\n  --bs-btn-disabled-color: var(--bs-gray-200);\n  --bs-btn-disabled-bg: var(--bs-white);\n  --bs-btn-disabled-border-color: var(--bs-gray-200);\n}\n.Button-module_tertiary__Nd7xM:focus {\n  background-color: #ced4da;\n  border-color: #ced4da;\n}\n\n.Button-module_primaryFlat__Q2XQG {\n  --bs-btn-color: var(--bs-primary-500);\n  --bs-btn-hover-color: var(--bs-btn-color);\n  --bs-btn-hover-bg: var(--bs-primary-25);\n  --bs-btn-active-color: var(--bs-primary-600);\n  --bs-btn-active-bg: var(--bs-primary-25);\n  --bs-btn-active-border-color: transparent;\n  --bs-btn-disabled-color: var(--bs-primary-100);\n  --bs-btn-disabled-border-color: transparent;\n}\n.Button-module_primaryFlat__Q2XQG:focus {\n  color: #b4050f;\n  background-color: #ffe7e8;\n}\n\n.Button-module_tertiaryFlat__VfPMQ {\n  --bs-btn-hover-bg: var(--bs-gray-200);\n  --bs-btn-active-border-color: transparent;\n  --bs-btn-active-bg: var(--bs-gray-200);\n  --bs-btn-disabled-color: var(--bs-gray-200);\n  --bs-btn-disabled-border-color: transparent;\n}\n.Button-module_tertiaryFlat__VfPMQ:focus {\n  background-color: #ced4da;\n}\n\n.Button-module_primaryLink__HeAj7 {\n  --bs-btn-color: var(--bs-primary-500);\n  --bs-btn-hover-color: var(--bs-primary-600);\n  --bs-btn-active-color: var(--bs-primary-600);\n  --bs-btn-active-border-color: transparent;\n  --bs-btn-disabled-color: var(--bs-primary-100);\n  --bs-btn-disabled-border-color: transparent;\n}\n.Button-module_primaryLink__HeAj7:focus {\n  color: #b4050f;\n}\n\n.Button-module_small__zfmcP {\n  height: 2.25rem;\n}\n\n.Button-module_regular__oNlU9 {\n  height: 2.5rem;\n}\n\n.Button-module_large__iWceB {\n  height: 3rem;\n}\n\n.Button-module_iconOnly__FNrSu.Button-module_small__zfmcP {\n  width: 2.25rem;\n}\n.Button-module_iconOnly__FNrSu.Button-module_regular__oNlU9 {\n  width: 2.5rem;\n}\n.Button-module_iconOnly__FNrSu.Button-module_large__iWceB {\n  width: 3rem;\n}";
var styles$5 = {"button":"Button-module_button__18Bed","primary":"Button-module_primary__st6yY","secondary":"Button-module_secondary__j-3rj","tertiary":"Button-module_tertiary__Nd7xM","primaryFlat":"Button-module_primaryFlat__Q2XQG","tertiaryFlat":"Button-module_tertiaryFlat__VfPMQ","primaryLink":"Button-module_primaryLink__HeAj7","small":"Button-module_small__zfmcP","regular":"Button-module_regular__oNlU9","large":"Button-module_large__iWceB","iconOnly":"Button-module_iconOnly__FNrSu"};
styleInject(css_248z$5);

var isAnchor = function (props) {
    return props.href !== undefined;
};
/**
 * IFE-003 Button
 *
 * The Button component which can be used as a link or a button.
 */
var Button = forwardRef(function (props, ref) {
    var _a;
    var _b = props.fillMode, fillMode = _b === void 0 ? 'solid' : _b, _c = props.size, size = _c === void 0 ? 'regular' : _c, _d = props.themeColor, themeColor = _d === void 0 ? 'primary' : _d, isActive = props.isActive, icon = props.icon, _e = props.iconPosition, iconPosition = _e === void 0 ? 'start' : _e, isLoading = props.isLoading, children = props.children, className = props.className, elementProps = __rest(props, ["fillMode", "size", "themeColor", "isActive", "icon", "iconPosition", "isLoading", "children", "className"]);
    var classes = clsx('btn d-inline-flex align-items-center justify-content-center', styles$5.button, className, (_a = {},
        _a["btn-primary-500 ".concat(styles$5.primary)] = fillMode === 'solid' && themeColor === 'primary',
        _a[styles$5.secondary] = fillMode === 'solid' && themeColor === 'secondary',
        _a[styles$5.tertiary] = fillMode === 'solid' && themeColor === 'tertiary',
        _a[styles$5.primaryFlat] = fillMode === 'flat' && themeColor === 'primary',
        _a[styles$5.tertiaryFlat] = fillMode === 'flat' && themeColor === 'tertiary',
        _a["p-0 ".concat(styles$5.primaryLink)] = fillMode === 'link' && themeColor === 'primary',
        _a["btn-sm ".concat(styles$5.small)] = size === 'small',
        _a[styles$5.regular] = size === 'regular',
        _a["btn-lg ".concat(styles$5.large)] = size === 'large',
        _a['gap-2'] = !!icon && children !== undefined,
        _a[styles$5.iconOnly] = !!icon && children === undefined,
        _a['px-12'] = isLoading,
        _a.active = isActive,
        _a));
    if (isAnchor(elementProps)) {
        var _f = elementProps, href = _f.href, as = _f.as, replace = _f.replace, scroll_1 = _f.scroll, shallow = _f.shallow, passHref = _f.passHref, prefetch = _f.prefetch, locale = _f.locale, rest = __rest(_f, ["href", "as", "replace", "scroll", "shallow", "passHref", "prefetch", "locale"]);
        var linkProps = {
            href: href,
            as: as,
            replace: replace,
            scroll: scroll_1,
            shallow: shallow,
            passHref: passHref,
            prefetch: prefetch,
            locale: locale
        };
        return (jsxs(Link, __assign({ className: classes }, linkProps, rest, { ref: ref, children: [isLoading && jsx(Spinner, { ariaLabel: 'Loading\u2026', variant: 'dots', size: 'small' }), !!icon && iconPosition === 'start' && !isLoading && (jsx(Icon, { icon: icon, className: 'd-block', fixedWidth: true })), !isLoading && children, !!icon && iconPosition === 'end' && !isLoading && (jsx(Icon, { icon: icon, className: 'd-block', fixedWidth: true }))] })));
    }
    return (jsxs("button", __assign({ type: 'button', className: classes, disabled: isLoading }, elementProps, { ref: ref, children: [isLoading && jsx(Spinner, { ariaLabel: 'Loading\u2026', variant: 'dots', size: 'small' }), !!icon && iconPosition === 'start' && !isLoading && (jsx(Icon, { icon: icon, className: 'd-block', fixedWidth: true })), !isLoading && children, !!icon && iconPosition === 'end' && !isLoading && (jsx(Icon, { icon: icon, className: 'd-block', fixedWidth: true }))] })));
});

var ButtonAction = function (_a) {
    var disabled = _a.disabled, onClick = _a.onClick, icon = _a.icon;
    return (jsx(Button, { className: "border-0", icon: icon, size: "small", fillMode: "flat", disabled: disabled, onClick: onClick }));
};
var ButtonActionsContainer = function (_a) {
    var disabled = _a.disabled, onApprove = _a.onApprove, onClear = _a.onClear;
    return (jsxs("div", { className: clsx('position-absolute top-0 d-flex align-items-center h-100 p-4 end-0'), "aria-hidden": true, children: [jsx(ButtonAction, { disabled: disabled, icon: "check", onClick: onApprove }), jsx(ButtonAction, { disabled: disabled, icon: "xmark", onClick: onClear })] }));
};

var css_248z$4 = ".InputWithLabel-module_input__-lUdQ[type=search]:not(:placeholder-shown) {\n  padding-right: 0.75rem !important;\n}\n.InputWithLabel-module_input__-lUdQ[type=search]::-webkit-search-cancel-button {\n  appearance: none;\n  width: 1.5rem;\n  height: 1.5rem;\n  z-index: 2;\n  background-color: #fff;\n  background-image: url(\"data:image/svg+xml;base64,PHN2ZyB2aWV3Qm94PSIwIDAgMjQgMjQiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+ICA8cGF0aCAgICBkPSJNMTcuNTgxOCAxNi40MTZDMTcuOTI5NSAxNi43NjM3IDE3LjkyOTUgMTcuMzI3OCAxNy41ODE4IDE3LjY3NTVDMTcuMjM0MSAxOC4wMjMyIDE2LjY3IDE4LjAyMzIgMTYuMzIyMyAxNy42NzU1TDExLjkzNzUgMTMuMjU4TDcuNTIxNDggMTcuNjc0QzcuMTczOCAxOC4wMjE3IDYuNjA5NyAxOC4wMjE3IDYuMjYxOTggMTcuNjc0QzUuOTE0MjcgMTcuMzI2NCA1LjkxNDMxIDE2Ljc2MjMgNi4yNjE5OCAxNi40MTQ1TDEwLjY3OTUgMTJMNi4yNjA3NiA3LjU1MDZDNS45MTMwOCA3LjIwMjkyIDUuOTEzMDggNi42Mzg4MiA2LjI2MDc2IDYuMjkxMTFDNi42MDg0NCA1Ljk0MzM5IDcuMTcyNTQgNS45NDM0MyA3LjUyMDI1IDYuMjkxMTFMMTEuOTM3NSAxMC43NDJMMTYuMzUzNSA2LjMyNTk5QzE2LjcwMTIgNS45NzgzMSAxNy4yNjUzIDUuOTc4MzEgMTcuNjEzIDYuMzI1OTlDMTcuOTYwNyA2LjY3MzY3IDE3Ljk2MDcgNy4yMzc3NyAxNy42MTMgNy41ODU0OEwxMy4xOTU1IDEyTDE3LjU4MTggMTYuNDE2WiIgICAgZmlsbD0iI2UyMDYxMyIgIC8+PC9zdmc+\");\n}\n.InputWithLabel-module_input__-lUdQ.InputWithLabel-module_hasAddonText__6bfkJ {\n  border-bottom-right-radius: 0;\n  border-top-right-radius: 0;\n}\n.InputWithLabel-module_input__-lUdQ.InputWithLabel-module_hasAddonText__6bfkJ:focus {\n  border-color: var(--bs-border-color);\n  box-shadow: none;\n}\n\n.InputWithLabel-module_addonTextWrapper__dzY9T:focus-within {\n  border-color: #f18389 !important;\n  box-shadow: 0 0 0 0.25rem rgba(0, 123, 255, 0.25);\n}\n\n.InputWithLabel-module_addonText__k540h {\n  border-bottom-left-radius: 0;\n  border-top-left-radius: 0;\n}";
var styles$4 = {"input":"InputWithLabel-module_input__-lUdQ","hasAddonText":"InputWithLabel-module_hasAddonText__6bfkJ","addonTextWrapper":"InputWithLabel-module_addonTextWrapper__dzY9T","addonText":"InputWithLabel-module_addonText__k540h"};
styleInject(css_248z$4);

var InputWithLabel = forwardRef(function (props, ref) {
    var _a, _b;
    var id = props.id, _c = props.type, type = _c === void 0 ? 'text' : _c, label = props.label, labelHint = props.labelHint, hint = props.hint, _d = props.isLabelSrOnly, isLabelSrOnly = _d === void 0 ? false : _d, errorMessage = props.errorMessage, icon = props.icon, _e = props.iconPosition, iconPosition = _e === void 0 ? 'start' : _e, _f = props.placeholder, placeholder = _f === void 0 ? ' ' : _f, _g = props.required, required = _g === void 0 ? false : _g, className = props.className, _h = props.variant, variant = _h === void 0 ? 'default' : _h, addonText = props.addonText, onApprove = props.onApprove, onClear = props.onClear, rest = __rest(props, ["id", "type", "label", "labelHint", "hint", "isLabelSrOnly", "errorMessage", "icon", "iconPosition", "placeholder", "required", "className", "variant", "addonText", "onApprove", "onClear"]);
    var handleOnApprove = function () {
        if (!!onApprove) {
            onApprove();
        }
    };
    var handleOnClear = function () {
        if (!!onClear) {
            onClear();
        }
    };
    return (jsxs("div", { className: className, children: [jsxs("label", { className: clsx('form-label d-block', {
                    'visually-hidden': isLabelSrOnly,
                    'text-error-500': !!errorMessage,
                }), htmlFor: id, children: [label, !!labelHint && (jsxs(Fragment$1, { children: [' ', jsx(Typography, { variant: "textSm", component: "span", className: "fw-normal", children: labelHint })] })), required && (jsx(Typography, { variant: "textSm", component: "span", className: "fw-normal fst-italic", children: "*" }))] }), jsxs("div", { className: clsx('position-relative', (_a = {},
                    _a["d-flex overflow-hidden border rounded ".concat(styles$4.addonTextWrapper)] = !!addonText,
                    _a)), children: [jsx("input", __assign({ ref: ref, id: id, type: type, placeholder: placeholder, className: clsx(styles$4.input, 'form-control form-control-lg', (_b = {
                                'ps-12': !!icon && iconPosition === 'start',
                                'pe-12': !!icon && iconPosition === 'end',
                                'end pe-24': variant === 'action',
                                'border-error-500': !!errorMessage
                            },
                            _b["border-0 rounded-0 ".concat(styles$4.hasAddonText)] = !!addonText,
                            _b)) }, rest)), !!addonText && (jsx(Typography, { component: "div", className: clsx('d-flex align-items-center pe-4', styles$4.addonText), children: addonText })), !!icon && !addonText && (jsx("div", { className: clsx('position-absolute top-0 d-flex align-items-center h-100 p-4 pe-none', {
                            'start-0': iconPosition === 'start',
                            'end-0': iconPosition === 'end',
                        }), "aria-hidden": true, children: jsx(Icon, { className: "text-gray-400", icon: icon, size: "lg" }) })), variant === 'action' && (jsx(ButtonActionsContainer, { disabled: !!props.disabled || !!props.readOnly, onApprove: handleOnApprove, onClear: handleOnClear }))] }), !!hint && !errorMessage && (jsx(Typography, { variant: "textSm", component: "span", className: "d-block mt-2", children: hint })), !!errorMessage && (jsx(Typography, { variant: "textSm", component: "span", className: "d-block mt-2 text-error-500", children: errorMessage }))] }));
});

/**
 * IFE-005 TechemLogo
 *
 * The TechemLogo component displays the Techem Logo as inline SVG.
 */
function TechemLogo(props) {
    return (jsxs("svg", __assign({ viewBox: "0 0 178.58 60.43" }, props, { children: [jsx("path", { fill: "#e30613", d: "M0 40.88h69.5a18.18 18.18 0 0 1 7.87 1.82A17.67 17.67 0 0 1 85 49.86a18.94 18.94 0 0 1 2.35 5.67 17.28 17.28 0 0 1 .81 4.9 28.21 28.21 0 0 0-6.33-6.6c-4.26-2.75-9.57-3.18-15.94-3.13H10.38a10.88 10.88 0 0 1-3.55-.42 8 8 0 0 1-3.75-2.18 10.31 10.31 0 0 1-2.36-3.72 11.4 11.4 0 0 1-.72-3.5zm178.58 0h-69.5a18.18 18.18 0 0 0-7.87 1.82 17.73 17.73 0 0 0-7.65 7.15 18.94 18.94 0 0 0-2.35 5.67 17.28 17.28 0 0 0-.81 4.9 28.21 28.21 0 0 1 6.33-6.6c4.27-2.75 9.57-3.18 16-3.13h55.47a10.88 10.88 0 0 0 3.55-.42 8.07 8.07 0 0 0 3.76-2.18 10.39 10.39 0 0 0 2.35-3.72 11.4 11.4 0 0 0 .72-3.49z" }), jsx("path", { fill: "#1d1d1b", d: "M39.52 20.33h-9.45c.13-1.19.05-5.64 4.8-5.64 5.08 0 4.53 4.2 4.65 5.64m8.26 3.74c0-3.16-.13-6-1.22-8.12-1.68-3.44-5-6.43-11.86-6.43-9.51 0-13.12 5.06-13.2 13.66-.06 7.67 5.25 12.91 14.5 12.47 4.16-.19 7.12-1 11.64-4L45.41 28A12.16 12.16 0 0 1 37 30.67c-6.63-.36-6.78-5-6.8-6.6zM125 20.33h-9.45c.12-1.19 0-5.64 4.79-5.64 5.08 0 4.53 4.2 4.66 5.64m8.26 3.74c0-3.16-.14-6-1.22-8.12-1.68-3.44-5-6.43-11.87-6.43-9.5 0-13.12 5.06-13.19 13.66-.07 7.67 5.24 12.91 14.46 12.47 4.15-.19 7.12-1 11.64-4L130.9 28a12.21 12.21 0 0 1-8.39 2.71c-6.63-.36-6.77-5-6.8-6.6zM19.57 9.73v5.9h-6v9.52c.05 3.05.05 5.26 3.59 5.26a14.14 14.14 0 0 0 2.31-.15v4.21a13.93 13.93 0 0 1-6 1.21c-7.37 0-8.85-2.46-8.85-7.23V15.63H0v-5.9h4.82V3.8l8.9-3.8v9.73zM77.87 4 87.09.17V13.7a9.91 9.91 0 0 1 7.61-3.25 9.57 9.57 0 0 1 6.78 2.55c2.43 2.21 2.43 4.92 2.43 8.36v14.32h-9.07V22.4c0-2.07 0-6.1-3.78-6.1-2.91 0-3.59 2.46-3.83 3.44a26.17 26.17 0 0 0-.23 4.78v11.16h-9.13zm58.92 6.06h8.78v5a8.9 8.9 0 0 1 8.15-5c1.39 0 6 .59 7.53 4.91a9.93 9.93 0 0 1 8.55-4.91 8.37 8.37 0 0 1 7.47 4.13c1.29 2.11 1.29 5.11 1.29 8v13.49h-9v-14c0-2.51 0-5.46-3.54-5.46a3.54 3.54 0 0 0-3.35 2.07 7.41 7.41 0 0 0-.53 3.49v13.9h-8.94V21.61c0-2.21 0-5.36-3.58-5.36-3.06 0-3.83 2.61-3.83 5.36v14.07h-9V10.06m-69.4 20.48a7.55 7.55 0 0 1-7.59-7.86 7.84 7.84 0 0 1 7.2-8.13c2.36-.18 3.59.18 6 1.81l2-3.5c-1.78-1.24-4.43-3.42-10.91-3.42C56 9.44 50.72 15 50.72 23c0 6.81 5.29 12.72 13.37 12.72 6.48 0 9.13-2.18 10.91-3.42l-2-3.52c-2.62 1.61-3.23 1.8-5.6 1.8" })] })));
}
function TechemLogoMonochrome(props) {
    return (jsx("svg", __assign({ fill: "currentColor", viewBox: "0 0 178.58 60.43" }, props, { children: jsx("path", { d: "M0 40.88h69.5a18.18 18.18 0 0 1 7.87 1.82A17.67 17.67 0 0 1 85 49.86a18.94 18.94 0 0 1 2.35 5.67 17.28 17.28 0 0 1 .81 4.9 28.21 28.21 0 0 0-6.33-6.6c-4.26-2.75-9.57-3.18-15.94-3.13H10.38a10.88 10.88 0 0 1-3.55-.42 8 8 0 0 1-3.75-2.18 10.31 10.31 0 0 1-2.36-3.72 11.4 11.4 0 0 1-.72-3.5zm178.58 0h-69.5a18.18 18.18 0 0 0-7.87 1.82 17.73 17.73 0 0 0-7.65 7.15 18.94 18.94 0 0 0-2.35 5.67 17.28 17.28 0 0 0-.81 4.9 28.21 28.21 0 0 1 6.33-6.6c4.27-2.75 9.57-3.18 16-3.13h55.47a10.88 10.88 0 0 0 3.55-.42 8.07 8.07 0 0 0 3.76-2.18 10.39 10.39 0 0 0 2.35-3.72 11.4 11.4 0 0 0 .72-3.49zM39.52 20.33h-9.45c.13-1.19.05-5.64 4.8-5.64 5.08 0 4.53 4.2 4.65 5.64m8.26 3.74c0-3.16-.13-6-1.22-8.12-1.68-3.44-5-6.43-11.86-6.43-9.51 0-13.12 5.06-13.2 13.66-.06 7.67 5.25 12.91 14.5 12.47 4.16-.19 7.12-1 11.64-4L45.41 28A12.16 12.16 0 0 1 37 30.67c-6.63-.36-6.78-5-6.8-6.6zM125 20.33h-9.45c.12-1.19 0-5.64 4.79-5.64 5.08 0 4.53 4.2 4.66 5.64m8.26 3.74c0-3.16-.14-6-1.22-8.12-1.68-3.44-5-6.43-11.87-6.43-9.5 0-13.12 5.06-13.19 13.66-.07 7.67 5.24 12.91 14.46 12.47 4.15-.19 7.12-1 11.64-4L130.9 28a12.21 12.21 0 0 1-8.39 2.71c-6.63-.36-6.77-5-6.8-6.6zM19.57 9.73v5.9h-6v9.52c.05 3.05.05 5.26 3.59 5.26a14.14 14.14 0 0 0 2.31-.15v4.21a13.93 13.93 0 0 1-6 1.21c-7.37 0-8.85-2.46-8.85-7.23V15.63H0v-5.9h4.82V3.8l8.9-3.8v9.73zM77.87 4 87.09.17V13.7a9.91 9.91 0 0 1 7.61-3.25 9.57 9.57 0 0 1 6.78 2.55c2.43 2.21 2.43 4.92 2.43 8.36v14.32h-9.07V22.4c0-2.07 0-6.1-3.78-6.1-2.91 0-3.59 2.46-3.83 3.44a26.17 26.17 0 0 0-.23 4.78v11.16h-9.13zm58.92 6.06h8.78v5a8.9 8.9 0 0 1 8.15-5c1.39 0 6 .59 7.53 4.91a9.93 9.93 0 0 1 8.55-4.91 8.37 8.37 0 0 1 7.47 4.13c1.29 2.11 1.29 5.11 1.29 8v13.49h-9v-14c0-2.51 0-5.46-3.54-5.46a3.54 3.54 0 0 0-3.35 2.07 7.41 7.41 0 0 0-.53 3.49v13.9h-8.94V21.61c0-2.21 0-5.36-3.58-5.36-3.06 0-3.83 2.61-3.83 5.36v14.07h-9V10.06m-69.4 20.48a7.55 7.55 0 0 1-7.59-7.86 7.84 7.84 0 0 1 7.2-8.13c2.36-.18 3.59.18 6 1.81l2-3.5c-1.78-1.24-4.43-3.42-10.91-3.42C56 9.44 50.72 15 50.72 23c0 6.81 5.29 12.72 13.37 12.72 6.48 0 9.13-2.18 10.91-3.42l-2-3.52c-2.62 1.61-3.23 1.8-5.6 1.8" }) })));
}

var imageExternal = {};

var getImgProps = {};

var warnOnce = {};

(function (exports) {
	Object.defineProperty(exports, "__esModule", {
	    value: true
	});
	Object.defineProperty(exports, "warnOnce", {
	    enumerable: true,
	    get: function() {
	        return warnOnce;
	    }
	});
	let warnOnce = (_)=>{};
	if (process.env.NODE_ENV !== "production") {
	    const warnings = new Set();
	    warnOnce = (msg)=>{
	        if (!warnings.has(msg)) {
	            console.warn(msg);
	        }
	        warnings.add(msg);
	    };
	}

	
} (warnOnce));

var imageBlurSvg = {};

/**
 * A shared function, used on both client and server, to generate a SVG blur placeholder.
 */

(function (exports) {
	Object.defineProperty(exports, "__esModule", {
	    value: true
	});
	Object.defineProperty(exports, "getImageBlurSvg", {
	    enumerable: true,
	    get: function() {
	        return getImageBlurSvg;
	    }
	});
	function getImageBlurSvg(param) {
	    let { widthInt, heightInt, blurWidth, blurHeight, blurDataURL, objectFit } = param;
	    const std = 20;
	    const svgWidth = blurWidth ? blurWidth * 40 : widthInt;
	    const svgHeight = blurHeight ? blurHeight * 40 : heightInt;
	    const viewBox = svgWidth && svgHeight ? "viewBox='0 0 " + svgWidth + " " + svgHeight + "'" : "";
	    const preserveAspectRatio = viewBox ? "none" : objectFit === "contain" ? "xMidYMid" : objectFit === "cover" ? "xMidYMid slice" : "none";
	    return "%3Csvg xmlns='http://www.w3.org/2000/svg' " + viewBox + "%3E%3Cfilter id='b' color-interpolation-filters='sRGB'%3E%3CfeGaussianBlur stdDeviation='" + std + "'/%3E%3CfeColorMatrix values='1 0 0 0 0 0 1 0 0 0 0 0 1 0 0 0 0 0 100 -1' result='s'/%3E%3CfeFlood x='0' y='0' width='100%25' height='100%25'/%3E%3CfeComposite operator='out' in='s'/%3E%3CfeComposite in2='SourceGraphic'/%3E%3CfeGaussianBlur stdDeviation='" + std + "'/%3E%3C/filter%3E%3Cimage width='100%25' height='100%25' x='0' y='0' preserveAspectRatio='" + preserveAspectRatio + "' style='filter: url(%23b);' href='" + blurDataURL + "'/%3E%3C/svg%3E";
	}

	
} (imageBlurSvg));

var imageConfig = {};

(function (exports) {
	Object.defineProperty(exports, "__esModule", {
	    value: true
	});
	function _export(target, all) {
	    for(var name in all)Object.defineProperty(target, name, {
	        enumerable: true,
	        get: all[name]
	    });
	}
	_export(exports, {
	    VALID_LOADERS: function() {
	        return VALID_LOADERS;
	    },
	    imageConfigDefault: function() {
	        return imageConfigDefault;
	    }
	});
	const VALID_LOADERS = [
	    "default",
	    "imgix",
	    "cloudinary",
	    "akamai",
	    "custom"
	];
	const imageConfigDefault = {
	    deviceSizes: [
	        640,
	        750,
	        828,
	        1080,
	        1200,
	        1920,
	        2048,
	        3840
	    ],
	    imageSizes: [
	        16,
	        32,
	        48,
	        64,
	        96,
	        128,
	        256,
	        384
	    ],
	    path: "/_next/image",
	    loader: "default",
	    loaderFile: "",
	    domains: [],
	    disableStaticImages: false,
	    minimumCacheTTL: 60,
	    formats: [
	        "image/webp"
	    ],
	    dangerouslyAllowSVG: false,
	    contentSecurityPolicy: "script-src 'none'; frame-src 'none'; sandbox;",
	    contentDispositionType: "inline",
	    remotePatterns: [],
	    unoptimized: false
	};

	
} (imageConfig));

(function (exports) {
	Object.defineProperty(exports, "__esModule", {
	    value: true
	});
	Object.defineProperty(exports, "getImgProps", {
	    enumerable: true,
	    get: function() {
	        return getImgProps;
	    }
	});
	const _warnonce = warnOnce;
	const _imageblursvg = imageBlurSvg;
	const _imageconfig = imageConfig;
	const VALID_LOADING_VALUES = [
	    "lazy",
	    "eager",
	    undefined
	];
	function isStaticRequire(src) {
	    return src.default !== undefined;
	}
	function isStaticImageData(src) {
	    return src.src !== undefined;
	}
	function isStaticImport(src) {
	    return typeof src === "object" && (isStaticRequire(src) || isStaticImageData(src));
	}
	const allImgs = new Map();
	let perfObserver;
	function getInt(x) {
	    if (typeof x === "undefined") {
	        return x;
	    }
	    if (typeof x === "number") {
	        return Number.isFinite(x) ? x : NaN;
	    }
	    if (typeof x === "string" && /^[0-9]+$/.test(x)) {
	        return parseInt(x, 10);
	    }
	    return NaN;
	}
	function getWidths(param, width, sizes) {
	    let { deviceSizes, allSizes } = param;
	    if (sizes) {
	        // Find all the "vw" percent sizes used in the sizes prop
	        const viewportWidthRe = /(^|\s)(1?\d?\d)vw/g;
	        const percentSizes = [];
	        for(let match; match = viewportWidthRe.exec(sizes); match){
	            percentSizes.push(parseInt(match[2]));
	        }
	        if (percentSizes.length) {
	            const smallestRatio = Math.min(...percentSizes) * 0.01;
	            return {
	                widths: allSizes.filter((s)=>s >= deviceSizes[0] * smallestRatio),
	                kind: "w"
	            };
	        }
	        return {
	            widths: allSizes,
	            kind: "w"
	        };
	    }
	    if (typeof width !== "number") {
	        return {
	            widths: deviceSizes,
	            kind: "w"
	        };
	    }
	    const widths = [
	        ...new Set(// > This means that most OLED screens that say they are 3x resolution,
	        // > are actually 3x in the green color, but only 1.5x in the red and
	        // > blue colors. Showing a 3x resolution image in the app vs a 2x
	        // > resolution image will be visually the same, though the 3x image
	        // > takes significantly more data. Even true 3x resolution screens are
	        // > wasteful as the human eye cannot see that level of detail without
	        // > something like a magnifying glass.
	        // https://blog.twitter.com/engineering/en_us/topics/infrastructure/2019/capping-image-fidelity-on-ultra-high-resolution-devices.html
	        [
	            width,
	            width * 2 /*, width * 3*/ 
	        ].map((w)=>allSizes.find((p)=>p >= w) || allSizes[allSizes.length - 1]))
	    ];
	    return {
	        widths,
	        kind: "x"
	    };
	}
	function generateImgAttrs(param) {
	    let { config, src, unoptimized, width, quality, sizes, loader } = param;
	    if (unoptimized) {
	        return {
	            src,
	            srcSet: undefined,
	            sizes: undefined
	        };
	    }
	    const { widths, kind } = getWidths(config, width, sizes);
	    const last = widths.length - 1;
	    return {
	        sizes: !sizes && kind === "w" ? "100vw" : sizes,
	        srcSet: widths.map((w, i)=>loader({
	                config,
	                src,
	                quality,
	                width: w
	            }) + " " + (kind === "w" ? w : i + 1) + kind).join(", "),
	        // It's intended to keep `src` the last attribute because React updates
	        // attributes in order. If we keep `src` the first one, Safari will
	        // immediately start to fetch `src`, before `sizes` and `srcSet` are even
	        // updated by React. That causes multiple unnecessary requests if `srcSet`
	        // and `sizes` are defined.
	        // This bug cannot be reproduced in Chrome or Firefox.
	        src: loader({
	            config,
	            src,
	            quality,
	            width: widths[last]
	        })
	    };
	}
	function getImgProps(param, _state) {
	    let { src, sizes, unoptimized = false, priority = false, loading, className, quality, width, height, fill = false, style, onLoad, onLoadingComplete, placeholder = "empty", blurDataURL, fetchPriority, layout, objectFit, objectPosition, lazyBoundary, lazyRoot, ...rest } = param;
	    const { imgConf, showAltText, blurComplete, defaultLoader } = _state;
	    let config;
	    let c = imgConf || _imageconfig.imageConfigDefault;
	    if ("allSizes" in c) {
	        config = c;
	    } else {
	        const allSizes = [
	            ...c.deviceSizes,
	            ...c.imageSizes
	        ].sort((a, b)=>a - b);
	        const deviceSizes = c.deviceSizes.sort((a, b)=>a - b);
	        config = {
	            ...c,
	            allSizes,
	            deviceSizes
	        };
	    }
	    let loader = rest.loader || defaultLoader;
	    // Remove property so it's not spread on <img> element
	    delete rest.loader;
	    delete rest.srcSet;
	    // This special value indicates that the user
	    // didn't define a "loader" prop or "loader" config.
	    const isDefaultLoader = "__next_img_default" in loader;
	    if (isDefaultLoader) {
	        if (config.loader === "custom") {
	            throw new Error('Image with src "' + src + '" is missing "loader" prop.' + "\nRead more: https://nextjs.org/docs/messages/next-image-missing-loader");
	        }
	    } else {
	        // The user defined a "loader" prop or config.
	        // Since the config object is internal only, we
	        // must not pass it to the user-defined "loader".
	        const customImageLoader = loader;
	        loader = (obj)=>{
	            const { config: _, ...opts } = obj;
	            return customImageLoader(opts);
	        };
	    }
	    if (layout) {
	        if (layout === "fill") {
	            fill = true;
	        }
	        const layoutToStyle = {
	            intrinsic: {
	                maxWidth: "100%",
	                height: "auto"
	            },
	            responsive: {
	                width: "100%",
	                height: "auto"
	            }
	        };
	        const layoutToSizes = {
	            responsive: "100vw",
	            fill: "100vw"
	        };
	        const layoutStyle = layoutToStyle[layout];
	        if (layoutStyle) {
	            style = {
	                ...style,
	                ...layoutStyle
	            };
	        }
	        const layoutSizes = layoutToSizes[layout];
	        if (layoutSizes && !sizes) {
	            sizes = layoutSizes;
	        }
	    }
	    let staticSrc = "";
	    let widthInt = getInt(width);
	    let heightInt = getInt(height);
	    let blurWidth;
	    let blurHeight;
	    if (isStaticImport(src)) {
	        const staticImageData = isStaticRequire(src) ? src.default : src;
	        if (!staticImageData.src) {
	            throw new Error("An object should only be passed to the image component src parameter if it comes from a static image import. It must include src. Received " + JSON.stringify(staticImageData));
	        }
	        if (!staticImageData.height || !staticImageData.width) {
	            throw new Error("An object should only be passed to the image component src parameter if it comes from a static image import. It must include height and width. Received " + JSON.stringify(staticImageData));
	        }
	        blurWidth = staticImageData.blurWidth;
	        blurHeight = staticImageData.blurHeight;
	        blurDataURL = blurDataURL || staticImageData.blurDataURL;
	        staticSrc = staticImageData.src;
	        if (!fill) {
	            if (!widthInt && !heightInt) {
	                widthInt = staticImageData.width;
	                heightInt = staticImageData.height;
	            } else if (widthInt && !heightInt) {
	                const ratio = widthInt / staticImageData.width;
	                heightInt = Math.round(staticImageData.height * ratio);
	            } else if (!widthInt && heightInt) {
	                const ratio = heightInt / staticImageData.height;
	                widthInt = Math.round(staticImageData.width * ratio);
	            }
	        }
	    }
	    src = typeof src === "string" ? src : staticSrc;
	    let isLazy = !priority && (loading === "lazy" || typeof loading === "undefined");
	    if (!src || src.startsWith("data:") || src.startsWith("blob:")) {
	        // https://developer.mozilla.org/docs/Web/HTTP/Basics_of_HTTP/Data_URIs
	        unoptimized = true;
	        isLazy = false;
	    }
	    if (config.unoptimized) {
	        unoptimized = true;
	    }
	    if (isDefaultLoader && src.endsWith(".svg") && !config.dangerouslyAllowSVG) {
	        // Special case to make svg serve as-is to avoid proxying
	        // through the built-in Image Optimization API.
	        unoptimized = true;
	    }
	    if (priority) {
	        fetchPriority = "high";
	    }
	    const qualityInt = getInt(quality);
	    if (process.env.NODE_ENV !== "production") {
	        if (config.output === "export" && isDefaultLoader && !unoptimized) {
	            throw new Error("Image Optimization using the default loader is not compatible with `{ output: 'export' }`.\n  Possible solutions:\n    - Remove `{ output: 'export' }` and run \"next start\" to run server mode including the Image Optimization API.\n    - Configure `{ images: { unoptimized: true } }` in `next.config.js` to disable the Image Optimization API.\n  Read more: https://nextjs.org/docs/messages/export-image-api");
	        }
	        if (!src) {
	            // React doesn't show the stack trace and there's
	            // no `src` to help identify which image, so we
	            // instead console.error(ref) during mount.
	            unoptimized = true;
	        } else {
	            if (fill) {
	                if (width) {
	                    throw new Error('Image with src "' + src + '" has both "width" and "fill" properties. Only one should be used.');
	                }
	                if (height) {
	                    throw new Error('Image with src "' + src + '" has both "height" and "fill" properties. Only one should be used.');
	                }
	                if ((style == null ? void 0 : style.position) && style.position !== "absolute") {
	                    throw new Error('Image with src "' + src + '" has both "fill" and "style.position" properties. Images with "fill" always use position absolute - it cannot be modified.');
	                }
	                if ((style == null ? void 0 : style.width) && style.width !== "100%") {
	                    throw new Error('Image with src "' + src + '" has both "fill" and "style.width" properties. Images with "fill" always use width 100% - it cannot be modified.');
	                }
	                if ((style == null ? void 0 : style.height) && style.height !== "100%") {
	                    throw new Error('Image with src "' + src + '" has both "fill" and "style.height" properties. Images with "fill" always use height 100% - it cannot be modified.');
	                }
	            } else {
	                if (typeof widthInt === "undefined") {
	                    throw new Error('Image with src "' + src + '" is missing required "width" property.');
	                } else if (isNaN(widthInt)) {
	                    throw new Error('Image with src "' + src + '" has invalid "width" property. Expected a numeric value in pixels but received "' + width + '".');
	                }
	                if (typeof heightInt === "undefined") {
	                    throw new Error('Image with src "' + src + '" is missing required "height" property.');
	                } else if (isNaN(heightInt)) {
	                    throw new Error('Image with src "' + src + '" has invalid "height" property. Expected a numeric value in pixels but received "' + height + '".');
	                }
	            }
	        }
	        if (!VALID_LOADING_VALUES.includes(loading)) {
	            throw new Error('Image with src "' + src + '" has invalid "loading" property. Provided "' + loading + '" should be one of ' + VALID_LOADING_VALUES.map(String).join(",") + ".");
	        }
	        if (priority && loading === "lazy") {
	            throw new Error('Image with src "' + src + '" has both "priority" and "loading=\'lazy\'" properties. Only one should be used.');
	        }
	        if (placeholder !== "empty" && placeholder !== "blur" && !placeholder.startsWith("data:image/")) {
	            throw new Error('Image with src "' + src + '" has invalid "placeholder" property "' + placeholder + '".');
	        }
	        if (placeholder !== "empty") {
	            if (widthInt && heightInt && widthInt * heightInt < 1600) {
	                (0, _warnonce.warnOnce)('Image with src "' + src + '" is smaller than 40x40. Consider removing the "placeholder" property to improve performance.');
	            }
	        }
	        if (placeholder === "blur" && !blurDataURL) {
	            const VALID_BLUR_EXT = [
	                "jpeg",
	                "png",
	                "webp",
	                "avif"
	            ] // should match next-image-loader
	            ;
	            throw new Error('Image with src "' + src + '" has "placeholder=\'blur\'" property but is missing the "blurDataURL" property.\n        Possible solutions:\n          - Add a "blurDataURL" property, the contents should be a small Data URL to represent the image\n          - Change the "src" property to a static import with one of the supported file types: ' + VALID_BLUR_EXT.join(",") + ' (animated images not supported)\n          - Remove the "placeholder" property, effectively no blur effect\n        Read more: https://nextjs.org/docs/messages/placeholder-blur-data-url');
	        }
	        if ("ref" in rest) {
	            (0, _warnonce.warnOnce)('Image with src "' + src + '" is using unsupported "ref" property. Consider using the "onLoad" property instead.');
	        }
	        if (!unoptimized && !isDefaultLoader) {
	            const urlStr = loader({
	                config,
	                src,
	                width: widthInt || 400,
	                quality: qualityInt || 75
	            });
	            let url;
	            try {
	                url = new URL(urlStr);
	            } catch (err) {}
	            if (urlStr === src || url && url.pathname === src && !url.search) {
	                (0, _warnonce.warnOnce)('Image with src "' + src + '" has a "loader" property that does not implement width. Please implement it or use the "unoptimized" property instead.' + "\nRead more: https://nextjs.org/docs/messages/next-image-missing-loader-width");
	            }
	        }
	        if (onLoadingComplete) {
	            (0, _warnonce.warnOnce)('Image with src "' + src + '" is using deprecated "onLoadingComplete" property. Please use the "onLoad" property instead.');
	        }
	        for (const [legacyKey, legacyValue] of Object.entries({
	            layout,
	            objectFit,
	            objectPosition,
	            lazyBoundary,
	            lazyRoot
	        })){
	            if (legacyValue) {
	                (0, _warnonce.warnOnce)('Image with src "' + src + '" has legacy prop "' + legacyKey + '". Did you forget to run the codemod?' + "\nRead more: https://nextjs.org/docs/messages/next-image-upgrade-to-13");
	            }
	        }
	        if (typeof window !== "undefined" && !perfObserver && window.PerformanceObserver) {
	            perfObserver = new PerformanceObserver((entryList)=>{
	                for (const entry of entryList.getEntries()){
	                    var _entry_element;
	                    // @ts-ignore - missing "LargestContentfulPaint" class with "element" prop
	                    const imgSrc = (entry == null ? void 0 : (_entry_element = entry.element) == null ? void 0 : _entry_element.src) || "";
	                    const lcpImage = allImgs.get(imgSrc);
	                    if (lcpImage && !lcpImage.priority && lcpImage.placeholder === "empty" && !lcpImage.src.startsWith("data:") && !lcpImage.src.startsWith("blob:")) {
	                        // https://web.dev/lcp/#measure-lcp-in-javascript
	                        (0, _warnonce.warnOnce)('Image with src "' + lcpImage.src + '" was detected as the Largest Contentful Paint (LCP). Please add the "priority" property if this image is above the fold.' + "\nRead more: https://nextjs.org/docs/api-reference/next/image#priority");
	                    }
	                }
	            });
	            try {
	                perfObserver.observe({
	                    type: "largest-contentful-paint",
	                    buffered: true
	                });
	            } catch (err) {
	                // Log error but don't crash the app
	                console.error(err);
	            }
	        }
	    }
	    const imgStyle = Object.assign(fill ? {
	        position: "absolute",
	        height: "100%",
	        width: "100%",
	        left: 0,
	        top: 0,
	        right: 0,
	        bottom: 0,
	        objectFit,
	        objectPosition
	    } : {}, showAltText ? {} : {
	        color: "transparent"
	    }, style);
	    const backgroundImage = !blurComplete && placeholder !== "empty" ? placeholder === "blur" ? 'url("data:image/svg+xml;charset=utf-8,' + (0, _imageblursvg.getImageBlurSvg)({
	        widthInt,
	        heightInt,
	        blurWidth,
	        blurHeight,
	        blurDataURL: blurDataURL || "",
	        objectFit: imgStyle.objectFit
	    }) + '")' : 'url("' + placeholder + '")' // assume `data:image/`
	     : null;
	    let placeholderStyle = backgroundImage ? {
	        backgroundSize: imgStyle.objectFit || "cover",
	        backgroundPosition: imgStyle.objectPosition || "50% 50%",
	        backgroundRepeat: "no-repeat",
	        backgroundImage
	    } : {};
	    if (process.env.NODE_ENV === "development") {
	        if (placeholderStyle.backgroundImage && placeholder === "blur" && (blurDataURL == null ? void 0 : blurDataURL.startsWith("/"))) {
	            // During `next dev`, we don't want to generate blur placeholders with webpack
	            // because it can delay starting the dev server. Instead, `next-image-loader.js`
	            // will inline a special url to lazily generate the blur placeholder at request time.
	            placeholderStyle.backgroundImage = 'url("' + blurDataURL + '")';
	        }
	    }
	    const imgAttributes = generateImgAttrs({
	        config,
	        src,
	        unoptimized,
	        width: widthInt,
	        quality: qualityInt,
	        sizes,
	        loader
	    });
	    if (process.env.NODE_ENV !== "production") {
	        if (typeof window !== "undefined") {
	            let fullUrl;
	            try {
	                fullUrl = new URL(imgAttributes.src);
	            } catch (e) {
	                fullUrl = new URL(imgAttributes.src, window.location.href);
	            }
	            allImgs.set(fullUrl.href, {
	                src,
	                priority,
	                placeholder
	            });
	        }
	    }
	    const props = {
	        ...rest,
	        loading: isLazy ? "lazy" : loading,
	        fetchPriority,
	        width: widthInt,
	        height: heightInt,
	        decoding: "async",
	        className,
	        style: {
	            ...imgStyle,
	            ...placeholderStyle
	        },
	        sizes: imgAttributes.sizes,
	        srcSet: imgAttributes.srcSet,
	        src: imgAttributes.src
	    };
	    const meta = {
	        unoptimized,
	        priority,
	        placeholder,
	        fill
	    };
	    return {
	        props,
	        meta
	    };
	}

	
} (getImgProps));

var imageComponent = {exports: {}};

var head = {exports: {}};

var sideEffect = {};

var hasRequiredSideEffect;

function requireSideEffect () {
	if (hasRequiredSideEffect) return sideEffect;
	hasRequiredSideEffect = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", {
		    value: true
		});
		Object.defineProperty(exports, "default", {
		    enumerable: true,
		    get: function() {
		        return SideEffect;
		    }
		});
		const _react = React__default;
		const isServer = typeof window === "undefined";
		const useClientOnlyLayoutEffect = isServer ? ()=>{} : _react.useLayoutEffect;
		const useClientOnlyEffect = isServer ? ()=>{} : _react.useEffect;
		function SideEffect(props) {
		    const { headManager, reduceComponentsToState } = props;
		    function emitChange() {
		        if (headManager && headManager.mountedInstances) {
		            const headElements = _react.Children.toArray(Array.from(headManager.mountedInstances).filter(Boolean));
		            headManager.updateHead(reduceComponentsToState(headElements, props));
		        }
		    }
		    if (isServer) {
		        var _headManager_mountedInstances;
		        headManager == null ? void 0 : (_headManager_mountedInstances = headManager.mountedInstances) == null ? void 0 : _headManager_mountedInstances.add(props.children);
		        emitChange();
		    }
		    useClientOnlyLayoutEffect(()=>{
		        var _headManager_mountedInstances;
		        headManager == null ? void 0 : (_headManager_mountedInstances = headManager.mountedInstances) == null ? void 0 : _headManager_mountedInstances.add(props.children);
		        return ()=>{
		            var _headManager_mountedInstances;
		            headManager == null ? void 0 : (_headManager_mountedInstances = headManager.mountedInstances) == null ? void 0 : _headManager_mountedInstances.delete(props.children);
		        };
		    });
		    // We need to call `updateHead` method whenever the `SideEffect` is trigger in all
		    // life-cycles: mount, update, unmount. However, if there are multiple `SideEffect`s
		    // being rendered, we only trigger the method from the last one.
		    // This is ensured by keeping the last unflushed `updateHead` in the `_pendingUpdate`
		    // singleton in the layout effect pass, and actually trigger it in the effect pass.
		    useClientOnlyLayoutEffect(()=>{
		        if (headManager) {
		            headManager._pendingUpdate = emitChange;
		        }
		        return ()=>{
		            if (headManager) {
		                headManager._pendingUpdate = emitChange;
		            }
		        };
		    });
		    useClientOnlyEffect(()=>{
		        if (headManager && headManager._pendingUpdate) {
		            headManager._pendingUpdate();
		            headManager._pendingUpdate = null;
		        }
		        return ()=>{
		            if (headManager && headManager._pendingUpdate) {
		                headManager._pendingUpdate();
		                headManager._pendingUpdate = null;
		            }
		        };
		    });
		    return null;
		}

		
	} (sideEffect));
	return sideEffect;
}

var ampContext_sharedRuntime = {};

var hasRequiredAmpContext_sharedRuntime;

function requireAmpContext_sharedRuntime () {
	if (hasRequiredAmpContext_sharedRuntime) return ampContext_sharedRuntime;
	hasRequiredAmpContext_sharedRuntime = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", {
		    value: true
		});
		Object.defineProperty(exports, "AmpStateContext", {
		    enumerable: true,
		    get: function() {
		        return AmpStateContext;
		    }
		});
		const _interop_require_default = _interop_require_default$1;
		const _react = /*#__PURE__*/ _interop_require_default._(React__default);
		const AmpStateContext = _react.default.createContext({});
		if (process.env.NODE_ENV !== "production") {
		    AmpStateContext.displayName = "AmpStateContext";
		}

		
	} (ampContext_sharedRuntime));
	return ampContext_sharedRuntime;
}

var headManagerContext_sharedRuntime = {};

var hasRequiredHeadManagerContext_sharedRuntime;

function requireHeadManagerContext_sharedRuntime () {
	if (hasRequiredHeadManagerContext_sharedRuntime) return headManagerContext_sharedRuntime;
	hasRequiredHeadManagerContext_sharedRuntime = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", {
		    value: true
		});
		Object.defineProperty(exports, "HeadManagerContext", {
		    enumerable: true,
		    get: function() {
		        return HeadManagerContext;
		    }
		});
		const _interop_require_default = _interop_require_default$1;
		const _react = /*#__PURE__*/ _interop_require_default._(React__default);
		const HeadManagerContext = _react.default.createContext({});
		if (process.env.NODE_ENV !== "production") {
		    HeadManagerContext.displayName = "HeadManagerContext";
		}

		
	} (headManagerContext_sharedRuntime));
	return headManagerContext_sharedRuntime;
}

var ampMode = {};

var hasRequiredAmpMode;

function requireAmpMode () {
	if (hasRequiredAmpMode) return ampMode;
	hasRequiredAmpMode = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", {
		    value: true
		});
		Object.defineProperty(exports, "isInAmpMode", {
		    enumerable: true,
		    get: function() {
		        return isInAmpMode;
		    }
		});
		function isInAmpMode(param) {
		    let { ampFirst = false, hybrid = false, hasQuery = false } = param === void 0 ? {} : param;
		    return ampFirst || hybrid && hasQuery;
		}

		
	} (ampMode));
	return ampMode;
}

var hasRequiredHead;

function requireHead () {
	if (hasRequiredHead) return head.exports;
	hasRequiredHead = 1;
	(function (module, exports) {
		"use client";
		Object.defineProperty(exports, "__esModule", {
		    value: true
		});
		function _export(target, all) {
		    for(var name in all)Object.defineProperty(target, name, {
		        enumerable: true,
		        get: all[name]
		    });
		}
		_export(exports, {
		    defaultHead: function() {
		        return defaultHead;
		    },
		    default: function() {
		        return _default;
		    }
		});
		const _interop_require_default = _interop_require_default$1;
		const _interop_require_wildcard = _interop_require_wildcard$1;
		const _react = /*#__PURE__*/ _interop_require_wildcard._(React__default);
		const _sideeffect = /*#__PURE__*/ _interop_require_default._(requireSideEffect());
		const _ampcontextsharedruntime = requireAmpContext_sharedRuntime();
		const _headmanagercontextsharedruntime = requireHeadManagerContext_sharedRuntime();
		const _ampmode = requireAmpMode();
		const _warnonce = warnOnce;
		function defaultHead(inAmpMode) {
		    if (inAmpMode === void 0) inAmpMode = false;
		    const head = [
		        /*#__PURE__*/ _react.default.createElement("meta", {
		            charSet: "utf-8"
		        })
		    ];
		    if (!inAmpMode) {
		        head.push(/*#__PURE__*/ _react.default.createElement("meta", {
		            name: "viewport",
		            content: "width=device-width"
		        }));
		    }
		    return head;
		}
		function onlyReactElement(list, child) {
		    // React children can be "string" or "number" in this case we ignore them for backwards compat
		    if (typeof child === "string" || typeof child === "number") {
		        return list;
		    }
		    // Adds support for React.Fragment
		    if (child.type === _react.default.Fragment) {
		        return list.concat(// @ts-expect-error @types/react does not remove fragments but this could also return ReactPortal[]
		        _react.default.Children.toArray(child.props.children).reduce(// @ts-expect-error @types/react does not remove fragments but this could also return ReactPortal[]
		        (fragmentList, fragmentChild)=>{
		            if (typeof fragmentChild === "string" || typeof fragmentChild === "number") {
		                return fragmentList;
		            }
		            return fragmentList.concat(fragmentChild);
		        }, []));
		    }
		    return list.concat(child);
		}
		const METATYPES = [
		    "name",
		    "httpEquiv",
		    "charSet",
		    "itemProp"
		];
		/*
		 returns a function for filtering head child elements
		 which shouldn't be duplicated, like <title/>
		 Also adds support for deduplicated `key` properties
		*/ function unique() {
		    const keys = new Set();
		    const tags = new Set();
		    const metaTypes = new Set();
		    const metaCategories = {};
		    return (h)=>{
		        let isUnique = true;
		        let hasKey = false;
		        if (h.key && typeof h.key !== "number" && h.key.indexOf("$") > 0) {
		            hasKey = true;
		            const key = h.key.slice(h.key.indexOf("$") + 1);
		            if (keys.has(key)) {
		                isUnique = false;
		            } else {
		                keys.add(key);
		            }
		        }
		        // eslint-disable-next-line default-case
		        switch(h.type){
		            case "title":
		            case "base":
		                if (tags.has(h.type)) {
		                    isUnique = false;
		                } else {
		                    tags.add(h.type);
		                }
		                break;
		            case "meta":
		                for(let i = 0, len = METATYPES.length; i < len; i++){
		                    const metatype = METATYPES[i];
		                    if (!h.props.hasOwnProperty(metatype)) continue;
		                    if (metatype === "charSet") {
		                        if (metaTypes.has(metatype)) {
		                            isUnique = false;
		                        } else {
		                            metaTypes.add(metatype);
		                        }
		                    } else {
		                        const category = h.props[metatype];
		                        const categories = metaCategories[metatype] || new Set();
		                        if ((metatype !== "name" || !hasKey) && categories.has(category)) {
		                            isUnique = false;
		                        } else {
		                            categories.add(category);
		                            metaCategories[metatype] = categories;
		                        }
		                    }
		                }
		                break;
		        }
		        return isUnique;
		    };
		}
		/**
		 *
		 * @param headChildrenElements List of children of <Head>
		 */ function reduceComponents(headChildrenElements, props) {
		    const { inAmpMode } = props;
		    return headChildrenElements.reduce(onlyReactElement, []).reverse().concat(defaultHead(inAmpMode).reverse()).filter(unique()).reverse().map((c, i)=>{
		        const key = c.key || i;
		        if (process.env.NODE_ENV !== "development" && process.env.__NEXT_OPTIMIZE_FONTS && !inAmpMode) {
		            if (c.type === "link" && c.props["href"] && // TODO(prateekbh@): Replace this with const from `constants` when the tree shaking works.
		            [
		                "https://fonts.googleapis.com/css",
		                "https://use.typekit.net/"
		            ].some((url)=>c.props["href"].startsWith(url))) {
		                const newProps = {
		                    ...c.props || {}
		                };
		                newProps["data-href"] = newProps["href"];
		                newProps["href"] = undefined;
		                // Add this attribute to make it easy to identify optimized tags
		                newProps["data-optimized-fonts"] = true;
		                return /*#__PURE__*/ _react.default.cloneElement(c, newProps);
		            }
		        }
		        if (process.env.NODE_ENV === "development") {
		            // omit JSON-LD structured data snippets from the warning
		            if (c.type === "script" && c.props["type"] !== "application/ld+json") {
		                const srcMessage = c.props["src"] ? '<script> tag with src="' + c.props["src"] + '"' : "inline <script>";
		                (0, _warnonce.warnOnce)("Do not add <script> tags using next/head (see " + srcMessage + "). Use next/script instead. \nSee more info here: https://nextjs.org/docs/messages/no-script-tags-in-head-component");
		            } else if (c.type === "link" && c.props["rel"] === "stylesheet") {
		                (0, _warnonce.warnOnce)('Do not add stylesheets using next/head (see <link rel="stylesheet"> tag with href="' + c.props["href"] + '"). Use Document instead. \nSee more info here: https://nextjs.org/docs/messages/no-stylesheets-in-head-component');
		            }
		        }
		        return /*#__PURE__*/ _react.default.cloneElement(c, {
		            key
		        });
		    });
		}
		/**
		 * This component injects elements to `<head>` of your page.
		 * To avoid duplicated `tags` in `<head>` you can use the `key` property, which will make sure every tag is only rendered once.
		 */ function Head(param) {
		    let { children } = param;
		    const ampState = (0, _react.useContext)(_ampcontextsharedruntime.AmpStateContext);
		    const headManager = (0, _react.useContext)(_headmanagercontextsharedruntime.HeadManagerContext);
		    return /*#__PURE__*/ _react.default.createElement(_sideeffect.default, {
		        reduceComponentsToState: reduceComponents,
		        headManager: headManager,
		        inAmpMode: (0, _ampmode.isInAmpMode)(ampState)
		    }, children);
		}
		const _default = Head;

		if ((typeof exports.default === 'function' || (typeof exports.default === 'object' && exports.default !== null)) && typeof exports.default.__esModule === 'undefined') {
		  Object.defineProperty(exports.default, '__esModule', { value: true });
		  Object.assign(exports.default, exports);
		  module.exports = exports.default;
		}

		
	} (head, head.exports));
	return head.exports;
}

var imageConfigContext_sharedRuntime = {};

var hasRequiredImageConfigContext_sharedRuntime;

function requireImageConfigContext_sharedRuntime () {
	if (hasRequiredImageConfigContext_sharedRuntime) return imageConfigContext_sharedRuntime;
	hasRequiredImageConfigContext_sharedRuntime = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", {
		    value: true
		});
		Object.defineProperty(exports, "ImageConfigContext", {
		    enumerable: true,
		    get: function() {
		        return ImageConfigContext;
		    }
		});
		const _interop_require_default = _interop_require_default$1;
		const _react = /*#__PURE__*/ _interop_require_default._(React__default);
		const _imageconfig = imageConfig;
		const ImageConfigContext = _react.default.createContext(_imageconfig.imageConfigDefault);
		if (process.env.NODE_ENV !== "production") {
		    ImageConfigContext.displayName = "ImageConfigContext";
		}

		
	} (imageConfigContext_sharedRuntime));
	return imageConfigContext_sharedRuntime;
}

var imageLoader = {};

var matchRemotePattern = {};

var micromatch = {exports: {}};

var hasRequiredMicromatch;

function requireMicromatch () {
	if (hasRequiredMicromatch) return micromatch.exports;
	hasRequiredMicromatch = 1;
	(()=>{var e={333:(e,t,r)=>{const n=r(137);const u=r(179);const s=r(13);const o=r(719);const braces=(e,t={})=>{let r=[];if(Array.isArray(e)){for(let n of e){let e=braces.create(n,t);if(Array.isArray(e)){r.push(...e);}else {r.push(e);}}}else {r=[].concat(braces.create(e,t));}if(t&&t.expand===true&&t.nodupes===true){r=[...new Set(r)];}return r};braces.parse=(e,t={})=>o(e,t);braces.stringify=(e,t={})=>{if(typeof e==="string"){return n(braces.parse(e,t),t)}return n(e,t)};braces.compile=(e,t={})=>{if(typeof e==="string"){e=braces.parse(e,t);}return u(e,t)};braces.expand=(e,t={})=>{if(typeof e==="string"){e=braces.parse(e,t);}let r=s(e,t);if(t.noempty===true){r=r.filter(Boolean);}if(t.nodupes===true){r=[...new Set(r)];}return r};braces.create=(e,t={})=>{if(e===""||e.length<3){return [e]}return t.expand!==true?braces.compile(e,t):braces.expand(e,t)};e.exports=braces;},179:(e,t,r)=>{const n=r(783);const u=r(617);const compile=(e,t={})=>{let walk=(e,r={})=>{let s=u.isInvalidBrace(r);let o=e.invalid===true&&t.escapeInvalid===true;let i=s===true||o===true;let a=t.escapeInvalid===true?"\\":"";let l="";if(e.isOpen===true){return a+e.value}if(e.isClose===true){return a+e.value}if(e.type==="open"){return i?a+e.value:"("}if(e.type==="close"){return i?a+e.value:")"}if(e.type==="comma"){return e.prev.type==="comma"?"":i?e.value:"|"}if(e.value){return e.value}if(e.nodes&&e.ranges>0){let r=u.reduce(e.nodes);let s=n(...r,{...t,wrap:false,toRegex:true});if(s.length!==0){return r.length>1&&s.length>1?`(${s})`:s}}if(e.nodes){for(let t of e.nodes){l+=walk(t,e);}}return l};return walk(e)};e.exports=compile;},457:e=>{e.exports={MAX_LENGTH:1024*64,CHAR_0:"0",CHAR_9:"9",CHAR_UPPERCASE_A:"A",CHAR_LOWERCASE_A:"a",CHAR_UPPERCASE_Z:"Z",CHAR_LOWERCASE_Z:"z",CHAR_LEFT_PARENTHESES:"(",CHAR_RIGHT_PARENTHESES:")",CHAR_ASTERISK:"*",CHAR_AMPERSAND:"&",CHAR_AT:"@",CHAR_BACKSLASH:"\\",CHAR_BACKTICK:"`",CHAR_CARRIAGE_RETURN:"\r",CHAR_CIRCUMFLEX_ACCENT:"^",CHAR_COLON:":",CHAR_COMMA:",",CHAR_DOLLAR:"$",CHAR_DOT:".",CHAR_DOUBLE_QUOTE:'"',CHAR_EQUAL:"=",CHAR_EXCLAMATION_MARK:"!",CHAR_FORM_FEED:"\f",CHAR_FORWARD_SLASH:"/",CHAR_HASH:"#",CHAR_HYPHEN_MINUS:"-",CHAR_LEFT_ANGLE_BRACKET:"<",CHAR_LEFT_CURLY_BRACE:"{",CHAR_LEFT_SQUARE_BRACKET:"[",CHAR_LINE_FEED:"\n",CHAR_NO_BREAK_SPACE:" ",CHAR_PERCENT:"%",CHAR_PLUS:"+",CHAR_QUESTION_MARK:"?",CHAR_RIGHT_ANGLE_BRACKET:">",CHAR_RIGHT_CURLY_BRACE:"}",CHAR_RIGHT_SQUARE_BRACKET:"]",CHAR_SEMICOLON:";",CHAR_SINGLE_QUOTE:"'",CHAR_SPACE:" ",CHAR_TAB:"\t",CHAR_UNDERSCORE:"_",CHAR_VERTICAL_LINE:"|",CHAR_ZERO_WIDTH_NOBREAK_SPACE:"\ufeff"};},13:(e,t,r)=>{const n=r(783);const u=r(137);const s=r(617);const append=(e="",t="",r=false)=>{let n=[];e=[].concat(e);t=[].concat(t);if(!t.length)return e;if(!e.length){return r?s.flatten(t).map((e=>`{${e}}`)):t}for(let u of e){if(Array.isArray(u)){for(let e of u){n.push(append(e,t,r));}}else {for(let e of t){if(r===true&&typeof e==="string")e=`{${e}}`;n.push(Array.isArray(e)?append(u,e,r):u+e);}}}return s.flatten(n)};const expand=(e,t={})=>{let r=t.rangeLimit===void 0?1e3:t.rangeLimit;let walk=(e,o={})=>{e.queue=[];let i=o;let a=o.queue;while(i.type!=="brace"&&i.type!=="root"&&i.parent){i=i.parent;a=i.queue;}if(e.invalid||e.dollar){a.push(append(a.pop(),u(e,t)));return}if(e.type==="brace"&&e.invalid!==true&&e.nodes.length===2){a.push(append(a.pop(),["{}"]));return}if(e.nodes&&e.ranges>0){let o=s.reduce(e.nodes);if(s.exceedsLimit(...o,t.step,r)){throw new RangeError("expanded array length exceeds range limit. Use options.rangeLimit to increase or disable the limit.")}let i=n(...o,t);if(i.length===0){i=u(e,t);}a.push(append(a.pop(),i));e.nodes=[];return}let l=s.encloseBrace(e);let c=e.queue;let p=e;while(p.type!=="brace"&&p.type!=="root"&&p.parent){p=p.parent;c=p.queue;}for(let t=0;t<e.nodes.length;t++){let r=e.nodes[t];if(r.type==="comma"&&e.type==="brace"){if(t===1)c.push("");c.push("");continue}if(r.type==="close"){a.push(append(a.pop(),c,l));continue}if(r.value&&r.type!=="open"){c.push(append(c.pop(),r.value));continue}if(r.nodes){walk(r,e);}}return c};return s.flatten(walk(e))};e.exports=expand;},719:(e,t,r)=>{const n=r(137);const{MAX_LENGTH:u,CHAR_BACKSLASH:s,CHAR_BACKTICK:o,CHAR_COMMA:i,CHAR_DOT:a,CHAR_LEFT_PARENTHESES:l,CHAR_RIGHT_PARENTHESES:c,CHAR_LEFT_CURLY_BRACE:p,CHAR_RIGHT_CURLY_BRACE:f,CHAR_LEFT_SQUARE_BRACKET:A,CHAR_RIGHT_SQUARE_BRACKET:R,CHAR_DOUBLE_QUOTE:_,CHAR_SINGLE_QUOTE:h,CHAR_NO_BREAK_SPACE:g,CHAR_ZERO_WIDTH_NOBREAK_SPACE:E}=r(457);const parse=(e,t={})=>{if(typeof e!=="string"){throw new TypeError("Expected a string")}let r=t||{};let C=typeof r.maxLength==="number"?Math.min(u,r.maxLength):u;if(e.length>C){throw new SyntaxError(`Input length (${e.length}), exceeds max characters (${C})`)}let y={type:"root",input:e,nodes:[]};let d=[y];let x=y;let b=y;let S=0;let H=e.length;let v=0;let $=0;let m;const advance=()=>e[v++];const push=e=>{if(e.type==="text"&&b.type==="dot"){b.type="text";}if(b&&b.type==="text"&&e.type==="text"){b.value+=e.value;return}x.nodes.push(e);e.parent=x;e.prev=b;b=e;return e};push({type:"bos"});while(v<H){x=d[d.length-1];m=advance();if(m===E||m===g){continue}if(m===s){push({type:"text",value:(t.keepEscaping?m:"")+advance()});continue}if(m===R){push({type:"text",value:"\\"+m});continue}if(m===A){S++;let t;while(v<H&&(t=advance())){m+=t;if(t===A){S++;continue}if(t===s){m+=advance();continue}if(t===R){S--;if(S===0){break}}}push({type:"text",value:m});continue}if(m===l){x=push({type:"paren",nodes:[]});d.push(x);push({type:"text",value:m});continue}if(m===c){if(x.type!=="paren"){push({type:"text",value:m});continue}x=d.pop();push({type:"text",value:m});x=d[d.length-1];continue}if(m===_||m===h||m===o){let e=m;let r;if(t.keepQuotes!==true){m="";}while(v<H&&(r=advance())){if(r===s){m+=r+advance();continue}if(r===e){if(t.keepQuotes===true)m+=r;break}m+=r;}push({type:"text",value:m});continue}if(m===p){$++;let e=b.value&&b.value.slice(-1)==="$"||x.dollar===true;let t={type:"brace",open:true,close:false,dollar:e,depth:$,commas:0,ranges:0,nodes:[]};x=push(t);d.push(x);push({type:"open",value:m});continue}if(m===f){if(x.type!=="brace"){push({type:"text",value:m});continue}let e="close";x=d.pop();x.close=true;push({type:e,value:m});$--;x=d[d.length-1];continue}if(m===i&&$>0){if(x.ranges>0){x.ranges=0;let e=x.nodes.shift();x.nodes=[e,{type:"text",value:n(x)}];}push({type:"comma",value:m});x.commas++;continue}if(m===a&&$>0&&x.commas===0){let e=x.nodes;if($===0||e.length===0){push({type:"text",value:m});continue}if(b.type==="dot"){x.range=[];b.value+=m;b.type="range";if(x.nodes.length!==3&&x.nodes.length!==5){x.invalid=true;x.ranges=0;b.type="text";continue}x.ranges++;x.args=[];continue}if(b.type==="range"){e.pop();let t=e[e.length-1];t.value+=b.value+m;b=t;x.ranges--;continue}push({type:"dot",value:m});continue}push({type:"text",value:m});}do{x=d.pop();if(x.type!=="root"){x.nodes.forEach((e=>{if(!e.nodes){if(e.type==="open")e.isOpen=true;if(e.type==="close")e.isClose=true;if(!e.nodes)e.type="text";e.invalid=true;}}));let e=d[d.length-1];let t=e.nodes.indexOf(x);e.nodes.splice(t,1,...x.nodes);}}while(d.length>0);push({type:"eos"});return y};e.exports=parse;},137:(e,t,r)=>{const n=r(617);e.exports=(e,t={})=>{let stringify=(e,r={})=>{let u=t.escapeInvalid&&n.isInvalidBrace(r);let s=e.invalid===true&&t.escapeInvalid===true;let o="";if(e.value){if((u||s)&&n.isOpenOrClose(e)){return "\\"+e.value}return e.value}if(e.value){return e.value}if(e.nodes){for(let t of e.nodes){o+=stringify(t);}}return o};return stringify(e)};},617:(e,t)=>{t.isInteger=e=>{if(typeof e==="number"){return Number.isInteger(e)}if(typeof e==="string"&&e.trim()!==""){return Number.isInteger(Number(e))}return false};t.find=(e,t)=>e.nodes.find((e=>e.type===t));t.exceedsLimit=(e,r,n=1,u)=>{if(u===false)return false;if(!t.isInteger(e)||!t.isInteger(r))return false;return (Number(r)-Number(e))/Number(n)>=u};t.escapeNode=(e,t=0,r)=>{let n=e.nodes[t];if(!n)return;if(r&&n.type===r||n.type==="open"||n.type==="close"){if(n.escaped!==true){n.value="\\"+n.value;n.escaped=true;}}};t.encloseBrace=e=>{if(e.type!=="brace")return false;if(e.commas>>0+e.ranges>>0===0){e.invalid=true;return true}return false};t.isInvalidBrace=e=>{if(e.type!=="brace")return false;if(e.invalid===true||e.dollar)return true;if(e.commas>>0+e.ranges>>0===0){e.invalid=true;return true}if(e.open!==true||e.close!==true){e.invalid=true;return true}return false};t.isOpenOrClose=e=>{if(e.type==="open"||e.type==="close"){return true}return e.open===true||e.close===true};t.reduce=e=>e.reduce(((e,t)=>{if(t.type==="text")e.push(t.value);if(t.type==="range")t.type="text";return e}),[]);t.flatten=(...e)=>{const t=[];const flat=e=>{for(let r=0;r<e.length;r++){let n=e[r];Array.isArray(n)?flat(n):n!==void 0&&t.push(n);}return t};flat(e);return t};},783:(e,t,r)=>{
	/*!
	 * fill-range <https://github.com/jonschlinkert/fill-range>
	 *
	 * Copyright (c) 2014-present, Jon Schlinkert.
	 * Licensed under the MIT License.
	 */
	const n=r(837);const u=r(492);const isObject=e=>e!==null&&typeof e==="object"&&!Array.isArray(e);const transform=e=>t=>e===true?Number(t):String(t);const isValidValue=e=>typeof e==="number"||typeof e==="string"&&e!=="";const isNumber=e=>Number.isInteger(+e);const zeros=e=>{let t=`${e}`;let r=-1;if(t[0]==="-")t=t.slice(1);if(t==="0")return false;while(t[++r]==="0");return r>0};const stringify=(e,t,r)=>{if(typeof e==="string"||typeof t==="string"){return true}return r.stringify===true};const pad=(e,t,r)=>{if(t>0){let r=e[0]==="-"?"-":"";if(r)e=e.slice(1);e=r+e.padStart(r?t-1:t,"0");}if(r===false){return String(e)}return e};const toMaxLen=(e,t)=>{let r=e[0]==="-"?"-":"";if(r){e=e.slice(1);t--;}while(e.length<t)e="0"+e;return r?"-"+e:e};const toSequence=(e,t)=>{e.negatives.sort(((e,t)=>e<t?-1:e>t?1:0));e.positives.sort(((e,t)=>e<t?-1:e>t?1:0));let r=t.capture?"":"?:";let n="";let u="";let s;if(e.positives.length){n=e.positives.join("|");}if(e.negatives.length){u=`-(${r}${e.negatives.join("|")})`;}if(n&&u){s=`${n}|${u}`;}else {s=n||u;}if(t.wrap){return `(${r}${s})`}return s};const toRange=(e,t,r,n)=>{if(r){return u(e,t,{wrap:false,...n})}let s=String.fromCharCode(e);if(e===t)return s;let o=String.fromCharCode(t);return `[${s}-${o}]`};const toRegex=(e,t,r)=>{if(Array.isArray(e)){let t=r.wrap===true;let n=r.capture?"":"?:";return t?`(${n}${e.join("|")})`:e.join("|")}return u(e,t,r)};const rangeError=(...e)=>new RangeError("Invalid range arguments: "+n.inspect(...e));const invalidRange=(e,t,r)=>{if(r.strictRanges===true)throw rangeError([e,t]);return []};const invalidStep=(e,t)=>{if(t.strictRanges===true){throw new TypeError(`Expected step "${e}" to be a number`)}return []};const fillNumbers=(e,t,r=1,n={})=>{let u=Number(e);let s=Number(t);if(!Number.isInteger(u)||!Number.isInteger(s)){if(n.strictRanges===true)throw rangeError([e,t]);return []}if(u===0)u=0;if(s===0)s=0;let o=u>s;let i=String(e);let a=String(t);let l=String(r);r=Math.max(Math.abs(r),1);let c=zeros(i)||zeros(a)||zeros(l);let p=c?Math.max(i.length,a.length,l.length):0;let f=c===false&&stringify(e,t,n)===false;let A=n.transform||transform(f);if(n.toRegex&&r===1){return toRange(toMaxLen(e,p),toMaxLen(t,p),true,n)}let R={negatives:[],positives:[]};let push=e=>R[e<0?"negatives":"positives"].push(Math.abs(e));let _=[];let h=0;while(o?u>=s:u<=s){if(n.toRegex===true&&r>1){push(u);}else {_.push(pad(A(u,h),p,f));}u=o?u-r:u+r;h++;}if(n.toRegex===true){return r>1?toSequence(R,n):toRegex(_,null,{wrap:false,...n})}return _};const fillLetters=(e,t,r=1,n={})=>{if(!isNumber(e)&&e.length>1||!isNumber(t)&&t.length>1){return invalidRange(e,t,n)}let u=n.transform||(e=>String.fromCharCode(e));let s=`${e}`.charCodeAt(0);let o=`${t}`.charCodeAt(0);let i=s>o;let a=Math.min(s,o);let l=Math.max(s,o);if(n.toRegex&&r===1){return toRange(a,l,false,n)}let c=[];let p=0;while(i?s>=o:s<=o){c.push(u(s,p));s=i?s-r:s+r;p++;}if(n.toRegex===true){return toRegex(c,null,{wrap:false,options:n})}return c};const fill=(e,t,r,n={})=>{if(t==null&&isValidValue(e)){return [e]}if(!isValidValue(e)||!isValidValue(t)){return invalidRange(e,t,n)}if(typeof r==="function"){return fill(e,t,1,{transform:r})}if(isObject(r)){return fill(e,t,0,r)}let u={...n};if(u.capture===true)u.wrap=true;r=r||u.step||1;if(!isNumber(r)){if(r!=null&&!isObject(r))return invalidStep(r,u);return fill(e,t,1,r)}if(isNumber(e)&&isNumber(t)){return fillNumbers(e,t,r,u)}return fillLetters(e,t,Math.max(Math.abs(r),1),u)};e.exports=fill;},357:e=>{
	/*!
	 * is-number <https://github.com/jonschlinkert/is-number>
	 *
	 * Copyright (c) 2014-present, Jon Schlinkert.
	 * Released under the MIT License.
	 */
	e.exports=function(e){if(typeof e==="number"){return e-e===0}if(typeof e==="string"&&e.trim()!==""){return Number.isFinite?Number.isFinite(+e):isFinite(+e)}return false};},971:(e,t,r)=>{const n=r(837);const u=r(333);const s=r(251);const o=r(513);const isEmptyString=e=>e===""||e==="./";const micromatch=(e,t,r)=>{t=[].concat(t);e=[].concat(e);let n=new Set;let u=new Set;let o=new Set;let i=0;let onResult=e=>{o.add(e.output);if(r&&r.onResult){r.onResult(e);}};for(let o=0;o<t.length;o++){let a=s(String(t[o]),{...r,onResult:onResult},true);let l=a.state.negated||a.state.negatedExtglob;if(l)i++;for(let t of e){let e=a(t,true);let r=l?!e.isMatch:e.isMatch;if(!r)continue;if(l){n.add(e.output);}else {n.delete(e.output);u.add(e.output);}}}let a=i===t.length?[...o]:[...u];let l=a.filter((e=>!n.has(e)));if(r&&l.length===0){if(r.failglob===true){throw new Error(`No matches found for "${t.join(", ")}"`)}if(r.nonull===true||r.nullglob===true){return r.unescape?t.map((e=>e.replace(/\\/g,""))):t}}return l};micromatch.match=micromatch;micromatch.matcher=(e,t)=>s(e,t);micromatch.isMatch=(e,t,r)=>s(t,r)(e);micromatch.any=micromatch.isMatch;micromatch.not=(e,t,r={})=>{t=[].concat(t).map(String);let n=new Set;let u=[];let onResult=e=>{if(r.onResult)r.onResult(e);u.push(e.output);};let s=micromatch(e,t,{...r,onResult:onResult});for(let e of u){if(!s.includes(e)){n.add(e);}}return [...n]};micromatch.contains=(e,t,r)=>{if(typeof e!=="string"){throw new TypeError(`Expected a string: "${n.inspect(e)}"`)}if(Array.isArray(t)){return t.some((t=>micromatch.contains(e,t,r)))}if(typeof t==="string"){if(isEmptyString(e)||isEmptyString(t)){return false}if(e.includes(t)||e.startsWith("./")&&e.slice(2).includes(t)){return true}}return micromatch.isMatch(e,t,{...r,contains:true})};micromatch.matchKeys=(e,t,r)=>{if(!o.isObject(e)){throw new TypeError("Expected the first argument to be an object")}let n=micromatch(Object.keys(e),t,r);let u={};for(let t of n)u[t]=e[t];return u};micromatch.some=(e,t,r)=>{let n=[].concat(e);for(let e of [].concat(t)){let t=s(String(e),r);if(n.some((e=>t(e)))){return true}}return false};micromatch.every=(e,t,r)=>{let n=[].concat(e);for(let e of [].concat(t)){let t=s(String(e),r);if(!n.every((e=>t(e)))){return false}}return true};micromatch.all=(e,t,r)=>{if(typeof e!=="string"){throw new TypeError(`Expected a string: "${n.inspect(e)}"`)}return [].concat(t).every((t=>s(t,r)(e)))};micromatch.capture=(e,t,r)=>{let n=o.isWindows(r);let u=s.makeRe(String(e),{...r,capture:true});let i=u.exec(n?o.toPosixSlashes(t):t);if(i){return i.slice(1).map((e=>e===void 0?"":e))}};micromatch.makeRe=(...e)=>s.makeRe(...e);micromatch.scan=(...e)=>s.scan(...e);micromatch.parse=(e,t)=>{let r=[];for(let n of [].concat(e||[])){for(let e of u(String(n),t)){r.push(s.parse(e,t));}}return r};micromatch.braces=(e,t)=>{if(typeof e!=="string")throw new TypeError("Expected a string");if(t&&t.nobrace===true||!/\{.*\}/.test(e)){return [e]}return u(e,t)};micromatch.braceExpand=(e,t)=>{if(typeof e!=="string")throw new TypeError("Expected a string");return micromatch.braces(e,{...t,expand:true})};e.exports=micromatch;},251:(e,t,r)=>{e.exports=r(683);},356:(e,t,r)=>{const n=r(17);const u="\\\\/";const s=`[^${u}]`;const o="\\.";const i="\\+";const a="\\?";const l="\\/";const c="(?=.)";const p="[^/]";const f=`(?:${l}|$)`;const A=`(?:^|${l})`;const R=`${o}{1,2}${f}`;const _=`(?!${o})`;const h=`(?!${A}${R})`;const g=`(?!${o}{0,1}${f})`;const E=`(?!${R})`;const C=`[^.${l}]`;const y=`${p}*?`;const d={DOT_LITERAL:o,PLUS_LITERAL:i,QMARK_LITERAL:a,SLASH_LITERAL:l,ONE_CHAR:c,QMARK:p,END_ANCHOR:f,DOTS_SLASH:R,NO_DOT:_,NO_DOTS:h,NO_DOT_SLASH:g,NO_DOTS_SLASH:E,QMARK_NO_DOT:C,STAR:y,START_ANCHOR:A};const x={...d,SLASH_LITERAL:`[${u}]`,QMARK:s,STAR:`${s}*?`,DOTS_SLASH:`${o}{1,2}(?:[${u}]|$)`,NO_DOT:`(?!${o})`,NO_DOTS:`(?!(?:^|[${u}])${o}{1,2}(?:[${u}]|$))`,NO_DOT_SLASH:`(?!${o}{0,1}(?:[${u}]|$))`,NO_DOTS_SLASH:`(?!${o}{1,2}(?:[${u}]|$))`,QMARK_NO_DOT:`[^.${u}]`,START_ANCHOR:`(?:^|[${u}])`,END_ANCHOR:`(?:[${u}]|$)`};const b={alnum:"a-zA-Z0-9",alpha:"a-zA-Z",ascii:"\\x00-\\x7F",blank:" \\t",cntrl:"\\x00-\\x1F\\x7F",digit:"0-9",graph:"\\x21-\\x7E",lower:"a-z",print:"\\x20-\\x7E ",punct:"\\-!\"#$%&'()\\*+,./:;<=>?@[\\]^_`{|}~",space:" \\t\\r\\n\\v\\f",upper:"A-Z",word:"A-Za-z0-9_",xdigit:"A-Fa-f0-9"};e.exports={MAX_LENGTH:1024*64,POSIX_REGEX_SOURCE:b,REGEX_BACKSLASH:/\\(?![*+?^${}(|)[\]])/g,REGEX_NON_SPECIAL_CHARS:/^[^@![\].,$*+?^{}()|\\/]+/,REGEX_SPECIAL_CHARS:/[-*+?.^${}(|)[\]]/,REGEX_SPECIAL_CHARS_BACKREF:/(\\?)((\W)(\3*))/g,REGEX_SPECIAL_CHARS_GLOBAL:/([-*+?.^${}(|)[\]])/g,REGEX_REMOVE_BACKSLASH:/(?:\[.*?[^\\]\]|\\(?=.))/g,REPLACEMENTS:{"***":"*","**/**":"**","**/**/**":"**"},CHAR_0:48,CHAR_9:57,CHAR_UPPERCASE_A:65,CHAR_LOWERCASE_A:97,CHAR_UPPERCASE_Z:90,CHAR_LOWERCASE_Z:122,CHAR_LEFT_PARENTHESES:40,CHAR_RIGHT_PARENTHESES:41,CHAR_ASTERISK:42,CHAR_AMPERSAND:38,CHAR_AT:64,CHAR_BACKWARD_SLASH:92,CHAR_CARRIAGE_RETURN:13,CHAR_CIRCUMFLEX_ACCENT:94,CHAR_COLON:58,CHAR_COMMA:44,CHAR_DOT:46,CHAR_DOUBLE_QUOTE:34,CHAR_EQUAL:61,CHAR_EXCLAMATION_MARK:33,CHAR_FORM_FEED:12,CHAR_FORWARD_SLASH:47,CHAR_GRAVE_ACCENT:96,CHAR_HASH:35,CHAR_HYPHEN_MINUS:45,CHAR_LEFT_ANGLE_BRACKET:60,CHAR_LEFT_CURLY_BRACE:123,CHAR_LEFT_SQUARE_BRACKET:91,CHAR_LINE_FEED:10,CHAR_NO_BREAK_SPACE:160,CHAR_PERCENT:37,CHAR_PLUS:43,CHAR_QUESTION_MARK:63,CHAR_RIGHT_ANGLE_BRACKET:62,CHAR_RIGHT_CURLY_BRACE:125,CHAR_RIGHT_SQUARE_BRACKET:93,CHAR_SEMICOLON:59,CHAR_SINGLE_QUOTE:39,CHAR_SPACE:32,CHAR_TAB:9,CHAR_UNDERSCORE:95,CHAR_VERTICAL_LINE:124,CHAR_ZERO_WIDTH_NOBREAK_SPACE:65279,SEP:n.sep,extglobChars(e){return {"!":{type:"negate",open:"(?:(?!(?:",close:`))${e.STAR})`},"?":{type:"qmark",open:"(?:",close:")?"},"+":{type:"plus",open:"(?:",close:")+"},"*":{type:"star",open:"(?:",close:")*"},"@":{type:"at",open:"(?:",close:")"}}},globChars(e){return e===true?x:d}};},754:(e,t,r)=>{const n=r(356);const u=r(513);const{MAX_LENGTH:s,POSIX_REGEX_SOURCE:o,REGEX_NON_SPECIAL_CHARS:i,REGEX_SPECIAL_CHARS_BACKREF:a,REPLACEMENTS:l}=n;const expandRange=(e,t)=>{if(typeof t.expandRange==="function"){return t.expandRange(...e,t)}e.sort();const r=`[${e.join("-")}]`;try{new RegExp(r);}catch(t){return e.map((e=>u.escapeRegex(e))).join("..")}return r};const syntaxError=(e,t)=>`Missing ${e}: "${t}" - use "\\\\${t}" to match literal characters`;const parse=(e,t)=>{if(typeof e!=="string"){throw new TypeError("Expected a string")}e=l[e]||e;const r={...t};const c=typeof r.maxLength==="number"?Math.min(s,r.maxLength):s;let p=e.length;if(p>c){throw new SyntaxError(`Input length: ${p}, exceeds maximum allowed length: ${c}`)}const f={type:"bos",value:"",output:r.prepend||""};const A=[f];const R=r.capture?"":"?:";const _=u.isWindows(t);const h=n.globChars(_);const g=n.extglobChars(h);const{DOT_LITERAL:E,PLUS_LITERAL:C,SLASH_LITERAL:y,ONE_CHAR:d,DOTS_SLASH:x,NO_DOT:b,NO_DOT_SLASH:S,NO_DOTS_SLASH:H,QMARK:v,QMARK_NO_DOT:$,STAR:m,START_ANCHOR:T}=h;const globstar=e=>`(${R}(?:(?!${T}${e.dot?x:E}).)*?)`;const L=r.dot?"":b;const O=r.dot?v:$;let w=r.bash===true?globstar(r):m;if(r.capture){w=`(${w})`;}if(typeof r.noext==="boolean"){r.noextglob=r.noext;}const N={input:e,index:-1,start:0,dot:r.dot===true,consumed:"",output:"",prefix:"",backtrack:false,negated:false,brackets:0,braces:0,parens:0,quotes:0,globstar:false,tokens:A};e=u.removePrefix(e,N);p=e.length;const k=[];const I=[];const M=[];let P=f;let B;const eos=()=>N.index===p-1;const G=N.peek=(t=1)=>e[N.index+t];const D=N.advance=()=>e[++N.index];const remaining=()=>e.slice(N.index+1);const consume=(e="",t=0)=>{N.consumed+=e;N.index+=t;};const append=e=>{N.output+=e.output!=null?e.output:e.value;consume(e.value);};const negate=()=>{let e=1;while(G()==="!"&&(G(2)!=="("||G(3)==="?")){D();N.start++;e++;}if(e%2===0){return false}N.negated=true;N.start++;return true};const increment=e=>{N[e]++;M.push(e);};const decrement=e=>{N[e]--;M.pop();};const push=e=>{if(P.type==="globstar"){const t=N.braces>0&&(e.type==="comma"||e.type==="brace");const r=e.extglob===true||k.length&&(e.type==="pipe"||e.type==="paren");if(e.type!=="slash"&&e.type!=="paren"&&!t&&!r){N.output=N.output.slice(0,-P.output.length);P.type="star";P.value="*";P.output=w;N.output+=P.output;}}if(k.length&&e.type!=="paren"&&!g[e.value]){k[k.length-1].inner+=e.value;}if(e.value||e.output)append(e);if(P&&P.type==="text"&&e.type==="text"){P.value+=e.value;P.output=(P.output||"")+e.value;return}e.prev=P;A.push(e);P=e;};const extglobOpen=(e,t)=>{const n={...g[t],conditions:1,inner:""};n.prev=P;n.parens=N.parens;n.output=N.output;const u=(r.capture?"(":"")+n.open;increment("parens");push({type:e,value:t,output:N.output?"":d});push({type:"paren",extglob:true,value:D(),output:u});k.push(n);};const extglobClose=e=>{let t=e.close+(r.capture?")":"");if(e.type==="negate"){let n=w;if(e.inner&&e.inner.length>1&&e.inner.includes("/")){n=globstar(r);}if(n!==w||eos()||/^\)+$/.test(remaining())){t=e.close=`)$))${n}`;}if(e.prev.type==="bos"){N.negatedExtglob=true;}}push({type:"paren",extglob:true,value:B,output:t});decrement("parens");};if(r.fastpaths!==false&&!/(^[*!]|[/()[\]{}"])/.test(e)){let n=false;let s=e.replace(a,((e,t,r,u,s,o)=>{if(u==="\\"){n=true;return e}if(u==="?"){if(t){return t+u+(s?v.repeat(s.length):"")}if(o===0){return O+(s?v.repeat(s.length):"")}return v.repeat(r.length)}if(u==="."){return E.repeat(r.length)}if(u==="*"){if(t){return t+u+(s?w:"")}return w}return t?e:`\\${e}`}));if(n===true){if(r.unescape===true){s=s.replace(/\\/g,"");}else {s=s.replace(/\\+/g,(e=>e.length%2===0?"\\\\":e?"\\":""));}}if(s===e&&r.contains===true){N.output=e;return N}N.output=u.wrapOutput(s,N,t);return N}while(!eos()){B=D();if(B==="\0"){continue}if(B==="\\"){const e=G();if(e==="/"&&r.bash!==true){continue}if(e==="."||e===";"){continue}if(!e){B+="\\";push({type:"text",value:B});continue}const t=/^\\+/.exec(remaining());let n=0;if(t&&t[0].length>2){n=t[0].length;N.index+=n;if(n%2!==0){B+="\\";}}if(r.unescape===true){B=D()||"";}else {B+=D()||"";}if(N.brackets===0){push({type:"text",value:B});continue}}if(N.brackets>0&&(B!=="]"||P.value==="["||P.value==="[^")){if(r.posix!==false&&B===":"){const e=P.value.slice(1);if(e.includes("[")){P.posix=true;if(e.includes(":")){const e=P.value.lastIndexOf("[");const t=P.value.slice(0,e);const r=P.value.slice(e+2);const n=o[r];if(n){P.value=t+n;N.backtrack=true;D();if(!f.output&&A.indexOf(P)===1){f.output=d;}continue}}}}if(B==="["&&G()!==":"||B==="-"&&G()==="]"){B=`\\${B}`;}if(B==="]"&&(P.value==="["||P.value==="[^")){B=`\\${B}`;}if(r.posix===true&&B==="!"&&P.value==="["){B="^";}P.value+=B;append({value:B});continue}if(N.quotes===1&&B!=='"'){B=u.escapeRegex(B);P.value+=B;append({value:B});continue}if(B==='"'){N.quotes=N.quotes===1?0:1;if(r.keepQuotes===true){push({type:"text",value:B});}continue}if(B==="("){increment("parens");push({type:"paren",value:B});continue}if(B===")"){if(N.parens===0&&r.strictBrackets===true){throw new SyntaxError(syntaxError("opening","("))}const e=k[k.length-1];if(e&&N.parens===e.parens+1){extglobClose(k.pop());continue}push({type:"paren",value:B,output:N.parens?")":"\\)"});decrement("parens");continue}if(B==="["){if(r.nobracket===true||!remaining().includes("]")){if(r.nobracket!==true&&r.strictBrackets===true){throw new SyntaxError(syntaxError("closing","]"))}B=`\\${B}`;}else {increment("brackets");}push({type:"bracket",value:B});continue}if(B==="]"){if(r.nobracket===true||P&&P.type==="bracket"&&P.value.length===1){push({type:"text",value:B,output:`\\${B}`});continue}if(N.brackets===0){if(r.strictBrackets===true){throw new SyntaxError(syntaxError("opening","["))}push({type:"text",value:B,output:`\\${B}`});continue}decrement("brackets");const e=P.value.slice(1);if(P.posix!==true&&e[0]==="^"&&!e.includes("/")){B=`/${B}`;}P.value+=B;append({value:B});if(r.literalBrackets===false||u.hasRegexChars(e)){continue}const t=u.escapeRegex(P.value);N.output=N.output.slice(0,-P.value.length);if(r.literalBrackets===true){N.output+=t;P.value=t;continue}P.value=`(${R}${t}|${P.value})`;N.output+=P.value;continue}if(B==="{"&&r.nobrace!==true){increment("braces");const e={type:"brace",value:B,output:"(",outputIndex:N.output.length,tokensIndex:N.tokens.length};I.push(e);push(e);continue}if(B==="}"){const e=I[I.length-1];if(r.nobrace===true||!e){push({type:"text",value:B,output:B});continue}let t=")";if(e.dots===true){const e=A.slice();const n=[];for(let t=e.length-1;t>=0;t--){A.pop();if(e[t].type==="brace"){break}if(e[t].type!=="dots"){n.unshift(e[t].value);}}t=expandRange(n,r);N.backtrack=true;}if(e.comma!==true&&e.dots!==true){const r=N.output.slice(0,e.outputIndex);const n=N.tokens.slice(e.tokensIndex);e.value=e.output="\\{";B=t="\\}";N.output=r;for(const e of n){N.output+=e.output||e.value;}}push({type:"brace",value:B,output:t});decrement("braces");I.pop();continue}if(B==="|"){if(k.length>0){k[k.length-1].conditions++;}push({type:"text",value:B});continue}if(B===","){let e=B;const t=I[I.length-1];if(t&&M[M.length-1]==="braces"){t.comma=true;e="|";}push({type:"comma",value:B,output:e});continue}if(B==="/"){if(P.type==="dot"&&N.index===N.start+1){N.start=N.index+1;N.consumed="";N.output="";A.pop();P=f;continue}push({type:"slash",value:B,output:y});continue}if(B==="."){if(N.braces>0&&P.type==="dot"){if(P.value===".")P.output=E;const e=I[I.length-1];P.type="dots";P.output+=B;P.value+=B;e.dots=true;continue}if(N.braces+N.parens===0&&P.type!=="bos"&&P.type!=="slash"){push({type:"text",value:B,output:E});continue}push({type:"dot",value:B,output:E});continue}if(B==="?"){const e=P&&P.value==="(";if(!e&&r.noextglob!==true&&G()==="("&&G(2)!=="?"){extglobOpen("qmark",B);continue}if(P&&P.type==="paren"){const e=G();let t=B;if(e==="<"&&!u.supportsLookbehinds()){throw new Error("Node.js v10 or higher is required for regex lookbehinds")}if(P.value==="("&&!/[!=<:]/.test(e)||e==="<"&&!/<([!=]|\w+>)/.test(remaining())){t=`\\${B}`;}push({type:"text",value:B,output:t});continue}if(r.dot!==true&&(P.type==="slash"||P.type==="bos")){push({type:"qmark",value:B,output:$});continue}push({type:"qmark",value:B,output:v});continue}if(B==="!"){if(r.noextglob!==true&&G()==="("){if(G(2)!=="?"||!/[!=<:]/.test(G(3))){extglobOpen("negate",B);continue}}if(r.nonegate!==true&&N.index===0){negate();continue}}if(B==="+"){if(r.noextglob!==true&&G()==="("&&G(2)!=="?"){extglobOpen("plus",B);continue}if(P&&P.value==="("||r.regex===false){push({type:"plus",value:B,output:C});continue}if(P&&(P.type==="bracket"||P.type==="paren"||P.type==="brace")||N.parens>0){push({type:"plus",value:B});continue}push({type:"plus",value:C});continue}if(B==="@"){if(r.noextglob!==true&&G()==="("&&G(2)!=="?"){push({type:"at",extglob:true,value:B,output:""});continue}push({type:"text",value:B});continue}if(B!=="*"){if(B==="$"||B==="^"){B=`\\${B}`;}const e=i.exec(remaining());if(e){B+=e[0];N.index+=e[0].length;}push({type:"text",value:B});continue}if(P&&(P.type==="globstar"||P.star===true)){P.type="star";P.star=true;P.value+=B;P.output=w;N.backtrack=true;N.globstar=true;consume(B);continue}let t=remaining();if(r.noextglob!==true&&/^\([^?]/.test(t)){extglobOpen("star",B);continue}if(P.type==="star"){if(r.noglobstar===true){consume(B);continue}const n=P.prev;const u=n.prev;const s=n.type==="slash"||n.type==="bos";const o=u&&(u.type==="star"||u.type==="globstar");if(r.bash===true&&(!s||t[0]&&t[0]!=="/")){push({type:"star",value:B,output:""});continue}const i=N.braces>0&&(n.type==="comma"||n.type==="brace");const a=k.length&&(n.type==="pipe"||n.type==="paren");if(!s&&n.type!=="paren"&&!i&&!a){push({type:"star",value:B,output:""});continue}while(t.slice(0,3)==="/**"){const r=e[N.index+4];if(r&&r!=="/"){break}t=t.slice(3);consume("/**",3);}if(n.type==="bos"&&eos()){P.type="globstar";P.value+=B;P.output=globstar(r);N.output=P.output;N.globstar=true;consume(B);continue}if(n.type==="slash"&&n.prev.type!=="bos"&&!o&&eos()){N.output=N.output.slice(0,-(n.output+P.output).length);n.output=`(?:${n.output}`;P.type="globstar";P.output=globstar(r)+(r.strictSlashes?")":"|$)");P.value+=B;N.globstar=true;N.output+=n.output+P.output;consume(B);continue}if(n.type==="slash"&&n.prev.type!=="bos"&&t[0]==="/"){const e=t[1]!==void 0?"|$":"";N.output=N.output.slice(0,-(n.output+P.output).length);n.output=`(?:${n.output}`;P.type="globstar";P.output=`${globstar(r)}${y}|${y}${e})`;P.value+=B;N.output+=n.output+P.output;N.globstar=true;consume(B+D());push({type:"slash",value:"/",output:""});continue}if(n.type==="bos"&&t[0]==="/"){P.type="globstar";P.value+=B;P.output=`(?:^|${y}|${globstar(r)}${y})`;N.output=P.output;N.globstar=true;consume(B+D());push({type:"slash",value:"/",output:""});continue}N.output=N.output.slice(0,-P.output.length);P.type="globstar";P.output=globstar(r);P.value+=B;N.output+=P.output;N.globstar=true;consume(B);continue}const n={type:"star",value:B,output:w};if(r.bash===true){n.output=".*?";if(P.type==="bos"||P.type==="slash"){n.output=L+n.output;}push(n);continue}if(P&&(P.type==="bracket"||P.type==="paren")&&r.regex===true){n.output=B;push(n);continue}if(N.index===N.start||P.type==="slash"||P.type==="dot"){if(P.type==="dot"){N.output+=S;P.output+=S;}else if(r.dot===true){N.output+=H;P.output+=H;}else {N.output+=L;P.output+=L;}if(G()!=="*"){N.output+=d;P.output+=d;}}push(n);}while(N.brackets>0){if(r.strictBrackets===true)throw new SyntaxError(syntaxError("closing","]"));N.output=u.escapeLast(N.output,"[");decrement("brackets");}while(N.parens>0){if(r.strictBrackets===true)throw new SyntaxError(syntaxError("closing",")"));N.output=u.escapeLast(N.output,"(");decrement("parens");}while(N.braces>0){if(r.strictBrackets===true)throw new SyntaxError(syntaxError("closing","}"));N.output=u.escapeLast(N.output,"{");decrement("braces");}if(r.strictSlashes!==true&&(P.type==="star"||P.type==="bracket")){push({type:"maybe_slash",value:"",output:`${y}?`});}if(N.backtrack===true){N.output="";for(const e of N.tokens){N.output+=e.output!=null?e.output:e.value;if(e.suffix){N.output+=e.suffix;}}}return N};parse.fastpaths=(e,t)=>{const r={...t};const o=typeof r.maxLength==="number"?Math.min(s,r.maxLength):s;const i=e.length;if(i>o){throw new SyntaxError(`Input length: ${i}, exceeds maximum allowed length: ${o}`)}e=l[e]||e;const a=u.isWindows(t);const{DOT_LITERAL:c,SLASH_LITERAL:p,ONE_CHAR:f,DOTS_SLASH:A,NO_DOT:R,NO_DOTS:_,NO_DOTS_SLASH:h,STAR:g,START_ANCHOR:E}=n.globChars(a);const C=r.dot?_:R;const y=r.dot?h:R;const d=r.capture?"":"?:";const x={negated:false,prefix:""};let b=r.bash===true?".*?":g;if(r.capture){b=`(${b})`;}const globstar=e=>{if(e.noglobstar===true)return b;return `(${d}(?:(?!${E}${e.dot?A:c}).)*?)`};const create=e=>{switch(e){case"*":return `${C}${f}${b}`;case".*":return `${c}${f}${b}`;case"*.*":return `${C}${b}${c}${f}${b}`;case"*/*":return `${C}${b}${p}${f}${y}${b}`;case"**":return C+globstar(r);case"**/*":return `(?:${C}${globstar(r)}${p})?${y}${f}${b}`;case"**/*.*":return `(?:${C}${globstar(r)}${p})?${y}${b}${c}${f}${b}`;case"**/.*":return `(?:${C}${globstar(r)}${p})?${c}${f}${b}`;default:{const t=/^(.*?)\.(\w+)$/.exec(e);if(!t)return;const r=create(t[1]);if(!r)return;return r+c+t[2]}}};const S=u.removePrefix(e,x);let H=create(S);if(H&&r.strictSlashes!==true){H+=`${p}?`;}return H};e.exports=parse;},683:(e,t,r)=>{const n=r(17);const u=r(700);const s=r(754);const o=r(513);const i=r(356);const isObject=e=>e&&typeof e==="object"&&!Array.isArray(e);const picomatch=(e,t,r=false)=>{if(Array.isArray(e)){const n=e.map((e=>picomatch(e,t,r)));const arrayMatcher=e=>{for(const t of n){const r=t(e);if(r)return r}return false};return arrayMatcher}const n=isObject(e)&&e.tokens&&e.input;if(e===""||typeof e!=="string"&&!n){throw new TypeError("Expected pattern to be a non-empty string")}const u=t||{};const s=o.isWindows(t);const i=n?picomatch.compileRe(e,t):picomatch.makeRe(e,t,false,true);const a=i.state;delete i.state;let isIgnored=()=>false;if(u.ignore){const e={...t,ignore:null,onMatch:null,onResult:null};isIgnored=picomatch(u.ignore,e,r);}const matcher=(r,n=false)=>{const{isMatch:o,match:l,output:c}=picomatch.test(r,i,t,{glob:e,posix:s});const p={glob:e,state:a,regex:i,posix:s,input:r,output:c,match:l,isMatch:o};if(typeof u.onResult==="function"){u.onResult(p);}if(o===false){p.isMatch=false;return n?p:false}if(isIgnored(r)){if(typeof u.onIgnore==="function"){u.onIgnore(p);}p.isMatch=false;return n?p:false}if(typeof u.onMatch==="function"){u.onMatch(p);}return n?p:true};if(r){matcher.state=a;}return matcher};picomatch.test=(e,t,r,{glob:n,posix:u}={})=>{if(typeof e!=="string"){throw new TypeError("Expected input to be a string")}if(e===""){return {isMatch:false,output:""}}const s=r||{};const i=s.format||(u?o.toPosixSlashes:null);let a=e===n;let l=a&&i?i(e):e;if(a===false){l=i?i(e):e;a=l===n;}if(a===false||s.capture===true){if(s.matchBase===true||s.basename===true){a=picomatch.matchBase(e,t,r,u);}else {a=t.exec(l);}}return {isMatch:Boolean(a),match:a,output:l}};picomatch.matchBase=(e,t,r,u=o.isWindows(r))=>{const s=t instanceof RegExp?t:picomatch.makeRe(t,r);return s.test(n.basename(e))};picomatch.isMatch=(e,t,r)=>picomatch(t,r)(e);picomatch.parse=(e,t)=>{if(Array.isArray(e))return e.map((e=>picomatch.parse(e,t)));return s(e,{...t,fastpaths:false})};picomatch.scan=(e,t)=>u(e,t);picomatch.compileRe=(e,t,r=false,n=false)=>{if(r===true){return e.output}const u=t||{};const s=u.contains?"":"^";const o=u.contains?"":"$";let i=`${s}(?:${e.output})${o}`;if(e&&e.negated===true){i=`^(?!${i}).*$`;}const a=picomatch.toRegex(i,t);if(n===true){a.state=e;}return a};picomatch.makeRe=(e,t,r=false,n=false)=>{if(!e||typeof e!=="string"){throw new TypeError("Expected a non-empty string")}const u=t||{};let o={negated:false,fastpaths:true};let i="";let a;if(e.startsWith("./")){e=e.slice(2);i=o.prefix="./";}if(u.fastpaths!==false&&(e[0]==="."||e[0]==="*")){a=s.fastpaths(e,t);}if(a===undefined){o=s(e,t);o.prefix=i+(o.prefix||"");}else {o.output=a;}return picomatch.compileRe(o,t,r,n)};picomatch.toRegex=(e,t)=>{try{const r=t||{};return new RegExp(e,r.flags||(r.nocase?"i":""))}catch(e){if(t&&t.debug===true)throw e;return /$^/}};picomatch.constants=i;e.exports=picomatch;},700:(e,t,r)=>{const n=r(513);const{CHAR_ASTERISK:u,CHAR_AT:s,CHAR_BACKWARD_SLASH:o,CHAR_COMMA:i,CHAR_DOT:a,CHAR_EXCLAMATION_MARK:l,CHAR_FORWARD_SLASH:c,CHAR_LEFT_CURLY_BRACE:p,CHAR_LEFT_PARENTHESES:f,CHAR_LEFT_SQUARE_BRACKET:A,CHAR_PLUS:R,CHAR_QUESTION_MARK:_,CHAR_RIGHT_CURLY_BRACE:h,CHAR_RIGHT_PARENTHESES:g,CHAR_RIGHT_SQUARE_BRACKET:E}=r(356);const isPathSeparator=e=>e===c||e===o;const depth=e=>{if(e.isPrefix!==true){e.depth=e.isGlobstar?Infinity:1;}};const scan=(e,t)=>{const r=t||{};const C=e.length-1;const y=r.parts===true||r.scanToEnd===true;const d=[];const x=[];const b=[];let S=e;let H=-1;let v=0;let $=0;let m=false;let T=false;let L=false;let O=false;let w=false;let N=false;let k=false;let I=false;let M=false;let P=0;let B;let G;let D={value:"",depth:0,isGlob:false};const eos=()=>H>=C;const peek=()=>S.charCodeAt(H+1);const advance=()=>{B=G;return S.charCodeAt(++H)};while(H<C){G=advance();let e;if(G===o){k=D.backslashes=true;G=advance();if(G===p){N=true;}continue}if(N===true||G===p){P++;while(eos()!==true&&(G=advance())){if(G===o){k=D.backslashes=true;advance();continue}if(G===p){P++;continue}if(N!==true&&G===a&&(G=advance())===a){m=D.isBrace=true;L=D.isGlob=true;M=true;if(y===true){continue}break}if(N!==true&&G===i){m=D.isBrace=true;L=D.isGlob=true;M=true;if(y===true){continue}break}if(G===h){P--;if(P===0){N=false;m=D.isBrace=true;M=true;break}}}if(y===true){continue}break}if(G===c){d.push(H);x.push(D);D={value:"",depth:0,isGlob:false};if(M===true)continue;if(B===a&&H===v+1){v+=2;continue}$=H+1;continue}if(r.noext!==true){const e=G===R||G===s||G===u||G===_||G===l;if(e===true&&peek()===f){L=D.isGlob=true;O=D.isExtglob=true;M=true;if(y===true){while(eos()!==true&&(G=advance())){if(G===o){k=D.backslashes=true;G=advance();continue}if(G===g){L=D.isGlob=true;M=true;break}}continue}break}}if(G===u){if(B===u)w=D.isGlobstar=true;L=D.isGlob=true;M=true;if(y===true){continue}break}if(G===_){L=D.isGlob=true;M=true;if(y===true){continue}break}if(G===A){while(eos()!==true&&(e=advance())){if(e===o){k=D.backslashes=true;advance();continue}if(e===E){T=D.isBracket=true;L=D.isGlob=true;M=true;break}}if(y===true){continue}break}if(r.nonegate!==true&&G===l&&H===v){I=D.negated=true;v++;continue}if(r.noparen!==true&&G===f){L=D.isGlob=true;if(y===true){while(eos()!==true&&(G=advance())){if(G===f){k=D.backslashes=true;G=advance();continue}if(G===g){M=true;break}}continue}break}if(L===true){M=true;if(y===true){continue}break}}if(r.noext===true){O=false;L=false;}let U=S;let K="";let F="";if(v>0){K=S.slice(0,v);S=S.slice(v);$-=v;}if(U&&L===true&&$>0){U=S.slice(0,$);F=S.slice($);}else if(L===true){U="";F=S;}else {U=S;}if(U&&U!==""&&U!=="/"&&U!==S){if(isPathSeparator(U.charCodeAt(U.length-1))){U=U.slice(0,-1);}}if(r.unescape===true){if(F)F=n.removeBackslashes(F);if(U&&k===true){U=n.removeBackslashes(U);}}const Q={prefix:K,input:e,start:v,base:U,glob:F,isBrace:m,isBracket:T,isGlob:L,isExtglob:O,isGlobstar:w,negated:I};if(r.tokens===true){Q.maxDepth=0;if(!isPathSeparator(G)){x.push(D);}Q.tokens=x;}if(r.parts===true||r.tokens===true){let t;for(let n=0;n<d.length;n++){const u=t?t+1:v;const s=d[n];const o=e.slice(u,s);if(r.tokens){if(n===0&&v!==0){x[n].isPrefix=true;x[n].value=K;}else {x[n].value=o;}depth(x[n]);Q.maxDepth+=x[n].depth;}if(n!==0||o!==""){b.push(o);}t=s;}if(t&&t+1<e.length){const n=e.slice(t+1);b.push(n);if(r.tokens){x[x.length-1].value=n;depth(x[x.length-1]);Q.maxDepth+=x[x.length-1].depth;}}Q.slashes=d;Q.parts=b;}return Q};e.exports=scan;},513:(e,t,r)=>{const n=r(17);const u=process.platform==="win32";const{REGEX_BACKSLASH:s,REGEX_REMOVE_BACKSLASH:o,REGEX_SPECIAL_CHARS:i,REGEX_SPECIAL_CHARS_GLOBAL:a}=r(356);t.isObject=e=>e!==null&&typeof e==="object"&&!Array.isArray(e);t.hasRegexChars=e=>i.test(e);t.isRegexChar=e=>e.length===1&&t.hasRegexChars(e);t.escapeRegex=e=>e.replace(a,"\\$1");t.toPosixSlashes=e=>e.replace(s,"/");t.removeBackslashes=e=>e.replace(o,(e=>e==="\\"?"":e));t.supportsLookbehinds=()=>{const e=process.version.slice(1).split(".").map(Number);if(e.length===3&&e[0]>=9||e[0]===8&&e[1]>=10){return true}return false};t.isWindows=e=>{if(e&&typeof e.windows==="boolean"){return e.windows}return u===true||n.sep==="\\"};t.escapeLast=(e,r,n)=>{const u=e.lastIndexOf(r,n);if(u===-1)return e;if(e[u-1]==="\\")return t.escapeLast(e,r,u-1);return `${e.slice(0,u)}\\${e.slice(u)}`};t.removePrefix=(e,t={})=>{let r=e;if(r.startsWith("./")){r=r.slice(2);t.prefix="./";}return r};t.wrapOutput=(e,t={},r={})=>{const n=r.contains?"":"^";const u=r.contains?"":"$";let s=`${n}(?:${e})${u}`;if(t.negated===true){s=`(?:^(?!${s}).*$)`;}return s};},492:(e,t,r)=>{
	/*!
	 * to-regex-range <https://github.com/micromatch/to-regex-range>
	 *
	 * Copyright (c) 2015-present, Jon Schlinkert.
	 * Released under the MIT License.
	 */
	const n=r(357);const toRegexRange=(e,t,r)=>{if(n(e)===false){throw new TypeError("toRegexRange: expected the first argument to be a number")}if(t===void 0||e===t){return String(e)}if(n(t)===false){throw new TypeError("toRegexRange: expected the second argument to be a number.")}let u={relaxZeros:true,...r};if(typeof u.strictZeros==="boolean"){u.relaxZeros=u.strictZeros===false;}let s=String(u.relaxZeros);let o=String(u.shorthand);let i=String(u.capture);let a=String(u.wrap);let l=e+":"+t+"="+s+o+i+a;if(toRegexRange.cache.hasOwnProperty(l)){return toRegexRange.cache[l].result}let c=Math.min(e,t);let p=Math.max(e,t);if(Math.abs(c-p)===1){let r=e+"|"+t;if(u.capture){return `(${r})`}if(u.wrap===false){return r}return `(?:${r})`}let f=hasPadding(e)||hasPadding(t);let A={min:e,max:t,a:c,b:p};let R=[];let _=[];if(f){A.isPadded=f;A.maxLen=String(A.max).length;}if(c<0){let e=p<0?Math.abs(p):1;_=splitToPatterns(e,Math.abs(c),A,u);c=A.a=0;}if(p>=0){R=splitToPatterns(c,p,A,u);}A.negatives=_;A.positives=R;A.result=collatePatterns(_,R);if(u.capture===true){A.result=`(${A.result})`;}else if(u.wrap!==false&&R.length+_.length>1){A.result=`(?:${A.result})`;}toRegexRange.cache[l]=A;return A.result};function collatePatterns(e,t,r){let n=filterPatterns(e,t,"-",false)||[];let u=filterPatterns(t,e,"",false)||[];let s=filterPatterns(e,t,"-?",true)||[];let o=n.concat(s).concat(u);return o.join("|")}function splitToRanges(e,t){let r=1;let n=1;let u=countNines(e,r);let s=new Set([t]);while(e<=u&&u<=t){s.add(u);r+=1;u=countNines(e,r);}u=countZeros(t+1,n)-1;while(e<u&&u<=t){s.add(u);n+=1;u=countZeros(t+1,n)-1;}s=[...s];s.sort(compare);return s}function rangeToPattern(e,t,r){if(e===t){return {pattern:e,count:[],digits:0}}let n=zip(e,t);let u=n.length;let s="";let o=0;for(let e=0;e<u;e++){let[t,u]=n[e];if(t===u){s+=t;}else if(t!=="0"||u!=="9"){s+=toCharacterClass(t,u);}else {o++;}}if(o){s+=r.shorthand===true?"\\d":"[0-9]";}return {pattern:s,count:[o],digits:u}}function splitToPatterns(e,t,r,n){let u=splitToRanges(e,t);let s=[];let o=e;let i;for(let e=0;e<u.length;e++){let t=u[e];let a=rangeToPattern(String(o),String(t),n);let l="";if(!r.isPadded&&i&&i.pattern===a.pattern){if(i.count.length>1){i.count.pop();}i.count.push(a.count[0]);i.string=i.pattern+toQuantifier(i.count);o=t+1;continue}if(r.isPadded){l=padZeros(t,r,n);}a.string=l+a.pattern+toQuantifier(a.count);s.push(a);o=t+1;i=a;}return s}function filterPatterns(e,t,r,n,u){let s=[];for(let u of e){let{string:e}=u;if(!n&&!contains(t,"string",e)){s.push(r+e);}if(n&&contains(t,"string",e)){s.push(r+e);}}return s}function zip(e,t){let r=[];for(let n=0;n<e.length;n++)r.push([e[n],t[n]]);return r}function compare(e,t){return e>t?1:t>e?-1:0}function contains(e,t,r){return e.some((e=>e[t]===r))}function countNines(e,t){return Number(String(e).slice(0,-t)+"9".repeat(t))}function countZeros(e,t){return e-e%Math.pow(10,t)}function toQuantifier(e){let[t=0,r=""]=e;if(r||t>1){return `{${t+(r?","+r:"")}}`}return ""}function toCharacterClass(e,t,r){return `[${e}${t-e===1?"":"-"}${t}]`}function hasPadding(e){return /^-?(0+)\d/.test(e)}function padZeros(e,t,r){if(!t.isPadded){return e}let n=Math.abs(t.maxLen-String(e).length);let u=r.relaxZeros!==false;switch(n){case 0:return "";case 1:return u?"0?":"0";case 2:return u?"0{0,2}":"00";default:{return u?`0{0,${n}}`:`0{${n}}`}}}toRegexRange.cache={};toRegexRange.clearCache=()=>toRegexRange.cache={};e.exports=toRegexRange;},17:e=>{e.exports=require$$0;},837:e=>{e.exports=require$$1;}};var t={};function __nccwpck_require__(r){var n=t[r];if(n!==undefined){return n.exports}var u=t[r]={exports:{}};var s=true;try{e[r](u,u.exports,__nccwpck_require__);s=false;}finally{if(s)delete t[r];}return u.exports}if(typeof __nccwpck_require__!=="undefined")__nccwpck_require__.ab=__dirname+"/";var r=__nccwpck_require__(971);micromatch.exports=r;})();
	return micromatch.exports;
}

var hasRequiredMatchRemotePattern;

function requireMatchRemotePattern () {
	if (hasRequiredMatchRemotePattern) return matchRemotePattern;
	hasRequiredMatchRemotePattern = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", {
		    value: true
		});
		function _export(target, all) {
		    for(var name in all)Object.defineProperty(target, name, {
		        enumerable: true,
		        get: all[name]
		    });
		}
		_export(exports, {
		    matchRemotePattern: function() {
		        return matchRemotePattern;
		    },
		    hasMatch: function() {
		        return hasMatch;
		    }
		});
		const _micromatch = requireMicromatch();
		function matchRemotePattern(pattern, url) {
		    if (pattern.protocol !== undefined) {
		        const actualProto = url.protocol.slice(0, -1);
		        if (pattern.protocol !== actualProto) {
		            return false;
		        }
		    }
		    if (pattern.port !== undefined) {
		        if (pattern.port !== url.port) {
		            return false;
		        }
		    }
		    if (pattern.hostname === undefined) {
		        throw new Error("Pattern should define hostname but found\n" + JSON.stringify(pattern));
		    } else {
		        if (!(0, _micromatch.makeRe)(pattern.hostname).test(url.hostname)) {
		            return false;
		        }
		    }
		    var _pattern_pathname;
		    if (!(0, _micromatch.makeRe)((_pattern_pathname = pattern.pathname) != null ? _pattern_pathname : "**").test(url.pathname)) {
		        return false;
		    }
		    return true;
		}
		function hasMatch(domains, remotePatterns, url) {
		    return domains.some((domain)=>url.hostname === domain) || remotePatterns.some((p)=>matchRemotePattern(p, url));
		}

		
	} (matchRemotePattern));
	return matchRemotePattern;
}

var hasRequiredImageLoader;

function requireImageLoader () {
	if (hasRequiredImageLoader) return imageLoader;
	hasRequiredImageLoader = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", {
		    value: true
		});
		Object.defineProperty(exports, "default", {
		    enumerable: true,
		    get: function() {
		        return _default;
		    }
		});
		function defaultLoader(param) {
		    let { config, src, width, quality } = param;
		    if (process.env.NODE_ENV !== "production") {
		        const missingValues = [];
		        // these should always be provided but make sure they are
		        if (!src) missingValues.push("src");
		        if (!width) missingValues.push("width");
		        if (missingValues.length > 0) {
		            throw new Error("Next Image Optimization requires " + missingValues.join(", ") + " to be provided. Make sure you pass them as props to the `next/image` component. Received: " + JSON.stringify({
		                src,
		                width,
		                quality
		            }));
		        }
		        if (src.startsWith("//")) {
		            throw new Error('Failed to parse src "' + src + '" on `next/image`, protocol-relative URL (//) must be changed to an absolute URL (http:// or https://)');
		        }
		        if (!src.startsWith("/") && (config.domains || config.remotePatterns)) {
		            let parsedSrc;
		            try {
		                parsedSrc = new URL(src);
		            } catch (err) {
		                console.error(err);
		                throw new Error('Failed to parse src "' + src + '" on `next/image`, if using relative image it must start with a leading slash "/" or be an absolute URL (http:// or https://)');
		            }
		            if (process.env.NODE_ENV !== "test" && // micromatch isn't compatible with edge runtime
		            process.env.NEXT_RUNTIME !== "edge") {
		                // We use dynamic require because this should only error in development
		                const { hasMatch } = requireMatchRemotePattern();
		                if (!hasMatch(config.domains, config.remotePatterns, parsedSrc)) {
		                    throw new Error("Invalid src prop (" + src + ') on `next/image`, hostname "' + parsedSrc.hostname + '" is not configured under images in your `next.config.js`\n' + "See more info: https://nextjs.org/docs/messages/next-image-unconfigured-host");
		                }
		            }
		        }
		    }
		    return config.path + "?url=" + encodeURIComponent(src) + "&w=" + width + "&q=" + (quality || 75) + (process.env.NEXT_DEPLOYMENT_ID ? "&dpl=" + process.env.NEXT_DEPLOYMENT_ID : "");
		}
		// We use this to determine if the import is the default loader
		// or a custom loader defined by the user in next.config.js
		defaultLoader.__next_img_default = true;
		const _default = defaultLoader;

		
	} (imageLoader));
	return imageLoader;
}

(function (module, exports) {
	"use client";
	Object.defineProperty(exports, "__esModule", {
	    value: true
	});
	Object.defineProperty(exports, "Image", {
	    enumerable: true,
	    get: function() {
	        return Image;
	    }
	});
	const _interop_require_default = _interop_require_default$1;
	const _interop_require_wildcard = _interop_require_wildcard$1;
	const _react = /*#__PURE__*/ _interop_require_wildcard._(React__default);
	const _reactdom = /*#__PURE__*/ _interop_require_default._(require$$3);
	const _head = /*#__PURE__*/ _interop_require_default._(requireHead());
	const _getimgprops = getImgProps;
	const _imageconfig = imageConfig;
	const _imageconfigcontextsharedruntime = requireImageConfigContext_sharedRuntime();
	const _warnonce = warnOnce;
	const _routercontextsharedruntime = requireRouterContext_sharedRuntime();
	const _imageloader = /*#__PURE__*/ _interop_require_default._(requireImageLoader());
	// This is replaced by webpack define plugin
	const configEnv = process.env.__NEXT_IMAGE_OPTS;
	if (typeof window === "undefined") {
	    globalThis.__NEXT_IMAGE_IMPORTED = true;
	}
	// See https://stackoverflow.com/q/39777833/266535 for why we use this ref
	// handler instead of the img's onLoad attribute.
	function handleLoading(img, placeholder, onLoadRef, onLoadingCompleteRef, setBlurComplete, unoptimized) {
	    const src = img == null ? void 0 : img.src;
	    if (!img || img["data-loaded-src"] === src) {
	        return;
	    }
	    img["data-loaded-src"] = src;
	    const p = "decode" in img ? img.decode() : Promise.resolve();
	    p.catch(()=>{}).then(()=>{
	        if (!img.parentElement || !img.isConnected) {
	            // Exit early in case of race condition:
	            // - onload() is called
	            // - decode() is called but incomplete
	            // - unmount is called
	            // - decode() completes
	            return;
	        }
	        if (placeholder !== "empty") {
	            setBlurComplete(true);
	        }
	        if (onLoadRef == null ? void 0 : onLoadRef.current) {
	            // Since we don't have the SyntheticEvent here,
	            // we must create one with the same shape.
	            // See https://reactjs.org/docs/events.html
	            const event = new Event("load");
	            Object.defineProperty(event, "target", {
	                writable: false,
	                value: img
	            });
	            let prevented = false;
	            let stopped = false;
	            onLoadRef.current({
	                ...event,
	                nativeEvent: event,
	                currentTarget: img,
	                target: img,
	                isDefaultPrevented: ()=>prevented,
	                isPropagationStopped: ()=>stopped,
	                persist: ()=>{},
	                preventDefault: ()=>{
	                    prevented = true;
	                    event.preventDefault();
	                },
	                stopPropagation: ()=>{
	                    stopped = true;
	                    event.stopPropagation();
	                }
	            });
	        }
	        if (onLoadingCompleteRef == null ? void 0 : onLoadingCompleteRef.current) {
	            onLoadingCompleteRef.current(img);
	        }
	        if (process.env.NODE_ENV !== "production") {
	            const origSrc = new URL(src, "http://n").searchParams.get("url") || src;
	            if (img.getAttribute("data-nimg") === "fill") {
	                if (!unoptimized && (!img.getAttribute("sizes") || img.getAttribute("sizes") === "100vw")) {
	                    let widthViewportRatio = img.getBoundingClientRect().width / window.innerWidth;
	                    if (widthViewportRatio < 0.6) {
	                        (0, _warnonce.warnOnce)('Image with src "' + origSrc + '" has "fill" but is missing "sizes" prop. Please add it to improve page performance. Read more: https://nextjs.org/docs/api-reference/next/image#sizes');
	                    }
	                }
	                if (img.parentElement) {
	                    const { position } = window.getComputedStyle(img.parentElement);
	                    const valid = [
	                        "absolute",
	                        "fixed",
	                        "relative"
	                    ];
	                    if (!valid.includes(position)) {
	                        (0, _warnonce.warnOnce)('Image with src "' + origSrc + '" has "fill" and parent element with invalid "position". Provided "' + position + '" should be one of ' + valid.map(String).join(",") + ".");
	                    }
	                }
	                if (img.height === 0) {
	                    (0, _warnonce.warnOnce)('Image with src "' + origSrc + '" has "fill" and a height value of 0. This is likely because the parent element of the image has not been styled to have a set height.');
	                }
	            }
	            const heightModified = img.height.toString() !== img.getAttribute("height");
	            const widthModified = img.width.toString() !== img.getAttribute("width");
	            if (heightModified && !widthModified || !heightModified && widthModified) {
	                (0, _warnonce.warnOnce)('Image with src "' + origSrc + '" has either width or height modified, but not the other. If you use CSS to change the size of your image, also include the styles \'width: "auto"\' or \'height: "auto"\' to maintain the aspect ratio.');
	            }
	        }
	    });
	}
	function getDynamicProps(fetchPriority) {
	    const [majorStr, minorStr] = _react.version.split(".", 2);
	    const major = parseInt(majorStr, 10);
	    const minor = parseInt(minorStr, 10);
	    if (major > 18 || major === 18 && minor >= 3) {
	        // In React 18.3.0 or newer, we must use camelCase
	        // prop to avoid "Warning: Invalid DOM property".
	        // See https://github.com/facebook/react/pull/25927
	        return {
	            fetchPriority
	        };
	    }
	    // In React 18.2.0 or older, we must use lowercase prop
	    // to avoid "Warning: Invalid DOM property".
	    return {
	        fetchpriority: fetchPriority
	    };
	}
	const ImageElement = /*#__PURE__*/ (0, _react.forwardRef)((param, forwardedRef)=>{
	    let { src, srcSet, sizes, height, width, decoding, className, style, fetchPriority, placeholder, loading, unoptimized, fill, onLoadRef, onLoadingCompleteRef, setBlurComplete, setShowAltText, onLoad, onError, ...rest } = param;
	    return /*#__PURE__*/ _react.default.createElement("img", {
	        ...rest,
	        ...getDynamicProps(fetchPriority),
	        // It's intended to keep `loading` before `src` because React updates
	        // props in order which causes Safari/Firefox to not lazy load properly.
	        // See https://github.com/facebook/react/issues/25883
	        loading: loading,
	        width: width,
	        height: height,
	        decoding: decoding,
	        "data-nimg": fill ? "fill" : "1",
	        className: className,
	        style: style,
	        // It's intended to keep `src` the last attribute because React updates
	        // attributes in order. If we keep `src` the first one, Safari will
	        // immediately start to fetch `src`, before `sizes` and `srcSet` are even
	        // updated by React. That causes multiple unnecessary requests if `srcSet`
	        // and `sizes` are defined.
	        // This bug cannot be reproduced in Chrome or Firefox.
	        sizes: sizes,
	        srcSet: srcSet,
	        src: src,
	        ref: (0, _react.useCallback)((img)=>{
	            if (forwardedRef) {
	                if (typeof forwardedRef === "function") forwardedRef(img);
	                else if (typeof forwardedRef === "object") {
	                    // @ts-ignore - .current is read only it's usually assigned by react internally
	                    forwardedRef.current = img;
	                }
	            }
	            if (!img) {
	                return;
	            }
	            if (onError) {
	                // If the image has an error before react hydrates, then the error is lost.
	                // The workaround is to wait until the image is mounted which is after hydration,
	                // then we set the src again to trigger the error handler (if there was an error).
	                // eslint-disable-next-line no-self-assign
	                img.src = img.src;
	            }
	            if (process.env.NODE_ENV !== "production") {
	                if (!src) {
	                    console.error('Image is missing required "src" property:', img);
	                }
	                if (img.getAttribute("alt") === null) {
	                    console.error('Image is missing required "alt" property. Please add Alternative Text to describe the image for screen readers and search engines.');
	                }
	            }
	            if (img.complete) {
	                handleLoading(img, placeholder, onLoadRef, onLoadingCompleteRef, setBlurComplete, unoptimized);
	            }
	        }, [
	            src,
	            placeholder,
	            onLoadRef,
	            onLoadingCompleteRef,
	            setBlurComplete,
	            onError,
	            unoptimized,
	            forwardedRef
	        ]),
	        onLoad: (event)=>{
	            const img = event.currentTarget;
	            handleLoading(img, placeholder, onLoadRef, onLoadingCompleteRef, setBlurComplete, unoptimized);
	        },
	        onError: (event)=>{
	            // if the real image fails to load, this will ensure "alt" is visible
	            setShowAltText(true);
	            if (placeholder !== "empty") {
	                // If the real image fails to load, this will still remove the placeholder.
	                setBlurComplete(true);
	            }
	            if (onError) {
	                onError(event);
	            }
	        }
	    });
	});
	function ImagePreload(param) {
	    let { isAppRouter, imgAttributes } = param;
	    const opts = {
	        as: "image",
	        imageSrcSet: imgAttributes.srcSet,
	        imageSizes: imgAttributes.sizes,
	        crossOrigin: imgAttributes.crossOrigin,
	        referrerPolicy: imgAttributes.referrerPolicy,
	        ...getDynamicProps(imgAttributes.fetchPriority)
	    };
	    if (isAppRouter && _reactdom.default.preload) {
	        // See https://github.com/facebook/react/pull/26940
	        _reactdom.default.preload(imgAttributes.src, // @ts-expect-error TODO: upgrade to `@types/react-dom@18.3.x`
	        opts);
	        return null;
	    }
	    return /*#__PURE__*/ _react.default.createElement(_head.default, null, /*#__PURE__*/ _react.default.createElement("link", {
	        key: "__nimg-" + imgAttributes.src + imgAttributes.srcSet + imgAttributes.sizes,
	        rel: "preload",
	        // Note how we omit the `href` attribute, as it would only be relevant
	        // for browsers that do not support `imagesrcset`, and in those cases
	        // it would cause the incorrect image to be preloaded.
	        //
	        // https://html.spec.whatwg.org/multipage/semantics.html#attr-link-imagesrcset
	        href: imgAttributes.srcSet ? undefined : imgAttributes.src,
	        ...opts
	    }));
	}
	const Image = /*#__PURE__*/ (0, _react.forwardRef)((props, forwardedRef)=>{
	    const pagesRouter = (0, _react.useContext)(_routercontextsharedruntime.RouterContext);
	    // We're in the app directory if there is no pages router.
	    const isAppRouter = !pagesRouter;
	    const configContext = (0, _react.useContext)(_imageconfigcontextsharedruntime.ImageConfigContext);
	    const config = (0, _react.useMemo)(()=>{
	        const c = configEnv || configContext || _imageconfig.imageConfigDefault;
	        const allSizes = [
	            ...c.deviceSizes,
	            ...c.imageSizes
	        ].sort((a, b)=>a - b);
	        const deviceSizes = c.deviceSizes.sort((a, b)=>a - b);
	        return {
	            ...c,
	            allSizes,
	            deviceSizes
	        };
	    }, [
	        configContext
	    ]);
	    const { onLoad, onLoadingComplete } = props;
	    const onLoadRef = (0, _react.useRef)(onLoad);
	    (0, _react.useEffect)(()=>{
	        onLoadRef.current = onLoad;
	    }, [
	        onLoad
	    ]);
	    const onLoadingCompleteRef = (0, _react.useRef)(onLoadingComplete);
	    (0, _react.useEffect)(()=>{
	        onLoadingCompleteRef.current = onLoadingComplete;
	    }, [
	        onLoadingComplete
	    ]);
	    const [blurComplete, setBlurComplete] = (0, _react.useState)(false);
	    const [showAltText, setShowAltText] = (0, _react.useState)(false);
	    const { props: imgAttributes, meta: imgMeta } = (0, _getimgprops.getImgProps)(props, {
	        defaultLoader: _imageloader.default,
	        imgConf: config,
	        blurComplete,
	        showAltText
	    });
	    return /*#__PURE__*/ _react.default.createElement(_react.default.Fragment, null, /*#__PURE__*/ _react.default.createElement(ImageElement, {
	        ...imgAttributes,
	        unoptimized: imgMeta.unoptimized,
	        placeholder: imgMeta.placeholder,
	        fill: imgMeta.fill,
	        onLoadRef: onLoadRef,
	        onLoadingCompleteRef: onLoadingCompleteRef,
	        setBlurComplete: setBlurComplete,
	        setShowAltText: setShowAltText,
	        ref: forwardedRef
	    }), imgMeta.priority ? /*#__PURE__*/ _react.default.createElement(ImagePreload, {
	        isAppRouter: isAppRouter,
	        imgAttributes: imgAttributes
	    }) : null);
	});

	if ((typeof exports.default === 'function' || (typeof exports.default === 'object' && exports.default !== null)) && typeof exports.default.__esModule === 'undefined') {
	  Object.defineProperty(exports.default, '__esModule', { value: true });
	  Object.assign(exports.default, exports);
	  module.exports = exports.default;
	}

	
} (imageComponent, imageComponent.exports));

var imageComponentExports = imageComponent.exports;

(function (exports) {
	Object.defineProperty(exports, "__esModule", {
	    value: true
	});
	function _export(target, all) {
	    for(var name in all)Object.defineProperty(target, name, {
	        enumerable: true,
	        get: all[name]
	    });
	}
	_export(exports, {
	    unstable_getImgProps: function() {
	        return unstable_getImgProps;
	    },
	    default: function() {
	        return _default;
	    }
	});
	const _interop_require_default = _interop_require_default$1;
	const _getimgprops = getImgProps;
	const _warnonce = warnOnce;
	const _imagecomponent = imageComponentExports;
	const _imageloader = /*#__PURE__*/ _interop_require_default._(requireImageLoader());
	const unstable_getImgProps = (imgProps)=>{
	    (0, _warnonce.warnOnce)("Warning: unstable_getImgProps() is experimental and may change or be removed at any time. Use at your own risk.");
	    const { props } = (0, _getimgprops.getImgProps)(imgProps, {
	        defaultLoader: _imageloader.default,
	        // This is replaced by webpack define plugin
	        imgConf: process.env.__NEXT_IMAGE_OPTS
	    });
	    for (const [key, value] of Object.entries(props)){
	        if (value === undefined) {
	            delete props[key];
	        }
	    }
	    return {
	        props
	    };
	};
	const _default = _imagecomponent.Image;

	
} (imageExternal));

var image = imageExternal;

var Image = /*@__PURE__*/getDefaultExportFromCjs(image);

var css_248z$3 = ".ImageCard-module_card__kVCRN {\n  min-height: 15rem;\n}\n\n@media (min-width: 576px) {\n  .ImageCard-module_content__IuIf1 {\n    max-width: 80%;\n  }\n  .ImageCard-module_content__IuIf1::before {\n    content: \"\";\n    position: absolute;\n    inset: 0;\n    z-index: -1;\n    background: linear-gradient(90deg, rgba(33, 37, 41, 0.8) 0%, rgba(33, 37, 41, 0) 100%);\n  }\n}\n\n.ImageCard-module_imageWrapperMobile__fo8CC {\n  min-height: 8.5rem;\n}\n.ImageCard-module_imageWrapperMobile__fo8CC::after {\n  content: \"\";\n  position: absolute;\n  inset: 0;\n  background: linear-gradient(90deg, rgba(33, 37, 41, 0.8) 0%, rgba(33, 37, 41, 0) 100%);\n}\n\n@media (min-width: 576px) {\n  .ImageCard-module_description__YMAtH {\n    color: #fff;\n  }\n}\n\n.ImageCard-module_buttonFlat__HousR {\n  color: #fff;\n}\n.ImageCard-module_buttonFlat__HousR:hover, .ImageCard-module_buttonFlat__HousR:active {\n  color: #e20613;\n}";
var styles$3 = {"card":"ImageCard-module_card__kVCRN","content":"ImageCard-module_content__IuIf1","imageWrapperMobile":"ImageCard-module_imageWrapperMobile__fo8CC","description":"ImageCard-module_description__YMAtH","buttonFlat":"ImageCard-module_buttonFlat__HousR"};
styleInject(css_248z$3);

/**
 * IFE-115 ImageCard
 */
function ImageCard(_a) {
    var _b;
    var image = _a.image, title = _a.title, description = _a.description, icon = _a.icon, button = _a.button, className = _a.className;
    return (jsxs("div", { className: clsx('card overflow-hidden', styles$3.card, className), children: [jsxs("div", { className: clsx('position-relative d-sm-none', styles$3.imageWrapperMobile), children: [jsx(Image, __assign({ className: 'object-fit-cover bg-secondary' }, image, { alt: image.alt, fill: true })), (!!icon || !!title) && (jsxs("div", { className: 'position-relative z-1 p-6', children: [!!icon && jsx(Icon, { className: 'align-self-start mb-4 text-white', icon: icon, size: '2x' }), !!title && (jsx(Typography, { variant: 'h4', component: 'h3', className: 'text-white', children: title }))] }))] }), jsx(Image, __assign({ className: 'd-none d-sm-block object-fit-cover bg-secondary' }, image, { alt: image.alt, fill: true })), (!!icon || !!title || !!description || !!button) && (jsxs("div", { className: clsx('card-body d-flex flex-column justify-content-between z-1', styles$3.content), children: [jsxs("div", { children: [!!icon && (jsx(Icon, { className: 'd-none d-sm-block align-self-start mb-6 text-white', icon: icon, size: '2x' })), !!title && (jsx(Typography, { variant: 'h4', component: 'h3', className: 'd-none d-sm-block mb-4 text-white', children: title })), !!description && (jsx(Typography, { component: 'div', className: clsx('card-text', styles$3.description), children: description }))] }), !!button && (jsx(Button, __assign({ className: clsx('mt-6 align-self-start', (_b = {},
                            _b[styles$3.buttonFlat] = button.fillMode === 'flat',
                            _b['ms-n4'] = button.fillMode === 'flat',
                            _b)), size: 'large' }, button)))] }))] }));
}

/**
 * IFE-101 Section
 *
 * The Section component can be used to group content in a section with an optional headline, icon and action bar.
 */
function Section(_a) {
    var headline = _a.headline, icon = _a.icon, actionBarComponent = _a.actionBarComponent, _b = _a.variant, variant = _b === void 0 ? 'default' : _b, children = _a.children, className = _a.className;
    return (jsxs("section", { className: className, children: [(!!headline || !!actionBarComponent) && (jsxs("div", { className: clsx('d-flex justify-content-between align-items-center', {
                    'mb-6': variant === 'default',
                    'mb-4 pb-3 border-bottom': variant === 'small'
                }), children: [jsxs(Typography, { variant: variant === 'small' ? 'h4' : 'h3', component: 'h2', children: [!!icon && (jsx(Icon, { className: variant === 'small' ? 'd-none d-md-inline-block me-4' : 'me-3', icon: icon, size: variant === 'small' ? 'xs' : undefined, fixedWidth: true })), headline] }), !!actionBarComponent && (jsx("div", { className: 'flex-shrink-0 ms-auto', children: actionBarComponent }))] })), jsx("div", { children: children })] }));
}

/**
 * IFE-016 IconWithText
 */
function IconWithText(_a) {
    var icon = _a.icon, text = _a.text, className = _a.className;
    return (jsxs("div", { className: clsx('hstack gap-2', className), children: [jsx(Icon, { icon: icon, fixedWidth: true }), text] }));
}

var css_248z$2 = ".FeatureCard-module_button__DG5qx {\n  all: unset;\n}";
var styles$2 = {"button":"FeatureCard-module_button__DG5qx"};
styleInject(css_248z$2);

/**
 * IFE-102 FeatureCard
 *
 * The FeatureCard component is a card with a title, description and an optional footer text, icon and link.
 */
function FeatureCard(_a) {
    var title = _a.title, description = _a.description, icon = _a.icon, link = _a.link, onClick = _a.onClick, button = _a.button, _b = _a.size, size = _b === void 0 ? 'regular' : _b, _c = _a.textColor, textColor = _c === void 0 ? 'default' : _c, errorMessage = _a.errorMessage, className = _a.className;
    return (jsxs("div", { className: clsx('card card-body', { 'border-error-100': !!errorMessage }, className), children: [(!!icon || !!errorMessage) && (jsxs("div", { className: 'd-flex justify-content-between', children: [!!icon && (jsx(Icon, { className: clsx('align-self-start text-body', {
                            'mb-1': size === 'small',
                            'mb-6': size === 'regular'
                        }), icon: icon, size: size === 'small' ? '1x' : '2x' })), !!errorMessage && (jsxs("div", { className: 'd-flex align-self-start align-items-center ms-auto mb-6', children: [jsx(Typography, { variant: 'textMd', className: 'text-error-500', children: errorMessage }), jsx(Icon, { className: 'ms-2 text-error-500', icon: 'circle-exclamation' })] }))] })), !!link ? (jsxs(Link, __assign({ className: 'text-decoration-none stretched-link' }, link, { children: [!!title && (jsx(Typography, { variant: size === 'small' ? 'textMdBold' : 'h4', component: 'h3', className: 'card-title text-body', children: title })), jsx(Typography, { component: 'div', className: clsx('card-text', {
                            'text-body': textColor === 'default',
                            'text-gray-600': textColor === 'light'
                        }), children: description })] }))) : !!onClick ? (jsxs("button", { className: clsx(styles$2.button, 'stretched-link'), onClick: onClick, children: [!!title && (jsx(Typography, { variant: size === 'small' ? 'textMdBold' : 'h4', component: 'h3', className: 'card-title text-body', children: title })), jsx(Typography, { component: 'div', className: clsx('card-text', {
                            'text-body': textColor === 'default',
                            'text-gray-600': textColor === 'light'
                        }), children: description })] })) : (jsxs(Fragment$1, { children: [!!title && (jsx(Typography, { variant: size === 'small' ? 'textMdBold' : 'h4', component: 'h3', className: 'card-title text-body', children: title })), jsx(Typography, { component: 'div', className: clsx('card-text', {
                            'text-body': textColor === 'default',
                            'text-gray-600': textColor === 'light'
                        }), children: description })] })), !!button && jsx(Button, __assign({ className: 'mt-4 align-self-start', size: 'large' }, button))] }));
}

/**
 * LYE-001 Container
 *
 * The Container is a fundamental building block that contains, pad, and aligns your content within a given device or viewport.
 */
function Container(_a) {
    var _b = _a.component, Component = _b === void 0 ? 'div' : _b, children = _a.children, className = _a.className;
    return jsx(Component, { className: clsx('container', className), children: children });
}

/**
 * IFE-202 GlobalFooter
 *
 * The GlobalFooter component displays the footer with brand logo and navigation items.
 */
function GlobalFooter(_a) {
    var logoLink = _a.logoLink, navigationComponent = _a.navigationComponent, className = _a.className;
    return (jsx("footer", { className: clsx('mt-12 mt-md-32 py-10 border-top', className), children: jsx(Container, { children: jsxs("div", { className: "d-flex flex-column flex-lg-row justify-content-between align-items-center gap-8", children: [jsx(Link, __assign({}, logoLink, { className: "flex-shrink-0", children: jsx(TechemLogoMonochrome, { className: "flex-shrink-0 text-gray-700", height: 40 }) })), navigationComponent] }) }) }));
}

/**
 * IFE-202 GlobalFooter: Navigation
 *
 * Navigation componenet for the GlobalFooter component.
 */
function Navigation(_a) {
    var items = _a.items;
    return (jsx("nav", { className: "d-flex flex-column flex-lg-row align-items-center gap-4 gap-lg-12", children: items.map(function (item) { return (jsx(Link, __assign({ className: "text-body text-decoration-none text-gray-700" }, item.link, { children: jsx(Typography, { component: "span", children: item.title }) }), item.key || item.title)); }) }));
}

var css_248z$1 = ".CardGrid-module_cardGrid__7xfdP {\n  display: grid;\n  gap: 1.5rem;\n}\n@media (min-width: 834px) {\n  .CardGrid-module_cardGrid__7xfdP {\n    grid-template-columns: repeat(3, minmax(10px, 1fr));\n    row-gap: 3rem;\n  }\n  .CardGrid-module_cardGrid__7xfdP.CardGrid-module_twoColumns__d5J3x {\n    grid-template-columns: repeat(2, minmax(10px, 1fr));\n  }\n}";
var styles$1 = {"cardGrid":"CardGrid-module_cardGrid__7xfdP","twoColumns":"CardGrid-module_twoColumns__d5J3x"};
styleInject(css_248z$1);

/**
 * LYE-003 CardGrid
 *
 * The CardGrid component displays a set of cards (like IFE-102 FeatureCard or IFE-103 PropertyCard) in a two or three column grid.
 */
function CardGrid(_a) {
    var _b;
    var _c = _a.columns, columns = _c === void 0 ? 3 : _c, children = _a.children, className = _a.className;
    return jsx("div", { className: clsx(styles$1.cardGrid, className, (_b = {}, _b[styles$1.twoColumns] = columns === 2, _b)), children: children });
}

/**
 * Listens for when the escape key is down
 */ function $addc16e1bbe58fd0$export$3a72a57244d6e765(onEscapeKeyDownProp, ownerDocument = globalThis === null || globalThis === void 0 ? void 0 : globalThis.document) {
    const onEscapeKeyDown = $b1b2314f5f9a1d84$export$25bec8c6f54ee79a(onEscapeKeyDownProp);
    useEffect(()=>{
        const handleKeyDown = (event)=>{
            if (event.key === 'Escape') onEscapeKeyDown(event);
        };
        ownerDocument.addEventListener('keydown', handleKeyDown);
        return ()=>ownerDocument.removeEventListener('keydown', handleKeyDown)
        ;
    }, [
        onEscapeKeyDown,
        ownerDocument
    ]);
}

const $5cb92bef7577960e$var$CONTEXT_UPDATE = 'dismissableLayer.update';
const $5cb92bef7577960e$var$POINTER_DOWN_OUTSIDE = 'dismissableLayer.pointerDownOutside';
const $5cb92bef7577960e$var$FOCUS_OUTSIDE = 'dismissableLayer.focusOutside';
let $5cb92bef7577960e$var$originalBodyPointerEvents;
const $5cb92bef7577960e$var$DismissableLayerContext = /*#__PURE__*/ createContext({
    layers: new Set(),
    layersWithOutsidePointerEventsDisabled: new Set(),
    branches: new Set()
});
const $5cb92bef7577960e$export$177fb62ff3ec1f22 = /*#__PURE__*/ forwardRef((props, forwardedRef)=>{
    var _node$ownerDocument;
    const { disableOutsidePointerEvents: disableOutsidePointerEvents = false , onEscapeKeyDown: onEscapeKeyDown , onPointerDownOutside: onPointerDownOutside , onFocusOutside: onFocusOutside , onInteractOutside: onInteractOutside , onDismiss: onDismiss , ...layerProps } = props;
    const context = useContext($5cb92bef7577960e$var$DismissableLayerContext);
    const [node1, setNode] = useState(null);
    const ownerDocument = (_node$ownerDocument = node1 === null || node1 === void 0 ? void 0 : node1.ownerDocument) !== null && _node$ownerDocument !== void 0 ? _node$ownerDocument : globalThis === null || globalThis === void 0 ? void 0 : globalThis.document;
    const [, force] = useState({});
    const composedRefs = $6ed0406888f73fc4$export$c7b2cbe3552a0d05(forwardedRef, (node)=>setNode(node)
    );
    const layers = Array.from(context.layers);
    const [highestLayerWithOutsidePointerEventsDisabled] = [
        ...context.layersWithOutsidePointerEventsDisabled
    ].slice(-1); // prettier-ignore
    const highestLayerWithOutsidePointerEventsDisabledIndex = layers.indexOf(highestLayerWithOutsidePointerEventsDisabled); // prettier-ignore
    const index = node1 ? layers.indexOf(node1) : -1;
    const isBodyPointerEventsDisabled = context.layersWithOutsidePointerEventsDisabled.size > 0;
    const isPointerEventsEnabled = index >= highestLayerWithOutsidePointerEventsDisabledIndex;
    const pointerDownOutside = $5cb92bef7577960e$var$usePointerDownOutside((event)=>{
        const target = event.target;
        const isPointerDownOnBranch = [
            ...context.branches
        ].some((branch)=>branch.contains(target)
        );
        if (!isPointerEventsEnabled || isPointerDownOnBranch) return;
        onPointerDownOutside === null || onPointerDownOutside === void 0 || onPointerDownOutside(event);
        onInteractOutside === null || onInteractOutside === void 0 || onInteractOutside(event);
        if (!event.defaultPrevented) onDismiss === null || onDismiss === void 0 || onDismiss();
    }, ownerDocument);
    const focusOutside = $5cb92bef7577960e$var$useFocusOutside((event)=>{
        const target = event.target;
        const isFocusInBranch = [
            ...context.branches
        ].some((branch)=>branch.contains(target)
        );
        if (isFocusInBranch) return;
        onFocusOutside === null || onFocusOutside === void 0 || onFocusOutside(event);
        onInteractOutside === null || onInteractOutside === void 0 || onInteractOutside(event);
        if (!event.defaultPrevented) onDismiss === null || onDismiss === void 0 || onDismiss();
    }, ownerDocument);
    $addc16e1bbe58fd0$export$3a72a57244d6e765((event)=>{
        const isHighestLayer = index === context.layers.size - 1;
        if (!isHighestLayer) return;
        onEscapeKeyDown === null || onEscapeKeyDown === void 0 || onEscapeKeyDown(event);
        if (!event.defaultPrevented && onDismiss) {
            event.preventDefault();
            onDismiss();
        }
    }, ownerDocument);
    useEffect(()=>{
        if (!node1) return;
        if (disableOutsidePointerEvents) {
            if (context.layersWithOutsidePointerEventsDisabled.size === 0) {
                $5cb92bef7577960e$var$originalBodyPointerEvents = ownerDocument.body.style.pointerEvents;
                ownerDocument.body.style.pointerEvents = 'none';
            }
            context.layersWithOutsidePointerEventsDisabled.add(node1);
        }
        context.layers.add(node1);
        $5cb92bef7577960e$var$dispatchUpdate();
        return ()=>{
            if (disableOutsidePointerEvents && context.layersWithOutsidePointerEventsDisabled.size === 1) ownerDocument.body.style.pointerEvents = $5cb92bef7577960e$var$originalBodyPointerEvents;
        };
    }, [
        node1,
        ownerDocument,
        disableOutsidePointerEvents,
        context
    ]);
    /**
   * We purposefully prevent combining this effect with the `disableOutsidePointerEvents` effect
   * because a change to `disableOutsidePointerEvents` would remove this layer from the stack
   * and add it to the end again so the layering order wouldn't be _creation order_.
   * We only want them to be removed from context stacks when unmounted.
   */ useEffect(()=>{
        return ()=>{
            if (!node1) return;
            context.layers.delete(node1);
            context.layersWithOutsidePointerEventsDisabled.delete(node1);
            $5cb92bef7577960e$var$dispatchUpdate();
        };
    }, [
        node1,
        context
    ]);
    useEffect(()=>{
        const handleUpdate = ()=>force({})
        ;
        document.addEventListener($5cb92bef7577960e$var$CONTEXT_UPDATE, handleUpdate);
        return ()=>document.removeEventListener($5cb92bef7577960e$var$CONTEXT_UPDATE, handleUpdate)
        ;
    }, []);
    return /*#__PURE__*/ createElement($8927f6f2acc4f386$export$250ffa63cdc0d034.div, _extends({}, layerProps, {
        ref: composedRefs,
        style: {
            pointerEvents: isBodyPointerEventsDisabled ? isPointerEventsEnabled ? 'auto' : 'none' : undefined,
            ...props.style
        },
        onFocusCapture: $e42e1063c40fb3ef$export$b9ecd428b558ff10(props.onFocusCapture, focusOutside.onFocusCapture),
        onBlurCapture: $e42e1063c40fb3ef$export$b9ecd428b558ff10(props.onBlurCapture, focusOutside.onBlurCapture),
        onPointerDownCapture: $e42e1063c40fb3ef$export$b9ecd428b558ff10(props.onPointerDownCapture, pointerDownOutside.onPointerDownCapture)
    }));
});
/* -----------------------------------------------------------------------------------------------*/ /**
 * Listens for `pointerdown` outside a react subtree. We use `pointerdown` rather than `pointerup`
 * to mimic layer dismissing behaviour present in OS.
 * Returns props to pass to the node we want to check for outside events.
 */ function $5cb92bef7577960e$var$usePointerDownOutside(onPointerDownOutside, ownerDocument = globalThis === null || globalThis === void 0 ? void 0 : globalThis.document) {
    const handlePointerDownOutside = $b1b2314f5f9a1d84$export$25bec8c6f54ee79a(onPointerDownOutside);
    const isPointerInsideReactTreeRef = useRef(false);
    const handleClickRef = useRef(()=>{});
    useEffect(()=>{
        const handlePointerDown = (event)=>{
            if (event.target && !isPointerInsideReactTreeRef.current) {
                const eventDetail = {
                    originalEvent: event
                };
                function handleAndDispatchPointerDownOutsideEvent() {
                    $5cb92bef7577960e$var$handleAndDispatchCustomEvent($5cb92bef7577960e$var$POINTER_DOWN_OUTSIDE, handlePointerDownOutside, eventDetail, {
                        discrete: true
                    });
                }
                /**
         * On touch devices, we need to wait for a click event because browsers implement
         * a ~350ms delay between the time the user stops touching the display and when the
         * browser executres events. We need to ensure we don't reactivate pointer-events within
         * this timeframe otherwise the browser may execute events that should have been prevented.
         *
         * Additionally, this also lets us deal automatically with cancellations when a click event
         * isn't raised because the page was considered scrolled/drag-scrolled, long-pressed, etc.
         *
         * This is why we also continuously remove the previous listener, because we cannot be
         * certain that it was raised, and therefore cleaned-up.
         */ if (event.pointerType === 'touch') {
                    ownerDocument.removeEventListener('click', handleClickRef.current);
                    handleClickRef.current = handleAndDispatchPointerDownOutsideEvent;
                    ownerDocument.addEventListener('click', handleClickRef.current, {
                        once: true
                    });
                } else handleAndDispatchPointerDownOutsideEvent();
            }
            isPointerInsideReactTreeRef.current = false;
        };
        /**
     * if this hook executes in a component that mounts via a `pointerdown` event, the event
     * would bubble up to the document and trigger a `pointerDownOutside` event. We avoid
     * this by delaying the event listener registration on the document.
     * This is not React specific, but rather how the DOM works, ie:
     * ```
     * button.addEventListener('pointerdown', () => {
     *   console.log('I will log');
     *   document.addEventListener('pointerdown', () => {
     *     console.log('I will also log');
     *   })
     * });
     */ const timerId = window.setTimeout(()=>{
            ownerDocument.addEventListener('pointerdown', handlePointerDown);
        }, 0);
        return ()=>{
            window.clearTimeout(timerId);
            ownerDocument.removeEventListener('pointerdown', handlePointerDown);
            ownerDocument.removeEventListener('click', handleClickRef.current);
        };
    }, [
        ownerDocument,
        handlePointerDownOutside
    ]);
    return {
        // ensures we check React component tree (not just DOM tree)
        onPointerDownCapture: ()=>isPointerInsideReactTreeRef.current = true
    };
}
/**
 * Listens for when focus happens outside a react subtree.
 * Returns props to pass to the root (node) of the subtree we want to check.
 */ function $5cb92bef7577960e$var$useFocusOutside(onFocusOutside, ownerDocument = globalThis === null || globalThis === void 0 ? void 0 : globalThis.document) {
    const handleFocusOutside = $b1b2314f5f9a1d84$export$25bec8c6f54ee79a(onFocusOutside);
    const isFocusInsideReactTreeRef = useRef(false);
    useEffect(()=>{
        const handleFocus = (event)=>{
            if (event.target && !isFocusInsideReactTreeRef.current) {
                const eventDetail = {
                    originalEvent: event
                };
                $5cb92bef7577960e$var$handleAndDispatchCustomEvent($5cb92bef7577960e$var$FOCUS_OUTSIDE, handleFocusOutside, eventDetail, {
                    discrete: false
                });
            }
        };
        ownerDocument.addEventListener('focusin', handleFocus);
        return ()=>ownerDocument.removeEventListener('focusin', handleFocus)
        ;
    }, [
        ownerDocument,
        handleFocusOutside
    ]);
    return {
        onFocusCapture: ()=>isFocusInsideReactTreeRef.current = true
        ,
        onBlurCapture: ()=>isFocusInsideReactTreeRef.current = false
    };
}
function $5cb92bef7577960e$var$dispatchUpdate() {
    const event = new CustomEvent($5cb92bef7577960e$var$CONTEXT_UPDATE);
    document.dispatchEvent(event);
}
function $5cb92bef7577960e$var$handleAndDispatchCustomEvent(name, handler, detail, { discrete: discrete  }) {
    const target = detail.originalEvent.target;
    const event = new CustomEvent(name, {
        bubbles: false,
        cancelable: true,
        detail: detail
    });
    if (handler) target.addEventListener(name, handler, {
        once: true
    });
    if (discrete) $8927f6f2acc4f386$export$6d1a0317bde7de7f(target, event);
    else target.dispatchEvent(event);
}

const $d3863c46a17e8a28$var$AUTOFOCUS_ON_MOUNT = 'focusScope.autoFocusOnMount';
const $d3863c46a17e8a28$var$AUTOFOCUS_ON_UNMOUNT = 'focusScope.autoFocusOnUnmount';
const $d3863c46a17e8a28$var$EVENT_OPTIONS = {
    bubbles: false,
    cancelable: true
};
const $d3863c46a17e8a28$export$20e40289641fbbb6 = /*#__PURE__*/ forwardRef((props, forwardedRef)=>{
    const { loop: loop = false , trapped: trapped = false , onMountAutoFocus: onMountAutoFocusProp , onUnmountAutoFocus: onUnmountAutoFocusProp , ...scopeProps } = props;
    const [container1, setContainer] = useState(null);
    const onMountAutoFocus = $b1b2314f5f9a1d84$export$25bec8c6f54ee79a(onMountAutoFocusProp);
    const onUnmountAutoFocus = $b1b2314f5f9a1d84$export$25bec8c6f54ee79a(onUnmountAutoFocusProp);
    const lastFocusedElementRef = useRef(null);
    const composedRefs = $6ed0406888f73fc4$export$c7b2cbe3552a0d05(forwardedRef, (node)=>setContainer(node)
    );
    const focusScope = useRef({
        paused: false,
        pause () {
            this.paused = true;
        },
        resume () {
            this.paused = false;
        }
    }).current; // Takes care of trapping focus if focus is moved outside programmatically for example
    useEffect(()=>{
        if (trapped) {
            function handleFocusIn(event) {
                if (focusScope.paused || !container1) return;
                const target = event.target;
                if (container1.contains(target)) lastFocusedElementRef.current = target;
                else $d3863c46a17e8a28$var$focus(lastFocusedElementRef.current, {
                    select: true
                });
            }
            function handleFocusOut(event) {
                if (focusScope.paused || !container1) return;
                if (!container1.contains(event.relatedTarget)) $d3863c46a17e8a28$var$focus(lastFocusedElementRef.current, {
                    select: true
                });
            }
            document.addEventListener('focusin', handleFocusIn);
            document.addEventListener('focusout', handleFocusOut);
            return ()=>{
                document.removeEventListener('focusin', handleFocusIn);
                document.removeEventListener('focusout', handleFocusOut);
            };
        }
    }, [
        trapped,
        container1,
        focusScope.paused
    ]);
    useEffect(()=>{
        if (container1) {
            $d3863c46a17e8a28$var$focusScopesStack.add(focusScope);
            const previouslyFocusedElement = document.activeElement;
            const hasFocusedCandidate = container1.contains(previouslyFocusedElement);
            if (!hasFocusedCandidate) {
                const mountEvent = new CustomEvent($d3863c46a17e8a28$var$AUTOFOCUS_ON_MOUNT, $d3863c46a17e8a28$var$EVENT_OPTIONS);
                container1.addEventListener($d3863c46a17e8a28$var$AUTOFOCUS_ON_MOUNT, onMountAutoFocus);
                container1.dispatchEvent(mountEvent);
                if (!mountEvent.defaultPrevented) {
                    $d3863c46a17e8a28$var$focusFirst($d3863c46a17e8a28$var$removeLinks($d3863c46a17e8a28$var$getTabbableCandidates(container1)), {
                        select: true
                    });
                    if (document.activeElement === previouslyFocusedElement) $d3863c46a17e8a28$var$focus(container1);
                }
            }
            return ()=>{
                container1.removeEventListener($d3863c46a17e8a28$var$AUTOFOCUS_ON_MOUNT, onMountAutoFocus); // We hit a react bug (fixed in v17) with focusing in unmount.
                // We need to delay the focus a little to get around it for now.
                // See: https://github.com/facebook/react/issues/17894
                setTimeout(()=>{
                    const unmountEvent = new CustomEvent($d3863c46a17e8a28$var$AUTOFOCUS_ON_UNMOUNT, $d3863c46a17e8a28$var$EVENT_OPTIONS);
                    container1.addEventListener($d3863c46a17e8a28$var$AUTOFOCUS_ON_UNMOUNT, onUnmountAutoFocus);
                    container1.dispatchEvent(unmountEvent);
                    if (!unmountEvent.defaultPrevented) $d3863c46a17e8a28$var$focus(previouslyFocusedElement !== null && previouslyFocusedElement !== void 0 ? previouslyFocusedElement : document.body, {
                        select: true
                    });
                     // we need to remove the listener after we `dispatchEvent`
                    container1.removeEventListener($d3863c46a17e8a28$var$AUTOFOCUS_ON_UNMOUNT, onUnmountAutoFocus);
                    $d3863c46a17e8a28$var$focusScopesStack.remove(focusScope);
                }, 0);
            };
        }
    }, [
        container1,
        onMountAutoFocus,
        onUnmountAutoFocus,
        focusScope
    ]); // Takes care of looping focus (when tabbing whilst at the edges)
    const handleKeyDown = useCallback((event)=>{
        if (!loop && !trapped) return;
        if (focusScope.paused) return;
        const isTabKey = event.key === 'Tab' && !event.altKey && !event.ctrlKey && !event.metaKey;
        const focusedElement = document.activeElement;
        if (isTabKey && focusedElement) {
            const container = event.currentTarget;
            const [first, last] = $d3863c46a17e8a28$var$getTabbableEdges(container);
            const hasTabbableElementsInside = first && last; // we can only wrap focus if we have tabbable edges
            if (!hasTabbableElementsInside) {
                if (focusedElement === container) event.preventDefault();
            } else {
                if (!event.shiftKey && focusedElement === last) {
                    event.preventDefault();
                    if (loop) $d3863c46a17e8a28$var$focus(first, {
                        select: true
                    });
                } else if (event.shiftKey && focusedElement === first) {
                    event.preventDefault();
                    if (loop) $d3863c46a17e8a28$var$focus(last, {
                        select: true
                    });
                }
            }
        }
    }, [
        loop,
        trapped,
        focusScope.paused
    ]);
    return /*#__PURE__*/ createElement($8927f6f2acc4f386$export$250ffa63cdc0d034.div, _extends({
        tabIndex: -1
    }, scopeProps, {
        ref: composedRefs,
        onKeyDown: handleKeyDown
    }));
});
/* -------------------------------------------------------------------------------------------------
 * Utils
 * -----------------------------------------------------------------------------------------------*/ /**
 * Attempts focusing the first element in a list of candidates.
 * Stops when focus has actually moved.
 */ function $d3863c46a17e8a28$var$focusFirst(candidates, { select: select = false  } = {}) {
    const previouslyFocusedElement = document.activeElement;
    for (const candidate of candidates){
        $d3863c46a17e8a28$var$focus(candidate, {
            select: select
        });
        if (document.activeElement !== previouslyFocusedElement) return;
    }
}
/**
 * Returns the first and last tabbable elements inside a container.
 */ function $d3863c46a17e8a28$var$getTabbableEdges(container) {
    const candidates = $d3863c46a17e8a28$var$getTabbableCandidates(container);
    const first = $d3863c46a17e8a28$var$findVisible(candidates, container);
    const last = $d3863c46a17e8a28$var$findVisible(candidates.reverse(), container);
    return [
        first,
        last
    ];
}
/**
 * Returns a list of potential tabbable candidates.
 *
 * NOTE: This is only a close approximation. For example it doesn't take into account cases like when
 * elements are not visible. This cannot be worked out easily by just reading a property, but rather
 * necessitate runtime knowledge (computed styles, etc). We deal with these cases separately.
 *
 * See: https://developer.mozilla.org/en-US/docs/Web/API/TreeWalker
 * Credit: https://github.com/discord/focus-layers/blob/master/src/util/wrapFocus.tsx#L1
 */ function $d3863c46a17e8a28$var$getTabbableCandidates(container) {
    const nodes = [];
    const walker = document.createTreeWalker(container, NodeFilter.SHOW_ELEMENT, {
        acceptNode: (node)=>{
            const isHiddenInput = node.tagName === 'INPUT' && node.type === 'hidden';
            if (node.disabled || node.hidden || isHiddenInput) return NodeFilter.FILTER_SKIP; // `.tabIndex` is not the same as the `tabindex` attribute. It works on the
            // runtime's understanding of tabbability, so this automatically accounts
            // for any kind of element that could be tabbed to.
            return node.tabIndex >= 0 ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_SKIP;
        }
    });
    while(walker.nextNode())nodes.push(walker.currentNode); // we do not take into account the order of nodes with positive `tabIndex` as it
    // hinders accessibility to have tab order different from visual order.
    return nodes;
}
/**
 * Returns the first visible element in a list.
 * NOTE: Only checks visibility up to the `container`.
 */ function $d3863c46a17e8a28$var$findVisible(elements, container) {
    for (const element of elements){
        // we stop checking if it's hidden at the `container` level (excluding)
        if (!$d3863c46a17e8a28$var$isHidden(element, {
            upTo: container
        })) return element;
    }
}
function $d3863c46a17e8a28$var$isHidden(node, { upTo: upTo  }) {
    if (getComputedStyle(node).visibility === 'hidden') return true;
    while(node){
        // we stop at `upTo` (excluding it)
        if (upTo !== undefined && node === upTo) return false;
        if (getComputedStyle(node).display === 'none') return true;
        node = node.parentElement;
    }
    return false;
}
function $d3863c46a17e8a28$var$isSelectableInput(element) {
    return element instanceof HTMLInputElement && 'select' in element;
}
function $d3863c46a17e8a28$var$focus(element, { select: select = false  } = {}) {
    // only focus if that element is focusable
    if (element && element.focus) {
        const previouslyFocusedElement = document.activeElement; // NOTE: we prevent scrolling on focus, to minimize jarring transitions for users
        element.focus({
            preventScroll: true
        }); // only select if its not the same element, it supports selection and we need to select
        if (element !== previouslyFocusedElement && $d3863c46a17e8a28$var$isSelectableInput(element) && select) element.select();
    }
}
/* -------------------------------------------------------------------------------------------------
 * FocusScope stack
 * -----------------------------------------------------------------------------------------------*/ const $d3863c46a17e8a28$var$focusScopesStack = $d3863c46a17e8a28$var$createFocusScopesStack();
function $d3863c46a17e8a28$var$createFocusScopesStack() {
    /** A stack of focus scopes, with the active one at the top */ let stack = [];
    return {
        add (focusScope) {
            // pause the currently active focus scope (at the top of the stack)
            const activeFocusScope = stack[0];
            if (focusScope !== activeFocusScope) activeFocusScope === null || activeFocusScope === void 0 || activeFocusScope.pause();
             // remove in case it already exists (because we'll re-add it at the top of the stack)
            stack = $d3863c46a17e8a28$var$arrayRemove(stack, focusScope);
            stack.unshift(focusScope);
        },
        remove (focusScope) {
            var _stack$;
            stack = $d3863c46a17e8a28$var$arrayRemove(stack, focusScope);
            (_stack$ = stack[0]) === null || _stack$ === void 0 || _stack$.resume();
        }
    };
}
function $d3863c46a17e8a28$var$arrayRemove(array, item) {
    const updatedArray = [
        ...array
    ];
    const index = updatedArray.indexOf(item);
    if (index !== -1) updatedArray.splice(index, 1);
    return updatedArray;
}
function $d3863c46a17e8a28$var$removeLinks(items) {
    return items.filter((item)=>item.tagName !== 'A'
    );
}

const $f1701beae083dbae$export$602eac185826482c = /*#__PURE__*/ forwardRef((props, forwardedRef)=>{
    var _globalThis$document;
    const { container: container = globalThis === null || globalThis === void 0 ? void 0 : (_globalThis$document = globalThis.document) === null || _globalThis$document === void 0 ? void 0 : _globalThis$document.body , ...portalProps } = props;
    return container ? /*#__PURE__*/ require$$3.createPortal(/*#__PURE__*/ createElement($8927f6f2acc4f386$export$250ffa63cdc0d034.div, _extends({}, portalProps, {
        ref: forwardedRef
    })), container) : null;
});

/** Number of components which have requested interest to have focus guards */ let $3db38b7d1fb3fe6a$var$count = 0;
/**
 * Injects a pair of focus guards at the edges of the whole DOM tree
 * to ensure `focusin` & `focusout` events can be caught consistently.
 */ function $3db38b7d1fb3fe6a$export$b7ece24a22aeda8c() {
    useEffect(()=>{
        var _edgeGuards$, _edgeGuards$2;
        const edgeGuards = document.querySelectorAll('[data-radix-focus-guard]');
        document.body.insertAdjacentElement('afterbegin', (_edgeGuards$ = edgeGuards[0]) !== null && _edgeGuards$ !== void 0 ? _edgeGuards$ : $3db38b7d1fb3fe6a$var$createFocusGuard());
        document.body.insertAdjacentElement('beforeend', (_edgeGuards$2 = edgeGuards[1]) !== null && _edgeGuards$2 !== void 0 ? _edgeGuards$2 : $3db38b7d1fb3fe6a$var$createFocusGuard());
        $3db38b7d1fb3fe6a$var$count++;
        return ()=>{
            if ($3db38b7d1fb3fe6a$var$count === 1) document.querySelectorAll('[data-radix-focus-guard]').forEach((node)=>node.remove()
            );
            $3db38b7d1fb3fe6a$var$count--;
        };
    }, []);
}
function $3db38b7d1fb3fe6a$var$createFocusGuard() {
    const element = document.createElement('span');
    element.setAttribute('data-radix-focus-guard', '');
    element.tabIndex = 0;
    element.style.cssText = 'outline: none; opacity: 0; position: fixed; pointer-events: none';
    return element;
}

var zeroRightClassName = 'right-scroll-bar-position';
var fullWidthClassName = 'width-before-scroll-bar';
var noScrollbarsClassName = 'with-scroll-bars-hidden';
/**
 * Name of a CSS variable containing the amount of "hidden" scrollbar
 * ! might be undefined ! use will fallback!
 */
var removedBarSizeVariable = '--removed-body-scroll-bar-size';

/**
 * Assigns a value for a given ref, no matter of the ref format
 * @param {RefObject} ref - a callback function or ref object
 * @param value - a new value
 *
 * @see https://github.com/theKashey/use-callback-ref#assignref
 * @example
 * const refObject = useRef();
 * const refFn = (ref) => {....}
 *
 * assignRef(refObject, "refValue");
 * assignRef(refFn, "refValue");
 */
function assignRef(ref, value) {
    if (typeof ref === 'function') {
        ref(value);
    }
    else if (ref) {
        ref.current = value;
    }
    return ref;
}

/**
 * creates a MutableRef with ref change callback
 * @param initialValue - initial ref value
 * @param {Function} callback - a callback to run when value changes
 *
 * @example
 * const ref = useCallbackRef(0, (newValue, oldValue) => console.log(oldValue, '->', newValue);
 * ref.current = 1;
 * // prints 0 -> 1
 *
 * @see https://reactjs.org/docs/hooks-reference.html#useref
 * @see https://github.com/theKashey/use-callback-ref#usecallbackref---to-replace-reactuseref
 * @returns {MutableRefObject}
 */
function useCallbackRef(initialValue, callback) {
    var ref = useState(function () { return ({
        // value
        value: initialValue,
        // last callback
        callback: callback,
        // "memoized" public interface
        facade: {
            get current() {
                return ref.value;
            },
            set current(value) {
                var last = ref.value;
                if (last !== value) {
                    ref.value = value;
                    ref.callback(value, last);
                }
            },
        },
    }); })[0];
    // update callback
    ref.callback = callback;
    return ref.facade;
}

/**
 * Merges two or more refs together providing a single interface to set their value
 * @param {RefObject|Ref} refs
 * @returns {MutableRefObject} - a new ref, which translates all changes to {refs}
 *
 * @see {@link mergeRefs} a version without buit-in memoization
 * @see https://github.com/theKashey/use-callback-ref#usemergerefs
 * @example
 * const Component = React.forwardRef((props, ref) => {
 *   const ownRef = useRef();
 *   const domRef = useMergeRefs([ref, ownRef]); // 👈 merge together
 *   return <div ref={domRef}>...</div>
 * }
 */
function useMergeRefs(refs, defaultValue) {
    return useCallbackRef(defaultValue || null, function (newValue) { return refs.forEach(function (ref) { return assignRef(ref, newValue); }); });
}

function ItoI(a) {
    return a;
}
function innerCreateMedium(defaults, middleware) {
    if (middleware === void 0) { middleware = ItoI; }
    var buffer = [];
    var assigned = false;
    var medium = {
        read: function () {
            if (assigned) {
                throw new Error('Sidecar: could not `read` from an `assigned` medium. `read` could be used only with `useMedium`.');
            }
            if (buffer.length) {
                return buffer[buffer.length - 1];
            }
            return defaults;
        },
        useMedium: function (data) {
            var item = middleware(data, assigned);
            buffer.push(item);
            return function () {
                buffer = buffer.filter(function (x) { return x !== item; });
            };
        },
        assignSyncMedium: function (cb) {
            assigned = true;
            while (buffer.length) {
                var cbs = buffer;
                buffer = [];
                cbs.forEach(cb);
            }
            buffer = {
                push: function (x) { return cb(x); },
                filter: function () { return buffer; },
            };
        },
        assignMedium: function (cb) {
            assigned = true;
            var pendingQueue = [];
            if (buffer.length) {
                var cbs = buffer;
                buffer = [];
                cbs.forEach(cb);
                pendingQueue = buffer;
            }
            var executeQueue = function () {
                var cbs = pendingQueue;
                pendingQueue = [];
                cbs.forEach(cb);
            };
            var cycle = function () { return Promise.resolve().then(executeQueue); };
            cycle();
            buffer = {
                push: function (x) {
                    pendingQueue.push(x);
                    cycle();
                },
                filter: function (filter) {
                    pendingQueue = pendingQueue.filter(filter);
                    return buffer;
                },
            };
        },
    };
    return medium;
}
// eslint-disable-next-line @typescript-eslint/ban-types
function createSidecarMedium(options) {
    if (options === void 0) { options = {}; }
    var medium = innerCreateMedium(null);
    medium.options = __assign({ async: true, ssr: false }, options);
    return medium;
}

var SideCar$1 = function (_a) {
    var sideCar = _a.sideCar, rest = __rest(_a, ["sideCar"]);
    if (!sideCar) {
        throw new Error('Sidecar: please provide `sideCar` property to import the right car');
    }
    var Target = sideCar.read();
    if (!Target) {
        throw new Error('Sidecar medium not found');
    }
    return React.createElement(Target, __assign({}, rest));
};
SideCar$1.isSideCarExport = true;
function exportSidecar(medium, exported) {
    medium.useMedium(exported);
    return SideCar$1;
}

var effectCar = createSidecarMedium();

var nothing = function () {
    return;
};
/**
 * Removes scrollbar from the page and contain the scroll within the Lock
 */
var RemoveScroll = React.forwardRef(function (props, parentRef) {
    var ref = React.useRef(null);
    var _a = React.useState({
        onScrollCapture: nothing,
        onWheelCapture: nothing,
        onTouchMoveCapture: nothing,
    }), callbacks = _a[0], setCallbacks = _a[1];
    var forwardProps = props.forwardProps, children = props.children, className = props.className, removeScrollBar = props.removeScrollBar, enabled = props.enabled, shards = props.shards, sideCar = props.sideCar, noIsolation = props.noIsolation, inert = props.inert, allowPinchZoom = props.allowPinchZoom, _b = props.as, Container = _b === void 0 ? 'div' : _b, rest = __rest(props, ["forwardProps", "children", "className", "removeScrollBar", "enabled", "shards", "sideCar", "noIsolation", "inert", "allowPinchZoom", "as"]);
    var SideCar = sideCar;
    var containerRef = useMergeRefs([ref, parentRef]);
    var containerProps = __assign(__assign({}, rest), callbacks);
    return (React.createElement(React.Fragment, null,
        enabled && (React.createElement(SideCar, { sideCar: effectCar, removeScrollBar: removeScrollBar, shards: shards, noIsolation: noIsolation, inert: inert, setCallbacks: setCallbacks, allowPinchZoom: !!allowPinchZoom, lockRef: ref })),
        forwardProps ? (React.cloneElement(React.Children.only(children), __assign(__assign({}, containerProps), { ref: containerRef }))) : (React.createElement(Container, __assign({}, containerProps, { className: className, ref: containerRef }), children))));
});
RemoveScroll.defaultProps = {
    enabled: true,
    removeScrollBar: true,
    inert: false,
};
RemoveScroll.classNames = {
    fullWidth: fullWidthClassName,
    zeroRight: zeroRightClassName,
};

var currentNonce;
var getNonce = function () {
    if (currentNonce) {
        return currentNonce;
    }
    if (typeof __webpack_nonce__ !== 'undefined') {
        return __webpack_nonce__;
    }
    return undefined;
};

function makeStyleTag() {
    if (!document)
        return null;
    var tag = document.createElement('style');
    tag.type = 'text/css';
    var nonce = getNonce();
    if (nonce) {
        tag.setAttribute('nonce', nonce);
    }
    return tag;
}
function injectStyles(tag, css) {
    // @ts-ignore
    if (tag.styleSheet) {
        // @ts-ignore
        tag.styleSheet.cssText = css;
    }
    else {
        tag.appendChild(document.createTextNode(css));
    }
}
function insertStyleTag(tag) {
    var head = document.head || document.getElementsByTagName('head')[0];
    head.appendChild(tag);
}
var stylesheetSingleton = function () {
    var counter = 0;
    var stylesheet = null;
    return {
        add: function (style) {
            if (counter == 0) {
                if ((stylesheet = makeStyleTag())) {
                    injectStyles(stylesheet, style);
                    insertStyleTag(stylesheet);
                }
            }
            counter++;
        },
        remove: function () {
            counter--;
            if (!counter && stylesheet) {
                stylesheet.parentNode && stylesheet.parentNode.removeChild(stylesheet);
                stylesheet = null;
            }
        },
    };
};

/**
 * creates a hook to control style singleton
 * @see {@link styleSingleton} for a safer component version
 * @example
 * ```tsx
 * const useStyle = styleHookSingleton();
 * ///
 * useStyle('body { overflow: hidden}');
 */
var styleHookSingleton = function () {
    var sheet = stylesheetSingleton();
    return function (styles, isDynamic) {
        React.useEffect(function () {
            sheet.add(styles);
            return function () {
                sheet.remove();
            };
        }, [styles && isDynamic]);
    };
};

/**
 * create a Component to add styles on demand
 * - styles are added when first instance is mounted
 * - styles are removed when the last instance is unmounted
 * - changing styles in runtime does nothing unless dynamic is set. But with multiple components that can lead to the undefined behavior
 */
var styleSingleton = function () {
    var useStyle = styleHookSingleton();
    var Sheet = function (_a) {
        var styles = _a.styles, dynamic = _a.dynamic;
        useStyle(styles, dynamic);
        return null;
    };
    return Sheet;
};

var zeroGap = {
    left: 0,
    top: 0,
    right: 0,
    gap: 0,
};
var parse = function (x) { return parseInt(x || '', 10) || 0; };
var getOffset = function (gapMode) {
    var cs = window.getComputedStyle(document.body);
    var left = cs[gapMode === 'padding' ? 'paddingLeft' : 'marginLeft'];
    var top = cs[gapMode === 'padding' ? 'paddingTop' : 'marginTop'];
    var right = cs[gapMode === 'padding' ? 'paddingRight' : 'marginRight'];
    return [parse(left), parse(top), parse(right)];
};
var getGapWidth = function (gapMode) {
    if (gapMode === void 0) { gapMode = 'margin'; }
    if (typeof window === 'undefined') {
        return zeroGap;
    }
    var offsets = getOffset(gapMode);
    var documentWidth = document.documentElement.clientWidth;
    var windowWidth = window.innerWidth;
    return {
        left: offsets[0],
        top: offsets[1],
        right: offsets[2],
        gap: Math.max(0, windowWidth - documentWidth + offsets[2] - offsets[0]),
    };
};

var Style = styleSingleton();
// important tip - once we measure scrollBar width and remove them
// we could not repeat this operation
// thus we are using style-singleton - only the first "yet correct" style will be applied.
var getStyles = function (_a, allowRelative, gapMode, important) {
    var left = _a.left, top = _a.top, right = _a.right, gap = _a.gap;
    if (gapMode === void 0) { gapMode = 'margin'; }
    return "\n  .".concat(noScrollbarsClassName, " {\n   overflow: hidden ").concat(important, ";\n   padding-right: ").concat(gap, "px ").concat(important, ";\n  }\n  body {\n    overflow: hidden ").concat(important, ";\n    overscroll-behavior: contain;\n    ").concat([
        allowRelative && "position: relative ".concat(important, ";"),
        gapMode === 'margin' &&
            "\n    padding-left: ".concat(left, "px;\n    padding-top: ").concat(top, "px;\n    padding-right: ").concat(right, "px;\n    margin-left:0;\n    margin-top:0;\n    margin-right: ").concat(gap, "px ").concat(important, ";\n    "),
        gapMode === 'padding' && "padding-right: ".concat(gap, "px ").concat(important, ";"),
    ]
        .filter(Boolean)
        .join(''), "\n  }\n  \n  .").concat(zeroRightClassName, " {\n    right: ").concat(gap, "px ").concat(important, ";\n  }\n  \n  .").concat(fullWidthClassName, " {\n    margin-right: ").concat(gap, "px ").concat(important, ";\n  }\n  \n  .").concat(zeroRightClassName, " .").concat(zeroRightClassName, " {\n    right: 0 ").concat(important, ";\n  }\n  \n  .").concat(fullWidthClassName, " .").concat(fullWidthClassName, " {\n    margin-right: 0 ").concat(important, ";\n  }\n  \n  body {\n    ").concat(removedBarSizeVariable, ": ").concat(gap, "px;\n  }\n");
};
/**
 * Removes page scrollbar and blocks page scroll when mounted
 */
var RemoveScrollBar = function (props) {
    var noRelative = props.noRelative, noImportant = props.noImportant, _a = props.gapMode, gapMode = _a === void 0 ? 'margin' : _a;
    /*
     gap will be measured on every component mount
     however it will be used only by the "first" invocation
     due to singleton nature of <Style
     */
    var gap = React.useMemo(function () { return getGapWidth(gapMode); }, [gapMode]);
    return React.createElement(Style, { styles: getStyles(gap, !noRelative, gapMode, !noImportant ? '!important' : '') });
};

var passiveSupported = false;
if (typeof window !== 'undefined') {
    try {
        var options = Object.defineProperty({}, 'passive', {
            get: function () {
                passiveSupported = true;
                return true;
            },
        });
        // @ts-ignore
        window.addEventListener('test', options, options);
        // @ts-ignore
        window.removeEventListener('test', options, options);
    }
    catch (err) {
        passiveSupported = false;
    }
}
var nonPassive = passiveSupported ? { passive: false } : false;

var alwaysContainsScroll = function (node) {
    // textarea will always _contain_ scroll inside self. It only can be hidden
    return node.tagName === 'TEXTAREA';
};
var elementCanBeScrolled = function (node, overflow) {
    var styles = window.getComputedStyle(node);
    return (
    // not-not-scrollable
    styles[overflow] !== 'hidden' &&
        // contains scroll inside self
        !(styles.overflowY === styles.overflowX && !alwaysContainsScroll(node) && styles[overflow] === 'visible'));
};
var elementCouldBeVScrolled = function (node) { return elementCanBeScrolled(node, 'overflowY'); };
var elementCouldBeHScrolled = function (node) { return elementCanBeScrolled(node, 'overflowX'); };
var locationCouldBeScrolled = function (axis, node) {
    var current = node;
    do {
        // Skip over shadow root
        if (typeof ShadowRoot !== 'undefined' && current instanceof ShadowRoot) {
            current = current.host;
        }
        var isScrollable = elementCouldBeScrolled(axis, current);
        if (isScrollable) {
            var _a = getScrollVariables(axis, current), s = _a[1], d = _a[2];
            if (s > d) {
                return true;
            }
        }
        current = current.parentNode;
    } while (current && current !== document.body);
    return false;
};
var getVScrollVariables = function (_a) {
    var scrollTop = _a.scrollTop, scrollHeight = _a.scrollHeight, clientHeight = _a.clientHeight;
    return [
        scrollTop,
        scrollHeight,
        clientHeight,
    ];
};
var getHScrollVariables = function (_a) {
    var scrollLeft = _a.scrollLeft, scrollWidth = _a.scrollWidth, clientWidth = _a.clientWidth;
    return [
        scrollLeft,
        scrollWidth,
        clientWidth,
    ];
};
var elementCouldBeScrolled = function (axis, node) {
    return axis === 'v' ? elementCouldBeVScrolled(node) : elementCouldBeHScrolled(node);
};
var getScrollVariables = function (axis, node) {
    return axis === 'v' ? getVScrollVariables(node) : getHScrollVariables(node);
};
var getDirectionFactor = function (axis, direction) {
    /**
     * If the element's direction is rtl (right-to-left), then scrollLeft is 0 when the scrollbar is at its rightmost position,
     * and then increasingly negative as you scroll towards the end of the content.
     * @see https://developer.mozilla.org/en-US/docs/Web/API/Element/scrollLeft
     */
    return axis === 'h' && direction === 'rtl' ? -1 : 1;
};
var handleScroll = function (axis, endTarget, event, sourceDelta, noOverscroll) {
    var directionFactor = getDirectionFactor(axis, window.getComputedStyle(endTarget).direction);
    var delta = directionFactor * sourceDelta;
    // find scrollable target
    var target = event.target;
    var targetInLock = endTarget.contains(target);
    var shouldCancelScroll = false;
    var isDeltaPositive = delta > 0;
    var availableScroll = 0;
    var availableScrollTop = 0;
    do {
        var _a = getScrollVariables(axis, target), position = _a[0], scroll_1 = _a[1], capacity = _a[2];
        var elementScroll = scroll_1 - capacity - directionFactor * position;
        if (position || elementScroll) {
            if (elementCouldBeScrolled(axis, target)) {
                availableScroll += elementScroll;
                availableScrollTop += position;
            }
        }
        target = target.parentNode;
    } while (
    // portaled content
    (!targetInLock && target !== document.body) ||
        // self content
        (targetInLock && (endTarget.contains(target) || endTarget === target)));
    if (isDeltaPositive && ((noOverscroll && availableScroll === 0) || (!noOverscroll && delta > availableScroll))) {
        shouldCancelScroll = true;
    }
    else if (!isDeltaPositive &&
        ((noOverscroll && availableScrollTop === 0) || (!noOverscroll && -delta > availableScrollTop))) {
        shouldCancelScroll = true;
    }
    return shouldCancelScroll;
};

var getTouchXY = function (event) {
    return 'changedTouches' in event ? [event.changedTouches[0].clientX, event.changedTouches[0].clientY] : [0, 0];
};
var getDeltaXY = function (event) { return [event.deltaX, event.deltaY]; };
var extractRef = function (ref) {
    return ref && 'current' in ref ? ref.current : ref;
};
var deltaCompare = function (x, y) { return x[0] === y[0] && x[1] === y[1]; };
var generateStyle = function (id) { return "\n  .block-interactivity-".concat(id, " {pointer-events: none;}\n  .allow-interactivity-").concat(id, " {pointer-events: all;}\n"); };
var idCounter = 0;
var lockStack = [];
function RemoveScrollSideCar(props) {
    var shouldPreventQueue = React.useRef([]);
    var touchStartRef = React.useRef([0, 0]);
    var activeAxis = React.useRef();
    var id = React.useState(idCounter++)[0];
    var Style = React.useState(function () { return styleSingleton(); })[0];
    var lastProps = React.useRef(props);
    React.useEffect(function () {
        lastProps.current = props;
    }, [props]);
    React.useEffect(function () {
        if (props.inert) {
            document.body.classList.add("block-interactivity-".concat(id));
            var allow_1 = __spreadArray([props.lockRef.current], (props.shards || []).map(extractRef), true).filter(Boolean);
            allow_1.forEach(function (el) { return el.classList.add("allow-interactivity-".concat(id)); });
            return function () {
                document.body.classList.remove("block-interactivity-".concat(id));
                allow_1.forEach(function (el) { return el.classList.remove("allow-interactivity-".concat(id)); });
            };
        }
        return;
    }, [props.inert, props.lockRef.current, props.shards]);
    var shouldCancelEvent = React.useCallback(function (event, parent) {
        if ('touches' in event && event.touches.length === 2) {
            return !lastProps.current.allowPinchZoom;
        }
        var touch = getTouchXY(event);
        var touchStart = touchStartRef.current;
        var deltaX = 'deltaX' in event ? event.deltaX : touchStart[0] - touch[0];
        var deltaY = 'deltaY' in event ? event.deltaY : touchStart[1] - touch[1];
        var currentAxis;
        var target = event.target;
        var moveDirection = Math.abs(deltaX) > Math.abs(deltaY) ? 'h' : 'v';
        // allow horizontal touch move on Range inputs. They will not cause any scroll
        if ('touches' in event && moveDirection === 'h' && target.type === 'range') {
            return false;
        }
        var canBeScrolledInMainDirection = locationCouldBeScrolled(moveDirection, target);
        if (!canBeScrolledInMainDirection) {
            return true;
        }
        if (canBeScrolledInMainDirection) {
            currentAxis = moveDirection;
        }
        else {
            currentAxis = moveDirection === 'v' ? 'h' : 'v';
            canBeScrolledInMainDirection = locationCouldBeScrolled(moveDirection, target);
            // other axis might be not scrollable
        }
        if (!canBeScrolledInMainDirection) {
            return false;
        }
        if (!activeAxis.current && 'changedTouches' in event && (deltaX || deltaY)) {
            activeAxis.current = currentAxis;
        }
        if (!currentAxis) {
            return true;
        }
        var cancelingAxis = activeAxis.current || currentAxis;
        return handleScroll(cancelingAxis, parent, event, cancelingAxis === 'h' ? deltaX : deltaY, true);
    }, []);
    var shouldPrevent = React.useCallback(function (_event) {
        var event = _event;
        if (!lockStack.length || lockStack[lockStack.length - 1] !== Style) {
            // not the last active
            return;
        }
        var delta = 'deltaY' in event ? getDeltaXY(event) : getTouchXY(event);
        var sourceEvent = shouldPreventQueue.current.filter(function (e) { return e.name === event.type && e.target === event.target && deltaCompare(e.delta, delta); })[0];
        // self event, and should be canceled
        if (sourceEvent && sourceEvent.should) {
            if (event.cancelable) {
                event.preventDefault();
            }
            return;
        }
        // outside or shard event
        if (!sourceEvent) {
            var shardNodes = (lastProps.current.shards || [])
                .map(extractRef)
                .filter(Boolean)
                .filter(function (node) { return node.contains(event.target); });
            var shouldStop = shardNodes.length > 0 ? shouldCancelEvent(event, shardNodes[0]) : !lastProps.current.noIsolation;
            if (shouldStop) {
                if (event.cancelable) {
                    event.preventDefault();
                }
            }
        }
    }, []);
    var shouldCancel = React.useCallback(function (name, delta, target, should) {
        var event = { name: name, delta: delta, target: target, should: should };
        shouldPreventQueue.current.push(event);
        setTimeout(function () {
            shouldPreventQueue.current = shouldPreventQueue.current.filter(function (e) { return e !== event; });
        }, 1);
    }, []);
    var scrollTouchStart = React.useCallback(function (event) {
        touchStartRef.current = getTouchXY(event);
        activeAxis.current = undefined;
    }, []);
    var scrollWheel = React.useCallback(function (event) {
        shouldCancel(event.type, getDeltaXY(event), event.target, shouldCancelEvent(event, props.lockRef.current));
    }, []);
    var scrollTouchMove = React.useCallback(function (event) {
        shouldCancel(event.type, getTouchXY(event), event.target, shouldCancelEvent(event, props.lockRef.current));
    }, []);
    React.useEffect(function () {
        lockStack.push(Style);
        props.setCallbacks({
            onScrollCapture: scrollWheel,
            onWheelCapture: scrollWheel,
            onTouchMoveCapture: scrollTouchMove,
        });
        document.addEventListener('wheel', shouldPrevent, nonPassive);
        document.addEventListener('touchmove', shouldPrevent, nonPassive);
        document.addEventListener('touchstart', scrollTouchStart, nonPassive);
        return function () {
            lockStack = lockStack.filter(function (inst) { return inst !== Style; });
            document.removeEventListener('wheel', shouldPrevent, nonPassive);
            document.removeEventListener('touchmove', shouldPrevent, nonPassive);
            document.removeEventListener('touchstart', scrollTouchStart, nonPassive);
        };
    }, []);
    var removeScrollBar = props.removeScrollBar, inert = props.inert;
    return (React.createElement(React.Fragment, null,
        inert ? React.createElement(Style, { styles: generateStyle(id) }) : null,
        removeScrollBar ? React.createElement(RemoveScrollBar, { gapMode: "margin" }) : null));
}

var SideCar = exportSidecar(effectCar, RemoveScrollSideCar);

var ReactRemoveScroll = React.forwardRef(function (props, ref) { return (React.createElement(RemoveScroll, __assign({}, props, { ref: ref, sideCar: SideCar }))); });
ReactRemoveScroll.classNames = RemoveScroll.classNames;
var $67UHm$RemoveScroll = ReactRemoveScroll;

var getDefaultParent = function (originalTarget) {
    if (typeof document === 'undefined') {
        return null;
    }
    var sampleTarget = Array.isArray(originalTarget) ? originalTarget[0] : originalTarget;
    return sampleTarget.ownerDocument.body;
};
var counterMap = new WeakMap();
var uncontrolledNodes = new WeakMap();
var markerMap = {};
var lockCount = 0;
var unwrapHost = function (node) {
    return node && (node.host || unwrapHost(node.parentNode));
};
var correctTargets = function (parent, targets) {
    return targets
        .map(function (target) {
        if (parent.contains(target)) {
            return target;
        }
        var correctedTarget = unwrapHost(target);
        if (correctedTarget && parent.contains(correctedTarget)) {
            return correctedTarget;
        }
        console.error('aria-hidden', target, 'in not contained inside', parent, '. Doing nothing');
        return null;
    })
        .filter(function (x) { return Boolean(x); });
};
/**
 * Marks everything except given node(or nodes) as aria-hidden
 * @param {Element | Element[]} originalTarget - elements to keep on the page
 * @param [parentNode] - top element, defaults to document.body
 * @param {String} [markerName] - a special attribute to mark every node
 * @param {String} [controlAttribute] - html Attribute to control
 * @return {Undo} undo command
 */
var applyAttributeToOthers = function (originalTarget, parentNode, markerName, controlAttribute) {
    var targets = correctTargets(parentNode, Array.isArray(originalTarget) ? originalTarget : [originalTarget]);
    if (!markerMap[markerName]) {
        markerMap[markerName] = new WeakMap();
    }
    var markerCounter = markerMap[markerName];
    var hiddenNodes = [];
    var elementsToKeep = new Set();
    var elementsToStop = new Set(targets);
    var keep = function (el) {
        if (!el || elementsToKeep.has(el)) {
            return;
        }
        elementsToKeep.add(el);
        keep(el.parentNode);
    };
    targets.forEach(keep);
    var deep = function (parent) {
        if (!parent || elementsToStop.has(parent)) {
            return;
        }
        Array.prototype.forEach.call(parent.children, function (node) {
            if (elementsToKeep.has(node)) {
                deep(node);
            }
            else {
                var attr = node.getAttribute(controlAttribute);
                var alreadyHidden = attr !== null && attr !== 'false';
                var counterValue = (counterMap.get(node) || 0) + 1;
                var markerValue = (markerCounter.get(node) || 0) + 1;
                counterMap.set(node, counterValue);
                markerCounter.set(node, markerValue);
                hiddenNodes.push(node);
                if (counterValue === 1 && alreadyHidden) {
                    uncontrolledNodes.set(node, true);
                }
                if (markerValue === 1) {
                    node.setAttribute(markerName, 'true');
                }
                if (!alreadyHidden) {
                    node.setAttribute(controlAttribute, 'true');
                }
            }
        });
    };
    deep(parentNode);
    elementsToKeep.clear();
    lockCount++;
    return function () {
        hiddenNodes.forEach(function (node) {
            var counterValue = counterMap.get(node) - 1;
            var markerValue = markerCounter.get(node) - 1;
            counterMap.set(node, counterValue);
            markerCounter.set(node, markerValue);
            if (!counterValue) {
                if (!uncontrolledNodes.has(node)) {
                    node.removeAttribute(controlAttribute);
                }
                uncontrolledNodes.delete(node);
            }
            if (!markerValue) {
                node.removeAttribute(markerName);
            }
        });
        lockCount--;
        if (!lockCount) {
            // clear
            counterMap = new WeakMap();
            counterMap = new WeakMap();
            uncontrolledNodes = new WeakMap();
            markerMap = {};
        }
    };
};
/**
 * Marks everything except given node(or nodes) as aria-hidden
 * @param {Element | Element[]} originalTarget - elements to keep on the page
 * @param [parentNode] - top element, defaults to document.body
 * @param {String} [markerName] - a special attribute to mark every node
 * @return {Undo} undo command
 */
var hideOthers = function (originalTarget, parentNode, markerName) {
    if (markerName === void 0) { markerName = 'data-aria-hidden'; }
    var targets = Array.from(Array.isArray(originalTarget) ? originalTarget : [originalTarget]);
    var activeParentNode = parentNode || getDefaultParent(originalTarget);
    if (!activeParentNode) {
        return function () { return null; };
    }
    // we should not hide ariaLive elements - https://github.com/theKashey/aria-hidden/issues/10
    targets.push.apply(targets, Array.from(activeParentNode.querySelectorAll('[aria-live]')));
    return applyAttributeToOthers(targets, activeParentNode, markerName, 'aria-hidden');
};

/* -------------------------------------------------------------------------------------------------
 * Dialog
 * -----------------------------------------------------------------------------------------------*/ const $5d3850c4d0b4e6c7$var$DIALOG_NAME = 'Dialog';
const [$5d3850c4d0b4e6c7$var$createDialogContext, $5d3850c4d0b4e6c7$export$cc702773b8ea3e41] = $c512c27ab02ef895$export$50c7b4e9d9f19c1($5d3850c4d0b4e6c7$var$DIALOG_NAME);
const [$5d3850c4d0b4e6c7$var$DialogProvider, $5d3850c4d0b4e6c7$var$useDialogContext] = $5d3850c4d0b4e6c7$var$createDialogContext($5d3850c4d0b4e6c7$var$DIALOG_NAME);
const $5d3850c4d0b4e6c7$export$3ddf2d174ce01153 = (props)=>{
    const { __scopeDialog: __scopeDialog , children: children , open: openProp , defaultOpen: defaultOpen , onOpenChange: onOpenChange , modal: modal = true  } = props;
    const triggerRef = useRef(null);
    const contentRef = useRef(null);
    const [open = false, setOpen] = $71cd76cc60e0454e$export$6f32135080cb4c3({
        prop: openProp,
        defaultProp: defaultOpen,
        onChange: onOpenChange
    });
    return /*#__PURE__*/ createElement($5d3850c4d0b4e6c7$var$DialogProvider, {
        scope: __scopeDialog,
        triggerRef: triggerRef,
        contentRef: contentRef,
        contentId: $1746a345f3d73bb7$export$f680877a34711e37(),
        titleId: $1746a345f3d73bb7$export$f680877a34711e37(),
        descriptionId: $1746a345f3d73bb7$export$f680877a34711e37(),
        open: open,
        onOpenChange: setOpen,
        onOpenToggle: useCallback(()=>setOpen((prevOpen)=>!prevOpen
            )
        , [
            setOpen
        ]),
        modal: modal
    }, children);
};
/* -------------------------------------------------------------------------------------------------
 * DialogPortal
 * -----------------------------------------------------------------------------------------------*/ const $5d3850c4d0b4e6c7$var$PORTAL_NAME = 'DialogPortal';
const [$5d3850c4d0b4e6c7$var$PortalProvider, $5d3850c4d0b4e6c7$var$usePortalContext] = $5d3850c4d0b4e6c7$var$createDialogContext($5d3850c4d0b4e6c7$var$PORTAL_NAME, {
    forceMount: undefined
});
const $5d3850c4d0b4e6c7$export$dad7c95542bacce0 = (props)=>{
    const { __scopeDialog: __scopeDialog , forceMount: forceMount , children: children , container: container  } = props;
    const context = $5d3850c4d0b4e6c7$var$useDialogContext($5d3850c4d0b4e6c7$var$PORTAL_NAME, __scopeDialog);
    return /*#__PURE__*/ createElement($5d3850c4d0b4e6c7$var$PortalProvider, {
        scope: __scopeDialog,
        forceMount: forceMount
    }, Children.map(children, (child)=>/*#__PURE__*/ createElement($921a889cee6df7e8$export$99c2b779aa4e8b8b, {
            present: forceMount || context.open
        }, /*#__PURE__*/ createElement($f1701beae083dbae$export$602eac185826482c, {
            asChild: true,
            container: container
        }, child))
    ));
};
/* -------------------------------------------------------------------------------------------------
 * DialogOverlay
 * -----------------------------------------------------------------------------------------------*/ const $5d3850c4d0b4e6c7$var$OVERLAY_NAME = 'DialogOverlay';
const $5d3850c4d0b4e6c7$export$bd1d06c79be19e17 = /*#__PURE__*/ forwardRef((props, forwardedRef)=>{
    const portalContext = $5d3850c4d0b4e6c7$var$usePortalContext($5d3850c4d0b4e6c7$var$OVERLAY_NAME, props.__scopeDialog);
    const { forceMount: forceMount = portalContext.forceMount , ...overlayProps } = props;
    const context = $5d3850c4d0b4e6c7$var$useDialogContext($5d3850c4d0b4e6c7$var$OVERLAY_NAME, props.__scopeDialog);
    return context.modal ? /*#__PURE__*/ createElement($921a889cee6df7e8$export$99c2b779aa4e8b8b, {
        present: forceMount || context.open
    }, /*#__PURE__*/ createElement($5d3850c4d0b4e6c7$var$DialogOverlayImpl, _extends({}, overlayProps, {
        ref: forwardedRef
    }))) : null;
});
const $5d3850c4d0b4e6c7$var$DialogOverlayImpl = /*#__PURE__*/ forwardRef((props, forwardedRef)=>{
    const { __scopeDialog: __scopeDialog , ...overlayProps } = props;
    const context = $5d3850c4d0b4e6c7$var$useDialogContext($5d3850c4d0b4e6c7$var$OVERLAY_NAME, __scopeDialog);
    return(/*#__PURE__*/ // Make sure `Content` is scrollable even when it doesn't live inside `RemoveScroll`
    // ie. when `Overlay` and `Content` are siblings
    createElement($67UHm$RemoveScroll, {
        as: $5e63c961fc1ce211$export$8c6ed5c666ac1360,
        allowPinchZoom: true,
        shards: [
            context.contentRef
        ]
    }, /*#__PURE__*/ createElement($8927f6f2acc4f386$export$250ffa63cdc0d034.div, _extends({
        "data-state": $5d3850c4d0b4e6c7$var$getState(context.open)
    }, overlayProps, {
        ref: forwardedRef // We re-enable pointer-events prevented by `Dialog.Content` to allow scrolling the overlay.
        ,
        style: {
            pointerEvents: 'auto',
            ...overlayProps.style
        }
    }))));
});
/* -------------------------------------------------------------------------------------------------
 * DialogContent
 * -----------------------------------------------------------------------------------------------*/ const $5d3850c4d0b4e6c7$var$CONTENT_NAME = 'DialogContent';
const $5d3850c4d0b4e6c7$export$b6d9565de1e068cf = /*#__PURE__*/ forwardRef((props, forwardedRef)=>{
    const portalContext = $5d3850c4d0b4e6c7$var$usePortalContext($5d3850c4d0b4e6c7$var$CONTENT_NAME, props.__scopeDialog);
    const { forceMount: forceMount = portalContext.forceMount , ...contentProps } = props;
    const context = $5d3850c4d0b4e6c7$var$useDialogContext($5d3850c4d0b4e6c7$var$CONTENT_NAME, props.__scopeDialog);
    return /*#__PURE__*/ createElement($921a889cee6df7e8$export$99c2b779aa4e8b8b, {
        present: forceMount || context.open
    }, context.modal ? /*#__PURE__*/ createElement($5d3850c4d0b4e6c7$var$DialogContentModal, _extends({}, contentProps, {
        ref: forwardedRef
    })) : /*#__PURE__*/ createElement($5d3850c4d0b4e6c7$var$DialogContentNonModal, _extends({}, contentProps, {
        ref: forwardedRef
    })));
});
/* -----------------------------------------------------------------------------------------------*/ const $5d3850c4d0b4e6c7$var$DialogContentModal = /*#__PURE__*/ forwardRef((props, forwardedRef)=>{
    const context = $5d3850c4d0b4e6c7$var$useDialogContext($5d3850c4d0b4e6c7$var$CONTENT_NAME, props.__scopeDialog);
    const contentRef = useRef(null);
    const composedRefs = $6ed0406888f73fc4$export$c7b2cbe3552a0d05(forwardedRef, context.contentRef, contentRef); // aria-hide everything except the content (better supported equivalent to setting aria-modal)
    useEffect(()=>{
        const content = contentRef.current;
        if (content) return hideOthers(content);
    }, []);
    return /*#__PURE__*/ createElement($5d3850c4d0b4e6c7$var$DialogContentImpl, _extends({}, props, {
        ref: composedRefs // we make sure focus isn't trapped once `DialogContent` has been closed
        ,
        trapFocus: context.open,
        disableOutsidePointerEvents: true,
        onCloseAutoFocus: $e42e1063c40fb3ef$export$b9ecd428b558ff10(props.onCloseAutoFocus, (event)=>{
            var _context$triggerRef$c;
            event.preventDefault();
            (_context$triggerRef$c = context.triggerRef.current) === null || _context$triggerRef$c === void 0 || _context$triggerRef$c.focus();
        }),
        onPointerDownOutside: $e42e1063c40fb3ef$export$b9ecd428b558ff10(props.onPointerDownOutside, (event)=>{
            const originalEvent = event.detail.originalEvent;
            const ctrlLeftClick = originalEvent.button === 0 && originalEvent.ctrlKey === true;
            const isRightClick = originalEvent.button === 2 || ctrlLeftClick; // If the event is a right-click, we shouldn't close because
            // it is effectively as if we right-clicked the `Overlay`.
            if (isRightClick) event.preventDefault();
        }) // When focus is trapped, a `focusout` event may still happen.
        ,
        onFocusOutside: $e42e1063c40fb3ef$export$b9ecd428b558ff10(props.onFocusOutside, (event)=>event.preventDefault()
        )
    }));
});
/* -----------------------------------------------------------------------------------------------*/ const $5d3850c4d0b4e6c7$var$DialogContentNonModal = /*#__PURE__*/ forwardRef((props, forwardedRef)=>{
    const context = $5d3850c4d0b4e6c7$var$useDialogContext($5d3850c4d0b4e6c7$var$CONTENT_NAME, props.__scopeDialog);
    const hasInteractedOutsideRef = useRef(false);
    return /*#__PURE__*/ createElement($5d3850c4d0b4e6c7$var$DialogContentImpl, _extends({}, props, {
        ref: forwardedRef,
        trapFocus: false,
        disableOutsidePointerEvents: false,
        onCloseAutoFocus: (event)=>{
            var _props$onCloseAutoFoc;
            (_props$onCloseAutoFoc = props.onCloseAutoFocus) === null || _props$onCloseAutoFoc === void 0 || _props$onCloseAutoFoc.call(props, event);
            if (!event.defaultPrevented) {
                var _context$triggerRef$c2;
                if (!hasInteractedOutsideRef.current) (_context$triggerRef$c2 = context.triggerRef.current) === null || _context$triggerRef$c2 === void 0 || _context$triggerRef$c2.focus(); // Always prevent auto focus because we either focus manually or want user agent focus
                event.preventDefault();
            }
            hasInteractedOutsideRef.current = false;
        },
        onInteractOutside: (event)=>{
            var _props$onInteractOuts, _context$triggerRef$c3;
            (_props$onInteractOuts = props.onInteractOutside) === null || _props$onInteractOuts === void 0 || _props$onInteractOuts.call(props, event);
            if (!event.defaultPrevented) hasInteractedOutsideRef.current = true; // Prevent dismissing when clicking the trigger.
            // As the trigger is already setup to close, without doing so would
            // cause it to close and immediately open.
            //
            // We use `onInteractOutside` as some browsers also
            // focus on pointer down, creating the same issue.
            const target = event.target;
            const targetIsTrigger = (_context$triggerRef$c3 = context.triggerRef.current) === null || _context$triggerRef$c3 === void 0 ? void 0 : _context$triggerRef$c3.contains(target);
            if (targetIsTrigger) event.preventDefault();
        }
    }));
});
/* -----------------------------------------------------------------------------------------------*/ const $5d3850c4d0b4e6c7$var$DialogContentImpl = /*#__PURE__*/ forwardRef((props, forwardedRef)=>{
    const { __scopeDialog: __scopeDialog , trapFocus: trapFocus , onOpenAutoFocus: onOpenAutoFocus , onCloseAutoFocus: onCloseAutoFocus , ...contentProps } = props;
    const context = $5d3850c4d0b4e6c7$var$useDialogContext($5d3850c4d0b4e6c7$var$CONTENT_NAME, __scopeDialog);
    const contentRef = useRef(null);
    const composedRefs = $6ed0406888f73fc4$export$c7b2cbe3552a0d05(forwardedRef, contentRef); // Make sure the whole tree has focus guards as our `Dialog` will be
    // the last element in the DOM (beacuse of the `Portal`)
    $3db38b7d1fb3fe6a$export$b7ece24a22aeda8c();
    return /*#__PURE__*/ createElement(Fragment, null, /*#__PURE__*/ createElement($d3863c46a17e8a28$export$20e40289641fbbb6, {
        asChild: true,
        loop: true,
        trapped: trapFocus,
        onMountAutoFocus: onOpenAutoFocus,
        onUnmountAutoFocus: onCloseAutoFocus
    }, /*#__PURE__*/ createElement($5cb92bef7577960e$export$177fb62ff3ec1f22, _extends({
        role: "dialog",
        id: context.contentId,
        "aria-describedby": context.descriptionId,
        "aria-labelledby": context.titleId,
        "data-state": $5d3850c4d0b4e6c7$var$getState(context.open)
    }, contentProps, {
        ref: composedRefs,
        onDismiss: ()=>context.onOpenChange(false)
    }))), false);
});
/* -------------------------------------------------------------------------------------------------
 * DialogDescription
 * -----------------------------------------------------------------------------------------------*/ const $5d3850c4d0b4e6c7$var$DESCRIPTION_NAME = 'DialogDescription';
const $5d3850c4d0b4e6c7$export$94e94c2ec2c954d5 = /*#__PURE__*/ forwardRef((props, forwardedRef)=>{
    const { __scopeDialog: __scopeDialog , ...descriptionProps } = props;
    const context = $5d3850c4d0b4e6c7$var$useDialogContext($5d3850c4d0b4e6c7$var$DESCRIPTION_NAME, __scopeDialog);
    return /*#__PURE__*/ createElement($8927f6f2acc4f386$export$250ffa63cdc0d034.p, _extends({
        id: context.descriptionId
    }, descriptionProps, {
        ref: forwardedRef
    }));
});
/* -----------------------------------------------------------------------------------------------*/ function $5d3850c4d0b4e6c7$var$getState(open) {
    return open ? 'open' : 'closed';
}
const $5d3850c4d0b4e6c7$export$be92b6f5f03c0fe9 = $5d3850c4d0b4e6c7$export$3ddf2d174ce01153;
const $5d3850c4d0b4e6c7$export$602eac185826482c = $5d3850c4d0b4e6c7$export$dad7c95542bacce0;
const $5d3850c4d0b4e6c7$export$c6fdb837b070b4ff = $5d3850c4d0b4e6c7$export$bd1d06c79be19e17;
const $5d3850c4d0b4e6c7$export$7c6e2c02157bb7d2 = $5d3850c4d0b4e6c7$export$b6d9565de1e068cf;
const $5d3850c4d0b4e6c7$export$393edc798c47379d = $5d3850c4d0b4e6c7$export$94e94c2ec2c954d5;

var css_248z = ".SideSheet-module_overlay__Aoxam {\n  --bs-bg-opacity: 0.7;\n  place-items: center;\n  z-index: 1040;\n}\n.SideSheet-module_overlay__Aoxam[data-state=open] {\n  animation: SideSheet-module_fadeIn__0rSWB 0.3s ease-out;\n}\n.SideSheet-module_overlay__Aoxam[data-state=closed] {\n  animation: SideSheet-module_fadeOut__Tx0MH 0.3s ease-in;\n}\n\n@keyframes SideSheet-module_fadeIn__0rSWB {\n  from {\n    opacity: 0;\n  }\n  to {\n    opacity: 1;\n  }\n}\n@keyframes SideSheet-module_fadeOut__Tx0MH {\n  from {\n    opacity: 1;\n  }\n  to {\n    opacity: 0;\n  }\n}\n.SideSheet-module_content__hrDR0 {\n  right: 0;\n  bottom: 0;\n}\n@media (max-width: 833.98px) {\n  .SideSheet-module_content__hrDR0 {\n    left: 0;\n    max-height: 90vh;\n    border-top-right-radius: 1.5rem;\n    border-top-left-radius: 1.5rem;\n  }\n  .SideSheet-module_content__hrDR0[data-state=open] {\n    animation: SideSheet-module_slideInBottom__KMW4C 0.3s ease-out;\n  }\n  .SideSheet-module_content__hrDR0[data-state=closed] {\n    animation: SideSheet-module_slideOutBottom__kTfJQ 0.3s ease-out;\n  }\n}\n@media (min-width: 834px) {\n  .SideSheet-module_content__hrDR0 {\n    top: 0;\n    width: 504px;\n  }\n  .SideSheet-module_content__hrDR0[data-state=open] {\n    animation: SideSheet-module_slideInRight__prKWA 0.3s ease-out;\n  }\n  .SideSheet-module_content__hrDR0[data-state=closed] {\n    animation: SideSheet-module_slideOutRight__5cfYw 0.3s ease-out;\n  }\n}\n\n@keyframes SideSheet-module_slideInRight__prKWA {\n  from {\n    transform: translate3d(100%, 0, 0);\n  }\n  to {\n    transform: translate3d(0, 0, 0);\n  }\n}\n@keyframes SideSheet-module_slideOutRight__5cfYw {\n  from {\n    transform: translate3d(0, 0, 0);\n  }\n  to {\n    transform: translate3d(100%, 0, 0);\n  }\n}\n@keyframes SideSheet-module_slideInBottom__KMW4C {\n  from {\n    transform: translate3d(0, 100%, 0);\n  }\n  to {\n    transform: translate3d(0, 0, 0);\n  }\n}\n@keyframes SideSheet-module_slideOutBottom__kTfJQ {\n  from {\n    transform: translate3d(0, 0, 0);\n  }\n  to {\n    transform: translate3d(0, 100%, 0);\n  }\n}";
var styles = {"overlay":"SideSheet-module_overlay__Aoxam","fadeIn":"SideSheet-module_fadeIn__0rSWB","fadeOut":"SideSheet-module_fadeOut__Tx0MH","content":"SideSheet-module_content__hrDR0","slideInBottom":"SideSheet-module_slideInBottom__KMW4C","slideOutBottom":"SideSheet-module_slideOutBottom__kTfJQ","slideInRight":"SideSheet-module_slideInRight__prKWA","slideOutRight":"SideSheet-module_slideOutRight__5cfYw"};
styleInject(css_248z);

/**
 * IFE-120 SideSheet
 */
function SideSheet(_a) {
    var title = _a.title, onCloseRequest = _a.onCloseRequest, _b = _a.isOpen, isOpen = _b === void 0 ? false : _b, children = _a.children;
    return (jsx($5d3850c4d0b4e6c7$export$be92b6f5f03c0fe9, { open: isOpen, children: jsx($5d3850c4d0b4e6c7$export$602eac185826482c, { children: jsxs($5d3850c4d0b4e6c7$export$c6fdb837b070b4ff, { className: clsx(styles.overlay, 'position-fixed top-0 start-0 bottom-0 end-0 d-grid py-10 bg-gray-900'), children: [jsx("div", { className: "position-absolute top-0 start-0 bottom-0 end-0", onClick: onCloseRequest }), jsxs($5d3850c4d0b4e6c7$export$7c6e2c02157bb7d2, { className: clsx(styles.content, 'position-absolute d-flex flex-column overflow-hidden bg-gray-100'), onEscapeKeyDown: onCloseRequest, children: [jsxs("div", { className: "d-flex align-items-center justify-content-center justify-content-md-between px-10 py-6 bg-white", children: [jsx(Typography, { variant: "h3", children: title }), jsxs("button", { className: "d-none d-md-flex flex-column align-items-center gap-1 m-0 p-0 bg-transparent border-0 outline-0", onClick: onCloseRequest, children: [jsx(Icon, { icon: "xmark-large", size: "xl" }), jsx(Typography, { variant: "textXs", component: "span", children: "Schlie\u00DFen" })] })] }), jsx($5d3850c4d0b4e6c7$export$393edc798c47379d, { asChild: true, children: jsx("div", { className: "overflow-y-auto", children: jsx("div", { className: "px-8 py-6 p-md-10", children: children }) }) }), jsx("div", { className: "d-md-none px-4 pt-4 pb-12 bg-white border-top", children: jsx(Button, { className: "w-100", size: "large", onClick: onCloseRequest, children: "Schlie\u00DFen" }) })] })] }) }) }));
}

var aolLight = {
    prefix: 'd3x',
    iconName: 'aol-light',
    icon: [
        48,
        43,
        [],
        null,
        'm30.2928 10.391-8.8313-8.8323A5.3168 5.3168 0 0 0 17.705 0H5.3073C2.3955 0 0 2.3955 0 5.3073v31.8436c0 2.9117 2.3955 5.3073 5.3073 5.3073h17.6816l-.0336-.057-1.0726-1.8324a5.054 5.054 0 0 1-.3665-.7643H5.3072c-1.4547-.0022-2.6513-1.1989-2.6536-2.6536V5.3084c.0012-1.4559 1.1978-2.6525 2.6537-2.6548h10.6145v9.2883c.0011 2.1833 1.7967 3.9788 3.98 3.98H29.19v2.115c.9642.3687 1.8335 1.038 2.4525 2.009a6.1773 6.1773 0 0 1 .2011-.294v-5.6133a5.2905 5.2905 0 0 0-1.5508-3.7475ZM18.5754 2.8269c.371.1296.7185.3185 1.0056.6045l8.8313 8.8324c.2883.2827.4794.6313.6034 1.0056h-9.114c-.7274-.0022-1.324-.6-1.3263-1.3274V2.8257v.0011ZM6.542 10.19c0-.162.0078-.3218.0235-.4805H5.952c-.3252-.0033-.5911-.2737-.5889-.5989 0-.3318.2637-.6.5889-.6h.9005c.6503-1.6838 2.2492-2.8793 4.1285-2.8793h1.3341c.4548 0 .8246.3765.8246.8402 0 .4637-.3687.8391-.8246.8391h-1.334c-.9308 0-1.7587.4715-2.2671 1.2h2.8938c.3252 0 .5889.2682.5889.6v.0056c0 .324-.2648.591-.5889.5944H8.2291a2.9995 2.9995 0 0 0 0 .9587h3.3787c.3252 0 .5889.2682.5889.6v.0056c0 .324-.2648.591-.5889.5944H8.714a2.7664 2.7664 0 0 0 2.267 1.2h1.3341c.4548 0 .8246.3754.8246.8402 0 .4637-.3687.8391-.8246.8391h-1.334c-1.8794 0-3.4783-1.1955-4.1286-2.8793H5.952c-.324-.0034-.5889-.2704-.5889-.5944v-.0056c0-.3307.2637-.6.5889-.6h.6145a4.5925 4.5925 0 0 1-.0257-.4793h.0011Zm.0916 11.2346h9.866c.7307 0 1.3273-.5933 1.3273-1.3262 0-.7341-.5977-1.3274-1.3273-1.3274h-9.866c-.7273 0-1.3262.5989-1.3262 1.3262 0 .7285.5989 1.3274 1.3262 1.3274Zm0 5.3363h7.9889c.7296 0 1.3273-.5977 1.3273-1.3273 0-.7296-.5977-1.3263-1.3273-1.3263H6.6335c-.7273.0022-1.324.5989-1.3262 1.3263.001.7273.5989 1.3251 1.3262 1.3273Zm7.9889 5.3352H6.6335c-.7273-.0022-1.324-.5988-1.3262-1.3262.001-.7274.5989-1.3252 1.3262-1.3274h7.9889c.7296 0 1.3273.5978 1.3273 1.3274 0 .7296-.5977 1.3262-1.3273 1.3262ZM29.447 21.5956c-.9263-1.6201-3.2637-1.6201-4.19 0l-1.0726 1.877a2.408 2.408 0 0 0-.3184 1.1978v1.0559h-2.2738c-.6704 0-1.2123.6-1.2123 1.3408s.543 1.3408 1.2123 1.3408h2.2738v9.5866c0 .428.114.8491.3307 1.2179l1.0726 1.8324c.9319 1.5933 3.2335 1.5933 4.1654 0l1.0726-1.8324c.2168-.3699.3307-.79.3307-1.218v-13.325a2.408 2.408 0 0 0-.3184-1.1979l-1.0726-1.877v.001Zm-2.8995 3.1463.8045-1.4078.8045 1.4078v13.1788l-.8045 1.3743-.8045-1.3743V24.742Zm7.2894-3.1463c.9274-1.6201 3.2648-1.6201 4.191 0l1.0727 1.877a2.411 2.411 0 0 1 .3185 1.1978v13.323c0 .429-.114.8491-.3308 1.219l-1.0726 1.8324c-.9318 1.5933-3.2335 1.5933-4.1654 0l-1.0726-1.8324a2.4077 2.4077 0 0 1-.3307-1.218v-13.325c0-.4202.1095-.8325.3184-1.1979l1.0726-1.877-.001.001Zm2.0961 1.7385-.8045 1.4078v13.1788l.8045 1.3743.8045-1.3743V24.742l-.8045-1.4078Zm10.676-1.7385c-.9263-1.6201-3.2637-1.6201-4.19 0l-1.0726 1.877a2.408 2.408 0 0 0-.3184 1.1978v13.323c0 .429.114.8491.3307 1.219l1.0726 1.8324c.9319 1.5933 3.2336 1.5933 4.1654 0l1.0726-1.8324c.2168-.3699.3308-.79.3308-1.218v-13.325a2.408 2.408 0 0 0-.3185-1.1979l-1.0726-1.877v.001Zm-2.8994 3.1463.8044-1.4078.8045 1.4078v13.1788l-.8045 1.3743-.8044-1.3743V24.742Z',
    ],
};
var aolRegular = {
    prefix: 'd3x',
    iconName: 'aol-regular',
    icon: [
        48,
        43,
        [],
        null,
        'm24.104 1.5542 6.1888 6.19a5.3117 5.3117 0 0 1 1.5508 3.7497v8.258a5.6227 5.6227 0 0 0-.201.2928c-.9085-1.4257-2.3565-2.2022-3.8537-2.3285v-4.448H21.229c-1.4648 0-2.6537-1.1877-2.6537-2.6537V3.9911H5.3073c-.733 0-1.3263.5944-1.3263 1.3273l.0011 31.8325c0 .733.5944 1.3274 1.3274 1.3274H21.208a5.0957 5.0957 0 0 0 .6748 2.0894l1.1073 1.8905H5.3084c-2.9319 0-5.3073-2.3766-5.3073-5.3073L0 5.3073C0 2.3754 2.3765 0 5.3073 0h15.0425c1.4022 0 2.7541.5587 3.7541 1.5542ZM46.61 21.5956c-.9273-1.6213-3.2648-1.6213-4.191 0l-1.0727 1.877a2.411 2.411 0 0 0-.3184 1.1978v13.323c0 .429.114.8491.3307 1.219l1.0726 1.8335c.9319 1.5922 3.2336 1.5922 4.1654 0l1.0726-1.8335c.2168-.3699.3308-.79.3308-1.219v-13.323c0-.42-.1095-.8324-.3173-1.1977L46.61 21.5956Zm-8.581 0c-.9274-1.6213-3.2648-1.6213-4.191 0l-1.0727 1.877a2.411 2.411 0 0 0-.3184 1.1978v13.323c0 .429.114.8491.3307 1.219l1.0726 1.8335c.9319 1.5922 3.2336 1.5922 4.1654 0l1.0726-1.8335c.2168-.3699.3308-.79.3308-1.219v-13.323c0-.42-.1095-.8324-.3174-1.1977l-1.0726-1.8771Zm-8.581 0c-.9274-1.6213-3.2648-1.6213-4.191 0l-1.0727 1.877a2.411 2.411 0 0 0-.3184 1.1978v1.0559h-2.2738c-.6693 0-1.2123.6-1.2123 1.3408s.543 1.3408 1.2123 1.3408h2.2738v9.5855c0 .429.114.8491.3307 1.219l1.0726 1.8335c.9319 1.5922 3.2335 1.5922 4.1654 0l1.0726-1.8335c.2168-.3699.3307-.79.3307-1.219v-13.323c0-.42-.1095-.8324-.3173-1.1977l-1.0726-1.8771ZM8.2995 34.5096h7.9888c.7296 0 1.3263-.5967 1.3263-1.3263s-.5967-1.3274-1.3263-1.3274H8.2995c-.733 0-1.3274.5978-1.3274 1.3274 0 .7296.5944 1.3263 1.3274 1.3263Zm0-5.3353h7.9888c.7296 0 1.3263-.5977 1.3263-1.3273 0-.7296-.5967-1.3263-1.3263-1.3263H8.2995c-.733 0-1.3274.5967-1.3274 1.3263s.5944 1.3273 1.3274 1.3273Zm0-5.3363c-.733 0-1.3274-.5933-1.3274-1.3262 0-.733.5944-1.3274 1.3274-1.3274h9.8659c.7296 0 1.3263.5944 1.3263 1.3274 0 .733-.5967 1.3262-1.3263 1.3262h-9.866Zm.0804-11.2346c0-.181.01-.3598.028-.5363h-.6313a.804.804 0 0 1-.8045-.8045.804.804 0 0 1 .8045-.8045h1.0826c.8056-1.7039 2.5397-2.8827 4.5486-2.8827h.8045c.552 0 1.0056.4537 1.0056 1.0056 0 .552-.4536 1.0056-1.0056 1.0056h-.8045c-.8268 0-1.5754.333-2.1206.8715h2.1206a.804.804 0 0 1 .8045.8045.804.804 0 0 1-.8045.8045h-2.9687a3.03 3.03 0 0 0-.048.5363c0 .0905.0045.1799.0123.2681h3.0044a.804.804 0 0 1 .8045.8045.804.804 0 0 1-.8045.8045h-2.362c.5531.695 1.4056 1.1396 2.362 1.1396h.8045c.552 0 1.0056.4537 1.0056 1.0056 0 .552-.4536 1.0056-1.0056 1.0056h-.8045c-2.1128 0-3.9217-1.3039-4.6659-3.1508h-.9653a.804.804 0 0 1-.8045-.8045.804.804 0 0 1 .8045-.8045h.61a5.369 5.369 0 0 1-.0067-.2681Z',
    ],
};
var aolOverviewLight = {
    prefix: 'd3x',
    iconName: 'aol-overview-light',
    icon: [
        48,
        48,
        [],
        null,
        'M4.364 6.481C2.375 7.224.96 9.141.96 11.388V40.45c0 2.892 2.344 5.236 5.236 5.236h21.469c2.248 0 4.164-1.415 4.907-3.403h-3.037c-.475.485-1.138.785-1.87.785H6.196c-1.446 0-2.618-1.172-2.618-2.618V11.388c0-.732.301-1.394.786-1.87V6.481Zm7.854-3.733h21.469c1.446 0 2.618 1.173 2.618 2.618v4.628c.919.168 1.796.45 2.619.833V5.366c0-2.892-2.345-5.236-5.237-5.236H12.218c-2.892 0-5.236 2.344-5.236 5.236v29.062c0 2.892 2.344 5.236 5.236 5.236h21.469c2.892 0 5.237-2.344 5.237-5.236v-3.872l-.015-.013c-.818.38-1.69.661-2.604.828v3.057c0 1.447-1.172 2.618-2.618 2.618H12.218c-1.446 0-2.618-1.171-2.618-2.618V5.366c0-1.445 1.172-2.618 2.618-2.618Zm13.518 18.089c0 4.707 3.869 8.525 8.643 8.525 1.928 0 3.705-.626 5.144-1.674 1.841 1.817 3.689 3.639 5.531 5.454.495.488 1.307.488 1.802 0 .497-.489.497-1.296 0-1.786l-5.528-5.45c1.068-1.418 1.695-3.169 1.695-5.069 0-4.709-3.869-8.526-8.644-8.526-4.774 0-8.643 3.817-8.643 8.526Zm-11.423 8.616c0-.578.469-1.047 1.047-1.047h9.425c.579 0 1.048.469 1.048 1.047 0 .579-.469 1.048-1.048 1.048H15.36c-.578 0-1.047-.469-1.047-1.048Zm13.978-8.616c0-2.144 1.16-4.125 3.043-5.194 1.884-1.069 4.207-1.069 6.092 0 1.882 1.069 3.043 3.05 3.043 5.193 0 2.144-1.161 4.125-3.043 5.194-1.885 1.069-4.208 1.069-6.092 0-1.883-1.069-3.043-3.05-3.043-5.193ZM15.36 23.432h8.467c.19.731.456 1.432.786 2.094H15.36c-.578 0-1.047-.469-1.047-1.047s.469-1.047 1.047-1.047Zm0-4.975h8.344c-.14.678-.219 1.378-.227 2.095H15.36c-.578 0-1.047-.469-1.047-1.048 0-.578.469-1.047 1.047-1.047Zm.327-7.92c0-.177.01-.351.028-.524h-.617a.7844.7844 0 0 1-.785-.785c0-.433.351-.785.785-.785h1.057c.786-1.664 2.479-2.815 4.441-2.815h.786c.542 0 .982.44.982.982s-.44.982-.982.982h-.786c-.807 0-1.539.325-2.07.851h2.07c.435 0 .786.352.786.785 0 .434-.351.785-.786.785h-2.899c-.03.171-.046.345-.046.524 0 .088.003.176.011.262h2.934c.435 0 .786.352.786.785 0 .435-.351.786-.786.786H18.29c.54.678 1.373 1.113 2.306 1.113h.786c.542 0 .982.439.982.981 0 .543-.44.982-.982.982h-.786c-2.064 0-3.829-1.273-4.555-3.076h-.943a.7846.7846 0 0 1-.785-.786c0-.433.351-.785.785-.785h.596a4.5335 4.5335 0 0 1-.007-.262Z',
    ],
};
var aolOverviewRegular = {
    prefix: 'd3x',
    iconName: 'aol-overview-regular',
    icon: [
        48,
        48,
        [],
        null,
        'M5.4624 8.22c-2.9136 0-5.2776 2.376-5.2776 5.304v29.172c0 2.928 2.364 5.304 5.2776 5.304h21.6384c2.9136 0 5.2776-2.376 5.2776-5.304v-.264h-3.9576v.264c0 .732-.5904 1.3248-1.32 1.3248H5.4624c-.7296 0-1.32-.5928-1.32-1.3248V13.524c0-.732.5904-1.3248 1.32-1.3248V8.22ZM13.38 3.9792h21.636c.7296 0 1.32.5928 1.32 1.3248v6.0096a11.0752 11.0752 0 0 1 3.9576 1.5336V5.304c0-2.928-2.364-5.304-5.2776-5.304H13.38c-2.916 0-5.2776 2.376-5.2776 5.304v29.172c0 2.928 2.364 5.304 5.2776 5.304h21.636c2.7648 0 5.0328-2.1384 5.2584-4.8576l-1.9536-1.9656a11.03 11.03 0 0 1-1.9848.5496v.972c0 .732-.5904 1.3248-1.32 1.3248H13.38c-.7296 0-1.32-.5928-1.32-1.3248V5.304c0-.732.5904-1.3248 1.32-1.3248Zm33.8544 28.56-5.5776-5.604c.8088-1.3152 1.2744-2.8656 1.2744-4.524 0-4.7616-3.84-8.6184-8.5752-8.6184-4.7352 0-8.5752 3.8592-8.5752 8.6184s3.84 8.6184 8.5752 8.6184c1.6512 0 3.192-.4704 4.5024-1.2816l5.5776 5.604c.7728.7776 2.0256.7776 2.7984 0 .7728-.7776.7728-2.0376 0-2.8128Zm-32.0088-1.776c0-.5856.4728-1.0608 1.056-1.0608h7.6512c.5832 0 1.056.4752 1.056 1.0608 0 .5856-.4728 1.0608-1.056 1.0608h-7.6512c-.5832 0-1.056-.4752-1.056-1.0608Zm19.1304-3.7128c-2.5512 0-4.6176-2.0784-4.6176-4.6416 0-2.5632 2.0664-4.6416 4.6176-4.6416 2.5512 0 4.6176 2.0784 4.6176 4.6416 0 2.5632-2.0664 4.6416-4.6176 4.6416Zm-10.9896-2.388c.1488.7344.3672 1.4448.6504 2.1192-.0288.0024-.0552.0024-.084.0024h-7.6512c-.5832 0-1.056-.4752-1.056-1.0608 0-.5856.4728-1.0608 1.056-1.0608h7.0848Zm.1224-5.0376c-.1752.684-.2832 1.3944-.3264 2.1216h-6.8808c-.5832 0-1.056-.4752-1.056-1.0608 0-.5856.4728-1.0608 1.056-1.0608h7.2072Zm-6.876-8.0232c0-.18.0096-.3552.0264-.5304h-.6216c-.4368 0-.792-.3552-.792-.7944s.3552-.7968.792-.7968h1.0656c.792-1.6848 2.4984-2.8512 4.476-2.8512h.792c.5472 0 .9888.444.9888.996 0 .5472-.444.9936-.9888.9936h-.792c-.8136 0-1.5504.3312-2.0856.8616h2.0856c.4368 0 .792.3576.792.7968s-.3552.7944-.792.7944H18.636a3.2283 3.2283 0 0 0-.0456.5304c0 .0888.0048.18.012.264h2.9568c.4368 0 .792.3576.792.7968s-.3552.7944-.792.7944H19.236c.5448.6864 1.3824 1.128 2.3232 1.128h.792c.5472 0 .9888.4464.9888.996s-.444.9936-.9888.9936h-.792c-2.0784 0-3.8592-1.2888-4.5912-3.1152h-.9504c-.4368 0-.792-.3552-.792-.7944s.3552-.7968.792-.7968h.6a9.862 9.862 0 0 1-.0048-.2664Z',
    ],
};
var clicksLight = {
    prefix: 'd3x',
    iconName: 'clicks-light',
    icon: [
        48,
        48,
        [],
        null,
        'M43.2029 13.3583c2.3061 0 4.1749-1.8674 4.1749-4.1749V4.1746c0-2.306-1.8688-4.1749-4.175-4.1749h-5.0087c-2.3061 0-4.175 1.8688-4.175 4.175v5.0087c0 2.3075 1.8689 4.175 4.175 4.175h5.0088Zm.8352-9.1837v5.0088c0 .4598-.3754.8353-.8352.8353H38.194c-.4598 0-.8353-.3755-.8353-.8353V4.1746c0-.4598.3755-.8352.8353-.8352h5.0088c.4598 0 .8352.3754.8352.8352Zm-17.5335 9.1837c2.3061 0 4.175-1.8674 4.175-4.1749V4.1746c0-2.306-1.8689-4.1749-4.175-4.1749h-5.0088c-2.306 0-4.1749 1.8688-4.1749 4.175v5.0087c0 2.3075 1.8688 4.175 4.175 4.175h5.0087Zm.8353-9.1837v5.0088c0 .4598-.3755.8353-.8353.8353h-5.0088c-.4598 0-.8352-.3755-.8352-.8353V4.1746c0-.4598.3754-.8352.8352-.8352h5.0088c.4598 0 .8353.3754.8353.8352ZM9.8063 13.3583c2.3062 0 4.175-1.8674 4.175-4.1749V4.1746c0-2.306-1.8688-4.1749-4.175-4.1749H4.7976c-2.3062 0-4.175 1.8688-4.175 4.175v5.0087c0 2.3075 1.8688 4.175 4.175 4.175h5.0087Zm.8353-9.1837v5.0088c0 .4598-.374.8353-.8353.8353H4.7976c-.4613 0-.8353-.3755-.8353-.8353V4.1746c0-.4598.374-.8352.8353-.8352h5.0087c.4613 0 .8353.3754.8353.8352Zm8.9587 41.1207c-.689.0717-1.2937-.3558-1.5341-1.0026L9.8007 21.8544c-.2292-.6046-.0731-1.2937.3853-1.7535.4598-.4584 1.1488-.6159 1.7535-.4795l22.4383 8.3597a1.6705 1.6705 0 0 1 1.0925 1.5341c.0127.6876-.3979 1.3148-1.0349 1.4821l-7.717 3.2764L37.5936 45.149c.6524.6469.6524 1.7113 0 2.3582-.6525.658-1.71.658-2.361 0l-10.874-10.8753-3.182 7.724c-.2616.6258-.8874 1.0434-1.5764.9394Zm.2081-6.1787 2.4116-6.0747c.1673-.4064.5006-.741.9084-.907l6.076-2.5058-15.0207-5.6247 5.6247 15.1122Z',
    ],
};
var clicksRegular = {
    prefix: 'd3x',
    iconName: 'clicks-regular',
    icon: [
        48,
        48,
        [],
        null,
        'm33.762 26.39-21.761-6.394c-1.285-.383-2.485.722-2.103 2.103l6.394 21.666c.435 1.572 2.464 1.752 3.154.372l3.038-6.086c.074-.234.169-.276.276-.393l9.343 9.346c1.327 1.328 3.479 1.328 4.806 0 1.328-1.327 1.328-3.473 0-4.8l-9.345-9.346c.117-.107.249-.202.391-.277l6.087-3.037c1.38-.69 1.2-2.719-.28-3.154ZM47.79 9.346c0 2.347-1.901 4.248-4.248 4.248h-5.098c-2.347 0-4.248-1.901-4.248-4.248V4.248C34.196 1.901 36.097 0 38.444 0h5.098c2.347 0 4.248 1.901 4.248 4.248v5.098Zm-16.993 0c0 2.347-1.901 4.248-4.248 4.248h-5.098c-2.347 0-4.248-1.901-4.248-4.248V4.248C17.203 1.901 19.104 0 21.451 0h5.098c2.347 0 4.248 1.901 4.248 4.248v5.098Zm-16.993 0c0 2.347-1.901 4.248-4.248 4.248H4.458C2.112 13.594.21 11.693.21 9.346V4.248C.21 1.901 2.112 0 4.458 0h5.098c2.347 0 4.248 1.901 4.248 4.248v5.098Zm29.738-5.098h-5.098v5.098h5.098V4.248Zm-16.993 0h-5.098v5.098h5.098V4.248Zm-16.993 0H4.458v5.098h5.098V4.248Z',
    ],
};
var deviceCheckLight = {
    prefix: 'd3x',
    iconName: 'device-check-light',
    icon: [
        48,
        42,
        [],
        null,
        'M20.6025 0C9.2035 0 .0043 9.1992.0043 20.5982c-.3 14.2987 15.0987 24.5977 28.1975 19.0982-.6-.6-1.1-1.2999-1.5999-1.9998C15.103 41.9962 2.2041 32.897 2.4041 20.5982c.6-22.298 31.7971-24.6978 36.0967-2.9998.9.1 1.6999.2 2.4998.5C39.7007 7.8993 31.1016 0 20.6025 0Zm16.7984 19.8982c-5.8994 0-10.599 4.7996-10.599 10.599 0 5.7995 4.7996 10.5991 10.599 10.5991 5.8995 0 10.599-4.7996 10.599-10.599 0-5.7995-4.7995-10.599-10.599-10.599Zm0 18.8983c-4.5995 0-8.2992-3.6997-8.2992-8.1992 0-4.4996 3.6997-8.1993 8.2992-8.1993 4.5996 0 8.2993 3.6997 8.2993 8.1993 0 4.4995-3.7997 8.1992-8.2993 8.1992Zm-3.2997-9.0992c-.5-.5-1.1998-.5-1.6998 0s-.5 1.2 0 1.6999l2.9997 2.8997c.5.5 1.2.5 1.6999 0l5.2995-5.2995c.5-.5.5-1.1999 0-1.6999s-1.1999-.5-1.6998 0l-4.4996 4.4996-2.0999-2.0998ZM11.3033 12.399H8.2036a2.6885 2.6885 0 0 0-2.6998 2.6997v6.4995a2.6885 2.6885 0 0 0 2.6998 2.6997h3.0997a2.6885 2.6885 0 0 0 2.6998-2.6997v-6.4995c.1-1.4998-1.2-2.6997-2.6998-2.6997Zm10.599 0h-3.0997a2.6885 2.6885 0 0 0-2.6997 2.6997v6.4995a2.6885 2.6885 0 0 0 2.6997 2.6997h3.0997a2.6885 2.6885 0 0 0 2.6998-2.6997v-6.4995c.1-1.4998-1.0999-2.6997-2.6998-2.6997Zm13.3988 2.6997v2.5998c-.7999.1-1.5998.3-2.3997.6v-3.1998c0-.2-.2-.3-.3-.3h-3.0997c-.2 0-.3.2-.3.3v5.2996c-.8.7-1.4999 1.3998-2.1998 2.2998-.1-.3-.2-.7-.2-1.1v-6.4994a2.6885 2.6885 0 0 1 2.6998-2.6997h3.0997a2.6885 2.6885 0 0 1 2.6997 2.6997Zm-23.5978 6.4995c0 .2-.2.3-.3.3H8.2036c-.2 0-.3-.2-.3-.3v-6.4995c0-.2.2-.3.3-.3h3.0997c.2 0 .3.2.3.3v6.4995h.1Zm10.599 0c0 .2-.2.3-.3.3h-3.0997c-.2 0-.3-.2-.3-.3v-6.4995c0-.2.2-.3.3-.3h3.0997c.2 0 .3.2.3.3v6.4995Z',
    ],
};
var deviceCheckRegular = {
    prefix: 'd3x',
    iconName: 'device-check-regular',
    icon: [
        48,
        42,
        [],
        null,
        'M20.795 0c10.7169 0 19.54 8.1074 20.6718 18.5225a12.8968 12.8968 0 0 0-3.6942-.6437C36.387 9.7555 29.3134 3.571 20.795 3.571c-9.5129 0-17.2242 7.7113-17.2242 17.2241 0 9.5119 7.7113 17.2232 17.2242 17.2232 1.8215 0 3.577-.2827 5.225-.806a13.1023 13.1023 0 0 0 2.2617 2.9894c-2.3228.8963-4.8471 1.3885-7.4867 1.3885C9.3103 41.5902 0 32.28 0 20.7952 0 9.3103 9.3103 0 20.795 0ZM37.4048 20.28c-5.8487 0-10.5946 4.7629-10.5946 10.6336 0 5.8707 4.746 10.6336 10.5946 10.6336 5.8496 0 10.5955-4.763 10.5955-10.6336 0-5.8707-4.7459-10.6336-10.5955-10.6336Zm-2.008 14.421-2.9434-2.9533c-.4562-.4582-.4562-1.211 0-1.6692.4561-.4572 1.207-.4572 1.6631 0l2.1113 2.1193 4.4662-4.4822c.4561-.4582 1.207-.4582 1.6632 0 .456.4581.456 1.211 0 1.6691l-5.2973 5.3164c-.4572.4581-1.207.4581-1.6632 0ZM8.5924 15.124c0-1.5188 1.2311-2.7498 2.7499-2.7498h1.3754c1.5188 0 2.75 1.231 2.75 2.7498v4.1244c0 1.5188-1.2312 2.7499-2.75 2.7499h-1.3754c-1.5188 0-2.7499-1.2311-2.7499-2.75V15.124Zm8.9374 0c0-1.5188 1.231-2.7498 2.7499-2.7498h1.3744c1.5188 0 2.7499 1.231 2.7499 2.7498v4.1244c0 1.5188-1.231 2.7499-2.7499 2.7499h-1.3744c-1.5188 0-2.75-1.2311-2.75-2.75V15.124Zm15.8106 0v3.3996c-1.993.6576-3.7725 1.7854-5.209 3.2521-.9795-.421-1.6652-1.3935-1.6652-2.5273v-4.1244c0-1.5188 1.231-2.7498 2.7498-2.7498h1.3745c1.5188 0 2.7499 1.231 2.7499 2.7498Z',
    ],
};
var eedLight = {
    prefix: 'd3x',
    iconName: 'eed-light',
    icon: [
        48,
        41,
        [],
        null,
        'M44.0934 27.1984c.7351 0 1.3652.63 1.3652 1.4702v10.0812c0 .8401-.63 1.4702-1.3652 1.4702-.735 0-1.3651-.63-1.3651-1.4702V28.6686c0-.8402.63-1.4702 1.3651-1.4702Zm-11.5514 0c.735 0 1.3652.63 1.3652 1.4702v10.0812c0 .8401-.6301 1.4702-1.3652 1.4702-.735 0-1.3652-.63-1.3652-1.4702V28.6686c-.105-.8402.5251-1.4702 1.3652-1.4702Zm-5.7757 4.3055c.735 0 1.3652.63 1.3652 1.4702v5.7757c0 .8401-.6301 1.4702-1.3652 1.4702s-1.3652-.63-1.3652-1.4702v-5.7757c-.105-.8401.525-1.4702 1.3652-1.4702Zm11.5514-1.4702c.7351 0 1.3652.6301 1.3652 1.4702v7.1409c0 .84-.63 1.4702-1.3652 1.4702-.735 0-1.3652-.6301-1.3652-1.4702v-7.1409c-.105-.8401.5251-1.4702 1.3652-1.4702ZM7.549 27.4084c0 1.3652 1.1552 2.5203 2.5203 2.5203h10.9214v2.5203H10.0692c-2.7303 0-5.0406-2.2053-5.0406-5.0406V14.5968l-2.9404 2.5203c-.525.42-1.2601.42-1.7852-.105-.42-.525-.42-1.2601.105-1.7852L17.9452.315c.42-.42 1.1551-.42 1.5752 0L36.9525 15.332c.5251.42.6301 1.2602.105 1.7852-.21.315-.63.315-.945.315-.315 0-.5251 0-.8402-.21l-2.9403-2.5203v2.2053h-2.5203v-4.4105L18.7853 2.9404 7.5489 12.4966v14.9118Zm40.115-9.4512c.525.6301.42 1.4702-.21 1.9953l-7.876 6.7208a1.422 1.422 0 0 1-1.7852 0l-5.4607-4.3055-7.246 6.1957c-.63.5251-1.4701.42-1.9952-.21-.525-.63-.42-1.4702.21-1.9952l8.086-6.8259a1.422 1.422 0 0 1 1.7853 0l5.4607 4.3056 7.0358-5.9858c.6301-.63 1.4702-.525 1.9953.105Zm-33.7092 1.0502c.42-.6301 1.1551-.8401 1.7852-.8401.6301 0 1.2602.21 1.7852.84.315.4201.8401 1.1552 1.3652 1.9953l.63 1.2602c.2101.42.3151.945.3151 1.4701 0 2.2053-1.8902 3.7805-3.9905 3.7805-2.1002 0-3.9905-1.5752-3.9905-3.7805 0-.525.105-1.05.315-1.4701l.6302-1.2602c.315-.8401.84-1.5752 1.1551-1.9952Zm.8401 3.2554c-.21.315-.315.63-.42.945-.105.3151-.105.4201-.105.5251 0 .6301.63 1.2602 1.4701 1.2602.9451 0 1.4702-.63 1.4702-1.2602 0 0 0-.21-.105-.525-.105-.315-.315-.6301-.42-.9451-.3151-.5251-.6301-1.0502-.9452-1.3652-.315.315-.63.84-.945 1.3652Zm6.1958-12.9166c.63 0 1.0501.525 1.0501 1.1551 0 1.4702-.735 2.7303-1.9952 3.4654-.5251.315-.9452.9452-.9452 1.5752 0 .6301-.525 1.0502-1.05 1.0502C17.42 16.592 17 16.067 17 15.4369c0-1.4702.735-2.7303 1.9952-3.4654.525-.315.9451-.9451.9451-1.5752-.105-.525.42-1.0501 1.0502-1.0501Zm-5.0407 0c.6301 0 1.0502.525 1.0502 1.1551 0 1.4702-.7351 2.7303-1.9953 3.4654-.525.315-.945.9452-.945 1.5752 0 .6301-.5252 1.0502-1.0502 1.0502-.63 0-1.0501-.5251-1.0501-1.1552 0-1.4702.735-2.7303 1.9952-3.4654.525-.315.9451-.9451.9451-1.5752-.105-.525.42-1.0501 1.0501-1.0501Z',
    ],
};
var eedRegular = {
    prefix: 'd3x',
    iconName: 'eed-regular',
    icon: [
        48,
        41,
        [],
        null,
        'M43.9278 27.27c1.0221 0 1.851.8288 1.851 1.8509v9.378c0 1.022-.8289 1.8508-1.851 1.8508s-1.8509-.8288-1.8509-1.8509V29.121c0-1.0221.8288-1.851 1.8509-1.851Zm-5.7995 2.838c1.0221 0 1.851.8288 1.851 1.851v6.4164c0 1.0221-.8289 1.851-1.851 1.851s-1.8509-.8289-1.8509-1.851V31.959c0-1.0222.8288-1.851 1.8509-1.851Zm-9.5013 1.851c0-1.0222-.8288-1.851-1.851-1.851-1.022 0-1.8508.8288-1.8508 1.851v6.4164c0 1.0221.8288 1.851 1.8509 1.851s1.8509-.8289 1.8509-1.851V31.959Zm5.6761-2.9615c0-1.0221-.8288-1.8509-1.8509-1.8509s-1.851.8288-1.851 1.8509v9.378c0 1.022.8289 1.8508 1.851 1.8508s1.8509-.8288 1.8509-1.8509v-9.3779ZM17.6707.4494c.692-.5975 1.7172-.5995 2.4123-.0041L37.2347 15.129c.7764.6643.8669 1.8334.2026 2.6098-.6653.7763-1.8334.8668-2.6108.2025l-1.8807-1.6113v.328H29.244V13.161l-10.361-8.87-10.3692 8.942v15.3943c0 .3414.2766.617.617.617h10.7353v3.7018H9.1308c-2.3846 0-4.3188-1.9332-4.3188-4.3188V16.4258l-1.7522 1.5105c-.7743.6684-1.9434.582-2.6108-.1923-.6673-.7743-.581-1.9434.1934-2.6108l4.5645-3.9363a1.871 1.871 0 0 1 .5409-.4668L17.6707.4494Zm29.8838 17.4725c.6653.7764.5749 1.9445-.2005 2.6098l-7.7738 6.6632a1.8511 1.8511 0 0 1-2.3609.0401l-4.9913-3.9928-6.8607 5.6987c-.7856.653-1.9527.545-2.6056-.2416-.654-.7856-.546-1.9527.2406-2.6057l8.0206-6.6632c.6735-.5594 1.6555-.5687 2.3393-.0216l4.9728 3.9784 6.6098-5.6658c.7763-.6653 1.9444-.5759 2.6097.2005Zm-30.2293 1.9003c-.3723-.51-.941-.6962-1.4077-.6962-.4659 0-1.0345.1851-1.4067.693l-.0052.0083c-.2468.3434-.6725.9563-1.0406 1.6113-.184.327-.364.6838-.5008 1.0355-.1306.3352-.255.7496-.255 1.1681 0 1.8047 1.5147 3.1342 3.2083 3.1342 1.6935 0 3.2082-1.3295 3.2082-3.1342 0-.4185-.1234-.8319-.254-1.1681-.1368-.3517-.3167-.7085-.5008-1.0355-.368-.655-.7938-1.2689-1.0447-1.6144l-.001-.002Zm2.8277-5.5671c-.547.2858-.8863.9264-.8863 1.5804 0 .5933-.473 1.0705-1.0571 1.0705-.584 0-1.0581-.5286-1.0581-1.0838 0-1.4037.7414-2.7157 1.9352-3.4242.546-.3229.8854-.9234.8854-1.566 0-.5934.474-1.0818 1.058-1.0818.5831 0 1.0571.4884 1.0571 1.0817 0 1.4036-.7403 2.7157-1.9342 3.4232Zm-4.9357 0c-.547.2858-.8864.9264-.8864 1.5804 0 .5933-.473 1.0684-1.057 1.0684-.5841 0-1.0582-.5265-1.0582-1.0817 0-1.4037.7414-2.7157 1.9353-3.4242.546-.3229.8853-.9234.8853-1.566 0-.5934.474-1.0829 1.0581-1.0829.583 0 1.057.4895 1.057 1.0828 0 1.4036-.7403 2.7157-1.9341 3.4232Z',
    ],
};
var energyCertificateLight = {
    prefix: 'd3x',
    iconName: 'energy-certificate-light',
    icon: [
        48,
        43,
        [],
        null,
        'm30.3776 10.4202-8.856-8.8572A5.3317 5.3317 0 0 0 17.7546 0H5.3221C2.4022 0 0 2.4022 0 5.322V37.255c0 2.92 2.4022 5.3222 5.3221 5.3222h15.646v-2.6723l-15.59.0112c-1.4677 0-2.717-1.1944-2.717-2.661V5.3231c.001-1.46 1.201-2.66 2.661-2.6622h10.6443v9.3143c.0011 2.1894 1.8017 3.99 3.991 3.991h9.3143v.706h2.6611v-2.4941a5.3053 5.3053 0 0 0-1.5552-3.758ZM18.6275 2.8347c.372.13.7204.3193 1.0084.6062l8.856 8.8571c.289.2835.4807.633.605 1.0084h-9.1395c-.7294-.0022-1.3277-.6016-1.33-1.331V2.8335v.0011ZM6.652 21.4835h9.8936c.7327 0 1.331-.595 1.331-1.33 0-.7361-.5994-1.331-1.331-1.331H6.652c-.7294 0-1.3311.6005-1.3311 1.331 0 .7294.6017 1.33 1.3311 1.33Zm8.0112 5.3513H6.6521c-.7294-.0023-1.3289-.6017-1.33-1.3311.0023-.7295.6006-1.3278 1.33-1.33h8.0112c.7317 0 1.3311.5983 1.3311 1.33 0 .7316-.5994 1.331-1.331 1.331Zm0 5.3501H6.6521c-.7294-.0022-1.3277-.6006-1.33-1.33.0011-.7294.6006-1.3288 1.33-1.331h8.0112c.7317 0 1.3311.5994 1.3311 1.331 0 .7317-.5994 1.33-1.331 1.33ZM11.228 7.0151a.8057.8057 0 0 0 .2286-.5625c0-.4425-.3642-.8067-.8079-.8067a.8097.8097 0 0 0-.5781.2443L6.4123 9.6594a.8069.8069 0 0 0-.2274.5613c0 .4426.3641.8067.8067.8067H9.936l-2.307 2.3967a.8026.8026 0 0 0-.2297.5636c0 .4426.3642.8067.8068.8067a.8051.8051 0 0 0 .586-.2521l3.6213-3.7614a.8101.8101 0 0 0 .2263-.5602c0-.4426-.3642-.8067-.8067-.8067H8.8986l2.3294-2.3978v-.001Zm21.5115 15.0353 1.5126.8068-1.5126.8067h-7.731c-.7373 0-1.3446.6073-1.3446 1.3445 0 .7373.6073 1.3446 1.3445 1.3446h7.7984c.3966 0 .7876-.0975 1.1383-.2846l2.0168-1.0757c1.7087-.9109 1.7087-3.3602 0-4.2711l-2.0168-1.0757a2.4183 2.4183 0 0 0-1.1383-.2845h-7.7984c-.7372 0-1.3445.6072-1.3445 1.3445 0 .7372.6073 1.3445 1.3445 1.3445h7.7311Zm6.5838 12.0852 2.0168-1.0756c1.7087-.911 1.7087-3.3603 0-4.2712l-2.0168-1.0756a2.4183 2.4183 0 0 0-1.1384-.2846H25.0084c-.7372 0-1.3445.6073-1.3445 1.3445 0 .7373.6073 1.3446 1.3445 1.3446h13.1093l1.5126.8067-1.5126.8067H25.0084c-.7372 0-1.3445.6073-1.3445 1.3446 0 .7372.6073 1.3445 1.3445 1.3445H38.185c.3967 0 .7877-.0975 1.1384-.2846Zm7.395 6.9916-2.0168 1.0756a2.4183 2.4183 0 0 1-1.1384.2846H25.0084c-.7372 0-1.3445-.6072-1.3445-1.3445 0-.7373.6073-1.3445 1.3445-1.3445H43.496l1.5126-.8068-1.5126-.8067H25.0084c-.7372 0-1.3445-.6073-1.3445-1.3445 0-.7373.6073-1.3446 1.3445-1.3446h18.5547a2.415 2.415 0 0 1 1.1384.2846l2.0168 1.0757c1.7087.9109 1.7087 3.3602 0 4.2711Z',
    ],
};
var energyCertificateRegular = {
    prefix: 'd3x',
    iconName: 'energy-certificate-regular',
    icon: [
        48,
        43,
        [],
        null,
        'm30.2085 7.7226-6.1717-6.1727c-.9983-.9928-2.3454-1.55-3.7437-1.55H5.2925C2.3688 0 0 2.3689 0 5.2926v31.755C0 39.97 2.37 42.34 5.2925 42.34h15.833V38.371H5.2935c-.731 0-1.3226-.5927-1.3226-1.3236L3.9688 5.3036c0-.7309.5928-1.3237 1.3237-1.3237h13.2312v6.605c0 1.462 1.1856 2.6463 2.6463 2.6463h6.5415v3.3482h4.0435V11.462a5.2945 5.2945 0 0 0-1.5465-3.7393Zm14.5114 34.2452 2.0056-1.0696c1.6992-.9059 1.6992-3.3415 0-4.2474l-2.0056-1.0696a2.4048 2.4048 0 0 0-1.132-.283H25.1366c-.7387 0-1.337.5983-1.337 1.337v4.2786c0 .7387.5983 1.337 1.337 1.337h18.4513c.3944 0 .7844-.097 1.132-.283Zm-28.478-7.5543H8.2751c-.7309 0-1.3225-.595-1.3225-1.3226s.5916-1.3237 1.3225-1.3237h7.9666c.7287 0 1.3237.5961 1.3237 1.3237 0 .7276-.595 1.3226-1.3237 1.3226Zm23.1298-.468 2.0056-1.0696c1.6992-.9059 1.6992-3.3416 0-4.2474l-2.0056-1.0696a2.4048 2.4048 0 0 0-1.132-.283H25.1366c-.7387 0-1.337.5983-1.337 1.337v4.2786c0 .7387.5983 1.337 1.337 1.337h13.103c.3945 0 .7834-.097 1.1321-.283ZM8.2752 29.0931h7.9666c.7287 0 1.3237-.596 1.3237-1.3237 0-.7275-.595-1.3225-1.3237-1.3225H8.2752c-.7309 0-1.3225.595-1.3225 1.3225 0 .7276.5916 1.3237 1.3225 1.3237Zm15.5243-8.5025c0-.7387.5984-1.337 1.337-1.337h7.755c.3944 0 .7844.0969 1.132.283l2.0056 1.0696c1.6992.9058 1.6992 3.3415 0 4.2474l-2.0056 1.0696a2.4015 2.4015 0 0 1-1.132.283h-7.755c-.7386 0-1.337-.5983-1.337-1.337v-4.2786Zm-15.5243 3.181h9.8385c.7287 0 1.3237-.5916 1.3237-1.3225 0-.731-.595-1.3237-1.3237-1.3237H8.2752c-.7309 0-1.3225.5928-1.3225 1.3237 0 .731.5916 1.3226 1.3225 1.3226ZM11.6758 8.126c.3855-.3966 1.0206-.4067 1.4173-.0212a1.0031 1.0031 0 0 1 .0211 1.4184l-1.9855 2.0446h2.4424c.4022 0 .7654.2407.9225.6106a1.003 1.003 0 0 1-.2005 1.0886l-3.6011 3.7404c-.3833.3989-1.0184.4111-1.4173.0278-.399-.3844-.4112-1.0195-.0268-1.4183l1.9655-2.0435H8.7566c-.5493 0-1.0017-.4524-1.0017-1.0017 0-.2607.1014-.5114.283-.6986l3.6379-3.747Z',
    ],
};
var fileContractPenLight = {
    prefix: 'd3x',
    iconName: 'file-contract-pen-light',
    icon: [
        48,
        43,
        [],
        null,
        'M.0004 5.3583c0-2.9551 2.388-5.3578 5.3248-5.3578h12.9991c1.0559 0 2.072.4262 2.8199 1.1797 3.213 3.2325 6.425 6.4717 9.637 9.7042a4.0273 4.0273 0 0 1 1.173 2.838v10.0712c-.89.8957-1.772 1.7833-2.663 2.679V16.066h-9.32c-2.205 0-3.994-1.8003-3.994-4.019V2.6704H5.3253c-1.4718.009-2.6618 1.2059-2.6618 2.688v32.1493c0 1.481 1.19 2.679 2.6618 2.679h16.177c-.025.109-.05.209-.083.3182-.191.7944-.15 1.615.117 2.3606H5.3253c-2.9369 0-5.3248-2.4027-5.3248-5.3578V5.3583Zm5.3248 1.3389c0-.7365.6002-1.3389 1.3321-1.3389h5.326c.732 0 1.331.6024 1.331 1.3389 0 .7376-.599 1.34-1.331 1.34h-5.326c-.732 0-1.332-.6024-1.332-1.34Zm0 5.359c0-.7365.6002-1.34 1.3321-1.34h5.326c.732 0 1.331.6035 1.331 1.34s-.599 1.3389-1.331 1.3389h-5.326c-.732 0-1.332-.6024-1.332-1.339Zm0 21.4325c0-.7365.6002-1.34 1.3321-1.34h.682c.583 0 1.107-.3842 1.273-.9537.416-1.382.824-2.763 1.24-4.145.283-.9537 1.149-1.5992 2.131-1.5992.982 0 1.847.6455 2.13 1.5912.3489 1.1718.699 2.3527 1.0479 3.5245a3.1146 3.1146 0 0 1 2.372-1.0968c1.182 0 2.263.6695 2.796 1.7253.158.3183.308.6274.466.9457h3.079a6.9363 6.9363 0 0 0-.882 2.0424c-.05.2091-.108.4274-.158.6365h-2.855c-.499 0-.965-.2842-1.1889-.7365-.283-.5615-.558-1.1218-.841-1.6833a.4584.4584 0 0 0-.4081-.2512c-.141 0-.283.067-.366.1842-.491.6535-.9729 1.307-1.4639 1.9594-.3.4103-.8081.6115-1.2991.5194-.491-.092-.8979-.4523-1.04-.9376-.466-1.5492-.924-3.1063-1.3889-4.6555-.275.9127-.541 1.8333-.816 2.746-.508 1.7003-2.064 2.863-3.828 2.863h-.682c-.732 0-1.332-.6023-1.332-1.3388Zm13.315-30.7673v9.3348c0 .7365.599 1.3389 1.331 1.3389h9.279a1.3278 1.3278 0 0 0-.35-.6195c-3.212-3.2313-6.432-6.4717-9.6439-9.703-.167-.1761-.383-.2932-.616-.3512Zm5.7 38.4778c.516-2.0924 1.04-4.178 1.556-6.2705.175-.7035.533-1.348 1.049-1.8674 4.26-4.286 8.529-8.5812 12.79-12.8672 1.564-1.5662 4.094-1.5662 5.65 0 .4818.4853.9649.9706 1.448 1.456 1.556 1.5741 1.556 4.1189 0 5.6851-4.2611 4.286-8.5301 8.5812-12.79 12.8684-.516.5103-1.157.8785-1.856 1.0547-2.08.5183-4.152 1.0457-6.233 1.565-.457.1172-.932-.017-1.265-.3511-.332-.3353-.466-.8127-.349-1.273Zm3.12-1.515 4.078-1.0218c.233-.058.441-.1762.616-.3512l8.8449-8.8995c-1.106-1.1138-2.2209-2.2356-3.328-3.3495a8134.6562 8134.6562 0 0 1-8.838 8.8995c-.166.1682-.291.3853-.349.6194-.3409 1.365-.6819 2.738-1.024 4.103ZM39.551 24.1619l3.329 3.3495c.691-.6956 1.373-1.3821 2.064-2.0766.516-.5194.516-1.373 0-1.8924-.483-.4853-.957-.9706-1.44-1.457-.516-.5183-1.365-.5183-1.88 0-.691.6955-1.382 1.382-2.073 2.0765Z',
    ],
};
var fileContractPenRegular = {
    prefix: 'd3x',
    iconName: 'file-contract-pen-regular',
    icon: [
        48,
        43,
        [],
        null,
        'M.0003 5.3227c0-2.9355 2.388-5.3222 5.3257-5.3222h13.773c1.4147 0 2.7628.5566 3.7608 1.5546 2.5132 2.5121 5.0265 5.0152 7.5397 7.5261.998.9981 1.5558 2.354 1.5558 3.7676V24.925c-1.3311 1.33-2.6623 2.6612-3.9945 3.9911v-15.61H21.304c-1.4734 0-2.6634-1.1888-2.6634-2.661V3.9916H5.326c-.7328 0-1.3311.5995-1.3311 1.3311v31.9358c0 .7316.5983 1.33 1.331 1.33h16.519c-.1412.5577-.2835 1.1233-.4246 1.68-.192.7733-.1502 1.5806.1163 2.3122H5.326c-2.9378 0-5.3257-2.3868-5.3257-5.3222V5.3227ZM6.6571 9.315c0-.7328.5995-1.3312 1.3323-1.3312h6.6567c.7328 0 1.3312.5984 1.3312 1.3312 0 .7316-.5984 1.33-1.3312 1.33H7.9894c-.7328 0-1.3323-.5984-1.3323-1.33Zm0 5.3222c0-.7316.5995-1.3311 1.3323-1.3311h6.6567c.7328 0 1.3312.5995 1.3312 1.3311 0 .7316-.5984 1.33-1.3312 1.33H7.9894c-.7328 0-1.3323-.5984-1.3323-1.33Zm0 18.629c0-.7316.5995-1.3311 1.3323-1.3311h.6819c.5826 0 1.1064-.3816 1.2735-.9473.4155-1.3729.8231-2.7447 1.2397-4.1176a2.2206 2.2206 0 0 1 2.1305-1.5885c.9823 0 1.847.6413 2.1305 1.5806.3161 1.0399.6243 2.0786.9405 3.1184.5238-.4572 1.2058-.7147 1.9216-.7147 1.1155 0 2.1395.6323 2.6385 1.6304.175.341.341.6898.516 1.0308h2.5042c.2755 0 .5329.0835.7496.2326-.2167.2167-.4245.4245-.6412.6401-.516.516-.9157 1.1234-1.181 1.7884h-2.2558c-.499 0-.9653-.2823-1.19-.7316-.2913-.5905-.5905-1.181-.8818-1.7715a.2861.2861 0 0 0-.2585-.158c-.1084 0-.2078.0576-.2574.158-.2913.5905-.5917 1.181-.8818 1.7715-.2416.4911-.7666.7734-1.3074.7316-.5408-.0418-.9981-.4155-1.1562-.9394-.4663-1.5388-.9235-3.0856-1.3898-4.6245-.2744.9066-.5408 1.8212-.8152 2.7278-.508 1.689-2.0638 2.844-3.8285 2.844h-.682c-.7327 0-1.3322-.5984-1.3322-1.33Zm17.4345 7.6593c.4155-1.663.832-3.3351 1.2487-4.9982a2.621 2.621 0 0 1 .6989-1.2385c3.5858-3.5847 7.1727-7.1615 10.7596-10.745h-.0079c1.9724 1.9702 3.9358 3.9335 5.9082 5.9037-3.5869 3.5847-7.1648 7.1614-10.7517 10.745-.341.341-.7745.5826-1.2397.6988-1.6642.4155-3.3374.8321-5.0016 1.2476-.4584.1163-.9326-.017-1.2645-.3488-.333-.3331-.4663-.8073-.35-1.2646ZM38.6798 22.064c.8151-.8152 1.6303-1.6304 2.4466-2.4455 1.3063-1.2973 3.412-1.2973 4.7103 0 .3986.3997.7903.7903 1.19 1.19 1.2973 1.3051 1.2973 3.4097 0 4.707-.8163.815-1.6314 1.6291-2.4466 2.4443l-5.9003-5.8958Z',
    ],
};
var fileInvoiceDollarMagnifyingGlassLight = {
    prefix: 'd3x',
    iconName: 'file-invoice-dollar-magnifying-glass-light',
    icon: [
        48,
        42,
        [],
        null,
        'M.0004 5.2495c0-2.8954 2.3874-5.2499 5.3237-5.2499h12.9873a4.021 4.021 0 0 1 2.8203 1.1568c3.2113 3.167 6.4317 6.3328 9.643 9.4987.6739.6648 1.0807 1.542 1.1557 2.4693-2.9943 0-5.7567.976-7.9783 2.6249h-3.993c-2.2046 0-3.9932-1.7636-3.9932-3.9374V2.6246H5.3162c-1.4636 0-2.6534 1.1738-2.6534 2.625v31.4993c0 1.451 1.1898 2.625 2.6613 2.625h21.2984c.5989 0 1.1489-.1978 1.5977-.525a13.1287 13.1287 0 0 0 3.0284.5c-.9239 1.5828-2.6545 2.6499-4.626 2.6499H5.324c-2.9363 0-5.3237-2.3545-5.3237-5.25V5.2496ZM5.324 6.562c0-.7216.6-1.3125 1.3318-1.3125h5.3249c.7318 0 1.3307.591 1.3307 1.3125 0 .7216-.5989 1.3125-1.3307 1.3125H6.6559c-.7318 0-1.3318-.591-1.3318-1.3125Zm0 5.2499c0-.7216.6-1.3125 1.3318-1.3125h5.3249c.7318 0 1.3307.591 1.3307 1.3125 0 .7216-.5989 1.3125-1.3307 1.3125H6.6559c-.7318 0-1.3318-.591-1.3318-1.3125Zm5.35 13.0179c-.008-1.559.9897-2.567 2.0385-3.109.6068-.3114 1.2807-.508 1.9466-.6148v-1.1238c0-.7216.5988-1.3125 1.3317-1.3125.7319 0 1.3307.5909 1.3307 1.3125h-.017v1.1159c.6909.0897 1.3567.2215 1.9715.3681.0841.017.167.05.242.083-.3079.8034-.5488 1.6397-.6908 2.5102-.05-.0091-.108-.017-.158-.025-.9318-.2137-1.8466-.3773-2.7034-.3944-.7238-.008-1.4897.1398-2.0226.4194-.5068.2625-.6148.5159-.6148.7704 0 .1227.016.2954.5489.5738.5818.3035 1.3977.525 2.4454.8046.017 0 .025.008.042.008.7068.1886 1.5386.4102 2.2965.7215a12.8995 12.8995 0 0 0 1.6807 5.734c-.292.2954-.633.5409-.9909.7386-.6238.3364-1.3227.5409-2.0295.6477v1.108c0 .7215-.5988 1.3113-1.3307 1.3113-.7329 0-1.3317-.5898-1.3317-1.3114v-1.1658c-.9068-.1557-1.7636-.4091-2.5204-.6398-.175-.0568-.35-.1057-.516-.1557-.7079-.2045-1.1067-.9352-.8988-1.6318.208-.6977.9489-1.0908 1.6557-.8863.2.058.3988.1148.5909.1727 1.1318.3364 2.0635.6148 3.0613.6477.7818.025 1.5397-.1318 2.0306-.4022.4488-.2455.5988-.508.5898-.8523 0-.1977-.066-.4023-.5819-.6897-.5738-.3194-1.3976-.5489-2.4294-.8205-.042-.008-.092-.0238-.133-.033-.9148-.2454-2.0136-.5329-2.8783-.992-.9568-.509-1.9386-1.3863-1.9477-2.8794l-.008-.008Zm7.9622-22.1643v9.1464c0 .7216.5989 1.3125 1.3307 1.3125h9.277c-.059-.2296-.175-.4432-.35-.6068-3.2112-3.1659-6.4305-6.3408-9.6418-9.5067a1.318 1.318 0 0 0-.6159-.3454Zm2.7454 23.5836c0-5.7999 4.767-10.4998 10.6487-10.4998 5.8817 0 10.6498 4.7 10.6498 10.4998 0 2.4283-.8318 4.659-2.2386 6.4385 2.3874 2.3545 4.784 4.717 7.1714 7.0714.516.508.516 1.3455 0 1.8534-.5159.509-1.3636.509-1.8795 0-2.3886-2.3534-4.784-4.7158-7.1714-7.0703-1.8057 1.3784-4.0693 2.2068-6.5317 2.2068-5.8817 0-10.6487-4.701-10.6487-10.4998Zm2.6625 0c0 2.8136 1.5215 5.4135 3.993 6.8169 2.4705 1.4022 5.5159 1.4022 7.9874 0 2.4704-1.4034 3.9931-4.0033 3.9931-6.817 0-2.8135-1.5227-5.4134-3.993-6.8168-2.4716-1.4023-5.517-1.4023-7.9874 0-2.4716 1.4034-3.9931 4.0033-3.9931 6.8169Z',
    ],
};
var fileInvoiceDollarMagnifyingGlassRegular = {
    prefix: 'd3x',
    iconName: 'file-invoice-dollar-magnifying-glass-regular',
    icon: [
        48,
        43,
        [],
        null,
        'M0 5.3116c0-2.9297 2.3915-5.312 5.3327-5.312h13.7911c1.416 0 2.7748.5566 3.7743 1.5526 2.5165 2.5063 5.025 5.0045 7.5405 7.5107 1.0006.996 1.5583 2.349 1.5583 3.7593v.4567c-1.3908 0-2.7334.2158-3.9992.606v-.606h-6.666c-1.4746 0-2.6669-1.1865-2.6669-2.6565V3.9839H5.3327c-.7333 0-1.3335.5967-1.3335 1.3277v31.8683c0 .731.6002 1.3289 1.3335 1.3289h20.8405c1.5583.755 3.2911 1.2106 5.1157 1.3105-.9169 1.5939-2.6497 2.6726-4.6246 2.6726H5.3327C2.3915 42.4919 0 40.1096 0 37.1799V5.3116Zm6.6661 3.983c0-.7297.6002-1.3276 1.3335-1.3276h6.666c.7333 0 1.3335.5979 1.3335 1.3277 0 .731-.6002 1.3277-1.3335 1.3277h-6.666c-.7333 0-1.3335-.5967-1.3335-1.3277Zm0 5.312c0-.731.6002-1.3277 1.3335-1.3277h6.666c.7333 0 1.3335.5968 1.3335 1.3278 0 .7298-.6002 1.3277-1.3335 1.3277h-6.666c-.7333 0-1.3335-.598-1.3335-1.3277Zm3.6664 9.8425c-.008-1.7431 1.0994-2.855 2.225-3.4438.5578-.2903 1.166-.49 1.7742-.6059v-.8469c0-.9122.7505-1.6593 1.6674-1.6593.9157 0 1.6662.747 1.6662 1.6593v.8389a19.0994 19.0994 0 0 1 1.7167.3397c.1916.0413.366.125.5244.2249-.4498.9616-.7826 1.983-.9995 3.0536-.0998-.008-.1996-.016-.2914-.0413-.917-.2158-1.8166-.3741-2.6417-.3902-.684-.008-1.392.1331-1.867.3821-.4327.2319-.4418.389-.4418.4889v.0333c.0252.0333.117.133.3672.2662.5416.2903 1.3162.498 2.3834.7884.016.008.0413.008.0585.016.6656.1745 1.4494.3902 2.1907.6886v.3155c0 2.2997.584 4.4651 1.6169 6.3494-.2422.2077-.5003.3821-.7666.5313-.5749.3156-1.2164.5221-1.8578.6472v.8377c0 .9134-.7505 1.6605-1.6674 1.6605-.9158 0-1.6662-.747-1.6662-1.6605v-.9215c-.8332-.1572-1.6078-.3982-2.2917-.6047-.1744-.0586-.35-.1079-.5083-.1584-.8825-.257-1.3828-1.1865-1.1246-2.0667.2582-.879 1.1911-1.377 2.0747-1.12.2089.0585.4085.124.6002.1825 1.1326.3408 2.0162.6059 2.9744.6391.7413.0253 1.4333-.125 1.867-.3649.3661-.2077.4246-.374.4166-.5726v-.0092c0-.0413.016-.1652-.4165-.3982-.5337-.2983-1.3082-.5221-2.3582-.8044-.0505-.0172-.0918-.0253-.1423-.0413-.9077-.241-2.0415-.5485-2.9492-1.0294-1.0167-.5393-2.1172-1.5273-2.1333-3.2039Zm10.9992 2.108c0-5.8673 4.7749-10.6227 10.6652-10.6227 5.8915 0 10.6664 4.7554 10.6664 10.6227 0 2.216-.684 4.2666-1.8418 5.9672 2.1998 2.1907 4.3916 4.3733 6.5915 6.5651.7826.7803.7826 2.0415 0 2.8126-.7838.7723-2.0507.7804-2.8253 0l-6.5914-6.5639-.008.008c-1.7076 1.1533-3.7663 1.8338-5.9914 1.8338-5.8903 0-10.6652-4.7554-10.6652-10.6228Zm3.9992 0c0 2.3743 1.2668 4.565 3.3336 5.7515a6.6866 6.6866 0 0 0 6.666 0c2.0667-1.1866 3.3325-3.3772 3.3325-5.7515a6.6271 6.6271 0 0 0-3.3325-5.7514 6.6866 6.6866 0 0 0-6.666 0c-2.0668 1.1865-3.3336 3.3783-3.3336 5.7514Z',
    ],
};
var legionelaLight = {
    prefix: 'd3x',
    iconName: 'legionela-light',
    icon: [
        48,
        44,
        [],
        null,
        'M15.6416 40.5636c-7.162 0-12.897-5.6973-12.897-12.7164 0-.837.3646-2.4891 1.3354-4.8947v-.0035c.9284-2.305 2.242-4.9061 3.7547-7.5953 2.5406-4.5117 5.5122-9.02 7.807-12.3426 2.2935 3.3226 5.265 7.8286 7.8068 12.3426.0869.1566.1749.3133.2607.4688.7409-.5397 1.539-1.0039 2.3816-1.388l-.2641-.4723C23.1375 9.1876 19.9955 4.446 17.647 1.0594c-.9799-1.4132-3.0345-1.4132-4.0144 0C11.2831 4.446 8.1412 9.191 5.452 13.9622c-1.5413 2.7441-2.9224 5.4607-3.9126 7.928C.5378 24.3738.0004 26.4398.0004 27.845c0 8.6438 7.0466 15.5097 15.6412 15.5097 4.0635 0 7.7817-1.5367 10.5727-4.0704a13.535 13.535 0 0 1-2.5497-1.4726c-2.1953 1.723-4.9805 2.752-8.023 2.752Zm31.957.247L40.25 33.4622c1.427-1.8385 2.313-4.1218 2.313-6.6303 0-5.998-4.8626-10.8608-10.8607-10.8608-5.9992 0-10.8608 4.8627-10.8608 10.8608 0 5.9992 4.8616 10.8608 10.8608 10.8608 2.5074 0 4.7884-.8827 6.6269-2.313l7.354 7.3438c.2688.2652.606.4024.9548.4024.3487 0 .694-.1315.9604-.3979.5351-.526.5351-1.4589 0-1.9174Zm-15.894-5.8288c-4.4922 0-8.1464-3.6554-8.1464-8.1465 0-4.4922 3.6542-8.1476 8.1465-8.1476 4.4922 0 8.1476 3.6554 8.1476 8.1476 0 4.4911-3.6577 8.1465-8.1476 8.1465Zm4.5118-11.1684-.3327.0995a2.0082 2.0082 0 0 0-.2059-.3019l-.216-.2252c-.039-.0332-.0744-.0663-.1155-.096a2.0358 2.0358 0 0 0-.3076-.1921l.088-.3327c.0606-.2299-.08-.4665-.3098-.526a.403.403 0 0 0-.1098-.0148.432.432 0 0 0-.4173.3212l-.088.3293h-.0195c-.0514-.0023-.1783-.0103-.3567.0172l-.2961.0686a2.1933 2.1933 0 0 0-.1372.0491 1.9173 1.9173 0 0 0-.3316.167l-.2413-.2436c-.096-.096-.3761-.2332-.6094-.0057a.4223.4223 0 0 0-.1234.2275.4319.4319 0 0 0 .1154.382l.2379.2435-.1864.3487-.0903.2744-.0332.151c-.016.096-.0434.1897-.0709.2823l-.0194.0606-.2218-.104c-.08-.0355-.1647-.0492-.247-.0355-.0114.0023-.0229.0023-.0332.0058a.4255.4255 0 0 0-.2938.2355.4331.4331 0 0 0 .016.3979.4088.4088 0 0 0 .1898.1726l.2252.1075-.2332.2984-.3327.319-.2881.2081-.1007-.2275a.4354.4354 0 0 0-.1623-.1864.4406.4406 0 0 0-.4093-.0331.4354.4354 0 0 0-.2436.2824.1123.1123 0 0 0-.0057.0331.4317.4317 0 0 0 .0297.2527l.0995.2218-.0857.0252c-.063.0194-.1258.0377-.1921.0491l-.2276.0526-.2526.0926a2.3357 2.3357 0 0 0-.351.2036l-.2573-.2333a.4376.4376 0 0 0-.3567-.104c-.0938.0137-.1818.0594-.2527.1372-.1578.1749-.1452.4493.0297.6082l.255.231-.151.3236-.0823.3098c-.008.0492-.016.0972-.0217.1464l-.0057.3613-.3316.096c-.2275.0686-.359.3076-.2904.5351a.4326.4326 0 0 0 .5351.2916l.3316-.0995c.0606.1098.1315.2115.208.3053l.215.2218a2.83 2.83 0 0 0 .1144.096c.096.0743.2012.1372.3075.1921l-.0846.3293a.4454.4454 0 0 0-.0149.1098c0 .1966.135.3693.3248.4173a.4263.4263 0 0 0 .3292-.0469.415.415 0 0 0 .1978-.2607l.0847-.3258h.0194c.0937.0022.2138 0 .3979-.0275l.3899-.08c.1097-.0275.2332-.0572.3727-.0983l.096.271c.0824.2229.3293.3407.5523.2583a.4305.4305 0 0 0 .1452-.088.4276.4276 0 0 0 .1315-.2253v-.0022a.4258.4258 0 0 0-.0137-.2333l-.096-.2664.431-.2.2744-.1487c.12-.0686.2412-.143.3567-.2195l.1726.2332a.4337.4337 0 0 0 .1727.135h.0034a.4511.4511 0 0 0 .2744.0216.465.465 0 0 0 .1555-.0743.4323.4323 0 0 0 .1132-.1235.4248.4248 0 0 0 .0606-.2606v-.0058a.414.414 0 0 0-.0823-.2138l-.1704-.2252.351-.3179.2196-.2218a7.2183 7.2183 0 0 0 .2767-.3133l.2332.1704a.4174.4174 0 0 0 .1978.0766.4407.4407 0 0 0 .2744-.0572.5308.5308 0 0 0 .127-.1154.4692.4692 0 0 0 .0708-.1487.4503.4503 0 0 0-.0194-.2687v-.0022c-.0275-.072-.0766-.135-.143-.1841l-.2298-.1681.239-.4025.1475-.2835.1704-.375.272.0926c.071.0251.1419.0274.2139.0171a.4232.4232 0 0 0 .247-.1406.4217.4217 0 0 0 .0846-.1452.4162.4162 0 0 0 .024-.1395.4308.4308 0 0 0-.2927-.407l-.2721-.0927.1234-.5019.047-.2664c.017-.1315.017-.2584.008-.3842v-.0251l.3292-.096a.43.43 0 0 0 .2561-.2059c.0549-.096.0686-.2138.0355-.3293-.0744-.2435-.327-.3487-.5351-.288Zm-1.7265 1.1308c-.0274.1624-.0686.335-.1235.5294a1.6307 1.6307 0 0 0-.0549.3327 1.7502 1.7502 0 0 0-.1566.2881c-.1143.279-.2607.5488-.4253.8061a2.061 2.061 0 0 0-.151.2961 1.7595 1.7595 0 0 0-.2378.2333c-.2012.239-.415.4585-.6448.6505a1.5435 1.5435 0 0 0-.2287.2367 1.6768 1.6768 0 0 0-.3019.1555 5.52 5.52 0 0 1-.806.4345 1.6857 1.6857 0 0 0-.2859.1555 1.6062 1.6062 0 0 0-.3373.0606c-.3098.088-.4859.1235-.5762.1372-.0663.008-.0823.008-.0903.0057h-.072c-.0275-.0137-.055-.0297-.0824-.0434a.3613.3613 0 0 1-.1532-.151 1.6647 1.6647 0 0 1-.0354-.0606c0-.0274-.0035-.0548-.0058-.0823a.3498.3498 0 0 1 .0435-.1978l.0412-.0743a1.3238 1.3238 0 0 0 .0663-.0435c.0663-.0468.1154-.0628.1509-.0686l.4368-.1074a1.6656 1.6656 0 0 0 .6002-.3042 1.6321 1.6321 0 0 0 .6449-.239 4.5276 4.5276 0 0 0 1.3388-1.2976 1.6375 1.6375 0 0 0 .2562-.6426 1.6408 1.6408 0 0 0 .3155-.5866c.0012-.0068.0023-.0137.0046-.0194.0229-.0743.096-.2995.1326-.5214.0046-.0274.0183-.072.0595-.1314a1.012 1.012 0 0 0 .0469-.0744c.0217-.0137.0468-.0274.0686-.0411.0663-.0412.1154-.0526.1406-.055.008 0 .0137-.0033.0183-.0033l.1269.0057c.0274.0137.0515.0309.0789.0446a.3396.3396 0 0 1 .1544.1452c.0137.024.0274.0491.0411.0709 0 .0194 0 .0354.0023.0548.0057.0686.0034.0995 0 .1018Z',
    ],
};
var legionelaRegular = {
    prefix: 'd3x',
    iconName: 'legionela-regular',
    icon: [
        48,
        45,
        [],
        null,
        'M26.581 14.8982c-2.8677-5.2395-6.33-10.4779-8.5479-13.6985-1.1027-1.5996-3.3982-1.5996-4.501 0C9.174 7.532 0 21.661 0 28.2707c0 8.8581 7.0662 15.9738 15.7826 15.9738 4.3774 0 8.3388-1.794 11.1974-4.7022a13.3933 13.3933 0 0 1-3.8096-2.3505c-1.9965 1.6997-4.573 2.7192-7.3878 2.7192-6.34 0-11.4783-5.1743-11.4783-11.6402 0-.7588.327-2.2753 1.2005-4.4965.8352-2.1313 2.0234-4.6235 3.3915-7.0348 2.2325-4.0637 4.8416-8.1172 6.8863-11.1726 2.0448 3.0554 4.6539 7.109 6.8875 11.1726.099.1743.1979.3496.2945.525a13.3995 13.3995 0 0 1 3.6163-2.3663Zm20.7714 24.8352-6.6008-6.6008c1.2365-1.7087 1.92-3.7692 1.92-5.9972 0-5.8971-4.7809-10.6791-10.6791-10.6791-5.8972 0-10.6792 4.782-10.6792 10.6791 0 5.8983 4.782 10.6791 10.6792 10.6791 2.2257 0 4.2873-.6834 5.9994-1.848l6.6008 6.602c.39.3945.8993.5856 1.4164.5856.5171 0 1.024-.1956 1.4153-.5857.779-.7823.779-2.0503-.072-2.835Zm-15.36-5.923c-3.6803 0-6.6738-2.9946-6.6738-6.675 0-3.6792 2.9935-6.6739 6.6739-6.6739 3.6803 0 6.675 2.9947 6.675 6.674 0 3.6803-2.9958 6.675-6.675 6.675Zm3.3556-8.504.2742-.0809c.19-.0562.3013-.2585.245-.4496-.0617-.208-.2764-.2957-.4518-.2428l-.2765.082a1.6733 1.6733 0 0 0-.7082-.6823l.073-.2788c.0506-.1933-.0663-.39-.2608-.4406-.2102-.054-.3957.082-.4417.2585l-.072.2743h-.0112c-.0855-.0056-.4913-.0304-.9454.2507l-.2001-.2024c-.0764-.0787-.3136-.199-.5126-.0067a.358.358 0 0 0-.1102.2585c0 .0944.0371.1844.1023.2518l.2001.2046c-.127.1911-.2203.408-.2608.6486-.0135.081-.037.1585-.0607.2361-.0056.0169-.01.0337-.0157.0517l-.1855-.0877c-.1787-.0832-.3957-.0078-.4811.1731-.0854.1799-.0068.3957.1742.48l.1889.0888a2.4137 2.4137 0 0 1-.715.6925l-.0843-.1888a.365.365 0 0 0-.4777-.1844c-.1821.0798-.2664.2945-.1866.4766l.0832.1855-.0709.0202c-.0528.0158-.1056.0315-.1618.0405-.262.0438-.4958.1495-.6981.2934l-.2136-.1922c-.1428-.1293-.3743-.1293-.5137.0258a.3633.3633 0 0 0-.0933.2417.362.362 0 0 0 .1191.2676l.2136.1933a1.66 1.66 0 0 0-.217.9566l-.2776.082a.3602.3602 0 0 0-.2585.3452c0 .0348.0045.0697.0146.1034.0562.1911.2585.3002.4507.244l.2766-.0821a1.683 1.683 0 0 0 .7104.6823l-.073.2766a.3622.3622 0 0 0 .2607.4406c.1945.0506.3924-.0663.4418-.2585l.0708-.271c.1518.0057.3924-.0022.9904-.173l.081.227c.0674.1877.2753.2867.463.2192a.3611.3611 0 0 0 .2204-.462l-.0787-.2226c.3114-.1326.607-.2945.888-.4766l.144.1911a.3658.3658 0 0 0 .508.0742.3604.3604 0 0 0 .145-.2889.3593.3593 0 0 0-.0719-.217l-.1416-.19a5.8471 5.8471 0 0 0 .7093-.716l.1934.1416c.1618.118.3889.0821.507-.0786a.3622.3622 0 0 0 .0696-.2136.36.36 0 0 0-.1484-.2912l-.1933-.1405c.181-.281.3384-.5778.4687-.8903l.2249.0776c.191.0652.3968-.0349.462-.2237a.3505.3505 0 0 0 .0202-.117.3617.3617 0 0 0-.245-.3417l-.2282-.0787c.054-.1922.1011-.3878.1349-.589.0213-.1349.0213-.2675.0135-.398Z',
    ],
};
var manualDeliveryLight = {
    prefix: 'd3x',
    iconName: 'manual-delivery-light',
    icon: [
        42,
        48,
        [],
        null,
        'M29.7933 10.2199 21.1076 1.533C20.1208.5495 18.8 0 17.4131 0H5.2197C2.3362 0 0 2.3363 0 5.2199l.001 30c0 2.8834 2.3363 5.2197 5.2198 5.2197h13.256c.3913 2.1429 1.4176 3.8648 2.332 4.934l.1582.1858v1.122c0 .7285.59 1.3186 1.3186 1.3186s1.3187-.59 1.3187-1.3186v-2.2418l-.423-.3912c-.6011-.5571-1.5726-1.866-2.0077-3.6088h2.3549c-.3582-.767-.6593-1.644-.8297-2.6099H5.2198c-1.4385 0-2.61-1.1714-2.61-2.6099v-30c0-1.4384 1.1715-2.6098 2.61-2.6098h10.4395v9.134c0 2.1616 1.7538 3.9154 3.9154 3.9154h9.134v8.0692l2.61 3.1901v-4.3494l6.5208 8.2494a5.5351 5.5351 0 0 1 1.1934 3.4352v12.4275c0 .7285.59 1.3186 1.3187 1.3186.7285 0 1.3186-.59 1.3186-1.3186V34.2539a8.1787 8.1787 0 0 0-1.7615-5.0703l-8.59-10.8681v-4.41c0-1.3856-.5462-2.7076-1.5254-3.6856Zm-5.5582 24.501-.034.2627c-.4495 3.4044 1.2944 6.0857 2.3538 7.1088.523.5054 1.3582.4912 1.8648-.032.5055-.524.4912-1.3593-.0319-1.8647-.7098-.6858-2.0879-2.8374-1.4593-5.478l.1418-.5935-4.6253-6.2934c-.4978-.678-.3923-1.6241.2428-2.1747.6869-.5967 1.7308-.5066 2.3066.1967l7.2154 8.8187c.4616.5637 1.2923.6461 1.856.1846.5638-.4604.6462-1.2912.1858-1.855l-7.2165-8.8186c-1.5165-1.8539-4.266-2.089-6.0758-.5187-1.6726 1.4516-1.9517 3.944-.6396 5.7286l3.9154 5.3285Zm-8.644-6.0395h-6.457c-.7177 0-1.3045.5868-1.3045 1.3044 0 .7187.5868 1.3055 1.3044 1.3055h6.4571c.721 0 1.3044-.5846 1.3044-1.3055 0-.7198-.5868-1.3044-1.3044-1.3044Zm0-5.2198h-6.457c-.7177 0-1.3045.5868-1.3045 1.3044s.5868 1.3055 1.3044 1.3055h6.4571c.721 0 1.3044-.5846 1.3044-1.3055 0-.7198-.5868-1.3044-1.3044-1.3044Zm-7.7615-3.9154c0 .7187.5868 1.3055 1.3044 1.3055h13.0505c.7176 0 1.3044-.5868 1.3044-1.3055 0-.7176-.5868-1.3044-1.3044-1.3044H9.134c-.7176 0-1.3044.5868-1.3044 1.3044Zm10.4396-16.767c.3648.1286.7066.3143.989.5956l8.6857 8.6857c.2835.2791.4714.622.5934.989h-8.9626c-.7187 0-1.3055-.5868-1.3055-1.3055V2.7792Z',
    ],
};
var manualDeliveryRegular = {
    prefix: 'd3x',
    iconName: 'manual-delivery-regular',
    icon: [
        42,
        48,
        [],
        null,
        'm29.7117 7.5946-6.0701-6.0701C22.6596.548 21.3348 0 19.9594 0H5.2054C2.3298 0 0 2.33 0 5.2056l.001 29.9178c0 2.8745 2.33 5.2054 5.2055 5.2054h11.3403c.4636 2.0833 1.4784 3.7557 2.3901 4.822v.8767c0 1.0893.8833 1.9726 1.9726 1.9726 1.0894 0 1.9726-.8833 1.9726-1.9726v-2.5228l-.6323-.5852c-.4438-.412-1.155-1.3392-1.6033-2.5907h1.5255c-.468-1.132-.8077-2.4482-.8635-3.9046H5.2076c-.7189 0-1.3019-.582-1.3019-1.3008l-.001-29.9069c0-.7189.5818-1.3019 1.3007-1.3019h13.0137v6.4964c0 1.4368 1.166 2.6028 2.6028 2.6028h6.434v3.325a1.981 1.981 0 0 0 0 .7276v3.7129c.6838.4054 1.3095.9358 1.8432 1.5879l2.1337 2.6082v-2.514l5.1978 6.5765a4.8644 4.8644 0 0 1 1.0488 3.018v13.9716c0 1.0893.8833 1.9726 1.9726 1.9726s1.9726-.8833 1.9726-1.9726V32.056a8.8118 8.8118 0 0 0-1.898-5.4641l-8.2938-10.4932v-4.8252a5.21 5.21 0 0 0-1.521-3.6789Zm-2.1896 29.6997c.2905 1.1288.8844 1.9704 1.245 2.3178.7824.7573.8044 2.0066.0471 2.7902-.7573.7824-2.0055.8043-2.789.047-1.1595-1.12-3.0258-3.99-2.5425-7.647l-3.749-5.1003c-1.5102-2.0559-1.1902-4.9282.7375-6.5995 2.0504-1.7786 5.1474-1.5463 6.9118.4932.0296.034.058.069.0876.1041l7.1957 8.7945c.6904.8428.5654 2.0855-.2773 2.776-.8427.6892-2.0855.5654-2.7759-.2773l-7.1956-8.7946c-.3397-.4153-.9556-.468-1.3611-.1161a.9524.9524 0 0 0-.1436 1.2833l4.7935 6.5205-.1842 3.4082ZM14.1085 23.896H9.76c-1.073 0-1.9518.8778-1.9518 1.9518 0 1.074.8745 1.9517 1.9518 1.9517h4.3485c1.0772 0 1.9517-.8734 1.9517-1.9517 0-1.0773-.8701-1.9518-1.9517-1.9518Zm-6.3003-5.8565c0 1.0817.8789 1.9518 1.9518 1.9518h11.7128a1.947 1.947 0 0 0 1.9518-1.9518 1.947 1.947 0 0 0-1.9518-1.9517H9.76c-1.073 0-1.9518.8789-1.9518 1.9517Z',
    ],
};
var smokeAlarmLight = {
    prefix: 'd3x',
    iconName: 'smoke-alarm-light',
    icon: [
        48,
        48,
        [],
        null,
        'M21.4675 39.0963c-1.3225-.6638-2.1475-1.995-2.1475-3.48v-1.1338c0-.8225-.6687-1.485-1.485-1.485-.8163 0-1.485.6625-1.485 1.485v1.1338c0 2.6124 1.455 4.9612 3.7862 6.1312 1.3263.6662 2.1538 1.995 2.1538 3.4775v1.1338c0 .8162.6662 1.485 1.485 1.485.8163 0 1.485-.6663 1.485-1.485V45.225c-.0025-2.61-1.455-4.9588-3.7925-6.1287Zm11.875 0c-1.3237-.6638-2.1487-1.995-2.1487-3.48v-1.1338c0-.8225-.6688-1.485-1.485-1.485-.8163 0-1.485.6625-1.485 1.485v1.1338c0 2.6124 1.455 4.9612 3.7862 6.1312 1.3263.6662 2.1538 1.995 2.1538 3.4775v1.1338c0 .8162.6662 1.485 1.485 1.485.8162 0 1.485-.6663 1.485-1.485V45.225c-.0025-2.61-1.455-4.9588-3.7913-6.1287ZM45.3 0H2.7C1.2087 0 0 1.2087 0 2.7V9c0 3.15 2.5525 5.7 5.7 5.7h.15v2.6337c0 1.05.3687 2.07 1.0412 2.88l4.05 4.8663A4.5083 4.5083 0 0 0 14.4 26.7h18.9c1.335 0 2.6038-.595 3.4588-1.62l4.05-4.8663a4.5045 4.5045 0 0 0 1.0412-2.88V14.7h.45c3.1475 0 5.7-2.55 5.7-5.7V2.7C48 1.2062 46.7913 0 45.3 0Zm-6.45 17.3313c0 .3512-.1225.69-.3475.96l-.0063.0062H33.15c-.8275 0-1.5.6713-1.5 1.5 0 .8275.6725 1.5 1.5 1.5H36l-1.5475 1.86a1.5074 1.5074 0 0 1-1.1525.54H14.4a1.5074 1.5074 0 0 1-1.1525-.54l-2.5463-3.06H18.45c.8275 0 1.5-.6725 1.5-1.5 0-.8288-.6725-1.5-1.5-1.5h-9.6v-2.4h30v2.6338ZM45 9c0 1.4912-1.2087 2.7-2.7 2.7H5.7C4.2088 11.7 3 10.4912 3 9V3h42v6ZM22.0937 6.8575c.19-.8062.9988-1.3062 1.8038-1.115.8062.19 1.3062.9988 1.115 1.8037-.19.8063-.9987 1.305-1.8038 1.115-.8062-.19-1.3062-.9987-1.115-1.8037Z',
    ],
};
var smokeAlarmRegular = {
    prefix: 'd3x',
    iconName: 'smoke-alarm-regular',
    icon: [
        48,
        48,
        [],
        null,
        'M19.9285 40.3103c1.2327.6387 1.9975 2.0678 1.9975 3.526 0 1.3236 1.0691 2.3867 2.3855 2.3867 1.3176 0 2.3866-1.177 2.3866-2.4158 0-3.132-1.6715-6.0582-4.366-7.6388-1.2315-.7212-1.9964-2.0593-1.9964-3.4933 0-1.3236-1.069-2.4121-2.3854-2.4121-1.3176 0-2.3867 1.0885-2.3867 2.4121 0 3.1321 1.6703 6.0582 4.3649 7.6352Zm13.1309 3.526c0-1.4582-.7649-2.8873-1.9976-3.526-2.6946-1.577-4.3637-4.503-4.3637-7.6352 0-1.3236 1.068-2.4157 2.3855-2.4157 1.3164 0 2.3855 1.092 2.3855 2.4157 0 1.434.766 2.7721 1.9975 3.4933 2.6934 1.5806 4.3649 4.5067 4.3649 7.6388 0 1.2388-1.0691 2.4134-2.3855 2.4134-1.3176 0-2.3866-1.0607-2.3866-2.3843ZM44.6544.1454 3.3455 0C1.4982 0 0 1.5042 0 3.3612v6.1406c0 3.3236 2.5673 6.0448 5.8182 6.2703v2.434c0 1.1963.417 2.3539 1.1782 3.2727l3.9272 4.7442a5.084 5.084 0 0 0 3.9127 1.8436h18.3273a5.0803 5.0803 0 0 0 3.914-1.8436l3.9272-4.7442c.7612-.9188 1.177-2.0764 1.177-3.2728v-2.2872c3.2509-.2255 5.8181-2.9467 5.8181-6.2715V3.5079c0-1.857-1.497-3.3612-3.3442-3.3625h-.0012ZM10.1819 18.206v-1.8339h27.6363v1.834a.7364.7364 0 0 1-.0933.3587h-4.7067c-.8024 0-1.4545.6546-1.4545 1.4618 0 .8073.6521 1.4619 1.4545 1.4619H35.32l-1.5976 1.9297a.728.728 0 0 1-.5588.263H14.8363a.728.728 0 0 1-.5587-.263L12.68 21.4885h8.9927c.8036 0 1.4545-.6546 1.4545-1.4619 0-.8072-.6509-1.4618-1.4545-1.4618H10.2751a.7398.7398 0 0 1-.0933-.3588ZM4.3636 9.5018v-5.114l39.2727.1407v5.1188c0 1.0497-.846 1.9006-1.8897 1.9006l-35.492-.1467c-1.0437 0-1.891-.8497-1.891-1.8994ZM24 9.7939c.9648 0 1.7454-.7854 1.7454-1.754 0-.9696-.7806-1.755-1.7454-1.755-.9637 0-1.7455.7854-1.7455 1.755 0 .9686.7818 1.754 1.7455 1.754Z',
    ],
};
var userArrowRotateRightLight = {
    prefix: 'd3x',
    iconName: 'user-arrow-rotate-right-light',
    icon: [
        48,
        44,
        [],
        null,
        'M5.1733 12.4695C10.191 3.4232 21.5917.1572 30.6379 5.1737c6.2232 3.4505 9.7124 9.9239 9.649 16.5758l-4.5736-2.5364c-.6815-.3772-1.539-.1316-1.9162.5498-.3783.6802-.1328 1.539.5486 1.9161l7.4368 4.1237c.6802.3783 1.539.1316 1.9161-.5486l4.1249-7.4368c.3771-.6814.1316-1.539-.5498-1.9161-.6803-.3783-1.539-.1328-1.9162.5486L43.083 20.55c-.343-7.2088-4.2987-14.0828-11.0775-17.8423-10.4091-5.772-23.525-2.0136-29.297 8.3954-5.7732 10.408-2.0137 23.525 8.3942 29.297 10.408 5.772 23.525 2.0137 29.297-8.3954.3783-.6802.1316-1.539-.5487-1.9161-.6814-.3772-1.539-.1316-1.9173.5498-5.0166 9.0462-16.4172 12.3123-25.4635 7.2957C3.424 32.9176.1567 21.517 5.1732 12.4695Zm12.1514 1.431c0-2.3368 1.8938-4.2307 4.2294-4.2307 2.3356 0 4.2294 1.8939 4.2294 4.2306 0 2.3356-1.8938 4.2283-4.2294 4.2283-2.3356 0-4.2294-1.8927-4.2294-4.2283Zm4.2294-7.0491c-3.8934 0-7.049 3.1556-7.049 7.049 0 3.8923 3.1556 7.049 7.049 7.049 3.8922 0 7.049-3.1567 7.049-7.049 0-3.8934-3.1568-7.049-7.049-7.049ZM16.7208 22.21a1.4116 1.4116 0 0 1 1.5167.518l3.5175 4.7453 3.5186-4.7452a1.4098 1.4098 0 0 1 1.5156-.5181c4.0614 1.1478 7.0502 4.8591 7.0502 9.2777 0 1.3088-1.0668 2.3508-2.3591 2.3508H12.0297c-1.2912 0-2.3591-1.042-2.3591-2.3508 0-4.4186 2.9888-8.13 7.0502-9.2777Zm14.2825 8.809c-.1762-2.5777-1.801-4.7723-4.086-5.7744l-3.265 4.4045c-.9434 1.2723-2.8513 1.2723-3.7935 0l-3.266-4.4045c-2.284 1.0021-3.9088 3.1967-4.0862 5.7743h18.4967Z',
    ],
};
var userArrowRotateRightRegular = {
    prefix: 'd3x',
    iconName: 'user-arrow-rotate-right-regular',
    icon: [
        48,
        44,
        [],
        null,
        'M30.0058 6.3913c-8.3898-4.6523-18.962-1.6226-23.6143 6.766-4.6524 8.3899-1.6238 18.9621 6.766 23.6144 7.939 4.4027 17.8366 1.9256 22.8167-5.4642.6498-.9633 1.9564-1.219 2.921-.5692.9633.6497 1.2177 1.9563.568 2.9208-6.1885 9.184-18.4815 12.2635-28.346 6.7921C.6952 34.6722-3.0682 21.5377 2.712 11.117 8.491.6951 21.6243-3.0684 32.0462 2.7118c6.0844 3.3742 9.8976 9.2538 10.8693 15.634l1.1409-2.0557c.5633-1.0166 1.8438-1.3835 2.8593-.8202 1.0166.5634 1.3835 1.844.8202 2.8605l-3.9884 7.1922c-.5634 1.0154-1.844 1.3823-2.8606.819l-7.192-3.9884c-1.0155-.5634-1.3824-1.844-.819-2.8606.5633-1.0154 1.8438-1.3823 2.8605-.819l3.1788 1.7635c-.381-5.6915-3.5552-11.0764-8.9094-14.0458Zm-15.1204 8.0892c0-3.3978 2.8546-6.0855 6.2904-6.0855 3.4357 0 6.289 2.6877 6.289 6.0855 0 3.399-2.8533 6.0868-6.289 6.0868-3.4358 0-6.2904-2.6878-6.2904-6.0868Zm2.0558 7.7508a1.012 1.012 0 0 1 1.1042.3799l3.3327 4.6085 3.3328-4.6085a1.0127 1.0127 0 0 1 1.1042-.38c3.5386 1.0238 6.114 4.327 6.114 8.2278 0 1.0201-.8179 1.8758-1.8629 1.8758H12.6901c-1.0439 0-1.8617-.8557-1.8617-1.8758 0-3.9009 2.5753-7.204 6.1128-8.2277Z',
    ],
};
var userSwitchLight = {
    prefix: 'd3x',
    iconName: 'user-switch-light',
    icon: [
        48,
        40,
        [],
        null,
        'M25.8859 36.8505H2.6605c-.1107 0-.2038-.092-.2038-.2069-.001-5.9809 4.8669-10.8477 10.8478-10.8477h7.7776c1.471 0 2.858.3217 4.1324.8647.4976.212 1.0065.0745 1.4565-.3072.5875-.5875.4148-1.6002-.3476-1.9292-1.6716-.694-3.3763-1.085-5.234-1.085h-7.785C5.953 23.3392-.0092 29.2962 0 36.6509c.002 1.4585 1.1999 2.6563 2.6605 2.6563H25.89c.6558 0 1.1875-.5317 1.1875-1.1875v-.0817c-.0714-.6579-.5317-1.1875-1.1916-1.1875Zm8.242 1.7926c.24-.239.36-.5534.36-.8679 0-.3144-.123-.63-.361-.8678l-4.0455-4.0456h16.69c.6796 0 1.2289-.5523 1.2289-1.2288 0-.6755-.5493-1.2279-1.2289-1.2279h-16.69l4.0466-4.0455c.48-.479.4789-1.2568 0-1.7357-.479-.48-1.2568-.48-1.7368 0L26.25 30.765c-.48.479-.48 1.2568 0 1.7368l6.1412 6.1412c.479.48 1.2568.48 1.7368 0Zm5.2734-13.2807c0 .3145.12.629.36.868.48.4799 1.2578.4799 1.7368 0l6.1412-6.1413c.48-.48.48-1.2579 0-1.7368l-6.1412-6.1412c-.48-.48-1.2579-.48-1.7368 0-.479.479-.48 1.2568 0 1.7357l4.0466 4.0455h-16.69c-.6786 0-1.2279.5524-1.2279 1.2279 0 .6765.5493 1.2288 1.2278 1.2288h16.69l-4.0455 4.0456c-.2379.2379-.361.5534-.361.8678Zm-22.2737-5.7088c5.4275 0 9.8268-4.3993 9.8268-9.8268S22.5551 0 17.1276 0c-5.3582 0-9.7575 4.3993-9.7575 9.8268s4.3993 9.8268 9.7575 9.8268Zm0-17.197c4.0642 0 7.3701 3.306 7.3701 7.3702 0 4.0631-3.306 7.37-7.3701 7.37-4.0642 0-7.3701-3.3069-7.3701-7.37 0-4.0642 3.3783-7.3701 7.3701-7.3701Z',
    ],
};
var userSwitchRegular = {
    prefix: 'd3x',
    iconName: 'user-switch-regular',
    icon: [
        48,
        40,
        [],
        null,
        'M2.4388 39.0207H30.124l-3.6587-3.6577H3.7347c.6007-4.8098 4.7092-8.5363 9.6792-8.5363h7.3163c1.5845 0 3.0755.385 4.396 1.0628l2.6904-2.6904c-2.0547-1.2856-4.4812-2.03-7.0864-2.03H13.414C6.005 23.169 0 29.174 0 36.5818c0 1.3462 1.0926 2.4388 2.4388 2.4388Zm32.8338-.6757c-.7147.7147-1.872.7147-2.5866 0l-6.0975-6.0965c-.7137-.7147-.7137-1.872 0-2.5866l6.0975-6.0965c.7147-.7147 1.872-.7147 2.5866 0 .7147.7137.7147 1.871 0 2.5856l-2.98 2.9728h13.8781c1.0104 0 1.8288.8153 1.8288 1.8288 0 1.0136-.8184 1.83-1.8288 1.83h-13.878l2.98 3.0404c.3583.2896.5329.7548.5329 1.2271 0 .4724-.1746.9376-.533 1.295Zm3.5078-13.378c.7147.7148 1.872.7148 2.5867 0l6.0964-6.0974c.7147-.7137.7147-1.871 0-2.5867l-6.0964-6.0964c-.7147-.7147-1.872-.7147-2.5867 0-.7147.7146-.7147 1.872 0 2.5866l2.98 2.9717h-13.878c-1.0105 0-1.83.8164-1.83 1.83 0 1.0134.8195 1.8287 1.83 1.8287h13.878l-2.98 3.0406c-.3584.2896-.534.7547-.534 1.227 0 .4724.1756.9376.534 1.296ZM7.3164 9.7553C7.3164 4.3672 11.6846 0 17.0027 0c5.3192 0 9.7552 4.3672 9.7552 9.7552 0 5.3879-4.3672 9.7551-9.7552 9.7551-5.3869 0-9.6863-4.3672-9.6863-9.7551Zm3.6587 0c0 3.362 2.7345 6.0975 6.0964 6.0975 3.362 0 6.0975-2.7356 6.0975-6.0975 0-3.362-2.7355-6.0965-6.0975-6.0965-3.3619 0-6.0964 2.7345-6.0964 6.0965Z',
    ],
};

/**
 * Copied icon definitions from @fortawesome/pro-light-svg-icons.
 * Usage requires a valid FontAwesome Pro license.
 */
var faArrowRightArrowLeftLight = {
    prefix: 'fal',
    iconName: 'arrow-right-arrow-left',
    icon: [
        448,
        512,
        [8644, 'exchange'],
        'f0ec',
        'M443.3 139.3c6.2-6.2 6.2-16.4 0-22.6l-96-96c-6.2-6.2-16.4-6.2-22.6 0s-6.2 16.4 0 22.6L393.4 112 16 112c-8.8 0-16 7.2-16 16s7.2 16 16 16l377.4 0-68.7 68.7c-6.2 6.2-6.2 16.4 0 22.6s16.4 6.2 22.6 0l96-96zm-342.6 352c6.2 6.2 16.4 6.2 22.6 0s6.2-16.4 0-22.6L54.6 400 432 400c8.8 0 16-7.2 16-16s-7.2-16-16-16L54.6 368l68.7-68.7c6.2-6.2 6.2-16.4 0-22.6s-16.4-6.2-22.6 0l-96 96c-6.2 6.2-6.2 16.4 0 22.6l96 96z',
    ],
};
var faCalendarClockLight = {
    prefix: 'fal',
    iconName: 'calendar-clock',
    icon: [
        576,
        512,
        ['calendar-time'],
        'e0d2',
        'M112 0c8.8 0 16 7.2 16 16V64H320V16c0-8.8 7.2-16 16-16s16 7.2 16 16V64h32c35.3 0 64 28.7 64 64v32 32 .7c-5.3-.5-10.6-.7-16-.7s-10.7 .2-16 .7V192H32V448c0 17.7 14.3 32 32 32H296.2c10 12.1 21.7 22.9 34.6 32H64c-35.3 0-64-28.7-64-64V192 160 128C0 92.7 28.7 64 64 64H96V16c0-8.8 7.2-16 16-16zM384 96H64c-17.7 0-32 14.3-32 32v32H416V128c0-17.7-14.3-32-32-32zm48 384a112 112 0 1 0 0-224 112 112 0 1 0 0 224zm0-256a144 144 0 1 1 0 288 144 144 0 1 1 0-288zm0 64c8.8 0 16 7.2 16 16v48h32c8.8 0 16 7.2 16 16s-7.2 16-16 16H432c-8.8 0-16-7.2-16-16V304c0-8.8 7.2-16 16-16z',
    ],
};
var faDoorClosedLight = {
    prefix: 'fal',
    iconName: 'door-closed',
    icon: [
        576,
        512,
        [128682],
        'f52a',
        'M416 32c17.7 0 32 14.3 32 32V480H128V64c0-17.7 14.3-32 32-32H416zm64 448V64c0-35.3-28.7-64-64-64H160C124.7 0 96 28.7 96 64V480H16c-8.8 0-16 7.2-16 16s7.2 16 16 16H96h32H448h32 80c8.8 0 16-7.2 16-16s-7.2-16-16-16H480zM408 256a24 24 0 1 0 -48 0 24 24 0 1 0 48 0z',
    ],
};
var faEqualsLight = {
    prefix: 'fal',
    iconName: 'equals',
    icon: [
        448,
        512,
        [62764],
        '3d',
        'M32 144c-8.8 0-16 7.2-16 16s7.2 16 16 16H416c8.8 0 16-7.2 16-16s-7.2-16-16-16H32zm0 192c-8.8 0-16 7.2-16 16s7.2 16 16 16H416c8.8 0 16-7.2 16-16s-7.2-16-16-16H32z',
    ],
};
var faEuroSignLight = {
    prefix: 'fal',
    iconName: 'euro-sign',
    icon: [
        320,
        512,
        [8364, 'eur', 'euro'],
        'f153',
        'M34.3 224c-1.5 10.5-2.3 21.1-2.3 32s.8 21.5 2.3 32H16c-8.8 0-16 7.2-16 16s7.2 16 16 16H41.3C68.8 412.5 154.5 480 256 480h49c8.8 0 16-7.2 16-16s-7.2-16-16-16H256c-83.6 0-154.7-53.4-181.1-128H272c8.8 0 16-7.2 16-16s-7.2-16-16-16H66.7c-1.7-10.4-2.7-21.1-2.7-32s.9-21.6 2.7-32H272c8.8 0 16-7.2 16-16s-7.2-16-16-16H74.9C101.3 117.4 172.4 64 256 64h49c8.8 0 16-7.2 16-16s-7.2-16-16-16H256C154.5 32 68.8 99.5 41.3 192H16c-8.8 0-16 7.2-16 16s7.2 16 16 16H34.3z',
    ],
};
var faFileCircleInfoLight = {
    prefix: 'fal',
    iconName: 'file-circle-info',
    icon: [
        576,
        512,
        [58604],
        'e493',
        'M64 480H296.2c9.8 11.8 21 22.3 33.5 31.3c-3.2 .5-6.4 .7-9.7 .7H64c-35.3 0-64-28.7-64-64V64C0 28.7 28.7 0 64 0H220.1c12.7 0 24.9 5.1 33.9 14.1L369.9 129.9c9 9 14.1 21.2 14.1 33.9v34.7c-11.2 3.2-21.9 7.4-32 12.6V192H240c-26.5 0-48-21.5-48-48V32H64C46.3 32 32 46.3 32 64V448c0 17.7 14.3 32 32 32zM351.5 160c-.7-2.8-2.1-5.4-4.2-7.4L231.4 36.7c-2.1-2.1-4.6-3.5-7.4-4.2V144c0 8.8 7.2 16 16 16H351.5zM544 368a112 112 0 1 0 -224 0 112 112 0 1 0 224 0zm-256 0a144 144 0 1 1 288 0 144 144 0 1 1 -288 0zm144-32a24 24 0 1 1 0-48 24 24 0 1 1 0 48zm-32 96c0-8.8 7.2-16 16-16V384c-8.8 0-16-7.2-16-16s7.2-16 16-16h16c8.8 0 16 7.2 16 16v48h0c8.8 0 16 7.2 16 16s-7.2 16-16 16H432 416c-8.8 0-16-7.2-16-16z',
    ],
};
var faFileExportLight = {
    prefix: 'fal',
    iconName: 'file-export',
    icon: [
        576,
        512,
        ['arrow-right-from-file'],
        'f56e',
        'M352 448c0 17.7-14.3 32-32 32H64c-17.7 0-32-14.3-32-32V64c0-17.7 14.3-32 32-32H192V144c0 26.5 21.5 48 48 48H352v64h32V163.9c0-12.7-5.1-24.9-14.1-33.9L254.1 14.1c-9-9-21.2-14.1-33.9-14.1H64C28.7 0 0 28.7 0 64V448c0 35.3 28.7 64 64 64H320c35.3 0 64-28.7 64-64V368H352v80zm-.5-288H240c-8.8 0-16-7.2-16-16V32.5c2.8 .7 5.4 2.1 7.4 4.2L347.3 152.6c2.1 2.1 3.5 4.6 4.2 7.4zm123.8 52.7c-6.2-6.2-16.4-6.2-22.6 0s-6.2 16.4 0 22.6L521.4 304H208c-8.8 0-16 7.2-16 16s7.2 16 16 16H521.4l-68.7 68.7c-6.2 6.2-6.2 16.4 0 22.6s16.4 6.2 22.6 0l96-96c6.2-6.2 6.2-16.4 0-22.6l-96-96z',
    ],
};
var faFolderMagnifyingGlassLight = {
    prefix: 'fal',
    iconName: 'folder-magnifying-glass',
    icon: [
        512,
        512,
        ['folder-search'],
        'e18b',
        'M32 96c0-17.7 14.3-32 32-32H188.1c8.5 0 16.6 3.4 22.6 9.4l35.9 35.9c12 12 28.3 18.7 45.3 18.7H448c17.7 0 32 14.3 32 32V416c0 17.7-14.3 32-32 32H64c-17.7 0-32-14.3-32-32V96zM64 32C28.7 32 0 60.7 0 96V416c0 35.3 28.7 64 64 64H448c35.3 0 64-28.7 64-64V160c0-35.3-28.7-64-64-64H291.9c-8.5 0-16.6-3.4-22.6-9.4L233.4 50.7c-12-12-28.3-18.7-45.3-18.7H64zM240 208a64 64 0 1 1 0 128 64 64 0 1 1 0-128zm0 160c20.7 0 39.9-6.6 55.6-17.7l45.1 45.1c6.2 6.2 16.4 6.2 22.6 0s6.2-16.4 0-22.6l-45.1-45.1C329.4 311.9 336 292.7 336 272c0-53-43-96-96-96s-96 43-96 96s43 96 96 96z',
    ],
};
var faHouseBuildingLight = {
    prefix: 'fal',
    iconName: 'house-building',
    icon: [
        640,
        512,
        [],
        'e1b1',
        'M320 32H576c17.7 0 32 14.3 32 32V448c0 17.7-14.3 32-32 32H414.4c-2.4 11.8-7.4 22.7-14.4 32H576c35.3 0 64-28.7 64-64V64c0-35.3-28.7-64-64-64H320c-35.3 0-64 28.7-64 64V95.3l32 29.3V64c0-17.7 14.3-32 32-32zM496 208c-17.7 0-32 14.3-32 32v32c0 17.7 14.3 32 32 32h32c17.7 0 32-14.3 32-32V240c0-17.7-14.3-32-32-32H496zm0 32h32v32H496V240zM464 368v32c0 17.7 14.3 32 32 32h32c17.7 0 32-14.3 32-32V368c0-17.7-14.3-32-32-32H496c-17.7 0-32 14.3-32 32zm64 0v32H496V368h32zM368 80c-17.7 0-32 14.3-32 32v32c0 17.7 14.3 32 32 32h32c17.7 0 32-14.3 32-32V112c0-17.7-14.3-32-32-32H368zm0 32h32v32H368V112zm96 0v32c0 17.7 14.3 32 32 32h32c17.7 0 32-14.3 32-32V112c0-17.7-14.3-32-32-32H496c-17.7 0-32 14.3-32 32zm64 0v32H496V112h32zM32 284.2c0-9 3.8-17.5 10.4-23.6l128-117.3c12.2-11.2 31-11.2 43.2 0l128 117.3c6.6 6.1 10.4 14.6 10.4 23.6V448c0 17.7-14.3 32-32 32H64c-17.7 0-32-14.3-32-32V284.2zM0 448c0 35.3 28.7 64 64 64H320c35.3 0 64-28.7 64-64V284.2c0-17.9-7.5-35.1-20.8-47.2l-128-117.3c-24.5-22.4-62-22.4-86.5 0L20.8 237C7.5 249.1 0 266.2 0 284.2V448zM224 288v64H160V288h64zm-64-32c-17.7 0-32 14.3-32 32v64c0 17.7 14.3 32 32 32h64c17.7 0 32-14.3 32-32V288c0-17.7-14.3-32-32-32H160z',
    ],
};
var faLocationPinLight = {
    prefix: 'fal',
    iconName: 'location-pin',
    icon: [
        384,
        512,
        ['map-marker'],
        'f041',
        'M335.4 255.4c-10.9 25.9-26.2 54-43.6 82.1c-34.1 55.3-74.4 108.2-99.9 140c-25.4-31.8-65.8-84.7-99.9-140c-17.3-28.1-32.7-56.3-43.6-82.1C37.4 229 32 207.6 32 192c0-88.4 71.6-160 160-160s160 71.6 160 160c0 15.6-5.4 37-16.6 63.4zM215.7 499.2C267 435 384 279.4 384 192C384 86 298 0 192 0S0 86 0 192c0 87.4 117 243 168.3 307.2c12.3 15.3 35.1 15.3 47.4 0z',
    ],
};
var faMagnifyingGlassLight = {
    prefix: 'fal',
    iconName: 'magnifying-glass',
    icon: [
        512,
        512,
        [128269, 'search'],
        'f002',
        'M384 208A176 176 0 1 0 32 208a176 176 0 1 0 352 0zM343.3 366C307 397.2 259.7 416 208 416C93.1 416 0 322.9 0 208S93.1 0 208 0S416 93.1 416 208c0 51.7-18.8 99-50 135.3L507.3 484.7c6.2 6.2 6.2 16.4 0 22.6s-16.4 6.2-22.6 0L343.3 366z',
    ],
};
var faMinusLight = {
    prefix: 'fal',
    iconName: 'minus',
    icon: [
        448,
        512,
        [8211, 8722, 10134, 'subtract'],
        'f068',
        'M432 256c0 8.8-7.2 16-16 16L32 272c-8.8 0-16-7.2-16-16s7.2-16 16-16l384 0c8.8 0 16 7.2 16 16z',
    ],
};
var faPersonWalkingArrowLoopLeftLight = {
    prefix: 'fal',
    iconName: 'person-walking-arrow-loop-left',
    icon: [
        640,
        512,
        [],
        'e551',
        'M176 56a24 24 0 1 1 48 0 24 24 0 1 1 -48 0zm80 0A56 56 0 1 0 144 56a56 56 0 1 0 112 0zM117.6 172.3c1.3-.7 2.6-1.4 3.9-2L95 255.2c-5.7 18.2 0 38.1 14.4 50.6L209.9 393l30.7 107.4c2.4 8.5 11.3 13.4 19.8 11s13.4-11.3 11-19.8L240.7 384.2c-1.7-6-5.1-11.3-9.8-15.4L187.3 331l37.8-109.2 8.8 26.4c5.6 16.9 16.7 31.4 31.5 41.3l29.7 19.8c7.4 4.9 17.3 2.9 22.2-4.4s2.9-17.3-4.4-22.2l-29.7-19.8c-8.9-5.9-15.5-14.6-18.9-24.8l-17.1-51.4c-11.7-35-44.5-58.7-81.4-58.7c-22.1 0-43.9 5.6-63.4 16.2l-12.7 6.9c-35.3 19.3-61 52.4-70.7 91.5l-2.4 9.5c-2.1 8.6 3.1 17.3 11.6 19.4s17.3-3.1 19.4-11.6l2.4-9.6c7.6-30.4 27.5-56.2 55-71.2l12.7-6.9zm7.9 92.5l32.7-104.5c2.5-.2 5-.3 7.5-.3c15.9 0 30.5 7 40.6 18.4l-45 130-30.9-26.8c-4.8-4.2-6.7-10.8-4.8-16.9zm-6.4 88.9c-7.9-4-17.5-.7-21.5 7.2L66.9 422.5 4.7 484.7c-6.2 6.2-6.2 16.4 0 22.6s16.4 6.2 22.6 0l62.2-62.2c2.4-2.4 4.5-5.2 6-8.3l30.8-61.7c4-7.9 .7-17.5-7.2-21.5zm334.6 90.6c6.8 5.7 16.9 4.7 22.5-2s4.7-16.9-2-22.5L412.2 368l75.8 0c83.9 0 152-68.1 152-152s-68.1-152-152-152L336 64c-8.8 0-16 7.2-16 16s7.2 16 16 16l152 0c66.3 0 120 53.7 120 120s-53.7 120-120 120l-75.8 0 62.1-51.7c6.8-5.7 7.7-15.7 2-22.5s-15.7-7.7-22.5-2l-96 80c-3.6 3-5.8 7.5-5.8 12.3s2.1 9.3 5.8 12.3l96 80z',
    ],
};
var faPlusLight = {
    prefix: 'fal',
    iconName: 'plus',
    icon: [
        448,
        512,
        [10133, 61543, 'add'],
        '2b',
        'M240 64c0-8.8-7.2-16-16-16s-16 7.2-16 16V240H32c-8.8 0-16 7.2-16 16s7.2 16 16 16H208V448c0 8.8 7.2 16 16 16s16-7.2 16-16V272H416c8.8 0 16-7.2 16-16s-7.2-16-16-16H240V64z',
    ],
};
var faScrewdriverWrenchLight = {
    prefix: 'fal',
    iconName: 'screwdriver-wrench',
    icon: [
        512,
        512,
        ['tools'],
        'f7d9',
        'M64.7 34.6L160 107.9V160H107.9L34.6 64.7 64.7 34.6zM192 169.3c0-.4 0-.9 0-1.3V104c0-7.5-3.5-14.5-9.4-19L78.6 5C69.1-2.4 55.6-1.5 47 7L7 47c-8.5 8.5-9.4 22-2.1 31.6l80 104c4.5 5.9 11.6 9.4 19 9.4h64c.4 0 .9 0 1.3 0L271.2 293.9c-19.5 30-16.2 70.5 10.1 96.8l112 112c12.5 12.5 32.8 12.5 45.3 0l64-64c12.5-12.5 12.5-32.8 0-45.3l-112-112c-26.3-26.3-66.8-29.7-96.8-10.1L192 169.3zM304 304c17.7-17.7 46.3-17.7 64 0L480 416l-64 64L304 368c-17.7-17.7-17.7-46.3 0-64zM104 424a16 16 0 1 0 -32 0 16 16 0 1 0 32 0zM23.2 376.8C8.3 391.7 0 411.8 0 432.8C0 476.5 35.5 512 79.2 512c21 0 41.1-8.3 56-23.2L238.5 385.5c-5.7-11.2-9.3-23.1-10.9-35.3L112.1 465.7c-8.7 8.7-20.6 13.6-32.9 13.6c-25.7 0-46.6-20.8-46.6-46.6c0-12.3 4.9-24.2 13.6-32.9L189.1 257l-23.1-23.1L23.2 376.8zM479.4 160c0 44.8-23.1 84.1-58 106.8l23.5 23.5c40.7-29 67.2-76.6 67.2-130.3c0-24.8-5.7-48.3-15.7-69.3c-4.4-9.2-16.5-10.5-23.7-3.3l-67.9 67.9c-3 3-7.1 4.7-11.3 4.7H368c-8.8 0-16-7.2-16-16V118.6c0-4.2 1.7-8.3 4.7-11.3l67.9-67.9c7.2-7.2 5.9-19.3-3.3-23.7C400.3 5.7 376.8 0 352 0C296.1 0 246.9 28.6 218.3 72.1l26 20C266.8 56.3 306.7 32.6 352 32.6c10.3 0 20.2 1.2 29.7 3.5L333.6 84.2c-9.1 9.1-14.2 21.5-14.2 34.4V144c0 26.9 21.8 48.6 48.6 48.6h25.4c12.9 0 25.3-5.1 34.4-14.2l48.1-48.1c2.3 9.5 3.5 19.5 3.5 29.7z',
    ],
};
var faSquareInfoLight = {
    prefix: 'fal',
    iconName: 'square-info',
    icon: [
        448,
        512,
        [8505, 'info-square'],
        'f30f',
        'M64 64C46.3 64 32 78.3 32 96V416c0 17.7 14.3 32 32 32H384c17.7 0 32-14.3 32-32V96c0-17.7-14.3-32-32-32H64zM0 96C0 60.7 28.7 32 64 32H384c35.3 0 64 28.7 64 64V416c0 35.3-28.7 64-64 64H64c-35.3 0-64-28.7-64-64V96zm200 64a24 24 0 1 1 48 0 24 24 0 1 1 -48 0zm-16 64h40c8.8 0 16 7.2 16 16V352h32c8.8 0 16 7.2 16 16s-7.2 16-16 16H176c-8.8 0-16-7.2-16-16s7.2-16 16-16h32V256H184c-8.8 0-16-7.2-16-16s7.2-16 16-16z',
    ],
};
var faTriangleExclamationLight = {
    prefix: 'fal',
    iconName: 'triangle-exclamation',
    icon: [
        512,
        512,
        [9888, 'exclamation-triangle', 'warning'],
        'f071',
        'M34.5 420.4c-1.6 2.8-2.5 6-2.5 9.3c0 10.2 8.2 18.4 18.4 18.4H461.6c10.2 0 18.4-8.2 18.4-18.4c0-3.3-.9-6.4-2.5-9.3L276.5 75.8C272.2 68.5 264.4 64 256 64s-16.2 4.5-20.5 11.8L34.5 420.4zM6.9 404.2l201-344.6C217.9 42.5 236.2 32 256 32s38.1 10.5 48.1 27.6l201 344.6c4.5 7.7 6.9 16.5 6.9 25.4c0 27.8-22.6 50.4-50.4 50.4H50.4C22.6 480 0 457.4 0 429.6c0-8.9 2.4-17.7 6.9-25.4zM256 160c8.8 0 16 7.2 16 16V304c0 8.8-7.2 16-16 16s-16-7.2-16-16V176c0-8.8 7.2-16 16-16zM232 384a24 24 0 1 1 48 0 24 24 0 1 1 -48 0z',
    ],
};
var faUserGearLight = {
    prefix: 'fal',
    iconName: 'user-gear',
    icon: [
        640,
        512,
        ['user-cog'],
        'f4fe',
        'M224 224a96 96 0 1 0 0-192 96 96 0 1 0 0 192zM32 480H351c15.6 13.1 36.9 13.7 53 4.8v.9c0 9.2 2.7 18.5 7.9 26.3H29.7C13.3 512 0 498.7 0 482.3C0 383.8 79.8 304 178.3 304h91.4c11.8 0 23.4 1.2 34.5 3.3c-1.6 13.7 3.2 26.6 11.6 36.1c-14.5-4.8-30-7.4-46.2-7.4H178.3c-80 0-145 64.3-146.3 144zM96 128a128 128 0 1 1 256 0A128 128 0 1 1 96 128zM373.5 394.1l17.2 30.5c15.9-6.3 33-3.7 46 3.7c12.6 7.2 23.6 20.1 26.6 36.7H497c3-16.6 13.9-29.5 26.5-36.7c12.9-7.4 30-9.9 45.9-3.6l17.2-30.5c-12.8-11.4-18.5-27.7-18.5-42.4s5.7-31 18.5-42.4l-17.2-30.5c-15.9 6.3-32.9 3.7-45.9-3.6C510.8 268 499.9 255 497 238.4H463.3c-3.1 16.6-14 29.5-26.6 36.7c-13 7.4-30 9.9-46 3.7l-17.2 30.5C386.3 320.7 392 337 392 351.7s-5.7 31-18.5 42.4zm26.1 61.7l-1.6 1c-10.7 6.3-24.4 2.6-30.5-8.2L341 401.7c-5.7-10.2-2.3-23.1 7.8-29c7.3-4.3 11.2-12.5 11.2-21s-3.9-16.7-11.2-21c-10-5.9-13.5-18.8-7.8-29l26.4-46.9c6.1-10.8 19.9-14.5 30.5-8.2l1.6 1c6.5 3.9 14.6 3.4 21.2-.3c6.6-3.8 11.3-10.6 11.3-18.2v-1.4c0-11.7 9.5-21.3 21.3-21.3h53.4c11.8 0 21.4 9.6 21.4 21.4v1.6c0 7.5 4.6 14.2 11.1 18c6.5 3.7 14.5 4.2 21 .3l1.8-1c10.7-6.3 24.4-2.6 30.5 8.2L619 301.7c5.7 10.2 2.3 23-7.8 29c-7.3 4.3-11.2 12.6-11.2 21s3.9 16.7 11.2 21c10 5.9 13.5 18.8 7.8 29l-26.4 46.9c-6.1 10.8-19.9 14.5-30.5 8.2l-1.8-1c-6.5-3.8-14.5-3.4-21 .3c-6.5 3.7-11.1 10.4-11.1 18v1.6c0 11.8-9.6 21.4-21.4 21.4H453.4c-11.7 0-21.3-9.5-21.3-21.3v-1.4c0-7.6-4.6-14.4-11.3-18.2c-6.6-3.7-14.7-4.2-21.2-.3zm80.5-160.1a56 56 0 1 1 0 112 56 56 0 1 1 0-112zm0 80a24 24 0 1 0 0-48 24 24 0 1 0 0 48z',
    ],
};
var faUserGroupLight = {
    prefix: 'fal',
    iconName: 'user-group',
    icon: [
        640,
        512,
        [128101, 'user-friends'],
        'f500',
        'M128 128a96 96 0 1 1 192 0 96 96 0 1 1 -192 0zM269.7 336c80 0 145 64.3 146.3 144H32c1.2-79.7 66.2-144 146.3-144h91.4zM224 256A128 128 0 1 0 224 0a128 128 0 1 0 0 256zm-45.7 48C79.8 304 0 383.8 0 482.3C0 498.7 13.3 512 29.7 512H418.3c16.4 0 29.7-13.3 29.7-29.7C448 383.8 368.2 304 269.7 304H178.3zm431 208c17 0 30.7-13.8 30.7-30.7C640 392.2 567.8 320 478.7 320H417.3c-4.4 0-8.8 .2-13.2 .5c11.3 9.4 21.6 19.9 30.7 31.5h43.9c71 0 128.6 57.2 129.3 128H480c0 .8 0 1.5 0 2.3c0 10.8-2.8 20.9-7.6 29.7H609.3zM432 256c61.9 0 112-50.1 112-112s-50.1-112-112-112c-24.8 0-47.7 8.1-66.3 21.7c5.2 9.8 9.3 20.3 12.4 31.2C392.3 71.9 411.2 64 432 64c44.2 0 80 35.8 80 80s-35.8 80-80 80c-25.2 0-47.6-11.6-62.3-29.8c-4.7 10.3-10.4 19.9-17 28.9C373 243.4 401 256 432 256z',
    ],
};

/**
 * Copied icon definitions from @fortawesome/pro-regular-svg-icons.
 * Usage requires a valid FontAwesome Pro license.
 */
var faAngleDown = {
    prefix: 'far',
    iconName: 'angle-down',
    icon: [
        448,
        512,
        [8964],
        'f107',
        'M241 337c-9.4 9.4-24.6 9.4-33.9 0L47 177c-9.4-9.4-9.4-24.6 0-33.9s24.6-9.4 33.9 0l143 143L367 143c9.4-9.4 24.6-9.4 33.9 0s9.4 24.6 0 33.9L241 337z',
    ],
};
var faApartment = {
    prefix: 'far',
    iconName: 'apartment',
    icon: [
        576,
        512,
        [],
        'e468',
        'M176 96V64c0-8.8 7.2-16 16-16H384c8.8 0 16 7.2 16 16V192c0 26.5 21.5 48 48 48h64c8.8 0 16 7.2 16 16V448c0 8.8-7.2 16-16 16H384 352 320V416c0-17.7-14.3-32-32-32s-32 14.3-32 32v48H192 64c-8.8 0-16-7.2-16-16V160c0-8.8 7.2-16 16-16h64c26.5 0 48-21.5 48-48zm16 416h64 64 32 32H512c35.3 0 64-28.7 64-64V256c0-35.3-28.7-64-64-64H496 448V144 64c0-35.3-28.7-64-64-64H192c-35.3 0-64 28.7-64 64V96H80 64C28.7 96 0 124.7 0 160V448c0 35.3 28.7 64 64 64H192zM352 336c8.8 0 16-7.2 16-16V288c0-8.8-7.2-16-16-16H320c-8.8 0-16 7.2-16 16v32c0 8.8 7.2 16 16 16h32zm144 80V384c0-8.8-7.2-16-16-16H448c-8.8 0-16 7.2-16 16v32c0 8.8 7.2 16 16 16h32c8.8 0 16-7.2 16-16zM128 336c8.8 0 16-7.2 16-16V288c0-8.8-7.2-16-16-16H96c-8.8 0-16 7.2-16 16v32c0 8.8 7.2 16 16 16h32zm16 80V384c0-8.8-7.2-16-16-16H96c-8.8 0-16 7.2-16 16v32c0 8.8 7.2 16 16 16h32c8.8 0 16-7.2 16-16zm112-80c8.8 0 16-7.2 16-16V288c0-8.8-7.2-16-16-16H224c-8.8 0-16 7.2-16 16v32c0 8.8 7.2 16 16 16h32zM368 96c0-8.8-7.2-16-16-16H320c-8.8 0-16 7.2-16 16v32c0 8.8 7.2 16 16 16h32c8.8 0 16-7.2 16-16V96zM256 80H224c-8.8 0-16 7.2-16 16v32c0 8.8 7.2 16 16 16h32c8.8 0 16-7.2 16-16V96c0-8.8-7.2-16-16-16zM368 224V192c0-8.8-7.2-16-16-16H320c-8.8 0-16 7.2-16 16v32c0 8.8 7.2 16 16 16h32c8.8 0 16-7.2 16-16zM480 336c8.8 0 16-7.2 16-16V288c0-8.8-7.2-16-16-16H448c-8.8 0-16 7.2-16 16v32c0 8.8 7.2 16 16 16h32zM144 224V192c0-8.8-7.2-16-16-16H96c-8.8 0-16 7.2-16 16v32c0 8.8 7.2 16 16 16h32c8.8 0 16-7.2 16-16zm112 16c8.8 0 16-7.2 16-16V192c0-8.8-7.2-16-16-16H224c-8.8 0-16 7.2-16 16v32c0 8.8 7.2 16 16 16h32z',
    ],
};
var faArrowDown = {
    prefix: 'far',
    iconName: 'arrow-down',
    icon: [
        384,
        512,
        [8595],
        'f063',
        'M174.6 472.6c4.5 4.7 10.8 7.4 17.4 7.4s12.8-2.7 17.4-7.4l168-176c9.2-9.6 8.8-24.8-.8-33.9s-24.8-8.8-33.9 .8L216 396.1 216 56c0-13.3-10.7-24-24-24s-24 10.7-24 24l0 340.1L41.4 263.4c-9.2-9.6-24.3-9.9-33.9-.8s-9.9 24.3-.8 33.9l168 176z',
    ],
};
var faArrowRight = {
    prefix: 'far',
    iconName: 'arrow-right',
    icon: [
        448,
        512,
        [8594],
        'f061',
        'M440.6 273.4c4.7-4.5 7.4-10.8 7.4-17.4s-2.7-12.8-7.4-17.4l-176-168c-9.6-9.2-24.8-8.8-33.9 .8s-8.8 24.8 .8 33.9L364.1 232 24 232c-13.3 0-24 10.7-24 24s10.7 24 24 24l340.1 0L231.4 406.6c-9.6 9.2-9.9 24.3-.8 33.9s24.3 9.9 33.9 .8l176-168z',
    ],
};
var faArrowRightLong = {
    prefix: 'far',
    iconName: 'arrow-right-long',
    icon: [
        512,
        512,
        ['long-arrow-right'],
        'f178',
        'M505 273c9.4-9.4 9.4-24.6 0-33.9L369 103c-9.4-9.4-24.6-9.4-33.9 0s-9.4 24.6 0 33.9l95 95L24 232c-13.3 0-24 10.7-24 24s10.7 24 24 24l406.1 0-95 95c-9.4 9.4-9.4 24.6 0 33.9s24.6 9.4 33.9 0L505 273z',
    ],
};
var faArrowRotateLeft = {
    prefix: 'far',
    iconName: 'arrow-rotate-left',
    icon: [
        512,
        512,
        [8634, 'arrow-left-rotate', 'arrow-rotate-back', 'arrow-rotate-backward', 'undo'],
        'f0e2',
        'M40 224c-13.3 0-24-10.7-24-24V56c0-13.3 10.7-24 24-24s24 10.7 24 24v80.1l20-23.5C125 63.4 186.9 32 256 32c123.7 0 224 100.3 224 224s-100.3 224-224 224c-50.4 0-97-16.7-134.4-44.8c-10.6-8-12.7-23-4.8-33.6s23-12.7 33.6-4.8C179.8 418.9 216.3 432 256 432c97.2 0 176-78.8 176-176s-78.8-176-176-176c-54.3 0-102.9 24.6-135.2 63.4l-.1 .2 0 0L93.1 176H184c13.3 0 24 10.7 24 24s-10.7 24-24 24H40z',
    ],
};
var faArrowRotateRight = {
    prefix: 'far',
    iconName: 'arrow-rotate-right',
    icon: [
        512,
        512,
        [8635, 'arrow-right-rotate', 'arrow-rotate-forward', 'redo'],
        'f01e',
        'M472 224c13.3 0 24-10.7 24-24V56c0-13.3-10.7-24-24-24s-24 10.7-24 24v80.1l-20-23.5C387 63.4 325.1 32 256 32C132.3 32 32 132.3 32 256s100.3 224 224 224c50.4 0 97-16.7 134.4-44.8c10.6-8 12.7-23 4.8-33.6s-23-12.7-33.6-4.8C332.2 418.9 295.7 432 256 432c-97.2 0-176-78.8-176-176s78.8-176 176-176c54.3 0 102.9 24.6 135.2 63.4l.1 .2 0 0L418.9 176H328c-13.3 0-24 10.7-24 24s10.7 24 24 24H472z',
    ],
};
var faArrowTurnDownRight = {
    prefix: 'far',
    iconName: 'arrow-turn-down-right',
    icon: [
        512,
        512,
        [],
        'e3d6',
        'M48 56c0-13.3-10.7-24-24-24S0 42.7 0 56V224c0 48.6 39.4 88 88 88H430.1l-87 87c-9.4 9.4-9.4 24.6 0 33.9s24.6 9.4 33.9 0L505 305c9.4-9.4 9.4-24.6 0-33.9L377 143c-9.4-9.4-24.6-9.4-33.9 0s-9.4 24.6 0 33.9l87 87H88c-22.1 0-40-17.9-40-40V56z',
    ],
};
var faArrowUp = {
    prefix: 'far',
    iconName: 'arrow-up',
    icon: [
        384,
        512,
        [8593],
        'f062',
        'M209.4 39.4C204.8 34.7 198.6 32 192 32s-12.8 2.7-17.4 7.4l-168 176c-9.2 9.6-8.8 24.8 .8 33.9s24.8 8.8 33.9-.8L168 115.9V456c0 13.3 10.7 24 24 24s24-10.7 24-24V115.9L342.6 248.6c9.2 9.6 24.3 9.9 33.9 .8s9.9-24.3 .8-33.9l-168-176z',
    ],
};
var faArrowUpArrowDown = {
    prefix: 'far',
    iconName: 'arrow-up-arrow-down',
    icon: [
        576,
        512,
        ['sort-up-down'],
        'e099',
        'M529 377c9.4-9.4 9.4-24.6 0-33.9s-24.6-9.4-33.9 0l-55 55V56c0-13.3-10.7-24-24-24s-24 10.7-24 24V398.1l-55-55c-9.4-9.4-24.6-9.4-33.9 0s-9.4 24.6 0 33.9l96 96c9.4 9.4 24.6 9.4 33.9 0l96-96zM177 39c-9.4-9.4-24.6-9.4-33.9 0L47 135c-9.4 9.4-9.4 24.6 0 33.9s24.6 9.4 33.9 0l55-55V456c0 13.3 10.7 24 24 24s24-10.7 24-24V113.9l55 55c9.4 9.4 24.6 9.4 33.9 0s9.4-24.6 0-33.9L177 39z',
    ],
};
var faArrowUpFromGroundWater = {
    prefix: 'far',
    iconName: 'arrow-up-from-ground-water',
    icon: [
        576,
        512,
        [],
        'e4b5',
        'M288 352c13.3 0 24-10.7 24-24V81.9l39 39c9.4 9.4 24.6 9.4 33.9 0s9.4-24.6 0-33.9L305 7c-9.4-9.4-24.6-9.4-33.9 0L191 87c-9.4 9.4-9.4 24.6 0 33.9s24.6 9.4 33.9 0l39-39V328c0 13.3 10.7 24 24 24zM80 430.1C62.8 445 41 456.8 18.8 461.8C5.9 464.7-2.3 477.5 .6 490.5s15.7 21.1 28.7 18.2C58 502.2 81.6 488.2 96 478.2c28.1 19.5 61.4 33.8 96 33.8s67.9-14.3 96-33.8c28.1 19.5 61.4 33.8 96 33.8s67.9-14.3 96-33.8c14.4 10 38 24 66.7 30.4c12.9 2.9 25.8-5.2 28.7-18.2s-5.2-25.8-18.2-28.7c-22-4.9-44.3-16.7-61.3-31.8c-9.1-8.1-22.8-8.1-31.9 0c-21.6 18.6-51.2 33.9-80 33.9s-58.5-15.3-80-33.9c-9.1-8.1-22.8-8.1-31.9 0c-21.5 18.6-51.2 33.9-80 33.9s-58.5-15.3-80-33.9c-9.1-8.1-22.8-8.1-31.9 0zM192 192H64c-35.3 0-64 28.7-64 64V425c5.3-3.1 11.2-5.4 17.5-6.9c10.4-2.4 21.2-7.2 30.5-13.5L48 256c0-8.8 7.2-16 16-16H192V192zm384 64c0-35.3-28.7-64-64-64H384v48H512c8.8 0 16 7.2 16 16V404.6c9.3 6.3 20.1 11.1 30.5 13.6c6.3 1.5 12.1 3.8 17.5 6.9V256z',
    ],
};
var faArrowUpRightFromSquare = {
    prefix: 'far',
    iconName: 'arrow-up-right-from-square',
    icon: [
        512,
        512,
        ['external-link'],
        'f08e',
        'M304 24c0 13.3 10.7 24 24 24H430.1L207 271c-9.4 9.4-9.4 24.6 0 33.9s24.6 9.4 33.9 0l223-223V184c0 13.3 10.7 24 24 24s24-10.7 24-24V24c0-13.3-10.7-24-24-24H328c-13.3 0-24 10.7-24 24zM72 32C32.2 32 0 64.2 0 104V440c0 39.8 32.2 72 72 72H408c39.8 0 72-32.2 72-72V312c0-13.3-10.7-24-24-24s-24 10.7-24 24V440c0 13.3-10.7 24-24 24H72c-13.3 0-24-10.7-24-24V104c0-13.3 10.7-24 24-24H200c13.3 0 24-10.7 24-24s-10.7-24-24-24H72z',
    ],
};
var faBacterium = {
    prefix: 'far',
    iconName: 'bacterium',
    icon: [
        512,
        512,
        [],
        'e05a',
        'M423.1 30.6c3.6-12.7-3.7-26-16.5-29.7s-26 3.7-29.7 16.5l-4.2 14.7c-9.8-.4-19.9 .5-29.9 2.8c-12.1 2.8-23.7 5.9-34.9 9.4l-5.9-13.7c-5.2-12.2-19.3-17.8-31.5-12.6s-17.8 19.3-12.6 31.5l4.9 11.3c-22 9.4-42 20.1-60.2 31.8L196 82.7c-7.4-11-22.3-14-33.3-6.7s-14 22.3-6.7 33.3l7.8 11.6c-18 15-33.7 30.8-47.3 47.1L103 157.3c-10.4-8.3-25.5-6.6-33.7 3.7s-6.6 25.5 3.7 33.7l15 12c-2.1 3.2-4.1 6.5-6 9.7c-8.6 14.3-16.5 29.5-23.3 45.2l-9.8-3.9c-12.3-4.9-26.3 1.1-31.2 13.4s1.1 26.3 13.4 31.2l11.4 4.5c-4.8 16.8-8.2 34-10 51c-.5 5-.6 10-.4 14.9l-14.7 4.2C4.7 380.6-2.7 393.8 .9 406.6s16.9 20.1 29.7 16.5l13.8-3.9c10.6 20.7 27.6 37.8 48.5 48.5l-3.9 13.7c-3.6 12.7 3.7 26 16.5 29.7s26-3.7 29.7-16.5l4.2-14.7c23.8 1 46.3-5.5 65.1-17.6L215 473c9.4 9.4 24.6 9.4 33.9 0s9.4-24.6 0-33.9l-10.6-10.6c9.1-14.1 15.1-30.5 17-48.3c.6-3.9 1.4-7.7 2.5-11.5l12.7 5.5c12.2 5.2 26.3-.4 31.5-12.6s-.4-26.3-12.6-31.5l-11.3-4.8c9.9-14.9 24.9-31.6 48.6-46l2.1 7.5c3.6 12.7 16.9 20.1 29.7 16.5s20.1-16.9 16.5-29.7L371 259.2c6.9-2.2 14.3-4.3 22.2-6.1c12.9-3 24.7-8 35.2-14.8L439 249c9.4 9.4 24.6 9.4 33.9 0s9.4-24.6 0-33.9l-10.6-10.6c12.2-19 18.6-41.6 17.6-65.1l14.7-4.2c12.7-3.6 20.1-16.9 16.5-29.7s-16.9-20.1-29.7-16.5l-13.7 3.9c-10.8-21.2-28-38-48.5-48.5l3.9-13.8zM207.9 372.9c-.1 .7-.2 1.4-.3 2.1c-3.8 34.6-34.6 59.9-69.4 56.8c-35.1-3.2-61-34.2-58-69.2c4.4-41 20.4-84 42.9-121.4c38-63.4 107.9-131.1 230.5-159.4c34.4-7.9 68.8 13.5 76.8 48s-13.5 68.8-48 76.8C297 226.1 254.9 270.3 232.9 306.9c-10.8 18-21.1 40.8-25 66zM192 320a32 32 0 1 0 -64 0 32 32 0 1 0 64 0zm48-88a24 24 0 1 0 0-48 24 24 0 1 0 0 48z',
    ],
};
var faBallotCheck = {
    prefix: 'far',
    iconName: 'ballot-check',
    icon: [
        448,
        512,
        [],
        'f733',
        'M384 48c8.8 0 16 7.2 16 16V448c0 8.8-7.2 16-16 16H64c-8.8 0-16-7.2-16-16V64c0-8.8 7.2-16 16-16H384zM64 0C28.7 0 0 28.7 0 64V448c0 35.3 28.7 64 64 64H384c35.3 0 64-28.7 64-64V64c0-35.3-28.7-64-64-64H64zM80 112v32c0 8.8 7.2 16 16 16h32c8.8 0 16-7.2 16-16V112c0-8.8-7.2-16-16-16H96c-8.8 0-16 7.2-16 16zM96 352c-8.8 0-16 7.2-16 16v32c0 8.8 7.2 16 16 16h32c8.8 0 16-7.2 16-16V368c0-8.8-7.2-16-16-16H96zm80-224c0 13.3 10.7 24 24 24H344c13.3 0 24-10.7 24-24s-10.7-24-24-24H200c-13.3 0-24 10.7-24 24zm32 128c0 13.3 10.7 24 24 24H344c13.3 0 24-10.7 24-24s-10.7-24-24-24H232c-13.3 0-24 10.7-24 24zM176 384c0 13.3 10.7 24 24 24H344c13.3 0 24-10.7 24-24s-10.7-24-24-24H200c-13.3 0-24 10.7-24 24zm-4.7-140.7c6.2-6.2 6.2-16.4 0-22.6s-16.4-6.2-22.6 0L112 257.4 99.3 244.7c-6.2-6.2-16.4-6.2-22.6 0s-6.2 16.4 0 22.6l24 24c6.2 6.2 16.4 6.2 22.6 0l48-48z',
    ],
};
var faBars = {
    prefix: 'far',
    iconName: 'bars',
    icon: [
        448,
        512,
        ['navicon'],
        'f0c9',
        'M0 88C0 74.7 10.7 64 24 64H424c13.3 0 24 10.7 24 24s-10.7 24-24 24H24C10.7 112 0 101.3 0 88zM0 248c0-13.3 10.7-24 24-24H424c13.3 0 24 10.7 24 24s-10.7 24-24 24H24c-13.3 0-24-10.7-24-24zM448 408c0 13.3-10.7 24-24 24H24c-13.3 0-24-10.7-24-24s10.7-24 24-24H424c13.3 0 24 10.7 24 24z',
    ],
};
var faBell = {
    prefix: 'far',
    iconName: 'bell',
    icon: [
        448,
        512,
        [128276, 61602],
        'f0f3',
        'M224 0c-17.7 0-32 14.3-32 32V49.9C119.5 61.4 64 124.2 64 200v33.4c0 45.4-15.5 89.5-43.8 124.9L5.3 377c-5.8 7.2-6.9 17.1-2.9 25.4S14.8 416 24 416H424c9.2 0 17.6-5.3 21.6-13.6s2.9-18.2-2.9-25.4l-14.9-18.6C399.5 322.9 384 278.8 384 233.4V200c0-75.8-55.5-138.6-128-150.1V32c0-17.7-14.3-32-32-32zm0 96h8c57.4 0 104 46.6 104 104v33.4c0 47.9 13.9 94.6 39.7 134.6H72.3C98.1 328 112 281.3 112 233.4V200c0-57.4 46.6-104 104-104h8zm64 352H224 160c0 17 6.7 33.3 18.7 45.3s28.3 18.7 45.3 18.7s33.3-6.7 45.3-18.7s18.7-28.3 18.7-45.3z',
    ],
};
var faBlockBrick = {
    prefix: 'far',
    iconName: 'block-brick',
    icon: [
        448,
        512,
        ['wall-brick'],
        'e3db',
        'M144 80v48h56 48 56V80H144zM96 128V80H64c-8.8 0-16 7.2-16 16v32H96zM48 176v56H200V176H48zm0 160H96V280H48v56zm0 48v32c0 8.8 7.2 16 16 16H200V384H144 96 48zm96-48H304V280H144v56zm208 0h48V280H352v56zm48 48H352 304 248v48H384c8.8 0 16-7.2 16-16V384zm0-208H248v56H400V176zm0-48V96c0-8.8-7.2-16-16-16H352v48h48zM0 96C0 60.7 28.7 32 64 32H384c35.3 0 64 28.7 64 64V416c0 35.3-28.7 64-64 64H64c-35.3 0-64-28.7-64-64V96z',
    ],
};
var faBlockBrickFire = {
    prefix: 'far',
    iconName: 'block-brick-fire',
    icon: [
        640,
        512,
        ['firewall'],
        'e3dc',
        'M176 80v48h32 48 80V80H176zm-48 48V80H96c-8.8 0-16 7.2-16 16v32h48zM80 176v56H208V176H80zm0 160h48V280H80v56zm0 48v32c0 8.8 7.2 16 16 16H208V384H176 128 80zm96-48H288.8c-.5 4.7-.8 9.4-.8 14.1c0 11.5 1 22.9 3 33.9H256v48h50.1c8.1 17.5 18.8 33.7 31.5 48H96c-35.3 0-64-28.7-64-64V96c0-35.3 28.7-64 64-64H416c35.3 0 64 28.7 64 64v48.6c-1.7-1.5-3.3-3.1-5-4.6c-17.7-16-44.7-16-62.5-.1c-12.9 11.5-25.1 23.7-36.5 36.1H256v56h76c-2.4 3.6-4.7 7.2-6.9 10.7c-7.4 12-14.2 24.5-19.9 37.3H176v56zM432 128V96c0-8.8-7.2-16-16-16H384v48h48zm21.5 35.8c19.7 17.8 38.2 37 55.5 57.7c7.9-9.9 16.8-20.7 26.5-29.5c5.6-5.1 14.4-5.1 20 0c24.7 22.7 45.6 52.7 60.4 81.1c14.5 28 24.2 58.8 24.2 79C640 440 568.7 512 480 512c-89.7 0-160-72.1-160-159.8c0-26.4 12.7-60.7 32.4-92.6c20-32.4 48.1-66.1 81.4-95.8c2.8-2.5 6.4-3.8 10-3.7c3.5 0 7 1.3 9.8 3.8zM530 433c30-21 38-63 20-96c-2-4-4-8-7-12l-36 42s-58-74-62-79c-30 37-45 58-45 82c0 49 36 78 81 78c18 0 34-5 49-15z',
    ],
};
var faBoltLightning = {
    prefix: 'far',
    iconName: 'bolt-lightning',
    icon: [
        384,
        512,
        [],
        'e0b7',
        'M77 48L51.4 240H208c7.4 0 14.4 3.4 18.9 9.2s6.2 13.4 4.4 20.6l-23 91.8L322.8 192H176c-7.9 0-15.3-3.9-19.8-10.4s-5.4-14.9-2.6-22.2L196.9 48H77zM30.4 34.7C33 14.8 50 0 70 0H208.6c28.1 0 47.5 28.3 37.3 54.5L211.1 144H337.9c32.1 0 51.1 35.8 33.2 62.4l-199.2 295c-6.5 9.7-19 13.2-29.7 8.5s-16.3-16.4-13.5-27.7L177.3 288H42.3C18.1 288-.6 266.7 2.6 242.7l27.7-208z',
    ],
};
var faBroom = {
    prefix: 'far',
    iconName: 'broom',
    icon: [
        576,
        512,
        [129529],
        'f51a',
        'M569 41c9.4-9.4 9.4-24.6 0-33.9s-24.6-9.4-33.9 0L335 207l-39-39c-5.1-5.1-12.1-8-19.4-8c-12.2 0-23 8.1-26.3 19.9l-15.9 55.6-.6-.1c-46.4-7.1-93.5 8.3-126.7 41.5L97 287c-18.4 18.4-29.7 42.4-32.4 68c-.4 3.8-.6 7.7-.6 11.6c0 9.6 7.8 17.3 17.3 17.3H104L81.3 404.9 57.1 427.3 7.2 473.4C2.6 477.6 0 483.5 0 489.7C0 502 10 512 22.3 512l172.7 0c39.1 0 76.6-15.5 104.2-43.2c33.2-33.2 48.6-80.2 41.5-126.7l-.1-.6 55.6-15.9c11.8-3.4 19.9-14.1 19.9-26.3c0-7.3-2.9-14.2-8-19.4l-39-39L569 41zM278.8 254.8l8-28.1 62.4 62.4-28.1 8-42.3-42.3zm-52.3 28l9.8 1.5 55.4 55.4 1.5 9.8c4.8 31.3-5.6 63-28 85.4c-18.6 18.6-43.9 29.1-70.3 29.1L88.1 464l48.5-44.7c14.5-13.4 19.3-34.3 12.1-52.7c-5.4-13.7-16.6-23.9-30.1-28.3c3.1-6.4 7.3-12.2 12.4-17.3l10.2-10.2c22.4-22.4 54.1-32.8 85.4-28z',
    ],
};
var faBroomBall = {
    prefix: 'far',
    iconName: 'broom-ball',
    icon: [
        576,
        512,
        ['quidditch', 'quidditch-broom-ball'],
        'f458',
        'M569 7c9.4 9.4 9.4 24.6 0 33.9L369 241l39 39c5.1 5.1 8 12.1 8 19.4c0 12.2-8.1 23-19.9 26.3l-55.6 15.9 .1 .6c7.1 46.4-8.3 93.4-41.5 126.7C271.5 496.5 234 512 194.9 512L22.3 512C10 512 0 502 0 489.7c0-6.2 2.6-12.1 7.2-16.4l49.9-46.1 24.3-22.4L104 384H81.3c-9.6 0-17.3-7.8-17.3-17.3c0-3.9 .2-7.8 .6-11.6c2.7-25.6 14-49.7 32.4-68l10.2-10.2c33.2-33.2 80.2-48.6 126.7-41.5l.6 .1 15.9-55.6c3.4-11.8 14.1-19.9 26.3-19.9c7.3 0 14.2 2.9 19.4 8l39 39L535 7c9.4-9.4 24.6-9.4 33.9 0zM321.2 297.2l28.1-8-62.4-62.4-8 28.1 42.3 42.3zm-84.9-12.9l-9.8-1.5c-31.3-4.8-63 5.6-85.4 28l-10.2 10.2c-5.1 5.1-9.3 11-12.4 17.3c13.5 4.3 24.8 14.5 30.1 28.3c7.2 18.4 2.4 39.3-12.1 52.7L88.1 464l106.8 0c26.4 0 51.7-10.5 70.3-29.1c22.4-22.4 32.8-54.1 28-85.4l-1.5-9.8-55.4-55.4zM528 432a32 32 0 1 0 -64 0 32 32 0 1 0 64 0zm-112 0a80 80 0 1 1 160 0 80 80 0 1 1 -160 0z',
    ],
};
var faBug = {
    prefix: 'far',
    iconName: 'bug',
    icon: [
        512,
        512,
        [],
        'f188',
        'M256 0c53 0 96 43 96 96v3.6c0 15.7-12.7 28.4-28.4 28.4H188.4c-15.7 0-28.4-12.7-28.4-28.4V96c0-53 43-96 96-96zM39 103c9.4-9.4 24.6-9.4 33.9 0l72.4 72.4C161.3 165.7 180 160 200 160H312c20 0 38.7 5.7 54.6 15.5L439 103c9.4-9.4 24.6-9.4 33.9 0s9.4 24.6 0 33.9l-72.4 72.4C410.3 225.3 416 244 416 264h72c13.3 0 24 10.7 24 24s-10.7 24-24 24H416v8c0 27.2-6.8 52.8-18.8 75.3L473 471c9.4 9.4 9.4 24.6 0 33.9s-24.6 9.4-33.9 0l-70.9-70.9C339.3 462.5 299.7 480 256 480s-83.3-17.5-112.2-45.9L73 505c-9.4 9.4-24.6 9.4-33.9 0s-9.4-24.6 0-33.9l75.8-75.8C102.8 372.8 96 347.2 96 320v-8H24c-13.3 0-24-10.7-24-24s10.7-24 24-24H96c0-20 5.7-38.7 15.5-54.6L39 137c-9.4-9.4-9.4-24.6 0-33.9zM144 264v56c0 53.6 37.7 98.4 88 109.4V280c0-13.3 10.7-24 24-24s24 10.7 24 24V429.4c50.3-11 88-55.8 88-109.4V264c0-30.9-25.1-56-56-56H200c-30.9 0-56 25.1-56 56z',
    ],
};
var faBuildingColumns = {
    prefix: 'far',
    iconName: 'building-columns',
    icon: [
        512,
        512,
        ['bank', 'institution', 'museum', 'university'],
        'f19c',
        'M267.6 3c-7.2-4-16-4-23.2 0L17.6 128.1C6.7 134.1 0 145.5 0 157.9C0 176.8 15.2 192 34.1 192H477.9c18.8 0 34.1-15.2 34.1-34.1c0-12.4-6.7-23.8-17.6-29.8L267.6 3zM228.3 144H88.2L256 51.4 423.8 144H283.7c2.7-4.7 4.3-10.2 4.3-16c0-17.7-14.3-32-32-32s-32 14.3-32 32c0 5.8 1.6 11.3 4.3 16zM64 224V384c-13.3 0-24 10.7-24 24s10.7 24 24 24H456c13.3 0 24-10.7 24-24s-10.7-24-24-24h-8V224H400V384H336V224H288V384H224V224H176V384H112V224H64zM32 464c-13.3 0-24 10.7-24 24s10.7 24 24 24H488c13.3 0 24-10.7 24-24s-10.7-24-24-24H32z',
    ],
};
var faBuildingShield = {
    prefix: 'far',
    iconName: 'building-shield',
    icon: [
        576,
        512,
        [],
        'e4d8',
        'M64 48H320c8.8 0 16 7.2 16 16V226.2L384 207V64c0-35.3-28.7-64-64-64H64C28.7 0 0 28.7 0 64V448c0 35.3 28.7 64 64 64H320c10.9 0 21.2-2.7 30.2-7.6c-15.4-12.4-28.5-26-39.5-40.4H240V400c0-26.5-21.5-48-48-48s-48 21.5-48 48v64H64c-8.8 0-16-7.2-16-16V64c0-8.8 7.2-16 16-16zM296 232c0-8.8-7.2-16-16-16H232c-8.8 0-16 7.2-16 16v48c0 8.8 7.2 16 16 16h24c.1-22.8 14-43.4 35.2-51.8l4.8-1.9V232zM88 104v48c0 8.8 7.2 16 16 16h48c8.8 0 16-7.2 16-16V104c0-8.8-7.2-16-16-16H104c-8.8 0-16 7.2-16 16zM232 88c-8.8 0-16 7.2-16 16v48c0 8.8 7.2 16 16 16h48c8.8 0 16-7.2 16-16V104c0-8.8-7.2-16-16-16H232zM88 232v48c0 8.8 7.2 16 16 16h48c8.8 0 16-7.2 16-16V232c0-8.8-7.2-16-16-16H104c-8.8 0-16 7.2-16 16zm335.1-6.3l-120 48C294 277.4 288 286.2 288 296c0 63.3 25.9 168.8 134.8 214.2c5.9 2.5 12.6 2.5 18.5 0C550.1 464.8 576 359.3 576 296c0-9.8-6-18.6-15.1-22.3l-120-48c-5.7-2.3-12.1-2.3-17.8 0zM527.4 312c-3.9 50.7-27.2 116.7-95.4 149.7V273.8L527.4 312z',
    ],
};
var faBullhorn = {
    prefix: 'far',
    iconName: 'bullhorn',
    icon: [
        512,
        512,
        [128226, 128363],
        'f0a1',
        'M432 73.9L421.3 84.6C364.5 141.4 288.1 174 208 175.9v.1V304v0 .1c80.1 1.9 156.5 34.5 213.3 91.3L432 406.1V73.9zM160 128h40.7c70 0 137.2-27.8 186.7-77.3l38-38C445.5-7.5 480 6.8 480 35.3V179.6c18.6 8.8 32 32.5 32 60.4s-13.4 51.6-32 60.4V444.7c0 28.5-34.5 42.8-54.6 22.6l-38-38c-47.8-47.8-112-75.4-179.4-77.2V456c0 30.9-25.1 56-56 56H120c-30.9 0-56-25.1-56-56l0-104c-35.3 0-64-28.7-64-64V192c0-35.3 28.7-64 64-64h96zM112 352V456c0 4.4 3.6 8 8 8h32c4.4 0 8-3.6 8-8V352H112zM64 176c-8.8 0-16 7.2-16 16v96c0 8.8 7.2 16 16 16l0 0h48 48V176l-96 0z',
    ],
};
var faCalendarDay = {
    prefix: 'far',
    iconName: 'calendar-day',
    icon: [
        448,
        512,
        [],
        'f783',
        'M128 0c13.3 0 24 10.7 24 24V64H296V24c0-13.3 10.7-24 24-24s24 10.7 24 24V64h40c35.3 0 64 28.7 64 64v16 48V448c0 35.3-28.7 64-64 64H64c-35.3 0-64-28.7-64-64V192 144 128C0 92.7 28.7 64 64 64h40V24c0-13.3 10.7-24 24-24zM400 192H48V448c0 8.8 7.2 16 16 16H384c8.8 0 16-7.2 16-16V192zM112 256h96c8.8 0 16 7.2 16 16v96c0 8.8-7.2 16-16 16H112c-8.8 0-16-7.2-16-16V272c0-8.8 7.2-16 16-16z',
    ],
};
var faCameraCctv = {
    prefix: 'far',
    iconName: 'camera-cctv',
    icon: [
        576,
        512,
        ['cctv'],
        'f8ac',
        'M54.9 192.2l169.3 70.5c.1 0 .2 .1 .2 .1c.6 .2 1.1 .4 1.6 .7l92.2 38.4 160-106.7L134.9 52.2l-80 140zM185.2 298.5l-165.5-69c-8.5-3.5-15-10.5-18-19.2s-2-18.2 2.5-26.2l96-168c8-14 25.2-19.9 40.1-13.7l384 160c10.9 4.5 18.4 14.7 19.5 26.5s-4.3 23.2-14.1 29.7l-192 128c-8.9 5.9-20.2 7-30.1 2.9L229.5 317 182.4 440.5C178.9 449.8 170 456 160 456H48v32c0 13.3-10.7 24-24 24s-24-10.7-24-24V432 376c0-13.3 10.7-24 24-24s24 10.7 24 24v32h95.5l41.7-109.5zm211.8 47.1l132.9-88.6 36.7 16.3c7.9 3.5 11.6 12.8 8.3 20.8l-40 96c-1.7 4-4.9 7.2-8.9 8.7s-8.5 1.5-12.5-.3L396.9 345.6z',
    ],
};
var faChalkboardUser = {
    prefix: 'far',
    iconName: 'chalkboard-user',
    icon: [
        640,
        512,
        ['chalkboard-teacher'],
        'f51c',
        'M576 48H224c-8.8 0-16 7.2-16 16v41.3c-14.8-6-31-9.3-48-9.3V64c0-35.3 28.7-64 64-64H576c35.3 0 64 28.7 64 64V352c0 35.3-28.7 64-64 64H544 520 392 368 336.8c-8.3-18-19.8-34.2-33.7-48H368V328c0-30.9 25.1-56 56-56h64c30.9 0 56 25.1 56 56v40h32c8.8 0 16-7.2 16-16V64c0-8.8-7.2-16-16-16zM496 368V328c0-4.4-3.6-8-8-8H424c-4.4 0-8 3.6-8 8v40h80zM208 224a48 48 0 1 0 -96 0 48 48 0 1 0 96 0zM64 224a96 96 0 1 1 192 0A96 96 0 1 1 64 224zM50.7 464H269.3c-9.5-36.8-42.9-64-82.6-64H133.3c-39.8 0-73.2 27.2-82.6 64zM0 485.3C0 411.7 59.7 352 133.3 352h53.3C260.3 352 320 411.7 320 485.3c0 14.7-11.9 26.7-26.7 26.7H26.7C11.9 512 0 500.1 0 485.3z',
    ],
};
var faChartBar = {
    prefix: 'far',
    iconName: 'chart-bar',
    icon: [
        512,
        512,
        ['bar-chart'],
        'f080',
        'M24 32c13.3 0 24 10.7 24 24V408c0 13.3 10.7 24 24 24H488c13.3 0 24 10.7 24 24s-10.7 24-24 24H72c-39.8 0-72-32.2-72-72V56C0 42.7 10.7 32 24 32zM128 136c0-13.3 10.7-24 24-24l208 0c13.3 0 24 10.7 24 24s-10.7 24-24 24l-208 0c-13.3 0-24-10.7-24-24zm24 72H296c13.3 0 24 10.7 24 24s-10.7 24-24 24H152c-13.3 0-24-10.7-24-24s10.7-24 24-24zm0 96H424c13.3 0 24 10.7 24 24s-10.7 24-24 24H152c-13.3 0-24-10.7-24-24s10.7-24 24-24z',
    ],
};
var faChartLineUp = {
    prefix: 'far',
    iconName: 'chart-line-up',
    icon: [
        512,
        512,
        [],
        'e0e5',
        'M48 56c0-13.3-10.7-24-24-24S0 42.7 0 56V408c0 39.8 32.2 72 72 72H488c13.3 0 24-10.7 24-24s-10.7-24-24-24H72c-13.3 0-24-10.7-24-24V56zm272 96c0 13.3 10.7 24 24 24h54.1L304 270.1l-79-79c-9.4-9.4-24.6-9.4-33.9 0l-80 80c-9.4 9.4-9.4 24.6 0 33.9s24.6 9.4 33.9 0l63-63 79 79c9.4 9.4 24.6 9.4 33.9 0l111-111V264c0 13.3 10.7 24 24 24s24-10.7 24-24V152c0-13.3-10.7-24-24-24H344c-13.3 0-24 10.7-24 24z',
    ],
};
var faChartMixed = {
    prefix: 'far',
    iconName: 'chart-mixed',
    icon: [
        512,
        512,
        ['analytics'],
        'f643',
        'M503.6 74.2c10.1-8.6 11.2-23.8 2.6-33.8s-23.8-11.2-33.8-2.6L320.2 168.3 207.8 69.9c-8.2-7.1-20.1-7.9-29.1-1.9L10.7 180c-11 7.4-14 22.3-6.7 33.3s22.3 14 33.3 6.7L190 118.2l114.2 99.9c9 7.9 22.4 7.9 31.4 .2l168-144zM200 432c0 4.4-3.6 8-8 8s-8-3.6-8-8V272c0-4.4 3.6-8 8-8s8 3.6 8 8V432zm-8-208c-26.5 0-48 21.5-48 48V432c0 26.5 21.5 48 48 48s48-21.5 48-48V272c0-26.5-21.5-48-48-48zM72 432c0 4.4-3.6 8-8 8s-8-3.6-8-8V368c0-4.4 3.6-8 8-8s8 3.6 8 8v64zM64 320c-26.5 0-48 21.5-48 48v64c0 26.5 21.5 48 48 48s48-21.5 48-48V368c0-26.5-21.5-48-48-48zm264 16v96c0 4.4-3.6 8-8 8s-8-3.6-8-8V336c0-4.4 3.6-8 8-8s8 3.6 8 8zm-56 0v96c0 26.5 21.5 48 48 48s48-21.5 48-48V336c0-26.5-21.5-48-48-48s-48 21.5-48 48zm184 96c0 4.4-3.6 8-8 8s-8-3.6-8-8V272c0-4.4 3.6-8 8-8s8 3.6 8 8V432zm-8-208c-26.5 0-48 21.5-48 48V432c0 26.5 21.5 48 48 48s48-21.5 48-48V272c0-26.5-21.5-48-48-48z',
    ],
};
var faChartPie = {
    prefix: 'far',
    iconName: 'chart-pie',
    icon: [
        576,
        512,
        ['pie-chart'],
        'f200',
        'M493.1 192H352V50.9C423.5 64 480 120.5 493.1 192zM352 240H527.4c9 0 16.6-7 16.6-16C544 100.3 443.7 0 320 0c-9 0-16 7.6-16 16.6V192v48h48zM222.1 321.9L348.4 448.2C325 458.4 299.2 464 272 464C166 464 80 378 80 272c0-83.6 53.4-154.7 128-181.1V288c0 12.7 5.1 24.9 14.1 33.9zM239 34.3C122.1 50.3 32 150.7 32 272c0 132.5 107.5 240 240 240c51.8 0 99.8-16.4 139-44.3c7.7-5.5 8.2-16.5 1.5-23.1L256 288V49.6c0-9.2-7.8-16.6-17-15.4zM558.4 288H320L478.7 446.7c5.8 5.8 15.2 6.3 21.2 .7c39.3-36.7 66.2-86.5 73.9-142.3c1.3-9.2-6.1-17-15.4-17z',
    ],
};
var faCheck = {
    prefix: 'far',
    iconName: 'check',
    icon: [
        448,
        512,
        [10003, 10004],
        'f00c',
        'M441 103c9.4 9.4 9.4 24.6 0 33.9L177 401c-9.4 9.4-24.6 9.4-33.9 0L7 265c-9.4-9.4-9.4-24.6 0-33.9s24.6-9.4 33.9 0l119 119L407 103c9.4-9.4 24.6-9.4 33.9 0z',
    ],
};
var faChevronDown = {
    prefix: 'far',
    iconName: 'chevron-down',
    icon: [
        512,
        512,
        [],
        'f078',
        'M239 401c9.4 9.4 24.6 9.4 33.9 0L465 209c9.4-9.4 9.4-24.6 0-33.9s-24.6-9.4-33.9 0l-175 175L81 175c-9.4-9.4-24.6-9.4-33.9 0s-9.4 24.6 0 33.9L239 401z',
    ],
};
var faChevronLeft = {
    prefix: 'far',
    iconName: 'chevron-left',
    icon: [
        320,
        512,
        [9001],
        'f053',
        'M15 239c-9.4 9.4-9.4 24.6 0 33.9L207 465c9.4 9.4 24.6 9.4 33.9 0s9.4-24.6 0-33.9L65.9 256 241 81c9.4-9.4 9.4-24.6 0-33.9s-24.6-9.4-33.9 0L15 239z',
    ],
};
var faChevronRight = {
    prefix: 'far',
    iconName: 'chevron-right',
    icon: [
        320,
        512,
        [9002],
        'f054',
        'M305 239c9.4 9.4 9.4 24.6 0 33.9L113 465c-9.4 9.4-24.6 9.4-33.9 0s-9.4-24.6 0-33.9l175-175L79 81c-9.4-9.4-9.4-24.6 0-33.9s24.6-9.4 33.9 0L305 239z',
    ],
};
var faChevronUp = {
    prefix: 'far',
    iconName: 'chevron-up',
    icon: [
        512,
        512,
        [],
        'f077',
        'M239 111c9.4-9.4 24.6-9.4 33.9 0L465 303c9.4 9.4 9.4 24.6 0 33.9s-24.6 9.4-33.9 0l-175-175L81 337c-9.4 9.4-24.6 9.4-33.9 0s-9.4-24.6 0-33.9L239 111z',
    ],
};
var faChevronsLeft = {
    prefix: 'far',
    iconName: 'chevrons-left',
    icon: [
        512,
        512,
        ['chevron-double-left'],
        'f323',
        'M47 239c-9.4 9.4-9.4 24.6 0 33.9L239 465c9.4 9.4 24.6 9.4 33.9 0s9.4-24.6 0-33.9L97.9 256 273 81c9.4-9.4 9.4-24.6 0-33.9s-24.6-9.4-33.9 0L47 239zM431 47L239 239c-9.4 9.4-9.4 24.6 0 33.9L431 465c9.4 9.4 24.6 9.4 33.9 0s9.4-24.6 0-33.9l-175-175L465 81c9.4-9.4 9.4-24.6 0-33.9s-24.6-9.4-33.9 0z',
    ],
};
var faChevronsRight = {
    prefix: 'far',
    iconName: 'chevrons-right',
    icon: [
        512,
        512,
        ['chevron-double-right'],
        'f324',
        'M465 239c9.4 9.4 9.4 24.6 0 33.9L273 465c-9.4 9.4-24.6 9.4-33.9 0s-9.4-24.6 0-33.9l175-175L239 81c-9.4-9.4-9.4-24.6 0-33.9s24.6-9.4 33.9 0L465 239zM81 47L273 239c9.4 9.4 9.4 24.6 0 33.9L81 465c-9.4 9.4-24.6 9.4-33.9 0s-9.4-24.6 0-33.9l175-175L47 81c-9.4-9.4-9.4-24.6 0-33.9s24.6-9.4 33.9 0z',
    ],
};
var faChimney = {
    prefix: 'far',
    iconName: 'chimney',
    icon: [
        448,
        512,
        [],
        'f78b',
        'M48 176H400V80H48v96zM0 64C0 46.3 14.3 32 32 32H416c17.7 0 32 14.3 32 32V192c0 17.7-14.3 32-32 32V360 464c0 13.3-10.7 24-24 24s-24-10.7-24-24V384H288v80c0 13.3-10.7 24-24 24s-24-10.7-24-24V384H184 80v80c0 13.3-10.7 24-24 24s-24-10.7-24-24V360l0-136c-17.7 0-32-14.3-32-32V64zM80 224V336h80V224H80zm128 0V336h56H368V224H208z',
    ],
};
var faCircleCheck = {
    prefix: 'far',
    iconName: 'circle-check',
    icon: [
        512,
        512,
        [61533, 'check-circle'],
        'f058',
        'M256 48a208 208 0 1 1 0 416 208 208 0 1 1 0-416zm0 464A256 256 0 1 0 256 0a256 256 0 1 0 0 512zM369 209c9.4-9.4 9.4-24.6 0-33.9s-24.6-9.4-33.9 0l-111 111-47-47c-9.4-9.4-24.6-9.4-33.9 0s-9.4 24.6 0 33.9l64 64c9.4 9.4 24.6 9.4 33.9 0L369 209z',
    ],
};
var faCircleExclamation = {
    prefix: 'far',
    iconName: 'circle-exclamation',
    icon: [
        512,
        512,
        ['exclamation-circle'],
        'f06a',
        'M256 48a208 208 0 1 1 0 416 208 208 0 1 1 0-416zm0 464A256 256 0 1 0 256 0a256 256 0 1 0 0 512zm0-384c-13.3 0-24 10.7-24 24V264c0 13.3 10.7 24 24 24s24-10.7 24-24V152c0-13.3-10.7-24-24-24zm32 224a32 32 0 1 0 -64 0 32 32 0 1 0 64 0z',
    ],
};
var faCircleInfo = {
    prefix: 'far',
    iconName: 'circle-info',
    icon: [
        512,
        512,
        ['info-circle'],
        'f05a',
        'M256 48a208 208 0 1 1 0 416 208 208 0 1 1 0-416zm0 464A256 256 0 1 0 256 0a256 256 0 1 0 0 512zM216 336c-13.3 0-24 10.7-24 24s10.7 24 24 24h80c13.3 0 24-10.7 24-24s-10.7-24-24-24h-8V248c0-13.3-10.7-24-24-24H216c-13.3 0-24 10.7-24 24s10.7 24 24 24h24v64H216zm40-144a32 32 0 1 0 0-64 32 32 0 1 0 0 64z',
    ],
};
var faCirclePlus = {
    prefix: 'far',
    iconName: 'circle-plus',
    icon: [
        512,
        512,
        ['plus-circle'],
        'f055',
        'M256 48a208 208 0 1 1 0 416 208 208 0 1 1 0-416zm0 464A256 256 0 1 0 256 0a256 256 0 1 0 0 512zM232 344c0 13.3 10.7 24 24 24s24-10.7 24-24V280h64c13.3 0 24-10.7 24-24s-10.7-24-24-24H280V168c0-13.3-10.7-24-24-24s-24 10.7-24 24v64H168c-13.3 0-24 10.7-24 24s10.7 24 24 24h64v64z',
    ],
};
var faClock = {
    prefix: 'far',
    iconName: 'clock',
    icon: [
        512,
        512,
        [128339, 'clock-four'],
        'f017',
        'M464 256A208 208 0 1 1 48 256a208 208 0 1 1 416 0zM0 256a256 256 0 1 0 512 0A256 256 0 1 0 0 256zM232 120V256c0 8 4 15.5 10.7 20l96 64c11 7.4 25.9 4.4 33.3-6.7s4.4-25.9-6.7-33.3L280 243.2V120c0-13.3-10.7-24-24-24s-24 10.7-24 24z',
    ],
};
var faCoins = {
    prefix: 'far',
    iconName: 'coins',
    icon: [
        512,
        512,
        [],
        'f51e',
        'M176 88v0c0 .1 .1 .6 .6 1.5c.6 1.2 2 3.1 4.7 5.5c.4 .3 .8 .7 1.2 1c-18.7 .4-36.9 1.7-54.4 4.1V88c0-18 9.7-32.4 21.1-42.7s26.7-18.5 43.5-24.9C226.4 7.5 271.5 0 320 0s93.6 7.5 127.3 20.3c16.8 6.4 32.1 14.6 43.5 24.9S512 70 512 88V192 296c0 18-9.7 32.4-21.1 42.7s-26.7 18.5-43.5 24.9c-9.6 3.7-20.1 6.9-31.3 9.6V323.6c5.1-1.5 9.8-3.1 14.2-4.8c13.6-5.2 23-10.8 28.5-15.7c2.7-2.4 4.1-4.3 4.7-5.5c.6-1.1 .6-1.5 .6-1.5v0V252.4c-5.3 2.6-10.9 5-16.7 7.2c-9.6 3.7-20.1 6.9-31.3 9.6V219.6c5.1-1.5 9.8-3.1 14.2-4.8c13.6-5.2 23-10.8 28.5-15.7c2.7-2.4 4.1-4.3 4.7-5.5c.5-.9 .6-1.4 .6-1.5v0 0V148.4c-5.3 2.6-10.9 5-16.7 7.2c-13.7 5.2-29.4 9.6-46.3 12.9c-5.1-7.5-11-13.9-16.8-19.1c-10.1-9.1-21.8-16.5-34-22.6c31.7-2.3 59.3-8.2 80-16.1c13.6-5.2 23-10.8 28.5-15.7c2.7-2.4 4.1-4.3 4.7-5.5c.5-.9 .6-1.4 .6-1.5v0 0 0c0 0 0-.5-.6-1.5c-.6-1.2-2-3.1-4.7-5.5c-5.5-5-14.9-10.6-28.5-15.7C403.2 54.9 364.2 48 320 48s-83.2 6.9-110.2 17.2c-13.6 5.2-23 10.8-28.5 15.7c-2.7 2.4-4.1 4.3-4.7 5.5c-.6 1.1-.6 1.5-.6 1.5l0 0zM48 216v0c0 .1 .1 .6 .6 1.5c.6 1.2 2 3.1 4.7 5.5c5.5 5 14.9 10.6 28.5 15.7c27 10.3 66 17.2 110.2 17.2s83.2-6.9 110.2-17.2c13.6-5.2 23-10.8 28.5-15.7c2.7-2.4 4.1-4.3 4.7-5.5c.5-.9 .6-1.4 .6-1.5v0 0 0c0 0 0-.5-.6-1.5c-.6-1.2-2-3.1-4.7-5.5c-5.5-5-14.9-10.6-28.5-15.7c-27-10.3-66-17.2-110.2-17.2s-83.2 6.9-110.2 17.2c-13.6 5.2-23 10.8-28.5 15.7c-2.7 2.4-4.1 4.3-4.7 5.5c-.6 1.1-.6 1.5-.6 1.5l0 0zM0 216c0-18 9.7-32.4 21.1-42.7s26.7-18.5 43.5-24.9C98.4 135.5 143.5 128 192 128s93.6 7.5 127.3 20.3c16.8 6.4 32.1 14.6 43.5 24.9S384 198 384 216V320 424c0 18-9.7 32.4-21.1 42.7s-26.7 18.5-43.5 24.9C285.6 504.5 240.5 512 192 512s-93.6-7.5-127.3-20.3c-16.8-6.4-32-14.6-43.5-24.9S0 442 0 424V320 216zM336 320V276.4c-5.3 2.6-10.9 5-16.7 7.2C285.6 296.5 240.5 304 192 304s-93.6-7.5-127.3-20.3c-5.8-2.2-11.4-4.6-16.7-7.2V320v0c0 .1 .1 .6 .6 1.5c.6 1.2 2 3.1 4.7 5.5c5.5 5 14.9 10.6 28.5 15.7c27 10.3 66 17.2 110.2 17.2s83.2-6.9 110.2-17.2c13.6-5.2 23-10.8 28.5-15.7c2.7-2.4 4.1-4.3 4.7-5.5c.5-.9 .6-1.4 .6-1.5v0 0zM64.7 387.7c-5.8-2.2-11.4-4.6-16.7-7.2V424l0 0c0 0 0 .5 .6 1.5c.6 1.2 2 3.1 4.7 5.5c5.5 5 14.9 10.6 28.5 15.7c27 10.3 66 17.2 110.2 17.2s83.2-6.9 110.2-17.2c13.6-5.2 23-10.8 28.5-15.7c2.7-2.4 4.1-4.3 4.7-5.5c.6-1.1 .6-1.5 .6-1.5v0V380.4c-5.3 2.6-10.9 5-16.7 7.2C285.6 400.5 240.5 408 192 408s-93.6-7.5-127.3-20.3z',
    ],
};
var faCommentLines = {
    prefix: 'far',
    iconName: 'comment-lines',
    icon: [
        512,
        512,
        [],
        'f4b0',
        'M168.2 384.9c-15-5.4-31.7-3.1-44.6 6.4c-8.2 6-22.3 14.8-39.4 22.7c5.6-14.7 9.9-31.3 11.3-49.4c1-12.9-3.3-25.7-11.8-35.5C60.4 302.8 48 272 48 240c0-79.5 83.3-160 208-160s208 80.5 208 160s-83.3 160-208 160c-31.6 0-61.3-5.5-87.8-15.1zM26.3 423.8c-1.6 2.7-3.3 5.4-5.1 8.1l-.3 .5c-1.6 2.3-3.2 4.6-4.8 6.9c-3.5 4.7-7.3 9.3-11.3 13.5c-4.6 4.6-5.9 11.4-3.4 17.4c2.5 6 8.3 9.9 14.8 9.9c5.1 0 10.2-.3 15.3-.8l.7-.1c4.4-.5 8.8-1.1 13.2-1.9c.8-.1 1.6-.3 2.4-.5c17.8-3.5 34.9-9.5 50.1-16.1c22.9-10 42.4-21.9 54.3-30.6c31.8 11.5 67 17.9 104.1 17.9c141.4 0 256-93.1 256-208S397.4 32 256 32S0 125.1 0 240c0 45.1 17.7 86.8 47.7 120.9c-1.9 24.5-11.4 46.3-21.4 62.9zM152 176c-13.3 0-24 10.7-24 24s10.7 24 24 24H360c13.3 0 24-10.7 24-24s-10.7-24-24-24H152zm0 96c-13.3 0-24 10.7-24 24s10.7 24 24 24H264c13.3 0 24-10.7 24-24s-10.7-24-24-24H152z',
    ],
};
var faCreditCardBlank = {
    prefix: 'far',
    iconName: 'credit-card-blank',
    icon: [
        576,
        512,
        [],
        'f389',
        'M64 80c-8.8 0-16 7.2-16 16V416c0 8.8 7.2 16 16 16H512c8.8 0 16-7.2 16-16V96c0-8.8-7.2-16-16-16H64zM0 96C0 60.7 28.7 32 64 32H512c35.3 0 64 28.7 64 64V416c0 35.3-28.7 64-64 64H64c-35.3 0-64-28.7-64-64V96zM96 360c0-13.3 10.7-24 24-24h48c13.3 0 24 10.7 24 24s-10.7 24-24 24H120c-13.3 0-24-10.7-24-24zm128 0c0-13.3 10.7-24 24-24H360c13.3 0 24 10.7 24 24s-10.7 24-24 24H248c-13.3 0-24-10.7-24-24z',
    ],
};
var faDesktop = {
    prefix: 'far',
    iconName: 'desktop',
    icon: [
        576,
        512,
        [128421, 61704, 'desktop-alt'],
        'f390',
        'M512 48H64c-8.8 0-16 7.2-16 16V256H528V64c0-8.8-7.2-16-16-16zm64 208v48 48c0 35.3-28.7 64-64 64H364.3l8 48H424c13.3 0 24 10.7 24 24s-10.7 24-24 24H352 224 152c-13.3 0-24-10.7-24-24s10.7-24 24-24h51.7l8-48H64c-35.3 0-64-28.7-64-64V304 256 64C0 28.7 28.7 0 64 0H512c35.3 0 64 28.7 64 64V256zM48 304v48c0 8.8 7.2 16 16 16H239.5c.3 0 .6 0 .8 0h95.2c.3 0 .6 0 .8 0H512c8.8 0 16-7.2 16-16V304H48zM252.3 464h71.3l-8-48H260.3l-8 48z',
    ],
};
var faDroplet = {
    prefix: 'far',
    iconName: 'droplet',
    icon: [
        384,
        512,
        [128167, 'tint'],
        'f043',
        'M192 464c-79.5 0-144-64.5-144-144c0-13 5.1-33.5 17-61.1c11.5-26.6 27.6-55.8 45.5-84.7c29-46.8 61-90.2 81.5-117c20.5 26.7 52.6 70.2 81.5 117c17.9 28.9 34 58 45.5 84.7c11.9 27.6 17 48.2 17 61.1c0 79.5-64.5 144-144 144zM0 320C0 426 86 512 192 512s192-86 192-192c0-91.2-130.2-262.3-166.6-308.3C211.4 4.2 202.5 0 192.9 0h-1.8c-9.6 0-18.5 4.2-24.5 11.7C130.2 57.7 0 228.8 0 320zm144-8c0-13.3-10.7-24-24-24s-24 10.7-24 24c0 57.4 46.6 104 104 104c13.3 0 24-10.7 24-24s-10.7-24-24-24c-30.9 0-56-25.1-56-56z',
    ],
};
var faElevator = {
    prefix: 'far',
    iconName: 'elevator',
    icon: [
        512,
        512,
        [],
        'e16d',
        'M132.7 4.7c6.2-6.2 16.4-6.2 22.6 0l64 64c4.6 4.6 5.9 11.5 3.5 17.4s-8.3 9.9-14.8 9.9H80c-6.5 0-12.3-3.9-14.8-9.9s-1.1-12.9 3.5-17.4l64-64zm224 86.6l-64-64c-4.6-4.6-5.9-11.5-3.5-17.4s8.3-9.9 14.8-9.9H432c6.5 0 12.3 3.9 14.8 9.9s1.1 12.9-3.5 17.4l-64 64c-6.2 6.2-16.4 6.2-22.6 0zM64 176c-8.8 0-16 7.2-16 16V448c0 8.8 7.2 16 16 16H448c8.8 0 16-7.2 16-16V192c0-8.8-7.2-16-16-16H64zM0 192c0-35.3 28.7-64 64-64H448c35.3 0 64 28.7 64 64V448c0 35.3-28.7 64-64 64H64c-35.3 0-64-28.7-64-64V192zM272 400c0-26.5 21.5-48 48-48h64c26.5 0 48 21.5 48 48c0 17.7-14.3 32-32 32H304c-17.7 0-32-14.3-32-32zM128 352h64c26.5 0 48 21.5 48 48c0 17.7-14.3 32-32 32H112c-17.7 0-32-14.3-32-32c0-26.5 21.5-48 48-48zm176-80a48 48 0 1 1 96 0 48 48 0 1 1 -96 0zM160 224a48 48 0 1 1 0 96 48 48 0 1 1 0-96z',
    ],
};
var faEllipsisVertical = {
    prefix: 'far',
    iconName: 'ellipsis-vertical',
    icon: [
        128,
        512,
        ['ellipsis-v'],
        'f142',
        'M64 368a48 48 0 1 0 0 96 48 48 0 1 0 0-96zm0-160a48 48 0 1 0 0 96 48 48 0 1 0 0-96zM112 96A48 48 0 1 0 16 96a48 48 0 1 0 96 0z',
    ],
};
var faEnvelope = {
    prefix: 'far',
    iconName: 'envelope',
    icon: [
        512,
        512,
        [128386, 9993, 61443],
        'f0e0',
        'M64 112c-8.8 0-16 7.2-16 16v22.1L220.5 291.7c20.7 17 50.4 17 71.1 0L464 150.1V128c0-8.8-7.2-16-16-16H64zM48 212.2V384c0 8.8 7.2 16 16 16H448c8.8 0 16-7.2 16-16V212.2L322 328.8c-38.4 31.5-93.7 31.5-132 0L48 212.2zM0 128C0 92.7 28.7 64 64 64H448c35.3 0 64 28.7 64 64V384c0 35.3-28.7 64-64 64H64c-35.3 0-64-28.7-64-64V128z',
    ],
};
var faEnvelopeCircleCheck = {
    prefix: 'far',
    iconName: 'envelope-circle-check',
    icon: [
        640,
        512,
        [],
        'e4e8',
        'M64 112H448c8.8 0 16 7.2 16 16v22.1l-22.6 18.5C458.6 163 477 160 496 160c5.4 0 10.7 .2 16 .7V128c0-35.3-28.7-64-64-64H64C28.7 64 0 92.7 0 128V384c0 35.3 28.7 64 64 64H360.2c-11.8-14.3-21.4-30.5-28.2-48H64c-8.8 0-16-7.2-16-16V212.2L190 328.8c37.7 31 91.8 31.5 130.1 1.5c.9-29.2 8.9-56.5 22.4-80.4l-50.9 41.8c-20.7 17-50.4 17-71.1 0L48 150.1V128c0-8.8 7.2-16 16-16zM640 336a144 144 0 1 0 -288 0 144 144 0 1 0 288 0zm-76.7-43.3c6.2 6.2 6.2 16.4 0 22.6l-72 72c-6.2 6.2-16.4 6.2-22.6 0l-40-40c-6.2-6.2-6.2-16.4 0-22.6s16.4-6.2 22.6 0L480 353.4l60.7-60.7c6.2-6.2 16.4-6.2 22.6 0z',
    ],
};
var faEnvelopeOpen = {
    prefix: 'far',
    iconName: 'envelope-open',
    icon: [
        512,
        512,
        [62135],
        'f2b6',
        'M255.4 48.2c.2-.1 .4-.2 .6-.2s.4 .1 .6 .2L460.6 194c2.1 1.5 3.4 3.9 3.4 6.5v13.6L291.5 355.7c-20.7 17-50.4 17-71.1 0L48 214.1V200.5c0-2.6 1.2-5 3.4-6.5L255.4 48.2zM48 276.2L190 392.8c38.4 31.5 93.7 31.5 132 0L464 276.2V456c0 4.4-3.6 8-8 8H56c-4.4 0-8-3.6-8-8V276.2zM256 0c-10.2 0-20.2 3.2-28.5 9.1L23.5 154.9C8.7 165.4 0 182.4 0 200.5V456c0 30.9 25.1 56 56 56H456c30.9 0 56-25.1 56-56V200.5c0-18.1-8.7-35.1-23.4-45.6L284.5 9.1C276.2 3.2 266.2 0 256 0z',
    ],
};
var faEnvelopeOpenText = {
    prefix: 'far',
    iconName: 'envelope-open-text',
    icon: [
        512,
        512,
        [],
        'f658',
        'M392 48H120c-4.4 0-8 3.6-8 8V225.2L69.2 190.1c-1.7-1.4-3.4-2.6-5.2-3.8V56C64 25.1 89.1 0 120 0H392c30.9 0 56 25.1 56 56V186.2c-1.8 1.2-3.5 2.5-5.2 3.8L400 225.2V56c0-4.4-3.6-8-8-8zM160 120c0-13.3 10.7-24 24-24H328c13.3 0 24 10.7 24 24s-10.7 24-24 24H184c-13.3 0-24-10.7-24-24zm0 80c0-13.3 10.7-24 24-24H328c13.3 0 24 10.7 24 24s-10.7 24-24 24H184c-13.3 0-24-10.7-24-24zM48 276.2V456c0 4.4 3.6 8 8 8H456c4.4 0 8-3.6 8-8V276.2L322 392.8c-38.4 31.5-93.7 31.5-132 0L48 276.2zM0 237.9C0 221.4 13.4 208 29.9 208c6.9 0 13.6 2.4 19 6.8l171.6 141c20.7 17 50.4 17 71.1 0l171.6-141c5.3-4.4 12.1-6.8 19-6.8c16.5 0 29.9 13.4 29.9 29.9V456c0 30.9-25.1 56-56 56H56c-30.9 0-56-25.1-56-56V237.9z',
    ],
};
var faEnvelopesBulk = {
    prefix: 'far',
    iconName: 'envelopes-bulk',
    icon: [
        640,
        512,
        ['mail-bulk'],
        'f674',
        'M112 48H432V96h48V48c0-26.5-21.5-48-48-48H112C85.5 0 64 21.5 64 48V192h48V48zM48 272H336v6.9L192.1 385.4c0 0-.1 0-.1 0s-.1 0-.1 0L48 278.9V272zm0 66.6L163.3 424c8.3 6.2 18.4 9.5 28.7 9.5s20.4-3.3 28.7-9.5L336 338.6V464H48V338.6zM0 272V464c0 26.5 21.5 48 48 48H336c26.5 0 48-21.5 48-48V272c0-26.5-21.5-48-48-48H48c-26.5 0-48 21.5-48 48zm272-80V176H592V368H416v48H592c26.5 0 48-21.5 48-48V176c0-26.5-21.5-48-48-48H272c-26.5 0-48 21.5-48 48v16h48zm240 16c-8.8 0-16 7.2-16 16v32c0 8.8 7.2 16 16 16h32c8.8 0 16-7.2 16-16V224c0-8.8-7.2-16-16-16H512z',
    ],
};
var faEuroSign = {
    prefix: 'far',
    iconName: 'euro-sign',
    icon: [
        320,
        512,
        [8364, 'eur', 'euro'],
        'f153',
        'M48.6 240c-.4 5.3-.6 10.6-.6 16s.2 10.7 .6 16H24c-13.3 0-24 10.7-24 24s10.7 24 24 24H57.3C84.8 412.5 170.5 480 272 480h24c13.3 0 24-10.7 24-24s-10.7-24-24-24H272c-74.6 0-138.4-46.4-164-112H264c13.3 0 24-10.7 24-24s-10.7-24-24-24H96.7c-.5-5.3-.7-10.6-.7-16s.2-10.7 .7-16H264c13.3 0 24-10.7 24-24s-10.7-24-24-24H108c25.6-65.6 89.4-112 164-112h24c13.3 0 24-10.7 24-24s-10.7-24-24-24H272C170.5 32 84.8 99.5 57.3 192H24c-13.3 0-24 10.7-24 24s10.7 24 24 24H48.6z',
    ],
};
var faEye = {
    prefix: 'far',
    iconName: 'eye',
    icon: [
        576,
        512,
        [128065],
        'f06e',
        'M288 80c-65.2 0-118.8 29.6-159.9 67.7C89.6 183.5 63 226 49.4 256c13.6 30 40.2 72.5 78.6 108.3C169.2 402.4 222.8 432 288 432s118.8-29.6 159.9-67.7C486.4 328.5 513 286 526.6 256c-13.6-30-40.2-72.5-78.6-108.3C406.8 109.6 353.2 80 288 80zM95.4 112.6C142.5 68.8 207.2 32 288 32s145.5 36.8 192.6 80.6c46.8 43.5 78.1 95.4 93 131.1c3.3 7.9 3.3 16.7 0 24.6c-14.9 35.7-46.2 87.7-93 131.1C433.5 443.2 368.8 480 288 480s-145.5-36.8-192.6-80.6C48.6 356 17.3 304 2.5 268.3c-3.3-7.9-3.3-16.7 0-24.6C17.3 208 48.6 156 95.4 112.6zM288 336c44.2 0 80-35.8 80-80s-35.8-80-80-80c-.7 0-1.3 0-2 0c1.3 5.1 2 10.5 2 16c0 35.3-28.7 64-64 64c-5.5 0-10.9-.7-16-2c0 .7 0 1.3 0 2c0 44.2 35.8 80 80 80zm0-208a128 128 0 1 1 0 256 128 128 0 1 1 0-256z',
    ],
};
var faFileContract = {
    prefix: 'far',
    iconName: 'file-contract',
    icon: [
        384,
        512,
        [],
        'f56c',
        'M48 448V64c0-8.8 7.2-16 16-16H224v80c0 17.7 14.3 32 32 32h80V448c0 8.8-7.2 16-16 16H64c-8.8 0-16-7.2-16-16zM64 0C28.7 0 0 28.7 0 64V448c0 35.3 28.7 64 64 64H320c35.3 0 64-28.7 64-64V154.5c0-17-6.7-33.3-18.7-45.3L274.7 18.7C262.7 6.7 246.5 0 229.5 0H64zM96 96c-8.8 0-16 7.2-16 16s7.2 16 16 16h80c8.8 0 16-7.2 16-16s-7.2-16-16-16H96zm0 64c-8.8 0-16 7.2-16 16s7.2 16 16 16h80c8.8 0 16-7.2 16-16s-7.2-16-16-16H96zm54.2 221.8L160 349l16.7 55.6c1.9 6.3 7.4 10.8 13.9 11.3s12.8-2.9 15.7-8.8l10.6-21.3c.6-1.2 1.8-1.9 3.1-1.9s2.5 .7 3.1 1.9l10.6 21.3c2.7 5.4 8.3 8.8 14.3 8.8h40c8.8 0 16-7.2 16-16s-7.2-16-16-16H257.9l-6.2-12.4c-6-12-18.3-19.6-31.7-19.6c-8.6 0-16.8 3.1-23.1 8.6l-11.3-37.5C182.2 311.7 171.8 304 160 304s-22.2 7.7-25.6 19.1l-14.9 49.5c-2 6.8-8.3 11.4-15.3 11.4H96c-8.8 0-16 7.2-16 16s7.2 16 16 16h8.2c21.2 0 39.9-13.9 46-34.2z',
    ],
};
var faFileInvoice = {
    prefix: 'far',
    iconName: 'file-invoice',
    icon: [
        384,
        512,
        [],
        'f570',
        'M64 464H320c8.8 0 16-7.2 16-16V160H256c-17.7 0-32-14.3-32-32V48H64c-8.8 0-16 7.2-16 16V448c0 8.8 7.2 16 16 16zM0 64C0 28.7 28.7 0 64 0H229.5c17 0 33.3 6.7 45.3 18.7l90.5 90.5c12 12 18.7 28.3 18.7 45.3V448c0 35.3-28.7 64-64 64H64c-35.3 0-64-28.7-64-64V64zm80 48c0-8.8 7.2-16 16-16h80c8.8 0 16 7.2 16 16s-7.2 16-16 16H96c-8.8 0-16-7.2-16-16zm0 64c0-8.8 7.2-16 16-16h80c8.8 0 16 7.2 16 16s-7.2 16-16 16H96c-8.8 0-16-7.2-16-16zM192 400c0-8.8 7.2-16 16-16h80c8.8 0 16 7.2 16 16s-7.2 16-16 16H208c-8.8 0-16-7.2-16-16zM112 224H272c17.7 0 32 14.3 32 32v64c0 17.7-14.3 32-32 32H112c-17.7 0-32-14.3-32-32V256c0-17.7 14.3-32 32-32z',
    ],
};
var faFileLines = {
    prefix: 'far',
    iconName: 'file-lines',
    icon: [
        384,
        512,
        [128441, 128462, 61686, 'file-alt', 'file-text'],
        'f15c',
        'M64 464c-8.8 0-16-7.2-16-16V64c0-8.8 7.2-16 16-16H224v80c0 17.7 14.3 32 32 32h80V448c0 8.8-7.2 16-16 16H64zM64 0C28.7 0 0 28.7 0 64V448c0 35.3 28.7 64 64 64H320c35.3 0 64-28.7 64-64V154.5c0-17-6.7-33.3-18.7-45.3L274.7 18.7C262.7 6.7 246.5 0 229.5 0H64zm56 256c-13.3 0-24 10.7-24 24s10.7 24 24 24H264c13.3 0 24-10.7 24-24s-10.7-24-24-24H120zm0 96c-13.3 0-24 10.7-24 24s10.7 24 24 24H264c13.3 0 24-10.7 24-24s-10.7-24-24-24H120z',
    ],
};
var faFilePdf = {
    prefix: 'far',
    iconName: 'file-pdf',
    icon: [
        512,
        512,
        [],
        'f1c1',
        'M64 464H96v48H64c-35.3 0-64-28.7-64-64V64C0 28.7 28.7 0 64 0H229.5c17 0 33.3 6.7 45.3 18.7l90.5 90.5c12 12 18.7 28.3 18.7 45.3V288H336V160H256c-17.7 0-32-14.3-32-32V48H64c-8.8 0-16 7.2-16 16V448c0 8.8 7.2 16 16 16zM176 352h32c30.9 0 56 25.1 56 56s-25.1 56-56 56H192v32c0 8.8-7.2 16-16 16s-16-7.2-16-16V448 368c0-8.8 7.2-16 16-16zm32 80c13.3 0 24-10.7 24-24s-10.7-24-24-24H192v48h16zm96-80h32c26.5 0 48 21.5 48 48v64c0 26.5-21.5 48-48 48H304c-8.8 0-16-7.2-16-16V368c0-8.8 7.2-16 16-16zm32 128c8.8 0 16-7.2 16-16V400c0-8.8-7.2-16-16-16H320v96h16zm80-112c0-8.8 7.2-16 16-16h48c8.8 0 16 7.2 16 16s-7.2 16-16 16H448v32h32c8.8 0 16 7.2 16 16s-7.2 16-16 16H448v48c0 8.8-7.2 16-16 16s-16-7.2-16-16V432 368z',
    ],
};
var faFilePlus = {
    prefix: 'far',
    iconName: 'file-plus',
    icon: [
        384,
        512,
        [],
        'f319',
        'M48 448V64c0-8.8 7.2-16 16-16H224v80c0 17.7 14.3 32 32 32h80V448c0 8.8-7.2 16-16 16H64c-8.8 0-16-7.2-16-16zM64 0C28.7 0 0 28.7 0 64V448c0 35.3 28.7 64 64 64H320c35.3 0 64-28.7 64-64V154.5c0-17-6.7-33.3-18.7-45.3L274.7 18.7C262.7 6.7 246.5 0 229.5 0H64zM192 208c-13.3 0-24 10.7-24 24v48H120c-13.3 0-24 10.7-24 24s10.7 24 24 24h48v48c0 13.3 10.7 24 24 24s24-10.7 24-24V328h48c13.3 0 24-10.7 24-24s-10.7-24-24-24H216V232c0-13.3-10.7-24-24-24z',
    ],
};
var faFireBurner = {
    prefix: 'far',
    iconName: 'fire-burner',
    icon: [
        640,
        512,
        [],
        'e4f1',
        'M293.5 3.8c19.7 17.8 38.2 37 55.5 57.7c7.9-9.9 16.8-20.7 26.5-29.5c5.6-5.1 14.4-5.1 20 0c24.7 22.7 45.6 52.7 60.4 81.1c14.5 28 24.2 58.8 24.2 79C480 280 408.7 352 320 352c-89.7 0-160-72.1-160-159.8c0-26.4 12.7-60.7 32.4-92.6c20-32.4 48.1-66.1 81.4-95.8c2.8-2.5 6.4-3.8 10-3.7c3.5 0 7 1.3 9.8 3.8zM370 273c30-21 38-63 20-96c-2-4-4-8-7-12l-36 42s-58-74-62-79c-30 37-45 58-45 82c0 49 36 78 81 78c18 0 34-5 49-15zM32 280c0-13.3 10.7-24 24-24h48c13.3 0 24 10.7 24 24s-10.7 24-24 24H80v80H560V304H536c-13.3 0-24-10.7-24-24s10.7-24 24-24h48c13.3 0 24 10.7 24 24V384c17.7 0 32 14.3 32 32v64c0 17.7-14.3 32-32 32H32c-17.7 0-32-14.3-32-32V416c0-17.7 14.3-32 32-32V280zM48 464H164.3c-2.7-4.7-4.3-10.2-4.3-16s1.6-11.3 4.3-16H48v32zm171.7 0h72.6c-2.7-4.7-4.3-10.2-4.3-16s1.6-11.3 4.3-16H219.7c2.7 4.7 4.3 10.2 4.3 16s-1.6 11.3-4.3 16zm128 0h72.6c-2.7-4.7-4.3-10.2-4.3-16s1.6-11.3 4.3-16H347.7c2.7 4.7 4.3 10.2 4.3 16s-1.6 11.3-4.3 16zM592 464V432H475.7c2.7 4.7 4.3 10.2 4.3 16s-1.6 11.3-4.3 16H592z',
    ],
};
var faFireExtinguisher = {
    prefix: 'far',
    iconName: 'fire-extinguisher',
    icon: [
        512,
        512,
        [129519],
        'f134',
        'M500.3 7.3C507.7 13.3 512 22.4 512 32v96c0 9.6-4.3 18.7-11.7 24.7s-17.2 8.5-26.6 6.6l-160-32C301.5 124.9 292 115.7 289 104H200v26.6c50.3 11 88 55.8 88 109.4V448c0 35.3-28.7 64-64 64H128c-35.3 0-64-28.7-64-64V240c0-53.6 37.7-98.4 88-109.4V104.9c-46.9 5.5-86.5 34.8-106.3 75.6c-5.8 11.9-20.2 16.9-32.1 11.1S-3.3 171.4 2.5 159.5C30.1 102.8 86.1 62.4 152 56.7V24c0-13.3 10.7-24 24-24s24 10.7 24 24V56h89c3-11.7 12.5-20.9 24.7-23.4l160-32c9.4-1.9 19.1 .6 26.6 6.6zM112 416v32c0 8.8 7.2 16 16 16h96c8.8 0 16-7.2 16-16V416H112zm0-48H240V240c0-35.3-28.7-64-64-64s-64 28.7-64 64V368z',
    ],
};
var faFireFlame = {
    prefix: 'far',
    iconName: 'fire-flame',
    icon: [
        384,
        512,
        ['flame'],
        'f6df',
        'M209.1 8.5c72.9 68 128.7 152.4 162.7 246.1l.7 1.9c7.6 20.9 11.5 42.9 11.5 65.1C384 426.7 297.2 512 192 512S0 426.7 0 321.6c0-22.2 3.9-44.2 11.5-65.1l.7-1.9L57.3 271l-.7 1.9c-5.7 15.6-8.6 32.1-8.6 48.7C48 399.7 112.8 464 192 464s144-64.3 144-142.4c0-16.6-2.9-33.1-8.6-48.7l42.3-15.4-42.3 15.4-.7-1.9c-29-79.8-75-152.2-134.7-212.2C132.3 118.8 86.3 191.2 57.3 271L12.2 254.6C46.2 160.8 102 76.5 174.9 8.5l3.3-3C181.9 2 186.9 0 192 0s10.1 2 13.8 5.5l3.3 3zM189.3 163.8c1.7-2.4 4.4-3.8 7.4-3.8c5 0 9.1 4.1 9.1 9.1v10.8c0 7.8 3.1 15.3 8.5 20.8l44.1 45.1c19 19.4 29.6 45.5 29.6 72.6V320c0 53-43 96-96 96s-96-43-96-96V308.6c0-18.4 6.6-36.3 18.6-50.3l6-7c1.8-2.1 4.4-3.3 7.1-3.3c5.2 0 9.4 4.2 9.4 9.4v40.9c0 20.8 16.9 37.7 37.7 37.7s37.7-16.9 37.7-37.7v-1.5c0-10.2-3.9-19.9-11-27.3l-11-11.4c-12.3-12.8-19.2-29.8-19.2-47.5c0-14.4 4.5-28.4 13-40.1l4.9-6.7z',
    ],
};
var faFireFlameSimple = {
    prefix: 'far',
    iconName: 'fire-flame-simple',
    icon: [
        384,
        512,
        ['burn'],
        'f46a',
        'M209.1 8.5c72.9 68 128.7 152.4 162.7 246.1l.7 1.9c7.6 20.9 11.5 42.9 11.5 65.1C384 426.7 297.2 512 192 512S0 426.7 0 321.6c0-22.2 3.9-44.2 11.5-65.1l.7-1.9L57.3 271l-.7 1.9c-5.7 15.6-8.6 32.1-8.6 48.7C48 399.7 112.8 464 192 464s144-64.3 144-142.4c0-16.6-2.9-33.1-8.6-48.7l42.3-15.4-42.3 15.4-.7-1.9c-29-79.8-75-152.2-134.7-212.2C132.3 118.8 86.3 191.2 57.3 271L12.2 254.6C46.2 160.8 102 76.5 174.9 8.5l3.3-3C181.9 2 186.9 0 192 0s10.1 2 13.8 5.5l3.3 3zm72.6 281.4c4.1 9.3 6.2 19.4 6.2 29.5c0 53-43 96.5-96 96.5s-96-43.5-96-96.5c0-10.1 2.1-20.3 6.2-29.5l1.9-4.3c15.8-35.4 37.9-67.7 65.3-95.1l8.9-8.9c3.6-3.6 8.5-5.6 13.6-5.6s10 2 13.6 5.6l8.9 8.9c27.4 27.4 49.6 59.7 65.3 95.1l1.9 4.3z',
    ],
};
var faFireSmoke = {
    prefix: 'far',
    iconName: 'fire-smoke',
    icon: [
        640,
        512,
        [],
        'f74b',
        'M293.5 3.8c-2.8-2.5-6.3-3.7-9.8-3.8c-3.6 0-7.2 1.2-10 3.7c-33.2 29.7-61.4 63.4-81.4 95.8c-19.7 31.9-32.4 66.2-32.4 92.6c0 19.8 3.6 38.8 10.1 56.3c9.6 3.3 18.7 7.6 27.3 12.7c12.7-13.6 27.6-25 44.2-33.8c-1.1-5.5-1.6-11.3-1.6-17.4c0-24 15-45 45-82c4 5 62 79 62 79l36-42c3 4 5 8 7 12c8.6 15.8 11.3 33.7 8.4 50.4c16.6 8.8 31.5 20.2 44.2 33.8c8.5-5.1 17.6-9.3 27.1-12.6C476.4 231 480 212 480 192.2c0-20.2-9.6-50.9-24.2-79C441.1 84.7 420.2 54.7 395.5 32c-5.6-5.1-14.4-5.2-20 0c-9.6 8.8-18.6 19.6-26.5 29.5c-17.3-20.7-35.8-39.9-55.5-57.7zM233.2 361.3c7-41.6 43.2-73.3 86.8-73.3s79.8 31.7 86.8 73.3c1.8 10.7 10.5 18.8 21.2 19.9s20.9-5.1 24.8-15.2c10.4-27 36.6-46 67.2-46c39.8 0 72 32.2 72 72c0 37.7-29 68.7-66 71.8c-.7 .1-1.3 .1-2 .2h-4H320 120h-4c-.7-.1-1.3-.2-2-.2c-36.9-3-66-34-66-71.8c0-39.8 32.2-72 72-72c30.6 0 56.7 19 67.2 46c3.9 10.1 14.1 16.3 24.8 15.2s19.4-9.2 21.2-19.9zM320 240c-49.4 0-92.7 26.4-116.5 65.8c-21.6-20.9-51-33.8-83.5-33.8C53.7 272 0 325.7 0 392c0 61.4 46.1 112 105.6 119.1c2 .6 4.2 .9 6.4 .9h8H320 520h8c2.2 0 4.3-.3 6.4-.9C593.9 504 640 453.4 640 392c0-66.3-53.7-120-120-120c-32.5 0-61.9 12.9-83.5 33.8C412.7 266.4 369.4 240 320 240z',
    ],
};
var faFlask = {
    prefix: 'far',
    iconName: 'flask',
    icon: [
        448,
        512,
        [],
        'f0c3',
        'M176 196.8c0 20.7-5.8 41-16.6 58.7L119.7 320H328.3l-39.7-64.5c-10.9-17.7-16.6-38-16.6-58.7V48H176V196.8zM320 48V196.8c0 11.8 3.3 23.5 9.5 33.5L437.7 406.2c6.7 10.9 10.3 23.5 10.3 36.4c0 38.3-31.1 69.4-69.4 69.4H69.4C31.1 512 0 480.9 0 442.6c0-12.8 3.6-25.4 10.3-36.4L118.5 230.4c6.2-10.1 9.5-21.7 9.5-33.5V48h-8c-13.3 0-24-10.7-24-24s10.7-24 24-24h40H288h40c13.3 0 24 10.7 24 24s-10.7 24-24 24h-8z',
    ],
};
var faFolderPlus = {
    prefix: 'far',
    iconName: 'folder-plus',
    icon: [
        512,
        512,
        [],
        'f65e',
        'M251.7 127.6l0 0c10.5 10.5 24.7 16.4 39.6 16.4H448c8.8 0 16 7.2 16 16V416c0 8.8-7.2 16-16 16H64c-8.8 0-16-7.2-16-16V96c0-8.8 7.2-16 16-16H197.5c4.2 0 8.3 1.7 11.3 4.7l33.9-33.9L208.8 84.7l42.9 42.9zm33.9-33.9L242.7 50.7c-12-12-28.3-18.7-45.3-18.7H64C28.7 32 0 60.7 0 96V416c0 35.3 28.7 64 64 64H448c35.3 0 64-28.7 64-64V160c0-35.3-28.7-64-64-64H291.3c-2.1 0-4.2-.8-5.7-2.3zM232 376c0 13.3 10.7 24 24 24s24-10.7 24-24V312h64c13.3 0 24-10.7 24-24s-10.7-24-24-24H280V200c0-13.3-10.7-24-24-24s-24 10.7-24 24v64H168c-13.3 0-24 10.7-24 24s10.7 24 24 24h64v64z',
    ],
};
var faForward = {
    prefix: 'far',
    iconName: 'forward',
    icon: [
        512,
        512,
        [9193],
        'f04e',
        'M224 94c0-16.6 13.5-30 30-30c6.3 0 12.5 2 17.6 5.7L502.1 236.6c6.2 4.5 9.9 11.7 9.9 19.4s-3.7 14.9-9.9 19.4L271.7 442.3c-5.1 3.7-11.3 5.7-17.6 5.7c-16.6 0-30-13.4-30-30V318.9L47.3 442.6c-5.1 3.5-11.1 5.4-17.3 5.4C13.5 448 0 434.5 0 417.9V94.1C0 77.5 13.5 64 30.1 64c6.2 0 12.2 1.9 17.3 5.4L224 193.1V94zm0 157.7L48 128.5v255L224 260.3v-8.6zM447.1 256L272 129.2V382.8L447.1 256z',
    ],
};
var faGear = {
    prefix: 'far',
    iconName: 'gear',
    icon: [
        512,
        512,
        [9881, 'cog'],
        'f013',
        'M256 0c17 0 33.6 1.7 49.8 4.8c7.9 1.5 21.8 6.1 29.4 20.1c2 3.7 3.6 7.6 4.6 11.8l9.3 38.5C350.5 81 360.3 86.7 366 85l38-11.2c4-1.2 8.1-1.8 12.2-1.9c16.1-.5 27 9.4 32.3 15.4c22.1 25.1 39.1 54.6 49.9 86.3c2.6 7.6 5.6 21.8-2.7 35.4c-2.2 3.6-4.9 7-8 10L459 246.3c-4.2 4-4.2 15.5 0 19.5l28.7 27.3c3.1 3 5.8 6.4 8 10c8.2 13.6 5.2 27.8 2.7 35.4c-10.8 31.7-27.8 61.1-49.9 86.3c-5.3 6-16.3 15.9-32.3 15.4c-4.1-.1-8.2-.8-12.2-1.9L366 427c-5.7-1.7-15.5 4-16.9 9.8l-9.3 38.5c-1 4.2-2.6 8.2-4.6 11.8c-7.7 14-21.6 18.5-29.4 20.1C289.6 510.3 273 512 256 512s-33.6-1.7-49.8-4.8c-7.9-1.5-21.8-6.1-29.4-20.1c-2-3.7-3.6-7.6-4.6-11.8l-9.3-38.5c-1.4-5.8-11.2-11.5-16.9-9.8l-38 11.2c-4 1.2-8.1 1.8-12.2 1.9c-16.1 .5-27-9.4-32.3-15.4c-22-25.1-39.1-54.6-49.9-86.3c-2.6-7.6-5.6-21.8 2.7-35.4c2.2-3.6 4.9-7 8-10L53 265.7c4.2-4 4.2-15.5 0-19.5L24.2 218.9c-3.1-3-5.8-6.4-8-10C8 195.3 11 181.1 13.6 173.6c10.8-31.7 27.8-61.1 49.9-86.3c5.3-6 16.3-15.9 32.3-15.4c4.1 .1 8.2 .8 12.2 1.9L146 85c5.7 1.7 15.5-4 16.9-9.8l9.3-38.5c1-4.2 2.6-8.2 4.6-11.8c7.7-14 21.6-18.5 29.4-20.1C222.4 1.7 239 0 256 0zM218.1 51.4l-8.5 35.1c-7.8 32.3-45.3 53.9-77.2 44.6L97.9 120.9c-16.5 19.3-29.5 41.7-38 65.7l26.2 24.9c24 22.8 24 66.2 0 89L59.9 325.4c8.5 24 21.5 46.4 38 65.7l34.6-10.2c31.8-9.4 69.4 12.3 77.2 44.6l8.5 35.1c24.6 4.5 51.3 4.5 75.9 0l8.5-35.1c7.8-32.3 45.3-53.9 77.2-44.6l34.6 10.2c16.5-19.3 29.5-41.7 38-65.7l-26.2-24.9c-24-22.8-24-66.2 0-89l26.2-24.9c-8.5-24-21.5-46.4-38-65.7l-34.6 10.2c-31.8 9.4-69.4-12.3-77.2-44.6l-8.5-35.1c-24.6-4.5-51.3-4.5-75.9 0zM208 256a48 48 0 1 0 96 0 48 48 0 1 0 -96 0zm48 96a96 96 0 1 1 0-192 96 96 0 1 1 0 192z',
    ],
};
var faGrid2 = {
    prefix: 'far',
    iconName: 'grid-2',
    icon: [
        512,
        512,
        [],
        'e196',
        'M80 80v96h96V80H80zM32 80c0-26.5 21.5-48 48-48h96c26.5 0 48 21.5 48 48v96c0 26.5-21.5 48-48 48H80c-26.5 0-48-21.5-48-48V80zM80 336v96h96V336H80zm-48 0c0-26.5 21.5-48 48-48h96c26.5 0 48 21.5 48 48v96c0 26.5-21.5 48-48 48H80c-26.5 0-48-21.5-48-48V336zM432 80H336v96h96V80zM336 32h96c26.5 0 48 21.5 48 48v96c0 26.5-21.5 48-48 48H336c-26.5 0-48-21.5-48-48V80c0-26.5 21.5-48 48-48zm0 304v96h96V336H336zm-48 0c0-26.5 21.5-48 48-48h96c26.5 0 48 21.5 48 48v96c0 26.5-21.5 48-48 48H336c-26.5 0-48-21.5-48-48V336z',
    ],
};
var faGrid2Plus = {
    prefix: 'far',
    iconName: 'grid-2-plus',
    icon: [
        512,
        512,
        [],
        'e197',
        'M176 80H80v96h96V80zM80 32h96c26.5 0 48 21.5 48 48v96c0 26.5-21.5 48-48 48H80c-26.5 0-48-21.5-48-48V80c0-26.5 21.5-48 48-48zm96 304H80v96h96V336zM80 288h96c26.5 0 48 21.5 48 48v96c0 26.5-21.5 48-48 48H80c-26.5 0-48-21.5-48-48V336c0-26.5 21.5-48 48-48zM336 80v96h96V80H336zm-48 0c0-26.5 21.5-48 48-48h96c26.5 0 48 21.5 48 48v96c0 26.5-21.5 48-48 48H336c-26.5 0-48-21.5-48-48V80zm96 192c13.3 0 24 10.7 24 24v64h64c13.3 0 24 10.7 24 24s-10.7 24-24 24H408v64c0 13.3-10.7 24-24 24s-24-10.7-24-24V408H296c-13.3 0-24-10.7-24-24s10.7-24 24-24h64V296c0-13.3 10.7-24 24-24z',
    ],
};
var faHammer = {
    prefix: 'far',
    iconName: 'hammer',
    icon: [
        576,
        512,
        [128296],
        'f6e3',
        'M432.8 204.9c-9.1-9.1-21.5-14.2-34.4-14.1c-11.3 .1-22.4-4.1-31-12.7L329.3 140c-5.9-5.9-9.3-14-9.3-22.4V105.5c0-17.6-9.6-33.7-25-42.1L273.3 51.5c8.4-2.3 17.1-3.5 25.8-3.5h18.1c24.5 0 48 9.3 65.8 26.1l44.6 42c9.7 9.1 14.3 21.7 13.5 34.1c-.8 13.7 4.3 27.1 14 36.8l37 37L472 244.1l-39.2-39.2zM216.7 75.3l.3 .2 55 30v12.1c0 21.1 8.4 41.4 23.3 56.3l38.1 38.1c18.1 18.1 41.8 26.9 65.4 26.7l39.2 39.2 1 1-16 16c-9.4 9.4-9.4 24.6 0 33.9s24.6 9.4 33.9 0L569 217c9.4-9.4 9.4-24.6 0-33.9s-24.6-9.4-33.9 0l-8 8-1-1-37-37c1.6-26.1-8-52.7-28.5-72l-44.6-42C389.2 14 353.9 0 317.2 0H299.2C279 0 259.1 4.2 240.7 12.4L219.1 22l-.3 .1-6.2 2.8-19.1 8.5c-5.6 2.5-9.2 7.9-9.5 14s3 11.8 8.3 14.7l18.4 10 6 3.3zm44.2 107.3L27.4 377.1C10.1 391.6 0 413.1 0 435.7C0 477.8 34.1 512 76.3 512c22.6 0 44.1-10.1 58.6-27.4L330 250.4c-6.8-4.5-13.2-9.7-19.2-15.7l-38.1-38.1c-4.3-4.3-8.3-9-11.8-14z',
    ],
};
var faHandWave = {
    prefix: 'far',
    iconName: 'hand-wave',
    icon: [
        512,
        512,
        [],
        'e1a7',
        'M336.2 82.3L335 73.8c-3-21.1-19.6-37.7-40.7-40.7l-8.5-1.2c-8.7-1.2-14.8-9.4-13.6-18.1S281.5-1.1 290.3 .2l8.5 1.2c35.2 5 62.9 32.7 67.9 67.9l1.2 8.5c1.2 8.7-4.8 16.9-13.6 18.1s-16.9-4.8-18.1-13.6zM80 68.7c17.9-17.9 43.8-23 66.2-15.2c3.1-6.8 7.4-13.1 13-18.7c25-25 65.5-25 90.5 0L368 153v-5.2c0-35.3 28.7-64 64-64s64 28.7 64 64V305.6c0 1.6 0 3.1-.1 4.7c0 .6 .1 1.1 .1 1.7c0 109.9-88.7 199.2-198.5 200l-.5 0h-1l0 0-6.6 0c-53 0-103.9-21.1-141.4-58.6L34.7 340.2c-25-25-25-65.5 0-90.5c3.9-3.9 8.2-7.2 12.7-9.9l-1.4-1.4c-25-25-25-65.5 0-90.5c5.6-5.6 11.9-9.9 18.7-13C57 112.5 62.1 86.6 80 68.7zm79.3 56.7l-.1-.1-22.6-22.6c-6.2-6.2-16.4-6.2-22.6 0s-6.2 16.4 0 22.6l22.5 22.5 .1 .1L232.7 244c9.4 9.4 9.4 24.6 0 33.9s-24.6 9.4-33.9 0l-96.1-96.1-.1-.1c-6.2-6.2-16.4-6.2-22.6 0s-6.2 16.4 0 22.6L119.5 244l.1 .1 56.6 56.6c9.4 9.4 9.4 24.6 0 33.9s-24.6 9.4-33.9 0L91.4 283.7l-.1-.1c-6.2-6.2-16.4-6.2-22.6 0s-6.2 16.4 0 22.6L181.9 419.5C210.4 448 249.1 464 289.4 464l6.6 0 0 0h.6l.2 0c82.8-.5 149.9-67.1 151.1-149.8c-.1-1-.1-2.1-.1-3.1c.1-1.8 .1-3.6 .1-5.5V147.9c0-8.8-7.2-16-16-16s-16 7.2-16 16V211c0 9.7-5.8 18.5-14.8 22.2s-19.3 1.7-26.2-5.2L215.8 68.7c-6.2-6.2-16.4-6.2-22.6 0c-6.2 6.2-6.2 16.3-.1 22.6l96.2 96.2c9.4 9.4 9.4 24.6 0 33.9s-24.6 9.4-33.9 0l-96.1-96.1zM13.7 368.2c8.7-1.2 16.9 4.8 18.1 13.6l1.2 8.5c3 21.1 19.6 37.7 40.7 40.7l8.5 1.2c8.7 1.3 14.8 9.4 13.6 18.1s-9.4 14.8-18.1 13.6l-8.5-1.2c-35.2-5-62.9-32.7-67.9-67.9L.2 386.3c-1.2-8.7 4.8-16.9 13.6-18.1z',
    ],
};
var faHorizontalRule = {
    prefix: 'far',
    iconName: 'horizontal-rule',
    icon: [
        640,
        512,
        [8213],
        'f86c',
        'M0 256c0-13.3 10.7-24 24-24H616c13.3 0 24 10.7 24 24s-10.7 24-24 24H24c-13.3 0-24-10.7-24-24z',
    ],
};
var faHouseBlank = {
    prefix: 'far',
    iconName: 'house-blank',
    icon: [
        576,
        512,
        ['home-blank'],
        'e487',
        'M303.5 5.7c-9-7.6-22.1-7.6-31.1 0l-264 224c-10.1 8.6-11.3 23.7-2.8 33.8s23.7 11.3 33.8 2.8L64 245.5V432c0 44.2 35.8 80 80 80H432c44.2 0 80-35.8 80-80V245.5l24.5 20.8c10.1 8.6 25.3 7.3 33.8-2.8s7.3-25.3-2.8-33.8l-264-224zM464 204.8V432c0 17.7-14.3 32-32 32H144c-17.7 0-32-14.3-32-32V204.8L288 55.5 464 204.8z',
    ],
};
var faHouseBuilding = {
    prefix: 'far',
    iconName: 'house-building',
    icon: [
        640,
        512,
        [],
        'e1b1',
        'M352 48H576c8.8 0 16 7.2 16 16V448c0 8.8-7.2 16-16 16H416c0 18-6 34.6-16 48H576c35.3 0 64-28.7 64-64V64c0-35.3-28.7-64-64-64H352c-35.3 0-64 28.7-64 64v60.6l48 44V64c0-8.8 7.2-16 16-16zm61.2 208H432c8.8 0 16-7.2 16-16V208c0-8.8-7.2-16-16-16H400c-8.8 0-16 7.2-16 16v4.6l6.1 5.6c11.2 10.3 19.2 23.4 23.1 37.9zm2.8 96h16c8.8 0 16-7.2 16-16V304c0-8.8-7.2-16-16-16H416v64zm64-144v32c0 8.8 7.2 16 16 16h32c8.8 0 16-7.2 16-16V208c0-8.8-7.2-16-16-16H496c-8.8 0-16 7.2-16 16zm16 80c-8.8 0-16 7.2-16 16v32c0 8.8 7.2 16 16 16h32c8.8 0 16-7.2 16-16V304c0-8.8-7.2-16-16-16H496zM400 96c-8.8 0-16 7.2-16 16v32c0 8.8 7.2 16 16 16h32c8.8 0 16-7.2 16-16V112c0-8.8-7.2-16-16-16H400zm80 16v32c0 8.8 7.2 16 16 16h32c8.8 0 16-7.2 16-16V112c0-8.8-7.2-16-16-16H496c-8.8 0-16 7.2-16 16zM20.8 237C7.5 249.1 0 266.2 0 284.2V448c0 35.3 28.7 64 64 64H320c35.3 0 64-28.7 64-64V284.2c0-17.9-7.5-35.1-20.8-47.2l-128-117.3c-24.5-22.4-62-22.4-86.5 0L20.8 237zM48 284.2c0-4.5 1.9-8.8 5.2-11.8L181.2 155c6.1-5.6 15.5-5.6 21.6 0l128 117.3c3.3 3 5.2 7.3 5.2 11.8V448c0 8.8-7.2 16-16 16H64c-8.8 0-16-7.2-16-16V284.2zM144 296v48c0 13.3 10.7 24 24 24h48c13.3 0 24-10.7 24-24V296c0-13.3-10.7-24-24-24H168c-13.3 0-24 10.7-24 24z',
    ],
};
var faIdCard = {
    prefix: 'far',
    iconName: 'id-card',
    icon: [
        576,
        512,
        [62147, 'drivers-license'],
        'f2c2',
        'M528 160V416c0 8.8-7.2 16-16 16H320c0-44.2-35.8-80-80-80H176c-44.2 0-80 35.8-80 80H64c-8.8 0-16-7.2-16-16V160H528zM64 32C28.7 32 0 60.7 0 96V416c0 35.3 28.7 64 64 64H512c35.3 0 64-28.7 64-64V96c0-35.3-28.7-64-64-64H64zM272 256a64 64 0 1 0 -128 0 64 64 0 1 0 128 0zm104-48c-13.3 0-24 10.7-24 24s10.7 24 24 24h80c13.3 0 24-10.7 24-24s-10.7-24-24-24H376zm0 96c-13.3 0-24 10.7-24 24s10.7 24 24 24h80c13.3 0 24-10.7 24-24s-10.7-24-24-24H376z',
    ],
};
var faInfinity = {
    prefix: 'far',
    iconName: 'infinity',
    icon: [
        640,
        512,
        [8734, 9854],
        'f534',
        'M0 237.3C0 159.3 63.3 96 141.3 96c39 0 76.2 16.1 102.9 44.5L320 221l75.8-80.5C422.5 112.1 459.7 96 498.7 96c78 0 141.3 63.3 141.3 141.3v37.4c0 78-63.3 141.3-141.3 141.3c-39 0-76.2-16.1-102.9-44.5L320 291l-75.8 80.5C217.5 399.9 180.3 416 141.3 416C63.3 416 0 352.7 0 274.7V237.3zM287 256l-77.8-82.6c-17.6-18.7-42.2-29.4-68-29.4C89.8 144 48 185.8 48 237.3v37.4c0 51.5 41.8 93.3 93.3 93.3c25.7 0 50.3-10.6 68-29.4L287 256zm65.9 0l77.8 82.6c17.6 18.7 42.2 29.4 68 29.4c51.5 0 93.3-41.8 93.3-93.3V237.3c0-51.5-41.8-93.3-93.3-93.3c-25.7 0-50.3 10.6-68 29.4L353 256z',
    ],
};
var faInputNumeric = {
    prefix: 'far',
    iconName: 'input-numeric',
    icon: [
        640,
        512,
        [],
        'e1bd',
        'M576 112c8.8 0 16 7.2 16 16V384c0 8.8-7.2 16-16 16H64c-8.8 0-16-7.2-16-16V128c0-8.8 7.2-16 16-16H576zM64 64C28.7 64 0 92.7 0 128V384c0 35.3 28.7 64 64 64H576c35.3 0 64-28.7 64-64V128c0-35.3-28.7-64-64-64H64zm40 120c0 13.3 10.7 24 24 24h8v96H120c-13.3 0-24 10.7-24 24s10.7 24 24 24h80c13.3 0 24-10.7 24-24s-10.7-24-24-24H184V184c0-13.3-10.7-24-24-24H128c-13.3 0-24 10.7-24 24zm190.6 30.4c5.7-8 17.5-8.6 24-1.2c5.2 5.9 5 14.7-.3 20.5l-72 78c-6.5 7-8.2 17.2-4.3 25.9s12.5 14.4 22 14.4h88c13.3 0 24-10.7 24-24s-10.7-24-24-24H318.8l34.8-37.7c22-23.8 22.4-60.3 1.1-84.7c-26.9-30.7-75.4-28.4-99.2 4.9l-11.1 15.6c-7.7 10.8-5.2 25.8 5.6 33.5s25.8 5.2 33.5-5.6l11.1-15.6z',
    ],
};
var faKey = {
    prefix: 'far',
    iconName: 'key',
    icon: [
        512,
        512,
        [128273],
        'f084',
        'M208 176c0-70.7 57.3-128 128-128s128 57.3 128 128s-57.3 128-128 128c-10.4 0-20.5-1.2-30.1-3.6c-8.1-2-16.7 .4-22.6 6.4L254.1 336H200c-13.3 0-24 10.7-24 24v40H136c-13.3 0-24 10.7-24 24v40H48V385.9L205.2 228.7c5.9-5.9 8.3-14.5 6.4-22.6c-2.3-9.6-3.6-19.7-3.6-30.1zM336 0C238.8 0 160 78.8 160 176c0 9.5 .7 18.8 2.2 27.9L7 359c-4.5 4.5-7 10.6-7 17V488c0 13.3 10.7 24 24 24H136c13.3 0 24-10.7 24-24V448h40c13.3 0 24-10.7 24-24V384h40c6.4 0 12.5-2.5 17-7l27.2-27.2c9.1 1.4 18.4 2.2 27.9 2.2c97.2 0 176-78.8 176-176S433.2 0 336 0zm32 176a32 32 0 1 0 0-64 32 32 0 1 0 0 64z',
    ],
};
var faLandmark = {
    prefix: 'far',
    iconName: 'landmark',
    icon: [
        512,
        512,
        [127963],
        'f66f',
        'M267.6 3c-7.2-4-16-4-23.2 0L17.6 128.1C6.7 134.1 0 145.5 0 157.9C0 176.8 15.2 192 34.1 192H477.9c18.8 0 34.1-15.2 34.1-34.1c0-12.4-6.7-23.8-17.6-29.8L267.6 3zM256 51.4L423.8 144H88.2L256 51.4zM112 224H64V384H56c-13.3 0-24 10.7-24 24s10.7 24 24 24H456c13.3 0 24-10.7 24-24s-10.7-24-24-24h-8V224H400V384H336V224H288V384H224V224H176V384H112V224zM0 488c0 13.3 10.7 24 24 24H488c13.3 0 24-10.7 24-24s-10.7-24-24-24H24c-13.3 0-24 10.7-24 24z',
    ],
};
var faLanguage = {
    prefix: 'far',
    iconName: 'language',
    icon: [
        640,
        512,
        [],
        'f1ab',
        'M64 64C28.7 64 0 92.7 0 128V384c0 35.3 28.7 64 64 64H272h32 16H576c35.3 0 64-28.7 64-64V128c0-35.3-28.7-64-64-64H320 304 272 64zm512 48c8.8 0 16 7.2 16 16V384c0 8.8-7.2 16-16 16H320V112H576zM178.3 175.9l64 144c4.5 10.1-.1 21.9-10.2 26.4s-21.9-.1-26.4-10.2L196.8 316H123.2l-8.9 20.1c-4.5 10.1-16.3 14.6-26.4 10.2s-14.6-16.3-10.2-26.4l64-144c3.2-7.2 10.4-11.9 18.3-11.9s15.1 4.7 18.3 11.9zM179 276l-19-42.8L141 276h38zM456 164c-11 0-20 9-20 20v4H384c-11 0-20 9-20 20s9 20 20 20h72 35.1c-7.3 16.7-17.4 31.9-29.8 45l-.5-.5-14.6-14.6c-7.8-7.8-20.5-7.8-28.3 0s-7.8 20.5 0 28.3L430 298.3c-5.9 3.6-12.1 6.9-18.5 9.8l-3.6 1.6c-10.1 4.5-14.6 16.3-10.2 26.4s16.3 14.6 26.4 10.2l3.6-1.6c12-5.3 23.4-11.8 34-19.4c4.3 3 8.6 5.8 13.1 8.5l18.9 11.3c9.5 5.7 21.8 2.6 27.4-6.9s2.6-21.8-6.9-27.4l-18.9-11.3c-.9-.5-1.8-1.1-2.7-1.6c17.2-18.8 30.7-40.9 39.6-65.4L534 228h2c11 0 20-9 20-20s-9-20-20-20H520 476v-4c0-11-9-20-20-20z',
    ],
};
var faLeaf = {
    prefix: 'far',
    iconName: 'leaf',
    icon: [
        512,
        512,
        [],
        'f06c',
        'M149.6 234.4c20.9-6.7 43.2-10.4 66.4-10.4h80c13.3 0 24 10.7 24 24s-10.7 24-24 24H216c-25.4 0-49.5 5.6-71 15.7C152.7 351 206.6 400 271.9 400h.1l.8 0 .1 0C370.2 399.5 464 299.7 464 156.6c0-12.8-.8-25.3-2.3-37.5C437 134.9 407.6 144 376 144l-104 0c-57.6 0-106.3 38.1-122.4 90.4zM96.4 260.1C102.5 168.5 178.8 96 272 96l104 0c28.7 0 54.8-10.8 74.6-28.5c.7-.6 1.4-1.3 2.1-1.9c6.6-6.2 12.5-13.2 17.4-20.9c1.6-2.5 3.2-5.1 4.6-7.8c3.5-6.5 13.6-6.8 16.2 .1c1.3 3.5 2.5 7 3.7 10.6c2.9 8.9 5.5 17.9 7.8 27.2c.5 1.9 .9 3.8 1.3 5.6c5.4 24.3 8.3 49.8 8.3 76.2C512 317.1 405.1 447.3 273 448l-1 0c-81.7 0-150.4-55.7-170.2-131.2C68.7 347.5 48 391.3 48 440v16c0 13.3-10.7 24-24 24s-24-10.7-24-24V440c0-75.1 38.3-141.2 96.4-179.9z',
    ],
};
var faLeafMaple = {
    prefix: 'far',
    iconName: 'leaf-maple',
    icon: [
        512,
        512,
        [127809],
        'f6f6',
        'M344.8 13.3c-3.7-7.4-11-12.4-19.3-13.2s-16.4 2.8-21.4 9.5L249.9 82l-2.1-16.1c-.9-7-4.8-13.2-10.7-17.1s-13.2-4.9-19.9-2.9L181.8 56.4 147 15.9c-4.6-5.3-11.2-8.4-18.2-8.4s-13.7 3.1-18.2 8.4L74 58.5 27 52.6c-7.9-1-15.8 2-21 8S-1.2 74.7 .8 82.4L41.4 238 10.7 258.5c-7 4.6-11 12.6-10.7 20.9s5 15.9 12.3 20l106.9 59.4L7 471c-9.4 9.4-9.4 24.6 0 33.9s24.6 9.4 33.9 0L153.2 392.8l59.4 106.9c4.1 7.3 11.6 12 20 12.3s16.3-3.7 20.9-10.7L274 470.6l155.6 40.6c7.7 2 15.9 .1 21.9-5.2s9-13.1 8-21l-5.4-42.8c-.3-2.7 .7-5.3 2.7-7.1l39.3-33.7c5.3-4.6 8.4-11.2 8.4-18.2s-3.1-13.7-8.4-18.2l-40.6-34.8 10.5-35.4c2-6.8 .9-14-2.9-19.9s-10.1-9.8-17.1-10.7L430 262.1l72.4-54.3c6.7-5 10.2-13.1 9.5-21.4s-5.7-15.6-13.2-19.3l-36.1-18.1c-3.4-1.7-5.2-5.7-4.1-9.4l22.6-79.2c2.4-8.4 .1-17.4-6.1-23.6s-15.2-8.5-23.6-6.1L372.2 53.5c-3.7 1.1-7.6-.7-9.4-4.1L344.8 13.3zM188.5 357.5L313.9 232.1c9.4-9.4 9.4-24.6 0-33.9s-24.6-9.4-33.9 0L154.5 323.5 70.1 276.6l2.7-1.8c14.2-9.5 20.8-26.9 16.5-43.4L56.2 104.6l24.7 3.1c8 1 16-2.1 21.2-8.2l26.7-31.1 26.7 31.1c6.2 7.2 16 10.1 25.1 7.4l23.2-6.9 6 46.8c1.2 9.7 8.2 17.7 17.7 20.2s19.5-1 25.3-8.8l66.5-88.7 .7 1.4C332 95 359.5 107.1 385.4 99.7l37.7-10.8-10.8 37.7c-7.4 25.9 4.7 53.4 28.8 65.5l1.4 .7-88.7 66.5c-7.8 5.9-11.3 15.9-8.8 25.3s10.5 16.4 20.2 17.7l46.8 6-6.9 23.2c-2.7 9.1 .2 18.9 7.4 25.1l31.1 26.7-18.1 15.5c-14.3 12.2-21.5 30.8-19.1 49.5l1 7.7L280.6 422.8c-16.5-4.3-33.9 2.3-43.4 16.5l-1.8 2.7-46.9-84.5z',
    ],
};
var faLightbulb = {
    prefix: 'far',
    iconName: 'lightbulb',
    icon: [
        384,
        512,
        [128161],
        'f0eb',
        'M297.2 248.9C311.6 228.3 320 203.2 320 176c0-70.7-57.3-128-128-128S64 105.3 64 176c0 27.2 8.4 52.3 22.8 72.9c3.7 5.3 8.1 11.3 12.8 17.7l0 0c12.9 17.7 28.3 38.9 39.8 59.8c10.4 19 15.7 38.8 18.3 57.5H109c-2.2-12-5.9-23.7-11.8-34.5c-9.9-18-22.2-34.9-34.5-51.8l0 0 0 0c-5.2-7.1-10.4-14.2-15.4-21.4C27.6 247.9 16 213.3 16 176C16 78.8 94.8 0 192 0s176 78.8 176 176c0 37.3-11.6 71.9-31.4 100.3c-5 7.2-10.2 14.3-15.4 21.4l0 0 0 0c-12.3 16.8-24.6 33.7-34.5 51.8c-5.9 10.8-9.6 22.5-11.8 34.5H226.4c2.6-18.7 7.9-38.6 18.3-57.5c11.5-20.9 26.9-42.1 39.8-59.8l0 0 0 0 0 0c4.7-6.4 9-12.4 12.7-17.7zM192 128c-26.5 0-48 21.5-48 48c0 8.8-7.2 16-16 16s-16-7.2-16-16c0-44.2 35.8-80 80-80c8.8 0 16 7.2 16 16s-7.2 16-16 16zm0 384c-44.2 0-80-35.8-80-80V416H272v16c0 44.2-35.8 80-80 80z',
    ],
};
var faLightbulbOn = {
    prefix: 'far',
    iconName: 'lightbulb-on',
    icon: [
        640,
        512,
        [],
        'f672',
        'M69.3 4C58.3-3.3 43.4-.3 36 10.7s-4.4 25.9 6.7 33.3l48 32c11 7.4 25.9 4.4 33.3-6.7s4.4-25.9-6.7-33.3L69.3 4zM597.3 44c11-7.4 14-22.3 6.7-33.3s-22.3-14-33.3-6.7l-48 32c-11 7.4-14 22.3-6.7 33.3s22.3 14 33.3 6.7l48-32zM24 160c-13.3 0-24 10.7-24 24s10.7 24 24 24H88c13.3 0 24-10.7 24-24s-10.7-24-24-24H24zm528 0c-13.3 0-24 10.7-24 24s10.7 24 24 24h64c13.3 0 24-10.7 24-24s-10.7-24-24-24H552zM117.3 332c11-7.4 14-22.3 6.7-33.3s-22.3-14-33.3-6.7l-48 32c-11 7.4-14 22.3-6.7 33.3s22.3 14 33.3 6.7l48-32zm432-39.9c-11-7.4-25.9-4.4-33.3 6.7s-4.4 25.9 6.7 33.3l48 32c11 7.4 25.9 4.4 33.3-6.7s4.4-25.9-6.7-33.3l-48-32zM448 176c0 27.2-8.4 52.3-22.8 72.9c-3.7 5.3-8 11.3-12.7 17.7l0 0 0 0 0 0 0 0c-12.9 17.7-28.3 38.9-39.8 59.8c-10.4 19-15.7 38.8-18.3 57.5H403c2.2-12 5.9-23.7 11.8-34.5c9.9-18 22.2-34.9 34.5-51.8l0 0 0 0 0 0c5.2-7.1 10.4-14.2 15.4-21.4c19.8-28.5 31.4-63 31.4-100.3C496 78.8 417.2 0 320 0S144 78.8 144 176c0 37.3 11.6 71.9 31.4 100.3c5 7.2 10.2 14.3 15.4 21.4l0 0 0 0c12.3 16.8 24.6 33.7 34.5 51.8c5.9 10.8 9.6 22.5 11.8 34.5h48.6c-2.6-18.7-7.9-38.6-18.3-57.5c-11.5-20.9-26.9-42.1-39.8-59.8l0 0 0 0c-4.7-6.4-9-12.4-12.8-17.7C200.4 228.3 192 203.2 192 176c0-70.7 57.3-128 128-128s128 57.3 128 128zm-176 0c0-26.5 21.5-48 48-48c8.8 0 16-7.2 16-16s-7.2-16-16-16c-44.2 0-80 35.8-80 80c0 8.8 7.2 16 16 16s16-7.2 16-16zM400 432V416H240v16c0 44.2 35.8 80 80 80s80-35.8 80-80z',
    ],
};
var faListCheck = {
    prefix: 'far',
    iconName: 'list-check',
    icon: [
        512,
        512,
        ['tasks'],
        'f0ae',
        'M153.8 72.1c8.9-9.9 8.1-25-1.8-33.9s-25-8.1-33.9 1.8L63.1 101.1 41 79C31.6 69.7 16.4 69.7 7 79s-9.4 24.6 0 33.9l40 40c4.7 4.7 11 7.2 17.6 7s12.8-3 17.2-7.9l72-80zm0 160c8.9-9.9 8.1-25-1.8-33.9s-25-8.1-33.9 1.8L63.1 261.1 41 239c-9.4-9.4-24.6-9.4-33.9 0s-9.4 24.6 0 33.9l40 40c4.7 4.7 11 7.2 17.6 7s12.8-3 17.2-7.9l72-80zM216 120H488c13.3 0 24-10.7 24-24s-10.7-24-24-24H216c-13.3 0-24 10.7-24 24s10.7 24 24 24zM192 256c0 13.3 10.7 24 24 24H488c13.3 0 24-10.7 24-24s-10.7-24-24-24H216c-13.3 0-24 10.7-24 24zM160 416c0 13.3 10.7 24 24 24H488c13.3 0 24-10.7 24-24s-10.7-24-24-24H184c-13.3 0-24 10.7-24 24zm-64 0a32 32 0 1 0 -64 0 32 32 0 1 0 64 0z',
    ],
};
var faListTimeline = {
    prefix: 'far',
    iconName: 'list-timeline',
    icon: [
        512,
        512,
        [],
        'e1d1',
        'M219.9 112H400V80H219.9l-16 16 16 16zm-72.6-4.7c-6.2-6.2-6.2-16.4 0-22.6l43.3-43.3c6-6 14.1-9.4 22.6-9.4H416c17.7 0 32 14.3 32 32v64c0 17.7-14.3 32-32 32H213.3c-8.5 0-16.6-3.4-22.6-9.4l-43.3-43.3zM64 128a32 32 0 1 1 0-64 32 32 0 1 1 0 64zm0 160a32 32 0 1 1 0-64 32 32 0 1 1 0 64zM32 416a32 32 0 1 1 64 0 32 32 0 1 1 -64 0zm187.9 16H400V400H219.9l-16 16 16 16zm-72.6-4.7c-6.2-6.2-6.2-16.4 0-22.6l43.3-43.3c6-6 14.1-9.4 22.6-9.4H416c17.7 0 32 14.3 32 32v64c0 17.7-14.3 32-32 32H213.3c-8.5 0-16.6-3.4-22.6-9.4l-43.3-43.3zM203.9 256l16 16H464V240H219.9l-16 16zm-13.3 54.6l-43.3-43.3c-6.2-6.2-6.2-16.4 0-22.6l43.3-43.3c6-6 14.1-9.4 22.6-9.4H480c17.7 0 32 14.3 32 32v64c0 17.7-14.3 32-32 32H213.3c-8.5 0-16.6-3.4-22.6-9.4z',
    ],
};
var faListTree = {
    prefix: 'far',
    iconName: 'list-tree',
    icon: [
        512,
        512,
        [],
        'e1d2',
        'M24 48C10.7 48 0 58.7 0 72v48c0 13.3 10.7 24 24 24V256 384c0 30.9 25.1 56 56 56h48c0 13.3 10.7 24 24 24h48c13.3 0 24-10.7 24-24V392c0-13.3-10.7-24-24-24H152c-13.3 0-24 10.7-24 24H80c-4.4 0-8-3.6-8-8V280h56c0 13.3 10.7 24 24 24h48c13.3 0 24-10.7 24-24V232c0-13.3-10.7-24-24-24H152c-13.3 0-24 10.7-24 24H72V144c13.3 0 24-10.7 24-24V72c0-13.3-10.7-24-24-24H24zM160 96c0 13.3 10.7 24 24 24H488c13.3 0 24-10.7 24-24s-10.7-24-24-24H184c-13.3 0-24 10.7-24 24zM288 256c0 13.3 10.7 24 24 24H488c13.3 0 24-10.7 24-24s-10.7-24-24-24H312c-13.3 0-24 10.7-24 24zm0 160c0 13.3 10.7 24 24 24H488c13.3 0 24-10.7 24-24s-10.7-24-24-24H312c-13.3 0-24 10.7-24 24z',
    ],
};
var faLock = {
    prefix: 'far',
    iconName: 'lock',
    icon: [
        448,
        512,
        [128274],
        'f023',
        'M144 128v64H304V128c0-44.2-35.8-80-80-80s-80 35.8-80 80zM96 192V128C96 57.3 153.3 0 224 0s128 57.3 128 128v64h32c35.3 0 64 28.7 64 64V448c0 35.3-28.7 64-64 64H64c-35.3 0-64-28.7-64-64V256c0-35.3 28.7-64 64-64H96zM48 256V448c0 8.8 7.2 16 16 16H384c8.8 0 16-7.2 16-16V256c0-8.8-7.2-16-16-16H64c-8.8 0-16 7.2-16 16z',
    ],
};
var faMagnifyingGlass = {
    prefix: 'far',
    iconName: 'magnifying-glass',
    icon: [
        512,
        512,
        [128269, 'search'],
        'f002',
        'M368 208A160 160 0 1 0 48 208a160 160 0 1 0 320 0zM337.1 371.1C301.7 399.2 256.8 416 208 416C93.1 416 0 322.9 0 208S93.1 0 208 0S416 93.1 416 208c0 48.8-16.8 93.7-44.9 129.1L505 471c9.4 9.4 9.4 24.6 0 33.9s-24.6 9.4-33.9 0L337.1 371.1z',
    ],
};
var faMemoCircleCheck = {
    prefix: 'far',
    iconName: 'memo-circle-check',
    icon: [
        576,
        512,
        [],
        'e1d9',
        'M64 48H320c8.8 0 16 7.2 16 16V220.5c14.6-9.5 30.8-17 48-21.8V64c0-35.3-28.7-64-64-64H64C28.7 0 0 28.7 0 64V448c0 35.3 28.7 64 64 64H320c3.3 0 6.6-.3 9.7-.7c-17.9-12.8-33.3-28.8-45.3-47.3H64c-8.8 0-16-7.2-16-16V64c0-8.8 7.2-16 16-16zm56 80c-13.3 0-24 10.7-24 24s10.7 24 24 24H264c13.3 0 24-10.7 24-24s-10.7-24-24-24H120zm0 96c-13.3 0-24 10.7-24 24s10.7 24 24 24H264c13.3 0 24-10.7 24-24s-10.7-24-24-24H120zm0 96c-13.3 0-24 10.7-24 24s10.7 24 24 24h48c13.3 0 24-10.7 24-24s-10.7-24-24-24H120zm456 48a144 144 0 1 0 -288 0 144 144 0 1 0 288 0zm-76.7-43.3c6.2 6.2 6.2 16.4 0 22.6l-72 72c-6.2 6.2-16.4 6.2-22.6 0l-40-40c-6.2-6.2-6.2-16.4 0-22.6s16.4-6.2 22.6 0L416 385.4l60.7-60.7c6.2-6.2 16.4-6.2 22.6 0z',
    ],
};
var faMemoCircleInfo = {
    prefix: 'far',
    iconName: 'memo-circle-info',
    icon: [
        576,
        512,
        [],
        'e49a',
        'M320 48H64c-8.8 0-16 7.2-16 16V448c0 8.8 7.2 16 16 16H284.5c12 18.4 27.4 34.5 45.3 47.3c-3.2 .5-6.4 .7-9.7 .7H64c-35.3 0-64-28.7-64-64V64C0 28.7 28.7 0 64 0H320c35.3 0 64 28.7 64 64V198.6c-17.2 4.9-33.4 12.3-48 21.8V64c0-8.8-7.2-16-16-16zM96 152c0-13.3 10.7-24 24-24H264c13.3 0 24 10.7 24 24s-10.7 24-24 24H120c-13.3 0-24-10.7-24-24zm24 72H264c13.3 0 24 10.7 24 24s-10.7 24-24 24H120c-13.3 0-24-10.7-24-24s10.7-24 24-24zm0 96h48c13.3 0 24 10.7 24 24s-10.7 24-24 24H120c-13.3 0-24-10.7-24-24s10.7-24 24-24zm312-96a144 144 0 1 1 0 288 144 144 0 1 1 0-288zm0 96a24 24 0 1 0 0-48 24 24 0 1 0 0 48zm-32 48c0 8.8 7.2 16 16 16h0v48h0c-8.8 0-16 7.2-16 16s7.2 16 16 16h16 16c8.8 0 16-7.2 16-16s-7.2-16-16-16V368c0-8.8-7.2-16-16-16H416c-8.8 0-16 7.2-16 16z',
    ],
};
var faMessageQuestion = {
    prefix: 'far',
    iconName: 'message-question',
    icon: [
        512,
        512,
        [],
        'e1e3',
        'M208 416c0-26.5-21.5-48-48-48H64c-8.8 0-16-7.2-16-16V64c0-8.8 7.2-16 16-16H448c8.8 0 16 7.2 16 16V352c0 8.8-7.2 16-16 16H309.3c-10.4 0-20.5 3.4-28.8 9.6L208 432V416zm-.2 76.2l.2-.2 101.3-76H448c35.3 0 64-28.7 64-64V64c0-35.3-28.7-64-64-64H64C28.7 0 0 28.7 0 64V352c0 35.3 28.7 64 64 64h48 48v48 4 .3 6.4V496c0 6.1 3.4 11.6 8.8 14.3s11.9 2.1 16.8-1.5L202.7 496l5.1-3.8zM169.8 117.3l-.4 1.2c-4.4 12.5 2.1 26.2 14.6 30.6s26.2-2.1 30.6-14.6l.4-1.2c1.1-3.2 4.2-5.3 7.5-5.3h58.3c8.4 0 15.1 6.8 15.1 15.1c0 5.4-2.9 10.4-7.6 13.1l-44.3 25.4c-7.5 4.3-12.1 12.2-12.1 20.8V216c0 13.3 10.7 24 24 24c13.1 0 23.8-10.5 24-23.6l32.3-18.5c19.6-11.3 31.7-32.2 31.7-54.8c0-34.9-28.3-63.1-63.1-63.1H222.6c-23.7 0-44.8 14.9-52.8 37.3zM288 304a32 32 0 1 0 -64 0 32 32 0 1 0 64 0z',
    ],
};
var faMeterFire = {
    prefix: 'far',
    iconName: 'meter-fire',
    icon: [
        640,
        512,
        [],
        'e1eb',
        'M256 48c68.7 0 129.5 33.3 167.4 84.6c16.7-8 37.2-5.5 51.5 7.4c5.6 5.1 11.1 10.2 16.6 15.5C452.5 64.1 361.7 0 256 0C114.6 0 0 114.6 0 256S114.6 512 256 512c33.6 0 65.7-6.5 95.1-18.3c-12.6-11.6-23.6-24.8-32.8-39.2c-19.7 6.2-40.6 9.5-62.3 9.5C141.1 464 48 370.9 48 256S141.1 48 256 48zM368 152c0-13.3-10.7-24-24-24s-24 10.7-24 24v48c0 11.2 7.6 20.6 18 23.2c9-12.9 19.1-25.7 30-38.3V152zm-176 0c0-13.3-10.7-24-24-24s-24 10.7-24 24v48c0 13.3 10.7 24 24 24s24-10.7 24-24V152zm88 0c0-13.3-10.7-24-24-24s-24 10.7-24 24v48c0 13.3 10.7 24 24 24s24-10.7 24-24V152zm173.5 11.6c-2.8-2.5-6.3-3.7-9.8-3.8c-3.6 0-7.2 1.2-10 3.7c-33.2 29.7-61.4 63.4-81.4 95.8C332.7 291.3 320 325.6 320 352c0 87.8 70.3 159.8 160 159.8c88.7 0 160-72 160-159.8c0-20.2-9.6-50.9-24.2-79c-14.8-28.5-35.7-58.5-60.4-81.1c-5.6-5.1-14.4-5.2-20 0c-9.6 8.8-18.6 19.6-26.5 29.5c-17.3-20.7-35.8-39.9-55.5-57.7zM530 432.9c-15 10-31 15-49 15c-45 0-81-29-81-78c0-24 15-45 45-82c4 5 62 79 62 79l36-42c3 4 5 8 7 12c18 33 10 75-20 96z',
    ],
};
var faMobileRetro = {
    prefix: 'far',
    iconName: 'mobile-retro',
    icon: [
        320,
        512,
        [],
        'e527',
        'M256 48c8.8 0 16 7.2 16 16V448c0 8.8-7.2 16-16 16H64c-8.8 0-16-7.2-16-16V64c0-8.8 7.2-16 16-16H256zM64 0C28.7 0 0 28.7 0 64V448c0 35.3 28.7 64 64 64H256c35.3 0 64-28.7 64-64V64c0-35.3-28.7-64-64-64H64zM80 160v64c0 17.7 14.3 32 32 32h96c17.7 0 32-14.3 32-32V160c0-17.7-14.3-32-32-32H112c-17.7 0-32 14.3-32 32zM96 352a24 24 0 1 0 0-48 24 24 0 1 0 0 48zm24 56a24 24 0 1 0 -48 0 24 24 0 1 0 48 0zm40-56a24 24 0 1 0 0-48 24 24 0 1 0 0 48zm24 56a24 24 0 1 0 -48 0 24 24 0 1 0 48 0zm40-56a24 24 0 1 0 0-48 24 24 0 1 0 0 48zm24 56a24 24 0 1 0 -48 0 24 24 0 1 0 48 0zM128 64c-8.8 0-16 7.2-16 16s7.2 16 16 16h64c8.8 0 16-7.2 16-16s-7.2-16-16-16H128z',
    ],
};
var faMoneyBillTrendUp = {
    prefix: 'far',
    iconName: 'money-bill-trend-up',
    icon: [
        512,
        512,
        [],
        'e529',
        'M320 24c0 13.3 10.7 24 24 24h54.1L286.7 159.3 191.6 77.8c-9-7.7-22.4-7.7-31.3 .1l-120 104c-10 8.7-11.1 23.8-2.4 33.9s23.8 11.1 33.9 2.4l104.4-90.5 96.3 82.5c9.5 8.2 23.7 7.6 32.6-1.3l127-127V136c0 13.3 10.7 24 24 24s24-10.7 24-24V24c0-13.3-10.7-24-24-24H344c-13.3 0-24 10.7-24 24zM103.5 304h305c3.6 29 26.6 51.9 55.5 55.5v49c-29 3.6-51.9 26.6-55.5 55.5h-305c-3.6-29-26.6-51.9-55.5-55.5v-49c28.9-3.6 51.9-26.6 55.5-55.5zM48 256c-26.5 0-48 21.5-48 48V464c0 26.5 21.5 48 48 48H464c26.5 0 48-21.5 48-48V304c0-26.5-21.5-48-48-48H48zM256 448a64 64 0 1 0 0-128 64 64 0 1 0 0 128z',
    ],
};
var faMoneyBillWave = {
    prefix: 'far',
    iconName: 'money-bill-wave',
    icon: [
        576,
        512,
        [],
        'f53a',
        'M265.7 407.9c4.5-1.1 9-2.3 13.5-3.4l0 0c55.9-14.3 119.4-30.6 185.3-28.7c4.1-31.4 30.9-55.7 63.5-55.7V144c-35.3 0-63.9-28.6-64-63.9c-47-1.7-96.8 9.5-153.7 24c-4.5 1.1-9 2.3-13.5 3.5l0 0c-55.9 14.3-119.4 30.6-185.3 28.7C107.4 167.7 80.5 192 48 192V368c35.3 0 63.9 28.6 64 63.9c47 1.7 96.8-9.5 153.7-24zM0 421.5V113C0 88.8 25.4 72.7 48.4 79C128.2 101 208.1 80.6 288 60.3c86.9-22.1 173.8-44.3 260.7-12C565.8 54.6 576 72 576 90.5V399c0 24.3-25.4 40.3-48.3 34C447.8 411 367.9 431.4 288 451.7c-86.9 22.1-173.8 44.3-260.7 12C10.2 457.4 0 440 0 421.5zM288 352c-44.2 0-80-43-80-96s35.8-96 80-96s80 43 80 96s-35.8 96-80 96z',
    ],
};
var faNewspaper = {
    prefix: 'far',
    iconName: 'newspaper',
    icon: [
        512,
        512,
        [128240],
        'f1ea',
        'M168 80c-13.3 0-24 10.7-24 24V408c0 8.4-1.4 16.5-4.1 24H440c13.3 0 24-10.7 24-24V104c0-13.3-10.7-24-24-24H168zM72 480c-39.8 0-72-32.2-72-72V112C0 98.7 10.7 88 24 88s24 10.7 24 24V408c0 13.3 10.7 24 24 24s24-10.7 24-24V104c0-39.8 32.2-72 72-72H440c39.8 0 72 32.2 72 72V408c0 39.8-32.2 72-72 72H72zM176 136c0-13.3 10.7-24 24-24h96c13.3 0 24 10.7 24 24v80c0 13.3-10.7 24-24 24H200c-13.3 0-24-10.7-24-24V136zm200-24h32c13.3 0 24 10.7 24 24s-10.7 24-24 24H376c-13.3 0-24-10.7-24-24s10.7-24 24-24zm0 80h32c13.3 0 24 10.7 24 24s-10.7 24-24 24H376c-13.3 0-24-10.7-24-24s10.7-24 24-24zM200 272H408c13.3 0 24 10.7 24 24s-10.7 24-24 24H200c-13.3 0-24-10.7-24-24s10.7-24 24-24zm0 80H408c13.3 0 24 10.7 24 24s-10.7 24-24 24H200c-13.3 0-24-10.7-24-24s10.7-24 24-24z',
    ],
};
var faPanelFire = {
    prefix: 'far',
    iconName: 'panel-fire',
    icon: [
        640,
        512,
        [],
        'e42f',
        'M384 80H64c-8.8 0-16 7.2-16 16V416c0 8.8 7.2 16 16 16H306.1c8.1 17.5 18.8 33.7 31.5 48H64c-35.3 0-64-28.7-64-64V96C0 60.7 28.7 32 64 32H384c35.3 0 64 28.7 64 64v32.2c-12.6-1.2-25.5 2.7-35.5 11.7c-4.2 3.8-8.4 7.6-12.5 11.5V96c0-8.8-7.2-16-16-16zM293.8 311.3c-1.8 .5-3.8 .7-5.8 .7c-13.3 0-24-10.7-24-24s10.7-24 24-24c8.2 0 15.5 4.1 19.8 10.4c-5.8 12.1-10.6 24.5-14 36.9zM96 160c0-17.7 14.3-32 32-32H320c17.7 0 32 14.3 32 32v32c0 17.7-14.3 32-32 32H128c-17.7 0-32-14.3-32-32V160zm32 104a24 24 0 1 1 0 48 24 24 0 1 1 0-48zm56 24a24 24 0 1 1 48 0 24 24 0 1 1 -48 0zM453.5 163.6c19.7 17.8 38.2 37 55.5 57.7c7.9-9.9 16.8-20.7 26.5-29.5c5.6-5.1 14.4-5.1 20 0c24.7 22.7 45.6 52.7 60.4 81.1c14.5 28 24.2 58.8 24.2 79c0 87.8-71.3 159.8-160 159.8c-89.7 0-160-72.1-160-159.8c0-26.4 12.7-60.7 32.4-92.6c20-32.4 48.1-66.1 81.4-95.8c2.8-2.5 6.4-3.8 10-3.7c3.5 0 7 1.3 9.8 3.8zM530 432.9c30-21 38-63 20-96c-2-4-4-8-7-12l-36 42s-58-74-62-79c-30 37-45 58-45 82c0 49 36 78 81 78c18 0 34-5 49-15z',
    ],
};
var faPen = {
    prefix: 'far',
    iconName: 'pen',
    icon: [
        512,
        512,
        [128394],
        'f304',
        'M36.4 360.9L13.4 439 1 481.2C-1.5 489.7 .8 498.8 7 505s15.3 8.5 23.7 6.1L73 498.6l78.1-23c12.4-3.6 23.7-9.9 33.4-18.4c1.4-1.2 2.7-2.5 4-3.8L492.7 149.3c21.9-21.9 24.6-55.6 8.2-80.5c-2.3-3.5-5.1-6.9-8.2-10L453.3 19.3c-25-25-65.5-25-90.5 0L58.6 323.5c-10.4 10.4-18 23.3-22.2 37.4zm46 13.5c1.7-5.6 4.5-10.8 8.4-15.2c.6-.6 1.1-1.2 1.7-1.8L321 129 383 191 154.6 419.5c-4.7 4.7-10.6 8.2-17 10.1l-23.4 6.9L59.4 452.6l16.1-54.8 6.9-23.4z',
    ],
};
var faPencil = {
    prefix: 'far',
    iconName: 'pencil',
    icon: [
        512,
        512,
        [9999, 61504, 'pencil-alt'],
        'f303',
        'M36.4 360.9L13.4 439 1 481.2C-1.5 489.7 .8 498.8 7 505s15.3 8.5 23.7 6.1L73 498.6l78.1-23c10.4-3 20.1-8 28.6-14.5l.3 .2 .5-.8c1.4-1.1 2.7-2.2 4-3.3c1.4-1.2 2.7-2.5 4-3.8L492.7 149.3c21.9-21.9 24.6-55.6 8.2-80.5c-2.3-3.5-5.1-6.9-8.2-10L453.3 19.3c-25-25-65.5-25-90.5 0L58.6 323.5c-2.5 2.5-4.9 5.2-7.1 8l-.8 .5 .2 .3c-6.5 8.5-11.4 18.2-14.5 28.6zM383 191L197.4 376.6l-49.6-12.4-12.4-49.6L321 129 383 191zM97 358.9l7.7 31c2.1 8.6 8.9 15.3 17.5 17.5l31 7.7-7.4 11.2c-2.6 1.4-5.3 2.6-8.1 3.4l-23.4 6.9L59.4 452.6l16.1-54.8 6.9-23.4c.8-2.8 2-5.6 3.4-8.1L97 358.9zM315.3 218.7c6.2-6.2 6.2-16.4 0-22.6s-16.4-6.2-22.6 0l-96 96c-6.2 6.2-6.2 16.4 0 22.6s16.4 6.2 22.6 0l96-96z',
    ],
};
var faPenSlash = {
    prefix: 'far',
    iconName: 'pen-slash',
    icon: [
        640,
        512,
        [],
        'e213',
        'M38.8 5.1C28.4-3.1 13.3-1.2 5.1 9.2S-1.2 34.7 9.2 42.9l592 464c10.4 8.2 25.5 6.3 33.7-4.1s6.3-25.5-4.1-33.7L409.9 296 556.7 149.3c21.9-21.9 24.6-55.6 8.2-80.5c-2.3-3.5-5.1-6.9-8.2-10L517.3 19.3c-25-25-65.5-25-90.5 0L264.2 181.8 38.8 5.1zM302.3 211.6L385 129 447 191l-75.1 75.1-69.6-54.5zM156.5 357.4l77.3-77.3-38-29.9-73.2 73.2c-10.4 10.4-18 23.3-22.2 37.4L77.4 439 65 481.2c-2.5 8.4-.2 17.5 6.1 23.7s15.3 8.5 23.7 6.1L137 498.6l78.1-23c12.4-3.6 23.7-9.9 33.4-18.4c1.4-1.2 2.7-2.5 4-3.8l88.6-88.6-38-29.9-84.6 84.6c-4.7 4.7-10.6 8.2-17 10.1l-23.4 6.9-54.8 16.1 16.1-54.8 6.9-23.4c1.7-5.6 4.5-10.8 8.4-15.2c.6-.6 1.1-1.2 1.7-1.8z',
    ],
};
var faPeopleRoof = {
    prefix: 'far',
    iconName: 'people-roof',
    icon: [
        640,
        512,
        [],
        'e537',
        'M332.3 3.4l296 176c11.4 6.8 15.1 21.5 8.4 32.9s-21.5 15.1-32.9 8.4L320 51.9 36.3 220.6c-11.4 6.8-26.1 3-32.9-8.4s-3-26.1 8.4-32.9l296-176c7.6-4.5 17-4.5 24.5 0zM496 256a40 40 0 1 1 0 80 40 40 0 1 1 0-80zM480 464h32V400.5c-2-.3-4.1-.5-6.2-.5H486.2c-2.1 0-4.2 .2-6.2 .5V464zm98.9 27.4l-18.9-35V480c0 17.7-14.3 32-32 32H464c-17.7 0-32-14.3-32-32V456.3l-18.9 35c-6.3 11.7-20.8 16-32.5 9.8s-16-20.8-9.8-32.5l36.3-67.5c-1.7-1.7-3.2-3.6-4.3-5.8l-18.9-35V400c0 17.7-14.3 32-32 32H288c-17.7 0-32-14.3-32-32V360.3l-18.9 35c-1.2 2.2-2.6 4.1-4.3 5.8l36.3 67.5c6.3 11.7 1.9 26.2-9.8 32.5s-26.2 1.9-32.5-9.8l-18.9-35V480c0 17.7-14.3 32-32 32H112c-17.7 0-32-14.3-32-32V456.3l-18.9 35c-6.3 11.7-20.8 16-32.5 9.8s-16-20.8-9.8-32.5l37.9-70.3c15.3-28.5 45.1-46.3 77.5-46.3h19.5c16.3 0 31.9 4.5 45.4 12.6l33.6-62.3c15.3-28.5 45.1-46.3 77.5-46.3h19.5c32.4 0 62.1 17.8 77.5 46.3l33.6 62.3c13.5-8.1 29.1-12.6 45.4-12.6h19.5c32.4 0 62.1 17.8 77.5 46.3l37.9 70.3c6.3 11.7 1.9 26.2-9.8 32.5s-26.2 1.9-32.5-9.8zM280 200a40 40 0 1 1 80 0 40 40 0 1 1 -80 0zm24 104.5V384h32V304.5c-2-.3-4.1-.5-6.2-.5H310.2c-2.1 0-4.2 .2-6.2 .5zM144 256a40 40 0 1 1 0 80 40 40 0 1 1 0-80zM128 464h32V400.5c-2-.3-4.1-.5-6.2-.5H134.2c-2.1 0-4.2 .2-6.2 .5V464z',
    ],
};
var faPersonShelter = {
    prefix: 'far',
    iconName: 'person-shelter',
    icon: [
        512,
        512,
        [],
        'e54f',
        'M268.1 3.3c-7.5-4.4-16.8-4.4-24.3 0l-232 136C4.5 143.6 0 151.5 0 160V488c0 13.3 10.7 24 24 24s24-10.7 24-24V173.8L256 51.8 464 173.8V488c0 13.3 10.7 24 24 24s24-10.7 24-24V160c0-8.5-4.5-16.4-11.9-20.7l-232-136zM256 208a40 40 0 1 0 0-80 40 40 0 1 0 0 80zm-16 64.5c2-.3 4.1-.5 6.2-.5h19.5c2.1 0 4.2 .2 6.2 .5V368H240V272.5zM240 416h32v72c0 13.3 10.7 24 24 24s24-10.7 24-24V328.3l18.9 35c6.3 11.7 20.8 16 32.5 9.8s16-20.8 9.8-32.5l-37.9-70.3c-15.3-28.5-45.1-46.3-77.5-46.3H246.2c-32.4 0-62.1 17.8-77.5 46.3l-37.9 70.3c-6.3 11.7-1.9 26.2 9.8 32.5s26.2 1.9 32.5-9.8l18.9-35V488c0 13.3 10.7 24 24 24s24-10.7 24-24V416z',
    ],
};
var faPhone = {
    prefix: 'far',
    iconName: 'phone',
    icon: [
        512,
        512,
        [128222, 128379],
        'f095',
        'M375.8 275.2c-16.4-7-35.4-2.4-46.7 11.4l-33.2 40.6c-46-26.7-84.4-65.1-111.1-111.1L225.3 183c13.8-11.3 18.5-30.3 11.4-46.7l-48-112C181.2 6.7 162.3-3.1 143.6 .9l-112 24C13.2 28.8 0 45.1 0 64v0C0 295.2 175.2 485.6 400.1 509.5c9.8 1 19.6 1.8 29.6 2.2c0 0 0 0 0 0c0 0 .1 0 .1 0c6.1 .2 12.1 .4 18.2 .4l0 0c18.9 0 35.2-13.2 39.1-31.6l24-112c4-18.7-5.8-37.6-23.4-45.1l-112-48zM441.5 464C225.8 460.5 51.5 286.2 48.1 70.5l99.2-21.3 43 100.4L154.4 179c-18.2 14.9-22.9 40.8-11.1 61.2c30.9 53.3 75.3 97.7 128.6 128.6c20.4 11.8 46.3 7.1 61.2-11.1l29.4-35.9 100.4 43L441.5 464zM48 64v0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0s0 0 0 0',
    ],
};
var faPiggyBank = {
    prefix: 'far',
    iconName: 'piggy-bank',
    icon: [
        576,
        512,
        [],
        'f4d3',
        'M277.1 48c23 0 42.5 14.7 49.8 35.2c4.4 12.5 18.1 19 30.6 14.6s19-18.1 14.6-30.6C358.3 28.1 321 0 277.1 0S195.8 28.1 182 67.2c-4.4 12.5 2.1 26.2 14.6 30.6s26.2-2.1 30.6-14.6C234.5 62.7 254.1 48 277.1 48zM66.7 197.5c11.9-5.9 16.7-20.3 10.7-32.2s-20.3-16.7-32.2-10.7l-4.7 2.3C15.7 169.3 0 194.7 0 222.6c0 37.4 28 68.3 64.2 72.9C66.4 344.7 91 388.2 128 416v48c0 26.5 21.5 48 48 48h48c26.5 0 48-21.5 48-48V448h48v16c0 26.5 21.5 48 48 48h48c26.5 0 48-21.5 48-48V426.6c18.7-10.8 35.1-25.4 48-42.6h32c17.7 0 32-14.3 32-32V256c0-17.7-14.3-32-32-32H530.7c-8.2-18.8-19.9-35.7-34.2-49.8l11.3-36.8-22.9-7 22.9 7c6.3-20.6-9.1-41.4-30.6-41.4H456c-31.5 0-60.2 12.2-81.6 32H224c-74.4 0-137 50.8-154.9 119.6c-12-2-21.1-12.5-21.1-25.1c0-9.6 5.4-18.4 14.1-22.8l4.7-2.3zM424 288a24 24 0 1 0 0-48 24 24 0 1 0 0 48zM402.3 168c13.1-14.7 32.1-23.9 53.2-24L446 174.8c-2.9 9.4 .2 19.6 7.9 25.8c17.4 13.9 30.4 32.8 37.1 54.5c3.1 10.1 12.4 17 22.9 17h14v64H499.4c-8.3 0-16 4.3-20.3 11.3c-11.7 18.7-28.7 33.7-48.9 42.8C421.5 394 416 402.5 416 412v52H368V424c0-13.3-10.7-24-24-24H248c-13.3 0-24 10.7-24 24v40H176V403.4c0-8.3-4.3-16-11.3-20.3c-31.7-19.8-52.7-55-52.7-95c0-61.9 50.1-112 112-112H384h0l.4 0c6.8 0 13.4-2.9 17.9-8z',
    ],
};
var faPlugCircleBolt = {
    prefix: 'far',
    iconName: 'plug-circle-bolt',
    icon: [
        576,
        512,
        [],
        'e55b',
        'M128 24c0-13.3-10.7-24-24-24S80 10.7 80 24v88h48V24zm176 0c0-13.3-10.7-24-24-24s-24 10.7-24 24v88h48V24zM24 144c-13.3 0-24 10.7-24 24s10.7 24 24 24h8v64c0 80.2 59 146.6 136 158.2V488c0 13.3 10.7 24 24 24s24-10.7 24-24V414.2c15.2-2.3 29.7-6.7 43.1-12.9c-2.1-10.8-3.1-21.9-3.1-33.3c0-7.1 .4-14.1 1.2-20.9C238.9 360.2 216.3 368 192 368c-61.9 0-112-50.1-112-112V192H304v55.2c13.8-14.6 30-26.8 48-36V192h8c13.3 0 24-10.7 24-24s-10.7-24-24-24h-8H304 80 32 24zM432 512a144 144 0 1 0 0-288 144 144 0 1 0 0 288zm47.9-225c4.3 3.7 5.4 9.9 2.6 14.9L452.4 356H488c5.2 0 9.8 3.3 11.4 8.2s-.1 10.3-4.2 13.4l-96 72c-4.5 3.4-10.8 3.2-15.1-.6s-5.4-9.9-2.6-14.9L411.6 380H376c-5.2 0-9.8-3.3-11.4-8.2s.1-10.3 4.2-13.4l96-72c4.5-3.4 10.8-3.2 15.1 .6z',
    ],
};
var faPlus = {
    prefix: 'far',
    iconName: 'plus',
    icon: [
        448,
        512,
        [10133, 61543, 'add'],
        '2b',
        'M248 72c0-13.3-10.7-24-24-24s-24 10.7-24 24V232H40c-13.3 0-24 10.7-24 24s10.7 24 24 24H200V440c0 13.3 10.7 24 24 24s24-10.7 24-24V280H408c13.3 0 24-10.7 24-24s-10.7-24-24-24H248V72z',
    ],
};
var faPlusLarge = {
    prefix: 'far',
    iconName: 'plus-large',
    icon: [
        512,
        512,
        [],
        'e59e',
        'M488 232c13.3 0 24 10.7 24 24s-10.7 24-24 24l-208 0 0 208c0 13.3-10.7 24-24 24s-24-10.7-24-24l0-208L24 280c-13.3 0-24-10.7-24-24s10.7-24 24-24H232l0-208c0-13.3 10.7-24 24-24s24 10.7 24 24l0 208 208 0z',
    ],
};
var faPowerOff = {
    prefix: 'far',
    iconName: 'power-off',
    icon: [
        512,
        512,
        [9211],
        'f011',
        'M280 24c0-13.3-10.7-24-24-24s-24 10.7-24 24V264c0 13.3 10.7 24 24 24s24-10.7 24-24V24zM134.2 107.3c10.7-7.9 12.9-22.9 5.1-33.6s-22.9-12.9-33.6-5.1C46.5 112.3 8 182.7 8 262C8 394.6 115.5 502 248 502s240-107.5 240-240c0-79.3-38.5-149.7-97.8-193.3c-10.7-7.9-25.7-5.6-33.6 5.1s-5.6 25.7 5.1 33.6c47.5 35 78.2 91.2 78.2 154.7c0 106-86 192-192 192S56 368 56 262c0-63.4 30.7-119.7 78.2-154.7z',
    ],
};
var faPumpSoap = {
    prefix: 'far',
    iconName: 'pump-soap',
    icon: [
        448,
        512,
        [],
        'e06b',
        'M112 56c0-30.9 25.1-56 56-56h48c25.4 0 46.8 16.9 53.7 40h46.4c19.1 0 37.4 7.6 50.9 21.1L409 103c9.4 9.4 9.4 24.6 0 33.9s-24.6 9.4-33.9 0L333.1 95c-4.5-4.5-10.6-7-17-7H272v40H224V56c0-4.4-3.6-8-8-8H168c-4.4 0-8 3.6-8 8v72H112V56zM101.5 222.7l-18.7 224C82.1 456 89.4 464 98.8 464H285.2c9.4 0 16.7-8 15.9-17.3l-18.7-224c-.7-8.3-7.6-14.7-15.9-14.7H117.4c-8.3 0-15.3 6.4-15.9 14.7zm-47.8-4c2.8-33.2 30.5-58.7 63.8-58.7H266.6c33.3 0 61 25.5 63.8 58.7l18.7 224c3.1 37.3-26.3 69.3-63.8 69.3H98.8c-37.4 0-66.9-32-63.8-69.3l18.7-224zm125.8 59.9c2.9-4.1 7.5-6.6 12.6-6.6s9.7 2.5 12.6 6.6l33.3 48.1c6.6 9.5 10.1 20.8 10.1 32.4v.9c0 30.9-25.1 56-56 56s-56-25.1-56-56v-.9c0-11.6 3.5-22.9 10.1-32.4l33.3-48.1z',
    ],
};
var faRecycle = {
    prefix: 'far',
    iconName: 'recycle',
    icon: [
        512,
        512,
        [9842, 9850, 9851],
        'f1b8',
        'M181.5 40.9C197.7 15.3 226 0 256 0s58.3 15.3 74.5 40.9L392.8 139l6.6-31.8c2.7-13 15.4-21.3 28.3-18.7s21.3 15.4 18.7 28.3l-19.4 94.4c-1.4 6.7-5.6 12.5-11.5 15.9s-13.1 4.2-19.6 2l-91.4-30.4c-12.6-4.2-19.4-17.8-15.2-30.3s17.8-19.4 30.3-15.2l32.2 10.7L290 66.6C282.7 55.1 269.8 48 256 48s-26.7 7.1-34 18.6l-21.5 33.9c-7 11-21.4 14.4-32.6 7.7c-11.6-6.9-15.1-22-7.9-33.4l21.5-33.9zM439 255.5c11.2-6.7 25.6-3.3 32.6 7.7l26.8 42.2c8.8 13.9 13.5 30 13.6 46.4c.3 48.6-39 88.2-87.6 88.2l-137.2 0 24.8 22c9.9 8.8 10.8 24 2 33.9s-24 10.8-33.9 2l-72-64c-5.1-4.6-8.1-11.1-8.1-17.9s2.9-13.4 8.1-17.9l72-64c9.9-8.8 25.1-7.9 33.9 2s7.9 25.1-2 33.9L287.1 392l137.3 0c22 0 39.8-17.9 39.6-39.9c0-7.4-2.2-14.7-6.2-21l-26.8-42.2c-7.2-11.4-3.6-26.5 7.9-33.4zm-311.6 3.7l-6.7-32.8L54.2 331.1c-4 6.3-6.1 13.6-6.2 21c-.1 22 17.7 39.9 39.6 39.9l48.4 0c13.3 0 24 10.7 24 24s-10.7 24-24 24l-48.4 0C39 440-.3 400.5 0 351.9c.1-16.4 4.8-32.5 13.6-46.4L78.8 202.8 47.6 213.1c-12.6 4.2-26.2-2.6-30.3-15.2s2.6-26.2 15.2-30.3l91.4-30.4c6.5-2.2 13.6-1.4 19.6 2s10.1 9.2 11.5 15.9l19.4 94.4c2.7 13-5.7 25.7-18.7 28.3s-25.7-5.7-28.3-18.7z',
    ],
};
var faScrewdriverWrench = {
    prefix: 'far',
    iconName: 'screwdriver-wrench',
    icon: [
        512,
        512,
        ['tools'],
        'f7d9',
        'M78.6 5C69.1-2.4 55.6-1.5 47 7L7 47c-8.5 8.5-9.4 22-2.1 31.6l80 104c4.5 5.9 11.6 9.4 19 9.4h54.1l109 109c-14.7 29-10 65.4 14.3 89.6l112 112c12.5 12.5 32.8 12.5 45.3 0l64-64c12.5-12.5 12.5-32.8 0-45.3l-112-112c-24.2-24.2-60.6-29-89.6-14.3l-109-109V104c0-7.5-3.5-14.5-9.4-19L78.6 5zM23.2 376.8C8.3 391.7 0 411.8 0 432.8C0 476.5 35.5 512 79.2 512c21 0 41.1-8.3 56-23.2L238.5 385.5c-8.9-17.6-12.8-37.1-11.6-56.3L101.3 454.9c-5.9 5.8-13.8 9.1-22.1 9.1C62 464 48 450 48 432.8c0-8.3 3.3-16.2 9.1-22.1L194.3 273.5l-33.9-33.9L23.2 376.8zM463.9 164c-1.4 38.9-22.6 72.8-53.8 91.8c1.1 1 2.1 2 3.1 3l31.6 31.6c40.7-29 67.2-76.6 67.2-130.3c0-24.8-5.7-48.3-15.7-69.3c-4.4-9.2-16.5-10.5-23.7-3.3l-67.9 67.9c-3 3-7.1 4.7-11.3 4.7H368c-8.8 0-16-7.2-16-16V118.6c0-4.2 1.7-8.3 4.7-11.3l67.9-67.9c7.2-7.2 5.9-19.3-3.3-23.7C400.3 5.7 376.8 0 352 0C294.7 0 244.4 30.1 216.2 75.4C221.2 84 224 93.8 224 104v40.8l16 16 0-.8c0-60.5 48-109.9 108-111.9L322.7 73.4l0 0c-12 12-18.7 28.3-18.7 45.3V144c0 35.3 28.7 64 64 64h25.4c17 0 33.3-6.7 45.3-18.7L463.9 164z',
    ],
};
var faShieldCheck = {
    prefix: 'far',
    iconName: 'shield-check',
    icon: [
        512,
        512,
        [],
        'f2f7',
        'M73 127L256 49.4 439 127c5.9 2.5 9.1 7.8 9 12.8c-.4 91.4-38.4 249.3-186.3 320.1c-3.6 1.7-7.8 1.7-11.3 0C102.4 389 64.5 231.2 64 139.7c0-5 3.1-10.2 9-12.8zM457.7 82.8L269.4 2.9C265.2 1 260.7 0 256 0s-9.2 1-13.4 2.9L54.3 82.8c-22 9.3-38.4 31-38.3 57.2c.5 99.2 41.3 280.7 213.6 363.2c16.7 8 36.1 8 52.8 0C454.8 420.7 495.5 239.2 496 140c.1-26.2-16.3-47.9-38.3-57.2zM369 209c9.4-9.4 9.4-24.6 0-33.9s-24.6-9.4-33.9 0l-111 111-47-47c-9.4-9.4-24.6-9.4-33.9 0s-9.4 24.6 0 33.9l64 64c9.4 9.4 24.6 9.4 33.9 0L369 209z',
    ],
};
var faShieldCross = {
    prefix: 'far',
    iconName: 'shield-cross',
    icon: [
        512,
        512,
        [9960],
        'f712',
        'M73 127L256 49.4 439 127c5.9 2.5 9.1 7.8 9 12.8c-.4 91.4-38.4 249.3-186.3 320.1c-3.6 1.7-7.8 1.7-11.3 0C102.4 389 64.5 231.2 64 139.7c0-5 3.1-10.2 9-12.8zM457.7 82.8L269.4 2.9C265.2 1 260.7 0 256 0s-9.2 1-13.4 2.9L54.3 82.8c-22 9.3-38.4 31-38.3 57.2c.5 99.2 41.3 280.7 213.6 363.2c16.7 8 36.1 8 52.8 0C454.8 420.7 495.5 239.2 496 140c.1-26.2-16.3-47.9-38.3-57.2zM280 120c0-13.3-10.7-24-24-24s-24 10.7-24 24v48H136c-13.3 0-24 10.7-24 24s10.7 24 24 24h96V392c0 13.3 10.7 24 24 24s24-10.7 24-24V216h96c13.3 0 24-10.7 24-24s-10.7-24-24-24H280V120z',
    ],
};
var faShieldKeyhole = {
    prefix: 'far',
    iconName: 'shield-keyhole',
    icon: [
        512,
        512,
        [],
        'e248',
        'M73 127L256 49.4 439 127c5.9 2.5 9.1 7.8 9 12.8c-.4 91.4-38.4 249.3-186.3 320.1c-3.6 1.7-7.8 1.7-11.3 0C102.4 389 64.5 231.2 64 139.7c0-5 3.1-10.2 9-12.8zM457.7 82.8L269.4 2.9C265.2 1 260.7 0 256 0s-9.2 1-13.4 2.9L54.3 82.8c-22 9.3-38.4 31-38.3 57.2c.5 99.2 41.3 280.7 213.6 363.2c16.7 8 36.1 8 52.8 0C454.8 420.7 495.5 239.2 496 140c.1-26.2-16.3-47.9-38.3-57.2zM312 208c0-30.9-25.1-56-56-56s-56 25.1-56 56c0 22.3 13.1 41.6 32 50.6V328c0 13.3 10.7 24 24 24s24-10.7 24-24V258.6c18.9-9 32-28.3 32-50.6z',
    ],
};
var faShieldQuartered = {
    prefix: 'far',
    iconName: 'shield-quartered',
    icon: [
        512,
        512,
        [],
        'e575',
        'M73 127c-5.9 2.5-9.1 7.8-9 12.8c.1 17.9 1.6 38.4 5.1 60.3H232V59.6L73 127zm6.7 121c20.9 73.9 65.9 153.7 152.3 202.3V248H79.7zM280 248V450.4C366.4 401.8 411.4 322 432.3 248H280zm162.9-48c3.5-21.9 5-42.3 5.1-60.3c0-5-3.1-10.2-9-12.8L280 59.6V200H442.9zM269.4 2.9L457.7 82.8c22 9.3 38.4 31 38.3 57.2c-.5 99.2-41.3 280.7-213.6 363.2c-16.7 8-36.1 8-52.8 0C57.3 420.7 16.5 239.2 16 140c-.1-26.2 16.3-47.9 38.3-57.2L242.7 2.9C246.8 1 251.4 0 256 0s9.2 1 13.4 2.9z',
    ],
};
var faShoePrints = {
    prefix: 'far',
    iconName: 'shoe-prints',
    icon: [
        640,
        512,
        [],
        'f54b',
        'M323.7 62.6C355.1 54.7 390.3 48 416 48c41.2 0 89.8 10.6 126.5 28.9C582 96.7 592 116.3 592 128c0 9.4-5.1 21.5-35.9 33.1c-29.4 11.1-67 14.9-92.1 14.9c-34.7 0-53.5-10.4-80.2-25.7l-.7-.4c-20.6-11.8-45.1-25.8-79.1-33V67.8c6-1.7 12.7-3.4 19.7-5.2zM256 160c48 0 76 16 104 32s56 32 104 32c56.4 0 176-16 176-96S512 0 416 0C352.3 0 256 32 256 32v80 48h0zM128 96c0 35.3 28.7 64 64 64h32V32H192c-35.3 0-64 28.7-64 64zm67.7 353.4c-7-1.8-13.7-3.5-19.7-5.2V395.1c34-7.3 58.5-21.3 79.1-33l.7-.4C282.5 346.4 301.3 336 336 336c25.2 0 62.7 3.7 92.1 14.9C458.9 362.5 464 374.6 464 384c0 11.7-10 31.3-49.5 51.1C377.8 453.4 329.2 464 288 464c-25.7 0-60.9-6.7-92.3-14.6zM128 352h0v48 80s96.3 32 160 32c96 0 224-48 224-128s-119.6-96-176-96c-48 0-76 16-104 32s-56 32-104 32zM0 416c0 35.3 28.7 64 64 64H96V352H64c-35.3 0-64 28.7-64 64z',
    ],
};
var faShovelSnow = {
    prefix: 'far',
    iconName: 'shovel-snow',
    icon: [
        512,
        512,
        [],
        'f7c3',
        'M391 7c9.4-9.4 24.6-9.4 33.9 0l80 80c9.4 9.4 9.4 24.6 0 33.9l-31.4 31.4c-15.1 15.1-35.6 23.6-57 23.6c-13.5 0-26.2-3.3-37.4-9.2L297.9 248l35.3 35.3c19.8 19.8 21.9 51.2 4.9 73.5L236.7 489.9c-10.6 13.9-27.1 22.1-44.5 22.1h-2.9c-14.9 0-29.1-5.9-39.6-16.4L16.4 362.3C5.9 351.8 0 337.6 0 322.7v-2.9c0-17.5 8.2-34 22.1-44.5L155.2 173.9c22.3-17 53.7-14.9 73.5 4.9L264 214.1l81.2-81.2c-5.9-11.2-9.2-23.9-9.2-37.4c0-21.4 8.5-41.9 23.6-57L391 7zm17 50.9L393.5 72.4c-6.1 6.1-9.5 14.4-9.5 23c0 18 14.6 32.6 32.6 32.6c8.6 0 16.9-3.4 23-9.5L454.1 104 408 57.9zM184.3 212L51.2 313.5c-2 1.5-3.2 3.9-3.2 6.4v2.9c0 2.1 .8 4.2 2.3 5.7L183.6 461.7c1.5 1.5 3.5 2.3 5.7 2.3h2.9c2.5 0 4.9-1.2 6.4-3.2L300 327.7c2.4-3.2 2.1-7.7-.7-10.5L194.8 212.7c-2.8-2.8-7.3-3.1-10.5-.7zm5.8 90.1l-32 32c-7.8 7.8-20.5 7.8-28.3 0s-7.8-20.5 0-28.3l32-32c7.8-7.8 20.5-7.8 28.3 0s7.8 20.5 0 28.3zm48 48l-32 32c-7.8 7.8-20.5 7.8-28.3 0s-7.8-20.5 0-28.3l32-32c7.8-7.8 20.5-7.8 28.3 0s7.8 20.5 0 28.3z',
    ],
};
var faSignalStreamSlash = {
    prefix: 'far',
    iconName: 'signal-stream-slash',
    icon: [
        640,
        512,
        [],
        'e250',
        'M38.8 5.1C28.4-3.1 13.3-1.2 5.1 9.2S-1.2 34.7 9.2 42.9l592 464c10.4 8.2 25.5 6.3 33.7-4.1s6.3-25.5-4.1-33.7l-91.7-71.9C562.6 355.5 576 307.3 576 256c0-69.7-24.8-133.6-66-183.4c-8.4-10.2-23.6-11.6-33.8-3.2s-11.6 23.6-3.2 33.8c34.3 41.5 55 94.7 55 152.8c0 40.1-9.8 77.9-27.2 111.2l-51.6-40.4c9.5-21.7 14.8-45.6 14.8-70.8c0-40.7-13.8-78.2-37-108c-8.1-10.5-23.2-12.3-33.7-4.2s-12.3 23.2-4.2 33.7C406 199.2 416 226.4 416 256c0 13.9-2.2 27.3-6.3 39.8L38.8 5.1zm84.7 188.2C116.1 212.8 112 233.9 112 256c0 40.7 13.8 78.2 37 108c8.1 10.5 23.2 12.3 33.7 4.2s12.3-23.2 4.2-33.7C170 312.8 160 285.6 160 256c0-10.7 1.3-21.1 3.8-31l-40.3-31.7zM33 122C11.9 162 0 207.6 0 256c0 69.7 24.8 133.6 66 183.4c8.4 10.2 23.6 11.6 33.8 3.2s11.6-23.6 3.2-33.8C68.6 367.3 48 314.1 48 256c0-37.1 8.4-72.3 23.5-103.7L33 122z',
    ],
};
var faSlidersSimple = {
    prefix: 'far',
    iconName: 'sliders-simple',
    icon: [
        512,
        512,
        [],
        'e253',
        'M80 336a32 32 0 1 0 0 64 32 32 0 1 0 0-64zm76.3 8L488 344c13.3 0 24 10.7 24 24s-10.7 24-24 24l-331.7 0c-10.2 32.5-40.5 56-76.3 56c-44.2 0-80-35.8-80-80s35.8-80 80-80c35.8 0 66.1 23.5 76.3 56zM400 144a32 32 0 1 0 64 0 32 32 0 1 0 -64 0zm-44.3-24c10.2-32.5 40.5-56 76.3-56c44.2 0 80 35.8 80 80s-35.8 80-80 80c-35.8 0-66.1-23.5-76.3-56L24 168c-13.3 0-24-10.7-24-24s10.7-24 24-24l331.7 0z',
    ],
};
var faSnowplow = {
    prefix: 'far',
    iconName: 'snowplow',
    icon: [
        640,
        512,
        [],
        'f7d2',
        'M298.8 48c3.2 0 6.1 1.9 7.4 4.9L371.8 208H219.5l-56.8-71-2.7-3.4V48H298.8zM429 220.3c-.4-1.2-.9-2.4-1.4-3.6L350.4 34.2C341.6 13.5 321.3 0 298.8 0H152c-22.1 0-40 17.9-40 40v88H96c-26.5 0-48 21.5-48 48V308.1C19 328.3 0 361.9 0 400c0 61.9 50.1 112 112 112H368c61.9 0 112-50.1 112-112c0-20.4-5.5-39.5-15-56h47v56c0 6 2.2 11.7 6.2 16.1l80 88c8.9 9.8 24.1 10.5 33.9 1.6s10.5-24.1 1.6-33.9L560 390.7V249.3l73.8-81.1c8.9-9.8 8.2-25-1.6-33.9s-25-8.2-33.9 1.6l-80 88c-4 4.4-6.2 10.2-6.2 16.1v56H432V238.5 232c0-4.2-1.1-8.2-3-11.7zm-45 68.8c-5.2-.7-10.6-1.1-16-1.1H112c-5.4 0-10.8 .4-16 1.1V176h36.5l56.8 71c4.6 5.7 11.4 9 18.7 9H384v33.1zM112 336H368c35.3 0 64 28.7 64 64s-28.7 64-64 64H112c-35.3 0-64-28.7-64-64s28.7-64 64-64zm32 64a24 24 0 1 0 -48 0 24 24 0 1 0 48 0zm56 24a24 24 0 1 0 0-48 24 24 0 1 0 0 48zm104-24a24 24 0 1 0 -48 0 24 24 0 1 0 48 0zm56 24a24 24 0 1 0 0-48 24 24 0 1 0 0 48z',
    ],
};
var faSprayCan = {
    prefix: 'far',
    iconName: 'spray-can',
    icon: [
        512,
        512,
        [],
        'f5bd',
        'M128 0C110.3 0 96 14.3 96 32v96H224V32c0-17.7-14.3-32-32-32H128zm96 208c26.5 0 48 21.5 48 48V440c0 13.3-10.7 24-24 24H72c-13.3 0-24-10.7-24-24V256c0-26.5 21.5-48 48-48H224zM96 160c-53 0-96 43-96 96V440c0 39.8 32.2 72 72 72H248c39.8 0 72-32.2 72-72V256c0-53-43-96-96-96H96zM224 336A64 64 0 1 0 96 336a64 64 0 1 0 128 0zM320 64a32 32 0 1 0 -64 0 32 32 0 1 0 64 0zm64 32a32 32 0 1 0 0-64 32 32 0 1 0 0 64zM512 64a32 32 0 1 0 -64 0 32 32 0 1 0 64 0zM480 192a32 32 0 1 0 0-64 32 32 0 1 0 0 64zm32 64a32 32 0 1 0 -64 0 32 32 0 1 0 64 0zM384 192a32 32 0 1 0 0-64 32 32 0 1 0 0 64z',
    ],
};
var faSprayCanSparkles = {
    prefix: 'far',
    iconName: 'spray-can-sparkles',
    icon: [
        512,
        512,
        ['air-freshener'],
        'f5d0',
        'M128 0h64c17.7 0 32 14.3 32 32v96H96V32c0-17.7 14.3-32 32-32zM96 208c-26.5 0-48 21.5-48 48V448c0 8.8 7.2 16 16 16H256c8.8 0 16-7.2 16-16V256c0-26.5-21.5-48-48-48H96zM0 256c0-53 43-96 96-96H224c53 0 96 43 96 96V448c0 35.3-28.7 64-64 64H64c-35.3 0-64-28.7-64-64V256zM381.8 51.6L352 64 339.6 93.8C339 95 337.4 96 336 96s-3-1-3.6-2.2L320 64 290.2 51.6C289 51 288 49.4 288 48c0-1.4 1-3 2.2-3.6L320 32 332.4 2.2C333 1 334.6 0 336 0s3 1 3.6 2.2L352 32l29.8 12.4C383 45 384 46.6 384 48c0 1.4-1 3-2.2 3.6zM448 64L418.2 51.6C417 51 416 49.4 416 48c0-1.4 1-3 2.2-3.6L448 32 460.4 2.2C461 1 462.6 0 464 0s3 1 3.6 2.2L480 32l29.8 12.4C511 45 512 46.6 512 48c0 1.4-1 3-2.2 3.6L480 64 467.6 93.8C467 95 465.4 96 464 96s-3-1-3.6-2.2L448 64zm32 160l29.8 12.4c1.2 .6 2.2 2.2 2.2 3.6c0 1.4-1 3-2.2 3.6L480 256l-12.4 29.8c-.6 1.2-2.2 2.2-3.6 2.2s-3-1-3.6-2.2L448 256l-29.8-12.4c-1.2-.6-2.2-2.2-2.2-3.6c0-1.4 1-3 2.2-3.6L448 224l12.4-29.8c.6-1.2 2.2-2.2 3.6-2.2s3 1 3.6 2.2L480 224zm-34.2-76.4L416 160l-12.4 29.8c-.6 1.2-2.2 2.2-3.6 2.2s-3-1-3.6-2.2L384 160l-29.8-12.4c-1.2-.6-2.2-2.2-2.2-3.6c0-1.4 1-3 2.2-3.6L384 128l12.4-29.8C397 97 398.6 96 400 96s3 1 3.6 2.2L416 128l29.8 12.4c1.2 .6 2.2 2.2 2.2 3.6c0 1.4-1 3-2.2 3.6zM160 272a64 64 0 1 1 0 128 64 64 0 1 1 0-128z',
    ],
};
var faStairs = {
    prefix: 'far',
    iconName: 'stairs',
    icon: [
        576,
        512,
        [],
        'e289',
        'M384 56c0-13.3 10.7-24 24-24H552c13.3 0 24 10.7 24 24s-10.7 24-24 24H432V200c0 13.3-10.7 24-24 24H304V328c0 13.3-10.7 24-24 24H176V456c0 13.3-10.7 24-24 24H24c-13.3 0-24-10.7-24-24s10.7-24 24-24H128V328c0-13.3 10.7-24 24-24H256V200c0-13.3 10.7-24 24-24H384V56z',
    ],
};
var faStamp = {
    prefix: 'far',
    iconName: 'stamp',
    icon: [
        512,
        512,
        [],
        'f5bf',
        'M299.3 116.7c3-6.2 4.7-13.2 4.7-20.7c0-26.5-21.5-48-48-48s-48 21.5-48 48c0 7.5 1.7 14.5 4.7 20.7l.3 .6c7 14.6 19 39.6 19 68.7c0 65.2-52.8 118-118 118H96c-26.5 0-48 21.5-48 48v8c0 4.4 3.6 8 8 8H456c4.4 0 8-3.6 8-8v-8c0-26.5-21.5-48-48-48H398c-65.2 0-118-52.8-118-118c0-29 12-54.1 19-68.6l0 0 .3-.6zm43.3 20.8C335.2 152.9 328 168.9 328 186c0 38.7 31.4 70 70 70h18c53 0 96 43 96 96v8c0 30.9-25.1 56-56 56H56c-30.9 0-56-25.1-56-56v-8c0-53 43-96 96-96h18c38.7 0 70-31.3 70-70c0-17.1-7.2-33.1-14.6-48.5c-6-12.6-9.4-26.6-9.4-41.5c0-53 43-96 96-96s96 43 96 96c0 14.9-3.4 28.9-9.4 41.5zM32 480c0-17.7 14.3-32 32-32H448c17.7 0 32 14.3 32 32s-14.3 32-32 32H64c-17.7 0-32-14.3-32-32z',
    ],
};
var faTable = {
    prefix: 'far',
    iconName: 'table',
    icon: [
        512,
        512,
        [],
        'f0ce',
        'M48 264V160H232V264H48zm0 48H232V432H64c-8.8 0-16-7.2-16-16V312zM280 432V312H464V416c0 8.8-7.2 16-16 16H280zM464 264H280V160H464V264zM64 32C28.7 32 0 60.7 0 96V416c0 35.3 28.7 64 64 64H448c35.3 0 64-28.7 64-64V96c0-35.3-28.7-64-64-64H64z',
    ],
};
var faTankWater = {
    prefix: 'far',
    iconName: 'tank-water',
    icon: [
        448,
        512,
        [],
        'e452',
        'M277.2 80L263.9 55.7 251 32H384c35.3 0 64 28.7 64 64V416c0 35.3-28.7 64-64 64H64c-35.3 0-64-28.7-64-64V96C0 60.7 28.7 32 64 32H197L184.1 55.7 170.8 80H64c-8.8 0-16 7.2-16 16V302.4c14.1-2.7 27.8-8.7 39.3-15.3c6.9-3.9 13.1-8.2 18.9-12.7c12.9-10 30.6-10 43.4 0c5.9 4.6 12 8.8 18.9 12.7c15.9 9 35.6 16.9 55.3 16.9s39.5-7.8 55.3-16.9c6.9-3.9 13.1-8.2 18.9-12.7c12.9-10 30.6-10 43.4 0c5.9 4.6 12 8.8 19 12.8c11.6 6.6 25.2 12.5 39.4 15.3V96c0-8.8-7.2-16-16-16H277.2zM48 416c0 8.8 7.2 16 16 16H384c8.8 0 16-7.2 16-16V351c-25-3.1-47.1-13-63.1-22.1c-6.5-3.7-12.2-7.4-17-10.7c-4.7 3.3-10.5 7-16.9 10.7c-19.3 11-47.6 23.1-79.1 23.1s-59.7-12.2-79.1-23.1c-6.5-3.7-12.2-7.4-16.9-10.7c-4.7 3.3-10.5 7-16.9 10.7c-16 9.1-38.1 19-63 22.1v65zM212.2 71c2.4-4.3 6.9-7 11.8-7s9.5 2.7 11.8 7L273 139.2c4.6 8.4 7 17.9 7 27.5V168c0 30.9-25.1 56-56 56s-56-25.1-56-56v-1.4c0-9.6 2.4-19 7-27.5L212.2 71z',
    ],
};
var faTelescope = {
    prefix: 'far',
    iconName: 'telescope',
    icon: [
        576,
        512,
        [128301],
        'e03e',
        'M459.7 231.7l61.5-35.5-80-138.6L379.7 93.1l80 138.6zM328.4 99.6c-6.2-14.6-.6-31.9 13.5-40.1L431.1 8C446.4-.8 466 4.4 474.8 19.7l96 166.3c8.8 15.3 3.6 34.9-11.7 43.7l-89.2 51.5c-15.3 8.8-34.9 3.6-43.7-11.7l-.5-.8-42.6 23.1c.6 4 .9 8.1 .9 12.3c0 20.1-7.4 38.5-19.7 52.5l49.9 122.4c5 12.3-.9 26.3-13.2 31.3s-26.3-.9-31.3-13.2l-47-115.3c-6 1.5-12.3 2.2-18.8 2.2s-12.8-.8-18.8-2.2l-47 115.3c-5 12.3-19 18.2-31.3 13.2s-18.2-19-13.2-31.3l44.3-108.8L149.5 418c-15.2 8.2-34.3 2.9-42.9-12.1l-.7-1.3L61.7 430.1c-11.5 6.6-26.2 2.7-32.8-8.8L3.1 376.4c-6.6-11.5-2.7-26.2 8.8-32.8l44.1-25.5L52 311.4c-8.6-14.9-3.9-33.8 10.6-43L328.4 99.6zm73.3 127.5l-49.5-85.7L101.3 300.7l39.1 67.7L226 322.1c-1.3-5.8-2-11.9-2-18.1c0-44.2 35.8-80 80-80c22.7 0 43.2 9.5 57.8 24.7l39.9-21.6zM304 336a32 32 0 1 0 0-64 32 32 0 1 0 0 64z',
    ],
};
var faThumbsDown = {
    prefix: 'far',
    iconName: 'thumbs-down',
    icon: [
        512,
        512,
        [128078, 61576],
        'f165',
        'M323.8 477.2c-38.2 10.9-78.1-11.2-89-49.4l-5.7-20c-3.7-13-10.4-25-19.5-35l-51.3-56.4c-8.9-9.8-8.2-25 1.6-33.9s25-8.2 33.9 1.6l51.3 56.4c14.1 15.5 24.4 34 30.1 54.1l5.7 20c3.6 12.7 16.9 20.1 29.7 16.5s20.1-16.9 16.5-29.7l-5.7-20c-5.7-19.9-14.7-38.7-26.6-55.5c-5.2-7.3-5.8-16.9-1.7-24.9s12.3-13 21.3-13L448 288c8.8 0 16-7.2 16-16c0-6.8-4.3-12.7-10.4-15c-7.4-2.8-13-9-14.9-16.7s.1-15.8 5.3-21.7c2.5-2.8 4-6.5 4-10.6c0-7.8-5.6-14.3-13-15.7c-8.2-1.6-15.1-7.3-18-15.2s-1.6-16.7 3.6-23.3c2.1-2.7 3.4-6.1 3.4-9.9c0-6.7-4.2-12.6-10.2-14.9c-11.5-4.5-17.7-16.9-14.4-28.8c.4-1.3 .6-2.8 .6-4.3c0-8.8-7.2-16-16-16H286.5c-12.6 0-25 3.7-35.5 10.7l-61.7 41.1c-11 7.4-25.9 4.4-33.3-6.7s-4.4-25.9 6.7-33.3l61.7-41.1c18.4-12.3 40-18.8 62.1-18.8H384c34.7 0 62.9 27.6 64 62c14.6 11.7 24 29.7 24 50c0 4.5-.5 8.8-1.3 13c15.4 11.7 25.3 30.2 25.3 51c0 6.5-1 12.8-2.8 18.7C504.8 238.3 512 254.3 512 272c0 35.3-28.6 64-64 64l-92.3 0c4.7 10.4 8.7 21.2 11.8 32.2l5.7 20c10.9 38.2-11.2 78.1-49.4 89zM32 384c-17.7 0-32-14.3-32-32V128c0-17.7 14.3-32 32-32H96c17.7 0 32 14.3 32 32V352c0 17.7-14.3 32-32 32H32z',
    ],
};
var faThumbsUp = {
    prefix: 'far',
    iconName: 'thumbs-up',
    icon: [
        512,
        512,
        [128077, 61575],
        'f164',
        'M323.8 34.8c-38.2-10.9-78.1 11.2-89 49.4l-5.7 20c-3.7 13-10.4 25-19.5 35l-51.3 56.4c-8.9 9.8-8.2 25 1.6 33.9s25 8.2 33.9-1.6l51.3-56.4c14.1-15.5 24.4-34 30.1-54.1l5.7-20c3.6-12.7 16.9-20.1 29.7-16.5s20.1 16.9 16.5 29.7l-5.7 20c-5.7 19.9-14.7 38.7-26.6 55.5c-5.2 7.3-5.8 16.9-1.7 24.9s12.3 13 21.3 13L448 224c8.8 0 16 7.2 16 16c0 6.8-4.3 12.7-10.4 15c-7.4 2.8-13 9-14.9 16.7s.1 15.8 5.3 21.7c2.5 2.8 4 6.5 4 10.6c0 7.8-5.6 14.3-13 15.7c-8.2 1.6-15.1 7.3-18 15.1s-1.6 16.7 3.6 23.3c2.1 2.7 3.4 6.1 3.4 9.9c0 6.7-4.2 12.6-10.2 14.9c-11.5 4.5-17.7 16.9-14.4 28.8c.4 1.3 .6 2.8 .6 4.3c0 8.8-7.2 16-16 16H286.5c-12.6 0-25-3.7-35.5-10.7l-61.7-41.1c-11-7.4-25.9-4.4-33.3 6.7s-4.4 25.9 6.7 33.3l61.7 41.1c18.4 12.3 40 18.8 62.1 18.8H384c34.7 0 62.9-27.6 64-62c14.6-11.7 24-29.7 24-50c0-4.5-.5-8.8-1.3-13c15.4-11.7 25.3-30.2 25.3-51c0-6.5-1-12.8-2.8-18.7C504.8 273.7 512 257.7 512 240c0-35.3-28.6-64-64-64l-92.3 0c4.7-10.4 8.7-21.2 11.8-32.2l5.7-20c10.9-38.2-11.2-78.1-49.4-89zM32 192c-17.7 0-32 14.3-32 32V448c0 17.7 14.3 32 32 32H96c17.7 0 32-14.3 32-32V224c0-17.7-14.3-32-32-32H32z',
    ],
};
var faToggleOn = {
    prefix: 'far',
    iconName: 'toggle-on',
    icon: [
        576,
        512,
        [],
        'f205',
        'M192 112c-79.5 0-144 64.5-144 144s64.5 144 144 144H384c79.5 0 144-64.5 144-144s-64.5-144-144-144H192zM0 256C0 150 86 64 192 64H384c106 0 192 86 192 192s-86 192-192 192H192C86 448 0 362 0 256zm384-96a96 96 0 1 1 0 192 96 96 0 1 1 0-192z',
    ],
};
var faToilet = {
    prefix: 'far',
    iconName: 'toilet',
    icon: [
        448,
        512,
        [128701],
        'f7d8',
        'M0 24C0 37.3 10.7 48 24 48H56 392h32c13.3 0 24-10.7 24-24s-10.7-24-24-24H392 56 24C10.7 0 0 10.7 0 24zM81.6 182.9l-1.6 .5V80H32V207.1l-.4 .4C17.5 219.4 8 235.7 8 256c0 72.7 33.4 120.2 78.4 148.3c1.7 1.1 3.5 2.1 5.2 3.2L69.3 448.2c-3.5 6.3-5.3 13.5-5.3 20.7c0 23.8 19.3 43.1 43.1 43.1H340.9c23.8 0 43.1-19.3 43.1-43.1c0-7.2-1.8-14.4-5.3-20.7l-22.3-40.7c1.8-1 3.5-2.1 5.2-3.2c45-28.1 78.4-75.6 78.4-148.3c0-20.3-9.5-36.6-23.6-48.5l-.4-.4V80H368V183.4l-1.6-.5C328.4 172 277.7 168 224 168s-104.4 4-142.4 14.9zM373.8 326.8c-9.8 15.7-22.9 27.6-37.6 36.9C303 384.4 259.9 392 224 392s-79-7.6-112.2-28.3c-14.7-9.2-27.8-21.2-37.6-36.9c2.4 .8 4.9 1.5 7.4 2.3C119.6 340 170.3 344 224 344s104.4-4 142.4-14.9c2.5-.7 4.9-1.5 7.4-2.3zM56 256c0-3.7 1.2-7.4 6.6-12c6.1-5.1 16.5-10.4 32.3-15C126.3 220 171.6 216 224 216s97.7 4 129.1 13.1c15.8 4.5 26.2 9.8 32.2 15c5.4 4.6 6.6 8.2 6.6 12s-1.2 7.4-6.6 12c-6.1 5.1-16.5 10.4-32.2 15C321.7 292 276.4 296 224 296s-97.7-4-129.1-13.1c-15.8-4.5-26.2-9.8-32.3-15c-5.4-4.6-6.6-8.2-6.6-12zM312.3 426.8L332.6 464H115.4l20.4-37.2c30.5 9.7 61.8 13.2 88.3 13.2s57.7-3.5 88.3-13.2zM128 80c-8.8 0-16 7.2-16 16s7.2 16 16 16h48c8.8 0 16-7.2 16-16s-7.2-16-16-16H128z',
    ],
};
var faToolbox = {
    prefix: 'far',
    iconName: 'toolbox',
    icon: [
        512,
        512,
        [129520],
        'f552',
        'M184 80H328c4.4 0 8 3.6 8 8v40H176V88c0-4.4 3.6-8 8-8zm-56 8v40H99.9c-12.7 0-24.9 5.1-33.9 14.1L14.1 193.9c-9 9-14.1 21.2-14.1 33.9V320v96c0 35.3 28.7 64 64 64H448c35.3 0 64-28.7 64-64V320 227.9c0-12.7-5.1-24.9-14.1-33.9l-51.9-51.9c-9-9-21.2-14.1-33.9-14.1H384V88c0-30.9-25.1-56-56-56H184c-30.9 0-56 25.1-56 56zM464 296H368V280c0-13.3-10.7-24-24-24s-24 10.7-24 24v16H192V280c0-13.3-10.7-24-24-24s-24 10.7-24 24v16H48V227.9L99.9 176H412.1L464 227.9V296zM48 344h96v16c0 13.3 10.7 24 24 24s24-10.7 24-24V344H320v16c0 13.3 10.7 24 24 24s24-10.7 24-24V344h96v72c0 8.8-7.2 16-16 16H64c-8.8 0-16-7.2-16-16V344z',
    ],
};
var faTowerBroadcast = {
    prefix: 'far',
    iconName: 'tower-broadcast',
    icon: [
        576,
        512,
        ['broadcast-tower'],
        'f519',
        'M54.8 1.9C42.6-3.4 28.5 2.3 23.3 14.4C8.3 49.3 0 87.7 0 128s8.3 78.7 23.3 113.6c5.2 12.2 19.3 17.8 31.5 12.6s17.8-19.3 12.6-31.5C54.9 193.6 48 161.6 48 128s6.9-65.6 19.4-94.6C72.6 21.2 67 7.1 54.8 1.9zm466.4 0C509 7.1 503.4 21.2 508.6 33.4c12.5 29 19.4 61 19.4 94.6s-6.9 65.6-19.4 94.6c-5.2 12.2 .4 26.3 12.6 31.5s26.3-.4 31.5-12.6c15-34.9 23.3-73.3 23.3-113.6s-8.3-78.7-23.3-113.6C547.5 2.3 533.4-3.4 521.2 1.9zM312 187.3c23.5-9.5 40-32.5 40-59.3c0-35.3-28.7-64-64-64s-64 28.7-64 64c0 26.9 16.5 49.9 40 59.3V488c0 13.3 10.7 24 24 24s24-10.7 24-24V187.3zM166.5 87.6c4.2-12.6-2.6-26.2-15.2-30.3S125.1 59.8 121 72.4c-5.8 17.5-9 36.2-9 55.6s3.1 38.1 9 55.6c4.2 12.6 17.8 19.4 30.3 15.2s19.4-17.8 15.2-30.3c-4.2-12.7-6.5-26.3-6.5-40.4s2.3-27.7 6.5-40.4zM455 72.4c-4.2-12.6-17.8-19.4-30.3-15.2s-19.4 17.8-15.2 30.3c4.2 12.7 6.5 26.3 6.5 40.4s-2.3 27.7-6.5 40.4c-4.2 12.6 2.6 26.2 15.2 30.3s26.2-2.6 30.3-15.2c5.8-17.5 9-36.2 9-55.6s-3.1-38.1-9-55.6z',
    ],
};
var faTrashCan = {
    prefix: 'far',
    iconName: 'trash-can',
    icon: [
        448,
        512,
        [61460, 'trash-alt'],
        'f2ed',
        'M170.5 51.6L151.5 80h145l-19-28.4c-1.5-2.2-4-3.6-6.7-3.6H177.1c-2.7 0-5.2 1.3-6.7 3.6zm147-26.6L354.2 80H368h48 8c13.3 0 24 10.7 24 24s-10.7 24-24 24h-8V432c0 44.2-35.8 80-80 80H112c-44.2 0-80-35.8-80-80V128H24c-13.3 0-24-10.7-24-24S10.7 80 24 80h8H80 93.8l36.7-55.1C140.9 9.4 158.4 0 177.1 0h93.7c18.7 0 36.2 9.4 46.6 24.9zM80 128V432c0 17.7 14.3 32 32 32H336c17.7 0 32-14.3 32-32V128H80zm80 64V400c0 8.8-7.2 16-16 16s-16-7.2-16-16V192c0-8.8 7.2-16 16-16s16 7.2 16 16zm80 0V400c0 8.8-7.2 16-16 16s-16-7.2-16-16V192c0-8.8 7.2-16 16-16s16 7.2 16 16zm80 0V400c0 8.8-7.2 16-16 16s-16-7.2-16-16V192c0-8.8 7.2-16 16-16s16 7.2 16 16z',
    ],
};
var faTriangleExclamation = {
    prefix: 'far',
    iconName: 'triangle-exclamation',
    icon: [
        512,
        512,
        [9888, 'exclamation-triangle', 'warning'],
        'f071',
        'M248.4 84.3c1.6-2.7 4.5-4.3 7.6-4.3s6 1.6 7.6 4.3L461.9 410c1.4 2.3 2.1 4.9 2.1 7.5c0 8-6.5 14.5-14.5 14.5H62.5c-8 0-14.5-6.5-14.5-14.5c0-2.7 .7-5.3 2.1-7.5L248.4 84.3zm-41-25L9.1 385c-6 9.8-9.1 21-9.1 32.5C0 452 28 480 62.5 480h387c34.5 0 62.5-28 62.5-62.5c0-11.5-3.2-22.7-9.1-32.5L304.6 59.3C294.3 42.4 275.9 32 256 32s-38.3 10.4-48.6 27.3zM288 368a32 32 0 1 0 -64 0 32 32 0 1 0 64 0zm-8-184c0-13.3-10.7-24-24-24s-24 10.7-24 24v96c0 13.3 10.7 24 24 24s24-10.7 24-24V184z',
    ],
};
var faTruckArrowRight = {
    prefix: 'far',
    iconName: 'truck-arrow-right',
    icon: [
        640,
        512,
        [],
        'e58b',
        'M64 48c-8.8 0-16 7.2-16 16V352c0 8.8 7.2 16 16 16H76.8c16.6-28.7 47.6-48 83.2-48s66.6 19.3 83.2 48H320h32c8.8 0 16-7.2 16-16V64c0-8.8-7.2-16-16-16H64zM480 512c-53 0-96-43-96-96h-8H352 320 256c0 53-43 96-96 96s-96-43-96-96c-35.3 0-64-28.7-64-64V64C0 28.7 28.7 0 64 0H352c35.3 0 64 28.7 64 64V96h42.7c14.9 0 29.1 5.9 39.6 16.4l93.3 93.3c10.5 10.5 16.4 24.7 16.4 39.6V368h8c13.3 0 24 10.7 24 24s-10.7 24-24 24H576c0 53-43 96-96 96zm78-272c-.1-.1-.2-.3-.4-.4l-93.3-93.3c-1.5-1.5-3.5-2.3-5.7-2.3H416v96H558zM160 464a48 48 0 1 0 0-96 48 48 0 1 0 0 96zm368-48a48 48 0 1 0 -96 0 48 48 0 1 0 96 0zM241 95l80 80c9.4 9.4 9.4 24.6 0 33.9l-80 80c-9.4 9.4-24.6 9.4-33.9 0s-9.4-24.6 0-33.9l39-39H112c-13.3 0-24-10.7-24-24s10.7-24 24-24H246.1l-39-39c-9.4-9.4-9.4-24.6 0-33.9s24.6-9.4 33.9 0z',
    ],
};
var faTruckContainer = {
    prefix: 'far',
    iconName: 'truck-container',
    icon: [
        640,
        512,
        [],
        'f4dc',
        'M0 104C0 64.2 32.2 32 72 32H328c39.8 0 72 32.2 72 72V288c0 13.3-10.7 24-24 24s-24-10.7-24-24V104c0-13.3-10.7-24-24-24H72c-13.3 0-24 10.7-24 24V288c0 13.3-10.7 24-24 24s-24-10.7-24-24V104zM608 400c0 44.2-35.8 80-80 80s-80-35.8-80-80H432 288c0 44.2-35.8 80-80 80c-26.2 0-49.4-12.6-64-32c-14.6 19.4-37.8 32-64 32c-44.2 0-80-35.8-80-80s35.8-80 80-80c26.2 0 49.4 12.6 64 32c14.6-19.4 37.8-32 64-32s49.4 12.6 64 32H432V256 168c0-13.3 10.7-24 24-24h72.8c16.8 0 32.7 7.5 43.3 20.5L631 236.4c5.8 7.1 9 16.1 9 25.3V272v16 80c0 17.7-14.3 32-32 32zM585 256l-50-61.1c-1.5-1.9-3.8-2.9-6.2-2.9H480v64H585zM136 136V264c0 13.3-10.7 24-24 24s-24-10.7-24-24V136c0-13.3 10.7-24 24-24s24 10.7 24 24zm88 0V264c0 13.3-10.7 24-24 24s-24-10.7-24-24V136c0-13.3 10.7-24 24-24s24 10.7 24 24zm88 0V264c0 13.3-10.7 24-24 24s-24-10.7-24-24V136c0-13.3 10.7-24 24-24s24 10.7 24 24zM528 432a32 32 0 1 0 0-64 32 32 0 1 0 0 64zM240 400a32 32 0 1 0 -64 0 32 32 0 1 0 64 0zM80 432a32 32 0 1 0 0-64 32 32 0 1 0 0 64z',
    ],
};
var faTruckDroplet = {
    prefix: 'far',
    iconName: 'truck-droplet',
    icon: [
        640,
        512,
        [],
        'e58c',
        'M64 48c-8.8 0-16 7.2-16 16V352c0 8.8 7.2 16 16 16H76.8c16.6-28.7 47.6-48 83.2-48s66.6 19.3 83.2 48H320h32c8.8 0 16-7.2 16-16V64c0-8.8-7.2-16-16-16H64zM480 512c-53 0-96-43-96-96h-8H352 320 256c0 53-43 96-96 96s-96-43-96-96c-35.3 0-64-28.7-64-64V64C0 28.7 28.7 0 64 0H352c35.3 0 64 28.7 64 64V96h42.7c14.9 0 29.1 5.9 39.6 16.4l93.3 93.3c10.5 10.5 16.4 24.7 16.4 39.6V368h8c13.3 0 24 10.7 24 24s-10.7 24-24 24H576c0 53-43 96-96 96zm78-272c-.1-.1-.2-.3-.4-.4l-93.3-93.3c-1.5-1.5-3.5-2.3-5.7-2.3H416v96H558zM160 464a48 48 0 1 0 0-96 48 48 0 1 0 0 96zm368-48a48 48 0 1 0 -96 0 48 48 0 1 0 96 0zM208 272c-39.8 0-72-29.6-72-66c0-27 39.4-82.9 59.9-110.3c6.1-8.2 18.1-8.2 24.2 0C240.6 123 280 179 280 206c0 36.5-32.2 66-72 66z',
    ],
};
var faTruckFast = {
    prefix: 'far',
    iconName: 'truck-fast',
    icon: [
        640,
        512,
        ['shipping-fast'],
        'f48b',
        'M352 48H128c-8.8 0-16 7.2-16 16V96H240c8.8 0 16 7.2 16 16s-7.2 16-16 16H16c-8.8 0-16-7.2-16-16s7.2-16 16-16H64V64C64 28.7 92.7 0 128 0H352c35.3 0 64 28.7 64 64V96h42.7c14.9 0 29.1 5.9 39.6 16.4l93.3 93.3c10.5 10.5 16.4 24.7 16.4 39.6V368h8c13.3 0 24 10.7 24 24s-10.7 24-24 24H576c0 53-43 96-96 96s-96-43-96-96h-8H352 320 256c0 53-43 96-96 96s-96-43-96-96V368 288h48v44.8c14.1-8.2 30.5-12.8 48-12.8c35.5 0 66.6 19.3 83.2 48H320h32c8.8 0 16-7.2 16-16V64c0-8.8-7.2-16-16-16zM557.7 239.6l-93.3-93.3c-1.5-1.5-3.5-2.3-5.7-2.3H416v96H558l-.2-.2-.2-.2zM208 416a48 48 0 1 0 -96 0 48 48 0 1 0 96 0zm272 48a48 48 0 1 0 0-96 48 48 0 1 0 0 96zM48 160H272c8.8 0 16 7.2 16 16s-7.2 16-16 16H48c-8.8 0-16-7.2-16-16s7.2-16 16-16zM16 224H240c8.8 0 16 7.2 16 16s-7.2 16-16 16H16c-8.8 0-16-7.2-16-16s7.2-16 16-16z',
    ],
};
var faTv = {
    prefix: 'far',
    iconName: 'tv',
    icon: [
        640,
        512,
        [63717, 'television', 'tv-alt'],
        'f26c',
        'M64 48c-8.8 0-16 7.2-16 16V352c0 8.8 7.2 16 16 16H576c8.8 0 16-7.2 16-16V64c0-8.8-7.2-16-16-16H64zM0 64C0 28.7 28.7 0 64 0H576c35.3 0 64 28.7 64 64V352c0 35.3-28.7 64-64 64H64c-35.3 0-64-28.7-64-64V64zM120 464H520c13.3 0 24 10.7 24 24s-10.7 24-24 24H120c-13.3 0-24-10.7-24-24s10.7-24 24-24z',
    ],
};
var faTvRetro = {
    prefix: 'far',
    iconName: 'tv-retro',
    icon: [
        512,
        512,
        [128250],
        'f401',
        'M135 7c9.4-9.4 24.6-9.4 33.9 0l87 87L343 7c9.4-9.4 24.6-9.4 33.9 0s9.4 24.6 0 33.9l-55 55H432c44.2 0 80 35.8 80 80V432c0 44.2-35.8 80-80 80H80c-44.2 0-80-35.8-80-80V176c0-44.2 35.8-80 80-80H190.1L135 41c-9.4-9.4-9.4-24.6 0-33.9zM80 144c-17.7 0-32 14.3-32 32V432c0 17.7 14.3 32 32 32H432c17.7 0 32-14.3 32-32V176c0-17.7-14.3-32-32-32H80zm336 56a24 24 0 1 1 0 48 24 24 0 1 1 0-48zM392 304a24 24 0 1 1 48 0 24 24 0 1 1 -48 0zM144 192H304c26.5 0 48 21.5 48 48V368c0 26.5-21.5 48-48 48H144c-26.5 0-48-21.5-48-48V240c0-26.5 21.5-48 48-48z',
    ],
};
var faUser = {
    prefix: 'far',
    iconName: 'user',
    icon: [
        448,
        512,
        [128100, 62144],
        'f007',
        'M304 128a80 80 0 1 0 -160 0 80 80 0 1 0 160 0zM96 128a128 128 0 1 1 256 0A128 128 0 1 1 96 128zM49.3 464H398.7c-8.9-63.3-63.3-112-129-112H178.3c-65.7 0-120.1 48.7-129 112zM0 482.3C0 383.8 79.8 304 178.3 304h91.4C368.2 304 448 383.8 448 482.3c0 16.4-13.3 29.7-29.7 29.7H29.7C13.3 512 0 498.7 0 482.3z',
    ],
};
var faUserCheck = {
    prefix: 'far',
    iconName: 'user-check',
    icon: [
        640,
        512,
        [],
        'f4fc',
        'M144 128a80 80 0 1 1 160 0 80 80 0 1 1 -160 0zm208 0A128 128 0 1 0 96 128a128 128 0 1 0 256 0zM269.7 352c65.7 0 120.1 48.7 129 112H49.3c8.9-63.3 63.3-112 129-112h91.4zm-91.4-48C79.8 304 0 383.8 0 482.3C0 498.7 13.3 512 29.7 512H418.3c16.4 0 29.7-13.3 29.7-29.7C448 383.8 368.2 304 269.7 304H224 178.3zM625 177c9.4-9.4 9.4-24.6 0-33.9s-24.6-9.4-33.9 0l-111 111-47-47c-9.4-9.4-24.6-9.4-33.9 0s-9.4 24.6 0 33.9l64 64c9.4 9.4 24.6 9.4 33.9 0L625 177z',
    ],
};
var faUserGear = {
    prefix: 'far',
    iconName: 'user-gear',
    icon: [
        640,
        512,
        ['user-cog'],
        'f4fe',
        'M224 208a80 80 0 1 0 0-160 80 80 0 1 0 0 160zM224 0a128 128 0 1 1 0 256A128 128 0 1 1 224 0zM49.3 464H336.6c3.3 4.2 6.7 8.2 10.3 12c15.7 16.9 39.6 18.4 57.2 8.7v.9c0 9.2 2.7 18.5 7.9 26.3H29.7C13.3 512 0 498.7 0 482.3C0 383.8 79.8 304 178.3 304H224h45.7c11.8 0 23.4 1.2 34.5 3.3c-2.1 18.5 7.4 35.6 21.8 44.8c-3.6 2.3-7 5.1-9.9 8.4c-14.4-5.5-30.1-8.5-46.5-8.5H178.3c-65.7 0-120.1 48.7-129 112zM436 218.2c0-7 4.5-13.3 11.3-14.8c10.5-2.4 21.5-3.7 32.7-3.7s22.2 1.3 32.7 3.7c6.8 1.5 11.3 7.8 11.3 14.8v30.6c7.9 3.4 15.4 7.7 22.3 12.8l24.9-14.3c6.1-3.5 13.7-2.7 18.5 2.4c7.6 8.1 14.3 17.2 20.1 27.2s10.3 20.4 13.5 31c2.1 6.7-1.1 13.7-7.2 17.2l-25 14.4c.4 4 .7 8.1 .7 12.3s-.2 8.2-.7 12.3l25 14.4c6.1 3.5 9.2 10.5 7.2 17.2c-3.3 10.6-7.8 21-13.5 31s-12.5 19.1-20.1 27.2c-4.8 5.1-12.5 5.9-18.5 2.4l-24.9-14.3c-6.9 5.1-14.3 9.4-22.3 12.8l0 30.6c0 7-4.5 13.3-11.3 14.8c-10.5 2.4-21.5 3.7-32.7 3.7s-22.2-1.3-32.7-3.7c-6.8-1.5-11.3-7.8-11.3-14.8V454.8c-8-3.4-15.6-7.7-22.5-12.9l-24.7 14.3c-6.1 3.5-13.7 2.7-18.5-2.4c-7.6-8.1-14.3-17.2-20.1-27.2s-10.3-20.4-13.5-31c-2.1-6.7 1.1-13.7 7.2-17.2l24.8-14.3c-.4-4.1-.7-8.2-.7-12.4s.2-8.3 .7-12.4L343.8 325c-6.1-3.5-9.2-10.5-7.2-17.2c3.3-10.6 7.7-21 13.5-31s12.5-19.1 20.1-27.2c4.8-5.1 12.4-5.9 18.5-2.4l24.8 14.3c6.9-5.1 14.5-9.4 22.5-12.9V218.2zm92.1 133.5a48.1 48.1 0 1 0 -96.1 0 48.1 48.1 0 1 0 96.1 0z',
    ],
};
var faUserGroup = {
    prefix: 'far',
    iconName: 'user-group',
    icon: [
        640,
        512,
        [128101, 'user-friends'],
        'f500',
        'M1.5 458.7c6-45.5 29.2-85.6 62.8-113.6C95.3 319.4 135 304 178.3 304H224h45.7c43.3 0 83 15.4 113.9 41.1c33.6 28 56.8 68.1 62.8 113.6c1 7.7 1.5 15.6 1.5 23.6c0 16.4-13.3 29.7-29.7 29.7H398.7 49.3 29.7C13.3 512 0 498.7 0 482.3c0-8 .5-15.9 1.5-23.6zM96 128a128 128 0 1 1 256 0A128 128 0 1 1 96 128zm208 0a80 80 0 1 0 -160 0 80 80 0 1 0 160 0zM178.3 352c-47.8 0-89.5 25.7-112.2 64c-8.6 14.4-14.4 30.7-16.8 48H98 350h48.8c-.4-2.7-.8-5.3-1.4-8c-2.9-14.3-8.2-27.8-15.5-40c-22.7-38.3-64.4-64-112.2-64H178.3zm431 160H472.4c4.9-8.8 7.6-18.9 7.6-29.7c0-65-29.5-123.2-75.9-161.8c4.3-.4 8.7-.5 13.2-.5h61.4C567.8 320 640 392.2 640 481.3c0 17-13.8 30.7-30.7 30.7zM432 256c-31 0-59-12.6-79.3-32.9C372.4 196.5 384 163.6 384 128c0-26.8-6.6-52.1-18.3-74.3C384.3 40.1 407.2 32 432 32c61.9 0 112 50.1 112 112s-50.1 112-112 112z',
    ],
};
var faUserLarge = {
    prefix: 'far',
    iconName: 'user-large',
    icon: [
        512,
        512,
        ['user-alt'],
        'f406',
        'M352 144a96 96 0 1 0 -192 0 96 96 0 1 0 192 0zm-240 0a144 144 0 1 1 288 0 144 144 0 1 1 -288 0zM49.3 464H462.7c-8.3-54.4-55.3-96-112-96H161.3c-56.7 0-103.6 41.6-112 96zM0 481.3C0 392.2 72.2 320 161.3 320H350.7C439.8 320 512 392.2 512 481.3c0 17-13.8 30.7-30.7 30.7H30.7C13.8 512 0 498.2 0 481.3z',
    ],
};
var faUserLargeSlash = {
    prefix: 'far',
    iconName: 'user-large-slash',
    icon: [
        640,
        512,
        ['user-alt-slash'],
        'f4fa',
        'M38.8 5.1C28.4-3.1 13.3-1.2 5.1 9.2S-1.2 34.7 9.2 42.9l592 464c10.4 8.2 25.5 6.3 33.7-4.1s6.3-25.5-4.1-33.7L381.9 274c48.5-23.2 82.1-72.7 82.1-130C464 64.5 399.5 0 320 0C250.4 0 192.4 49.3 178.9 114.9L38.8 5.1zM224.2 150.4c-.1-2.1-.2-4.3-.2-6.4c0-53 43-96 96-96s96 43 96 96c0 47.3-34.3 86.7-79.3 94.6L224.2 150.4zM545.5 512H528l-60.9-48H113.3c8.3-54.4 55.3-96 112-96H345.2l-60.9-48h-59C136.2 320 64 392.2 64 481.3c0 17 13.8 30.7 30.7 30.7H545.3l.3 0z',
    ],
};
var faUserPen = {
    prefix: 'far',
    iconName: 'user-pen',
    icon: [
        640,
        512,
        ['user-edit'],
        'f4ff',
        'M224 208a80 80 0 1 0 0-160 80 80 0 1 0 0 160zM224 0a128 128 0 1 1 0 256A128 128 0 1 1 224 0zM49.3 464H326.5l-5.1 20.2c-2.3 9.4-1.8 19 1.4 27.8H29.7C13.3 512 0 498.7 0 482.3C0 383.8 79.8 304 178.3 304H224h45.7c48.1 0 91.8 19.1 123.9 50.1L359.6 388c-23.4-22.3-55.1-36-89.9-36H178.3c-65.7 0-120.1 48.7-129 112zM613.8 235.7l14.4 14.4c15.6 15.6 15.6 40.9 0 56.6l-29.4 29.4-71-71 29.4-29.4c15.6-15.6 40.9-15.6 56.6 0zM375.9 417L505.1 287.8l71 71L446.9 487.9c-4.1 4.1-9.2 7-14.9 8.4l-60.1 15c-5.5 1.4-11.2-.2-15.2-4.2s-5.6-9.7-4.2-15.2l15-60.1c1.4-5.6 4.3-10.8 8.4-14.9z',
    ],
};
var faUserVneckHair = {
    prefix: 'far',
    iconName: 'user-vneck-hair',
    icon: [
        448,
        512,
        [],
        'e462',
        'M304 128c0-11.4-2.4-22.2-6.7-32H296c-20.5 0-38.7-9.6-50.4-24.5C231.9 95.7 205.8 112 176 112H145.6c-1 5.2-1.6 10.5-1.6 16v16c0 44.2 35.8 80 80 80s80-35.8 80-80V128zM96 128C96 57.3 153.3 0 224 0s128 57.3 128 128v16c0 70.7-57.3 128-128 128s-128-57.3-128-128V128zM48.3 464H399.7c-3.1-46.7-32.9-86.2-74.4-103.1l-50.2 66.9c-25.6 34.1-76.8 34.1-102.4 0l-50.2-66.9C81.2 377.8 51.3 417.3 48.3 464zm83.8-156.2c5.8-1.3 11.7 1.2 15.3 5.9l63.9 85.2c6.4 8.5 19.2 8.5 25.6 0l63.9-85.2c3.6-4.7 9.5-7.2 15.3-5.9C391.4 324.3 448 391.5 448 472v8c0 17.7-14.3 32-32 32H32c-17.7 0-32-14.3-32-32v-8c0-80.5 56.6-147.7 132.1-164.2z',
    ],
};
var faUserXmark = {
    prefix: 'far',
    iconName: 'user-xmark',
    icon: [
        640,
        512,
        ['user-times'],
        'f235',
        'M144 128a80 80 0 1 1 160 0 80 80 0 1 1 -160 0zm208 0A128 128 0 1 0 96 128a128 128 0 1 0 256 0zM269.7 352c65.7 0 120.1 48.7 129 112H49.3c8.9-63.3 63.3-112 129-112h91.4zm-91.4-48C79.8 304 0 383.8 0 482.3C0 498.7 13.3 512 29.7 512H418.3c16.4 0 29.7-13.3 29.7-29.7C448 383.8 368.2 304 269.7 304H224 178.3zM455 143c-9.4 9.4-9.4 24.6 0 33.9l47 47-47 47c-9.4 9.4-9.4 24.6 0 33.9s24.6 9.4 33.9 0l47-47 47 47c9.4 9.4 24.6 9.4 33.9 0s9.4-24.6 0-33.9l-47-47 47-47c9.4-9.4 9.4-24.6 0-33.9s-24.6-9.4-33.9 0l-47 47-47-47c-9.4-9.4-24.6-9.4-33.9 0z',
    ],
};
var faUsers = {
    prefix: 'far',
    iconName: 'users',
    icon: [
        640,
        512,
        [],
        'f0c0',
        'M144 160A80 80 0 1 0 144 0a80 80 0 1 0 0 160zm368 0A80 80 0 1 0 512 0a80 80 0 1 0 0 160zM0 298.7C0 310.4 9.6 320 21.3 320H234.7c.2 0 .4 0 .7 0c-26.6-23.5-43.3-57.8-43.3-96c0-7.6 .7-15 1.9-22.3c-13.6-6.3-28.7-9.7-44.6-9.7H106.7C47.8 192 0 239.8 0 298.7zM405.3 320H618.7c11.8 0 21.3-9.6 21.3-21.3C640 239.8 592.2 192 533.3 192H490.7c-15.9 0-31 3.5-44.6 9.7c1.3 7.2 1.9 14.7 1.9 22.3c0 38.2-16.8 72.5-43.3 96c.2 0 .4 0 .7 0zM320 176a48 48 0 1 1 0 96 48 48 0 1 1 0-96zm0 144a96 96 0 1 0 0-192 96 96 0 1 0 0 192zm-58.7 80H378.7c39.8 0 73.2 27.2 82.6 64H178.7c9.5-36.8 42.9-64 82.6-64zm0-48C187.7 352 128 411.7 128 485.3c0 14.7 11.9 26.7 26.7 26.7H485.3c14.7 0 26.7-11.9 26.7-26.7C512 411.7 452.3 352 378.7 352H261.3z',
    ],
};
var faVacuum = {
    prefix: 'far',
    iconName: 'vacuum',
    icon: [
        640,
        512,
        [],
        'e04d',
        'M290 48c-27.1 0-50.6 19-56.2 45.5L206 224H156.9L186.8 83.5C197.2 34.8 240.2 0 290 0H528c61.9 0 112 50.1 112 112c0 44.8-26.3 83.5-64.3 101.4C596.1 243.9 608 280.5 608 320V448c0 35.3-28.7 64-64 64H468.7c14.8-13 26.5-29.4 34-48H544c8.8 0 16-7.2 16-16V320c0-79.5-64.5-144-144-144c-8.8 0-16 7.2-16 16v97c-5.2-.7-10.6-1-16-1c-11 0-21.8 1.4-32 4V192c0-35.3 28.7-64 64-64c47.9 0 91.8 17.6 125.4 46.6c28.9-6.1 50.6-31.9 50.6-62.6c0-35.3-28.7-64-64-64H290zM150.1 256h49.1L171.7 385.1c29.8 5.5 52.3 31.6 52.3 62.9c0 35.3-28.7 64-64 64H32c-17.7 0-32-14.3-32-32c0-53 43-96 96-96h26.9l27.2-128zM432 416a48 48 0 1 0 -96 0 48 48 0 1 0 96 0zm-144 0a96 96 0 1 1 192 0 96 96 0 1 1 -192 0zM176 448c0-8.8-7.2-16-16-16H96c-20.9 0-38.7 13.4-45.3 32H160c8.8 0 16-7.2 16-16zm208-48a16 16 0 1 1 0 32 16 16 0 1 1 0-32z',
    ],
};
var faWashingMachine = {
    prefix: 'far',
    iconName: 'washing-machine',
    icon: [
        448,
        512,
        ['washer'],
        'f898',
        'M384 48c8.8 0 16 7.2 16 16V448c0 8.8-7.2 16-16 16H64c-8.8 0-16-7.2-16-16V64c0-8.8 7.2-16 16-16H384zM64 0C28.7 0 0 28.7 0 64V448c0 35.3 28.7 64 64 64H384c35.3 0 64-28.7 64-64V64c0-35.3-28.7-64-64-64H64zm64 104a24 24 0 1 0 -48 0 24 24 0 1 0 48 0zm56 24a24 24 0 1 0 0-48 24 24 0 1 0 0 48zM314.6 319.7c-3.5 .2-7 .3-10.6 .3c-32 0-56-16-80-32s-48-32-80-32c-3.5 0-7.1 .2-10.7 .4C146.4 218.9 182 192 224 192c53 0 96 43 96 96c0 11.1-1.9 21.8-5.4 31.7zM368 288A144 144 0 1 0 80 288a144 144 0 1 0 288 0z',
    ],
};
var faWater = {
    prefix: 'far',
    iconName: 'water',
    icon: [
        576,
        512,
        [],
        'f773',
        'M80 78.1c9.1-8.1 22.8-8.1 31.9 0c21.5 18.6 51.2 33.9 80 33.9s58.5-15.3 80-33.9c9.1-8.1 22.8-8.1 31.9 0c21.6 18.6 51.2 33.9 80 33.9s58.5-15.3 80-33.9c9.1-8.1 22.8-8.1 31.9 0c16.9 15.1 39.3 26.8 61.3 31.8c12.9 2.9 21.1 15.7 18.2 28.7s-15.7 21.1-28.7 18.2c-28.7-6.4-52.3-20.5-66.7-30.4c-28.1 19.5-61.4 33.8-96 33.8s-67.9-14.3-96-33.8c-28.1 19.5-61.4 33.8-96 33.8s-67.9-14.3-96-33.8c-14.4 10-38 24-66.7 30.4c-12.9 2.9-25.8-5.2-28.7-18.2s5.2-25.8 18.2-28.7C41 104.8 62.8 93 80 78.1zm0 288c9.1-8.1 22.8-8.1 31.9 0c21.5 18.6 51.2 33.9 80 33.9s58.5-15.3 80-33.9c9.1-8.1 22.8-8.1 31.9 0c21.6 18.6 51.2 33.9 80 33.9s58.5-15.3 80-33.9c9.1-8.1 22.8-8.1 31.9 0c16.9 15.1 39.3 26.8 61.3 31.8c12.9 2.9 21.1 15.7 18.2 28.7s-15.7 21.1-28.7 18.2c-28.7-6.4-52.3-20.5-66.7-30.4c-28.1 19.5-61.4 33.8-96 33.8s-67.9-14.3-96-33.8c-28.1 19.5-61.4 33.8-96 33.8s-67.9-14.3-96-33.8c-14.4 10-38 24-66.7 30.4c-12.9 2.9-25.8-5.2-28.7-18.2s5.2-25.8 18.2-28.7c22.2-5 44-16.8 61.2-31.7zm31.9-144c21.5 18.6 51.2 33.9 80 33.9s58.5-15.3 80-33.9c9.1-8.1 22.8-8.1 31.9 0c21.6 18.6 51.2 33.9 80 33.9s58.5-15.3 80-33.9c9.1-8.1 22.8-8.1 31.9 0c16.9 15.1 39.3 26.8 61.3 31.8c12.9 2.9 21.1 15.7 18.2 28.7s-15.7 21.1-28.7 18.2c-28.7-6.4-52.3-20.5-66.7-30.4c-28.1 19.5-61.4 33.8-96 33.8s-67.9-14.3-96-33.8c-28.1 19.5-61.4 33.8-96 33.8s-67.9-14.3-96-33.8c-14.4 10-38 24-66.7 30.4c-12.9 2.9-25.8-5.2-28.7-18.2s5.2-25.8 18.2-28.7c22.2-5 44-16.8 61.2-31.7c9.1-8.1 22.8-8.1 31.9 0z',
    ],
};
var faWaterArrowDown = {
    prefix: 'far',
    iconName: 'water-arrow-down',
    icon: [
        576,
        512,
        ['water-lower'],
        'f774',
        'M312 24c0-13.3-10.7-24-24-24s-24 10.7-24 24V161.7l-47.9-43.5c-9.8-8.9-25-8.2-33.9 1.6s-8.2 25 1.6 33.9l88 80c9.2 8.3 23.1 8.3 32.3 0l88-80c9.8-8.9 10.5-24.1 1.6-33.9s-24.1-10.5-33.9-1.6L312 161.7V24zM80 302.1C62.8 317 41 328.8 18.8 333.8C5.9 336.7-2.3 349.5 .6 362.5s15.7 21.1 28.7 18.2C58 374.2 81.6 360.2 96 350.2c28.1 19.5 61.4 33.8 96 33.8s67.9-14.3 96-33.8c28.1 19.5 61.4 33.8 96 33.8s67.9-14.3 96-33.8c14.4 10 38 24 66.7 30.4c12.9 2.9 25.8-5.2 28.7-18.2s-5.2-25.8-18.2-28.7c-22-4.9-44.3-16.7-61.3-31.8c-9.1-8.1-22.8-8.1-31.9 0c-21.5 18.6-51.2 33.9-80 33.9s-58.5-15.3-80-33.9c-9.1-8.1-22.8-8.1-31.9 0c-21.5 18.7-51.2 33.9-80 33.9s-58.5-15.3-80-33.9c-9.1-8.1-22.8-8.1-31.9 0zm0 128C62.8 445 41 456.8 18.8 461.8C5.9 464.7-2.3 477.5 .6 490.5s15.7 21.1 28.7 18.2C58 502.2 81.6 488.2 96 478.2c28.1 19.5 61.4 33.8 96 33.8s67.9-14.3 96-33.8c28.1 19.5 61.4 33.8 96 33.8s67.9-14.3 96-33.8c14.4 10 38 24 66.7 30.4c12.9 2.9 25.8-5.2 28.7-18.2s-5.2-25.8-18.2-28.7c-22-4.9-44.3-16.7-61.3-31.8c-9.1-8.1-22.8-8.1-31.9 0c-21.5 18.6-51.2 33.9-80 33.9s-58.5-15.3-80-33.9c-9.1-8.1-22.8-8.1-31.9 0c-21.5 18.6-51.2 33.9-80 33.9s-58.5-15.3-80-33.9c-9.1-8.1-22.8-8.1-31.9 0z',
    ],
};
var faWindowFrame = {
    prefix: 'far',
    iconName: 'window-frame',
    icon: [
        512,
        512,
        [129695],
        'e04f',
        'M80 464H232V272H80V464zm-48 0V64C32 28.7 60.7 0 96 0H416c35.3 0 64 28.7 64 64V464h8c13.3 0 24 10.7 24 24s-10.7 24-24 24h-8H432 80 32 24c-13.3 0-24-10.7-24-24s10.7-24 24-24h8zm400 0V272H280V464H432zM80 224H232V48H96c-8.8 0-16 7.2-16 16V224zm200 0H432V64c0-8.8-7.2-16-16-16H280V224z',
    ],
};
var faXmark = {
    prefix: 'far',
    iconName: 'xmark',
    icon: [
        384,
        512,
        [128473, 10005, 10006, 10060, 215, 'close', 'multiply', 'remove', 'times'],
        'f00d',
        'M345 137c9.4-9.4 9.4-24.6 0-33.9s-24.6-9.4-33.9 0l-119 119L73 103c-9.4-9.4-24.6-9.4-33.9 0s-9.4 24.6 0 33.9l119 119L39 375c-9.4 9.4-9.4 24.6 0 33.9s24.6 9.4 33.9 0l119-119L311 409c9.4 9.4 24.6 9.4 33.9 0s9.4-24.6 0-33.9l-119-119L345 137z',
    ],
};
var faXmarkLarge = {
    prefix: 'far',
    iconName: 'xmark-large',
    icon: [
        448,
        512,
        [],
        'e59b',
        'M41 39C31.6 29.7 16.4 29.7 7 39S-2.3 63.6 7 73l183 183L7 439c-9.4 9.4-9.4 24.6 0 33.9s24.6 9.4 33.9 0l183-183L407 473c9.4 9.4 24.6 9.4 33.9 0s9.4-24.6 0-33.9l-183-183L441 73c9.4-9.4 9.4-24.6 0-33.9s-24.6-9.4-33.9 0l-183 183L41 39z',
    ],
};

var clockOneRegular = {
    prefix: 'pkd',
    iconName: 'clock-one-regular',
    icon: [
        48,
        48,
        [],
        null,
        "M25.7812 18.1145L27.2656 15.8879C27.8148 15.0715 28.9207 14.8488 29.7371 15.3906C30.5535 15.9398 30.7762 17.0457 30.1676 17.8621L25.4176 24.9871C25.0465 25.6402 24.2375 25.9297 23.4805 25.6402C22.7309 25.477 22.152 24.7867 22.152 24V13.9062C22.152 12.9191 23.0129 12.125 23.9332 12.125C24.9871 12.125 25.7812 12.9191 25.7812 13.9062V18.1145ZM43 24C43 34.4945 34.4945 43 24 43C13.5055 43 5 34.4945 5 24C5 13.5055 13.5055 5 24 5C34.4945 5 43 13.5055 43 24ZM24 8.5625C15.4723 8.5625 8.5625 15.4723 8.5625 24C8.5625 32.5277 15.4723 39.4375 24 39.4375C32.5277 39.4375 39.4375 32.5277 39.4375 24C39.4375 15.4723 32.5277 8.5625 24 8.5625Z"
    ],
};
var calendarClockLight = {
    prefix: 'pkd',
    iconName: 'calendar-clock-light',
    icon: [
        48,
        48,
        [],
        null,
        "M11.3125 5C11.9656 5 12.5 5.5317 12.5 6.1875V9.75H26.75V6.1875C26.75 5.5317 27.2844 5 27.9375 5C28.5906 5 29.125 5.5317 29.125 6.1875V9.75H31.5C34.1199 9.75 36.25 11.8764 36.25 14.5V19.302C35.8566 19.2648 35.4633 19.25 35.0625 19.25C34.6617 19.25 34.2684 19.2648 33.875 19.302V19.25H5.375V38.25C5.375 39.5637 6.43855 40.625 7.75 40.625H24.9836C25.7332 41.523 26.5941 42.3246 27.5516 43H7.75C5.12637 43 3 40.8699 3 38.25V14.5C3 11.8764 5.12637 9.75 7.75 9.75H10.125V6.1875C10.125 5.5317 10.6594 5 11.3125 5ZM31.5 12.125H7.75C6.43855 12.125 5.375 13.1863 5.375 14.5V16.875H33.875V14.5C33.875 13.1863 32.8137 12.125 31.5 12.125ZM34.9957 26.375C35.7156 26.375 36.1832 26.9094 36.1832 27.5625V31.125H38.625C39.2781 31.125 39.8125 31.6594 39.8125 32.3125C39.8125 32.9656 39.2781 33.5 38.625 33.5H34.9957C34.4094 33.5 33.8082 32.9656 33.8082 32.3125V27.5625C33.8082 26.9094 34.4094 26.375 34.9957 26.375ZM24.375 32.3125C24.375 26.4121 29.1621 21.625 35.0625 21.625C40.9629 21.625 45.75 26.4121 45.75 32.3125C45.75 38.2129 40.9629 43 35.0625 43C29.1621 43 24.375 38.2129 24.375 32.3125ZM35.0625 40.625C39.6566 40.625 43.375 36.9066 43.375 32.3125C43.375 27.7184 39.6566 24 35.0625 24C30.4684 24 26.75 27.7184 26.75 32.3125C26.75 36.9066 30.4684 40.625 35.0625 40.625Z"
    ],
};
var checkLight = {
    prefix: 'pkd',
    iconName: 'check-light',
    icon: [
        48,
        48,
        [],
        null,
        "M39.9012 12.4735C40.3687 12.9336 40.3687 13.6906 39.9012 14.1508L19.7137 34.3383C19.2535 34.8059 18.4965 34.8059 18.0363 34.3383L7.34779 23.6508C6.88407 23.1906 6.88407 22.4336 7.34779 21.9735C7.81121 21.5059 8.56305 21.5059 9.02691 21.9735L18.875 31.8223L38.2238 12.4735C38.684 12.0088 39.441 12.0088 39.9012 12.4735Z"
    ],
};
var euroSignLight = {
    prefix: 'pkd',
    iconName: 'euro-sign-light',
    icon: [
        48,
        48,
        [],
        null,
        "M13.5625 21.625C12.9064 21.625 12.375 21.0906 12.375 20.4375C12.375 19.7844 12.9064 19.25 13.5625 19.25H15.4387C17.4812 12.3825 23.8418 7.375 31.375 7.375H34.9449C35.6648 7.375 36.1324 7.90641 36.1324 8.5625C36.1324 9.21859 35.6648 9.75 34.9449 9.75H31.375C25.1703 9.75 19.8934 13.7133 17.934 19.25H32.5625C33.2156 19.25 33.75 19.7844 33.75 20.4375C33.75 21.0906 33.2156 21.625 32.5625 21.625H17.3217C17.1925 22.3969 17.125 23.191 17.125 24C17.125 24.809 17.1925 25.6031 17.3217 26.375H32.5625C33.2156 26.375 33.75 26.9094 33.75 27.5625C33.75 28.2156 33.2156 28.75 32.5625 28.75H17.934C19.8934 34.2867 25.1703 38.25 31.375 38.25H34.9449C35.6648 38.25 36.1324 38.7844 36.1324 39.4375C36.1324 40.0906 35.6648 40.625 34.9449 40.625H31.375C23.8418 40.625 17.4812 35.6152 15.4387 28.75H13.5625C12.9064 28.75 12.375 28.2156 12.375 27.5625C12.375 26.9094 12.9064 26.375 13.5625 26.375H14.9185C14.8071 25.5957 14.75 24.809 14.75 24C14.75 23.191 14.8071 22.4043 14.9185 21.625H13.5625Z"
    ],
};
var laptopFileLight = {
    prefix: 'pkd',
    iconName: 'laptop-file-light',
    icon: [
        48,
        48,
        [],
        null,
        "M33.25 7.375H9.5C8.16406 7.375 7.125 8.48828 7.125 9.75V26.375H23.75V28.75H7.125H4.75H2.44922C2.89453 30.1602 4.15625 31.125 5.64062 31.125H23.75V33.5H5.64062C2.52344 33.5 0 30.9766 0 27.8594C0 27.043 0.59375 26.375 1.41016 26.375H4.75V9.75C4.75 7.15234 6.82812 5 9.5 5H33.25C35.8477 5 38 7.15234 38 9.75V12.125H35.625V9.75C35.625 8.48828 34.5117 7.375 33.25 7.375ZM28.5 39.4375C28.5 40.1055 29.0195 40.625 29.6875 40.625H43.9375C44.5312 40.625 45.125 40.1055 45.125 39.4375V26.375H39.1875C37.1836 26.375 35.625 24.8164 35.625 22.8125V16.875H29.6875C29.0195 16.875 28.5 17.4688 28.5 18.0625V39.4375ZM38 16.9492V22.8125C38 23.4805 38.5195 24 39.1875 24H45.0508C44.9766 23.8516 44.9023 23.6289 44.7539 23.4805L38.5195 17.2461C38.3711 17.0977 38.1484 17.0234 38 16.9492ZM40.2266 15.6133L46.4609 21.7734C47.1289 22.4414 47.5 23.4062 47.5 24.2969V39.4375C47.5 41.4414 45.8672 43 43.9375 43H29.6875C27.6836 43 26.125 41.4414 26.125 39.4375V18.0625C26.125 16.1328 27.6836 14.5 29.6875 14.5H37.7031C38.5938 14.5 39.5586 14.9453 40.2266 15.6133Z"
    ],
};
var helmSafetyLight = {
    prefix: 'pkd',
    iconName: 'helmet-safety-light',
    icon: [
        48,
        48,
        [],
        null,
        "M45.4308 31.5109C45.207 31.2743 44.895 31.1375 44.5691 31.1375C38.9819 31.1213 4.19412 31.1375 4.19412 31.1375C3.86712 31.1375 3.55518 31.272 3.33022 31.5109C3.10526 31.7475 2.98926 32.0699 3.009 32.3971C3.32346 37.7602 15.6171 40.625 24.3081 40.625L24.5517 40.625C33.0794 40.625 45.3626 37.7305 45.6817 32.3941C45.7648 32.0676 45.6535 31.7484 45.4308 31.5109ZM24.6347 38.25C24.5489 38.25 24.4713 38.2686 24.3761 38.25C16.6203 38.25 8.3523 35.9908 6.01441 33.5119C10.5925 33.5223 37.2593 33.5 42.7367 33.5074C40.4062 35.9715 32.1753 38.25 24.6347 38.25ZM6.56244 28.75C7.21854 28.75 7.74995 28.2156 7.74995 27.5625L7.74995 25.4027C7.74995 18.7772 12.1801 13.0973 18.4374 11.3086L18.4374 25.1875C18.4374 25.8406 18.9718 26.375 19.6249 26.375C20.2781 26.375 20.8124 25.8406 20.8124 25.1875L20.8124 9.75C20.8124 9.09598 21.3447 8.5625 21.9999 8.5625L26.7499 8.5625C27.4051 8.5625 27.9374 9.09598 27.9374 9.75L27.9374 25.1875C27.9374 25.8439 28.4686 26.375 29.0581 26.375C29.6477 26.375 30.3124 25.8406 30.3124 25.1875L30.3124 11.3041C36.5698 13.0928 40.9999 18.778 40.9999 25.3983L40.9999 27.5625C40.9999 28.2189 41.5311 28.75 42.1874 28.75C42.8438 28.75 43.3757 28.2189 43.3757 27.5625L43.3749 25.4027C43.3749 17.4168 37.8612 10.5664 30.1566 8.77031C29.7261 7.28297 28.3679 6.1875 26.7499 6.1875L21.9999 6.1875C20.3783 6.1875 19.0215 7.28297 18.5926 8.76809C10.8894 10.562 5.37494 17.4168 5.37494 25.4027L5.3742 27.5625C5.3742 28.2156 5.90635 28.75 6.56244 28.75Z"
    ],
};
var shieldCheckLight = {
    prefix: 'pkd',
    iconName: 'shield-check-light',
    icon: [
        48,
        48,
        [],
        null,
        "M39.6338 11.27L25.3838 5.33254C24.9478 5.14699 24.4813 5.05771 24.0152 5.05771C23.5491 5.05771 23.083 5.14647 22.6451 5.33202L8.39586 11.27C7.06883 11.817 6.19824 13.1173 6.19824 14.4904C6.19824 33.6611 20.2553 43.0572 24.0033 43.0572C27.6623 43.0572 41.8232 33.8021 41.8232 14.4904C41.8232 13.1173 40.9549 11.817 39.6338 11.27ZM39.4408 14.5795C39.4408 31.3009 27.1057 40.6822 24.0182 40.6822C20.8416 40.6154 8.57324 31.197 8.57324 14.5572C8.57324 14.0748 8.86084 13.648 9.29688 13.4625L23.5469 7.52496C23.6933 7.46355 23.8518 7.431 24.0113 7.431C24.165 7.431 24.3196 7.46122 24.4653 7.52496L38.7153 13.4625C39.3963 13.7482 39.4408 14.3939 39.4408 14.5795ZM29.9482 18.513C29.6444 18.513 29.3405 18.629 29.1088 18.861L22.0291 25.9423L18.9119 22.8252C18.6818 22.5877 18.3775 22.4763 18.0732 22.4763C17.395 22.4763 16.8857 23.031 16.8857 23.6638C16.8857 23.9677 17.0017 24.2715 17.2337 24.5032L21.1925 28.4613C21.4279 28.6884 21.7322 28.8072 22.0291 28.8072C22.326 28.8072 22.6368 28.6913 22.8685 28.4593L30.7877 20.5401C31.017 20.3091 31.1357 20.0048 31.1357 19.7005C31.1357 19.0697 30.6236 18.513 29.9482 18.513Z"
    ],
};
var headsetRegular = {
    prefix: 'pkd',
    iconName: 'headset-regular',
    icon: [
        48,
        48,
        [],
        null,
        "M20.0003 4.16663C11.1497 4.16663 4.45045 11.5328 4.16828 20L4.16699 23.2903C4.16699 25.8447 6.19255 27.9166 8.69062 27.9166H9.82184C11.0693 27.9166 12.0837 26.8837 12.0837 25.6035V17.3652C12.0837 16.0849 11.0693 15.052 9.82184 15.052H8.69062C8.48795 15.052 8.29825 15.0963 8.10318 15.123C10.0303 10.4443 14.638 7.14775 20.0003 7.14713C25.3651 7.14785 29.9704 10.4474 31.9001 15.07C31.7021 15.0953 31.5104 15.052 31.3125 15.052H30.1813C28.9313 15.052 27.917 16.0849 27.917 17.3652V25.6035C27.917 26.8837 28.9313 27.9166 30.1807 27.9166H31.3119C31.8608 27.9166 32.38 27.8015 32.8668 27.6185V28.9062C32.8668 30.2706 31.7572 31.3802 30.3928 31.3802H23.5461C23.0309 30.4957 22.0846 29.8958 20.9899 29.8958H19.0107C17.3717 29.8958 16.042 31.2255 16.042 32.8645C16.042 34.5035 17.3717 35.8333 18.9551 35.8333H20.9899C22.0859 35.8333 23.0328 35.2327 23.5467 34.3489H30.3909C33.3925 34.3489 35.8337 31.9077 35.8337 28.9062V20C35.5492 11.5328 28.8509 4.16663 20.0003 4.16663ZM8.69062 18.0208H9.11491V24.9479H8.69062C7.8334 24.9479 7.13574 24.15 7.13574 23.2903V20C7.13574 19.8509 7.15302 19.7062 7.15809 19.5583C7.218 18.7011 7.87236 18.0208 8.69062 18.0208ZM31.3125 24.9479H30.8857V18.0208H31.3098C32.1281 18.0208 32.7825 18.7036 32.8425 19.5584C32.8464 19.7093 32.8649 19.8515 32.8649 20V23.2928C32.8649 24.15 32.166 24.9479 31.3125 24.9479Z"
    ],
};
var calendarRegular = {
    prefix: 'pkd',
    iconName: 'calendar-regular',
    icon: [
        48,
        48,
        [],
        null,
        "M15.234 8.12496H24.1403V5.651C24.1403 4.8315 24.8021 4.16663 25.6247 4.16663C26.4473 4.16663 27.1091 4.8315 27.1091 5.651V8.12496H29.583C31.7663 8.12496 33.5413 9.89693 33.5413 12.0833V31.875C33.5413 34.0582 31.7663 35.8333 29.583 35.8333H9.79134C7.60498 35.8333 5.83301 34.0582 5.83301 31.875V12.0833C5.83301 9.89693 7.60498 8.12496 9.79134 8.12496H12.2653V5.651C12.2653 4.8315 12.9271 4.16663 13.7497 4.16663C14.5723 4.16663 15.234 4.8315 15.234 5.651V8.12496ZM8.80176 31.875C8.80176 32.4192 9.2446 32.8645 9.79134 32.8645H29.583C30.1273 32.8645 30.5726 32.4192 30.5726 31.875V16.0416H8.80176V31.875Z"
    ],
};
var gaugeSimpleHighRegular = {
    prefix: 'pkd',
    iconName: 'gauge-simple-high-regular',
    icon: [
        48,
        48,
        [],
        null,
        "M23.4639 25.9375C23.4639 27.8486 21.9115 29.401 20.0003 29.401C18.0892 29.401 16.5368 27.8486 16.5368 25.9375C16.5368 24.0573 18.0335 22.5296 19.9014 22.4739L24.0824 12.9677C24.4163 12.2194 25.2884 11.8792 26.043 12.207C26.7913 12.541 27.1315 13.413 26.748 14.1676L22.6165 23.6738C23.1484 24.2799 23.4639 25.0159 23.4639 25.9375ZM4.16699 20C4.16699 11.2545 11.2549 4.16663 20.0003 4.16663C28.7458 4.16663 35.8337 11.2545 35.8337 20C35.8337 28.7454 28.7458 35.8333 20.0003 35.8333C11.2549 35.8333 4.16699 28.7454 4.16699 20ZM20.0003 32.8645C27.1068 32.8645 32.8649 27.1064 32.8649 20C32.8649 12.8935 27.1068 7.13538 20.0003 7.13538C12.8939 7.13538 7.13574 12.8935 7.13574 20C7.13574 27.1064 12.8939 32.8645 20.0003 32.8645Z"
    ],
};
var houseSignalRegular = {
    prefix: 'pkd',
    iconName: 'house-signal-regular',
    icon: [
        48,
        48,
        [],
        null,
        "M22.3411 4.50024C22.8916 4.05542 23.6709 4.05542 24.2214 4.50024L37.5807 15.386C38.2116 15.9056 38.2549 16.8395 37.791 17.4765C37.2715 18.1074 36.3376 18.1507 35.7005 17.6868L34.1667 16.4375V26.4323C34.1667 28.3434 32.6143 29.8958 30.7031 29.8958H20.832C20.5846 28.8691 20.2445 27.8733 19.8115 26.927H30.7031C30.9753 26.927 31.1979 26.7044 31.1979 26.4323V14.0192L23.2812 7.56584L15.3646 14.0192V21.0267C14.4554 20.2535 13.4596 19.5732 12.3958 19.0042V16.4375L10.862 17.6868C10.6455 17.8662 10.3919 17.9713 10.1322 18.0084C9.73014 17.86 9.32194 17.7363 8.90755 17.6188C8.85807 17.5755 8.81478 17.526 8.77148 17.4765C8.25319 16.8395 8.34906 15.9056 8.98177 15.386L22.3411 4.50024ZM24.7656 16.0416C25.5882 16.0416 26.25 16.7034 26.25 17.526V20.4948C26.25 21.3173 25.5882 21.9791 24.7656 21.9791H21.7969C20.9743 21.9791 20.3125 21.3173 20.3125 20.4948V17.526C20.3125 16.7034 20.9743 16.0416 21.7969 16.0416H24.7656ZM18.3333 34.3489C18.3333 35.1715 17.6715 35.8333 16.849 35.8333C16.0264 35.8333 15.3646 35.1715 15.3646 34.3489C15.3646 28.0651 10.2682 22.9687 3.98437 22.9687C3.16488 22.9687 2.5 22.3069 2.5 21.4843C2.5 20.6617 3.16488 20 3.98437 20C11.9072 20 18.3333 26.4261 18.3333 34.3489ZM2.5 33.8541C2.5 32.7594 3.3863 31.875 4.47917 31.875C5.57204 31.875 6.45833 32.7594 6.45833 33.8541C6.45833 34.9489 5.57204 35.8333 4.47917 35.8333C3.3863 35.8333 2.5 34.9489 2.5 33.8541ZM2.5 27.4218C2.5 26.5992 3.16488 25.9375 3.98437 25.9375C8.62985 25.9375 12.3958 29.7041 12.3958 34.3489C12.3958 35.1715 11.734 35.8333 10.9115 35.8333C10.0889 35.8333 9.42708 35.1715 9.42708 34.3489C9.42708 31.3431 6.99023 28.9062 3.98437 28.9062C3.16488 28.9062 2.5 28.2444 2.5 27.4218Z"
    ],
};
var laptopMobileRegular = {
    prefix: 'pkd',
    iconName: 'laptop-mobile-regular',
    icon: [
        48,
        48,
        [],
        null,
        "M6.92708 8.12508C6.92708 7.57945 7.37104 7.1355 7.91667 7.1355L27.7083 7.1355C28.254 7.1355 28.6979 7.57945 28.6979 8.12508L28.6979 10.1042L31.6667 10.1042L31.6667 8.12508C31.6667 5.93872 29.8947 4.16675 27.7083 4.16675L7.91667 4.16675C5.73031 4.16675 3.95833 5.93872 3.95833 8.12508L3.95833 23.9584L0.989583 23.9584C0.443086 23.9584 0 24.4037 0 24.948C0 26.587 1.32913 27.9167 2.96875 27.9167L21.7708 27.9167L21.7708 23.9584L6.92708 23.9584L6.92708 8.12508ZM35.625 12.0834L27.7083 12.0834C25.522 12.0834 23.75 13.8554 23.75 16.0417L23.75 31.8751C23.75 34.0614 25.522 35.8334 27.7083 35.8334L35.625 35.8334C37.8114 35.8334 39.5833 34.0614 39.5833 31.8751L39.5833 16.0417C39.5833 13.8585 37.8083 12.0834 35.625 12.0834ZM36.6146 31.8751C36.6146 32.4207 36.1706 32.8647 35.625 32.8647L27.7083 32.8647C27.1627 32.8647 26.7188 32.4207 26.7188 31.8751L26.7188 16.0417C26.7188 15.4961 27.1627 15.0522 27.7083 15.0522L35.625 15.0522C36.1706 15.0522 36.6146 15.4961 36.6146 16.0417L36.6146 31.8751Z"
    ],
};
var handshakeAngleRegular = {
    prefix: 'pkd',
    iconName: 'handshake-angle-regular',
    icon: [
        48,
        48,
        [],
        null,
        "M39.0327 18.1318L31.4315 22.5528C31.0836 23.6352 30.2255 24.4776 29.1357 24.8097V25.691C29.1357 27.8168 27.6977 29.6252 25.7494 30.1893C25.1774 32.2996 23.2446 33.8526 20.9642 33.8526H13.8602L10.6688 35.7031C10.1895 35.9737 9.58645 35.8191 9.31617 35.3398L8.81953 34.4785C8.54894 33.9993 8.70356 33.3962 9.18289 33.1259L13.0686 30.8789H21.0223C22.1276 30.8789 23.017 29.9902 23.017 28.8923V27.4092H24.5088C25.452 27.4092 26.2251 26.6361 26.2251 25.6855V21.9727H28.1486C28.4192 21.9727 28.5877 21.7485 28.5877 21.478V19.4987C28.5877 19.2282 28.3635 19.004 28.1486 19.004H21.7719V19.938C21.7719 22.8362 19.2751 25.163 16.3144 24.8611C13.7322 24.5983 11.8767 22.2332 11.8767 19.6516V17.099L9.87096 18.3112C9.27102 18.6637 8.90612 19.3132 8.90612 20.0182V23.8362L2.0347 27.8397C1.55537 28.1102 0.952339 27.9479 0.682059 27.4687L0.242932 26.6108C-0.0276576 26.1394 0.126965 25.5285 0.606294 25.2582L6.04838 22.12V20.0121C6.04838 18.2654 6.96066 16.6499 8.45245 15.7531L12.1244 13.5581C12.4105 12.2908 13.1202 11.1776 14.3046 10.4743L16.2661 9.18169C17.364 8.49393 18.6319 8.18592 19.9307 8.18592H25.6666L32.3895 4.35252C32.8611 4.08199 33.4639 4.24429 33.7348 4.72349L34.2218 5.58134C34.4924 6.06054 34.3301 6.65565 33.9064 6.93397L26.5031 11.1465H19.9842C19.2977 11.1465 18.5233 11.3552 17.8894 11.7493L15.7713 12.986C15.2023 13.3386 14.8436 13.9818 14.8436 14.6621V19.9954C14.8436 21.0851 15.725 21.9739 16.7671 21.9739C17.8649 21.9739 18.6906 21.0851 18.6906 19.9954V16.0352H28.036C29.7907 16.0352 31.2286 17.3488 31.4532 19.0416L37.4364 15.5558C37.9158 15.2853 38.5188 15.4399 38.7891 15.9191L39.2282 16.777C39.7193 17.2598 39.5646 17.8535 39.0327 18.1318Z"
    ],
};
var questionRegular = {
    prefix: 'pkd',
    iconName: 'question-regular',
    icon: [
        48,
        48,
        [],
        null,
        "M19.8838 29.8958C18.7909 29.8958 17.9046 30.782 17.9046 31.8749C17.9046 32.9678 18.7909 33.8547 19.8838 33.8547C20.9767 33.8547 21.863 32.9678 21.863 31.8749C21.863 30.782 20.9785 29.8958 19.8838 29.8958ZM24.04 6.14575H17.4098C13.8634 6.14575 10.9775 9.03162 10.9775 12.578V13.5676C10.9775 14.3902 11.6424 15.052 12.4619 15.052C13.2814 15.052 13.9463 14.3902 13.9463 13.5676V12.578C13.9463 10.6681 15.4999 9.1145 17.4098 9.1145H24.04C26.112 9.1145 27.8005 10.803 27.8005 12.8749C27.8005 14.2764 27.0311 15.5511 25.7904 16.203L21.3063 18.565C19.457 19.5051 18.3994 21.3544 18.3994 23.3769V24.453C18.3994 25.2725 19.0643 25.938 19.8838 25.938C20.7033 25.938 21.3682 25.2756 21.3682 24.453V23.3769C21.3682 22.4559 21.8746 21.616 22.6905 21.1868L27.1727 18.8279C29.39 17.662 30.7692 15.3798 30.7692 12.8749C30.7692 9.1646 27.751 6.14575 24.04 6.14575Z\n"
    ],
};
var mailBoxRegular = {
    prefix: 'pkd',
    iconName: 'mailbox-regular',
    icon: [
        48,
        48,
        [],
        null,
        "M6.1123 17.0312V27.9167C6.1123 28.4733 6.54525 28.9062 7.10189 28.9062H17.9873V17.0312C17.9873 13.7533 15.3278 11.0938 12.0498 11.0938C8.77181 11.0938 6.1123 13.7533 6.1123 17.0312ZM17.9873 31.875H7.10189C4.87533 31.875 3.14355 30.1432 3.14355 27.9167V17.0312C3.14355 12.1452 7.10189 8.125 12.0498 8.125H29.8623C34.7484 8.125 38.7686 12.1452 38.7686 17.0312V27.9167C38.7686 30.1432 36.9749 31.875 34.8102 31.875H20.9561H17.9873ZM20.9561 17.0312V28.9062H34.8102C35.305 28.9062 35.7998 28.4733 35.7998 27.9167V17.0312C35.7998 13.7533 33.1403 11.0938 29.8623 11.0938H18.6676C20.0902 12.7018 20.9561 14.8047 20.9561 17.0312ZM9.57585 16.0417H14.5238C15.3278 16.0417 16.0081 16.722 16.0081 17.526C16.0081 18.3919 15.3278 19.0104 14.5238 19.0104H9.57585C8.70996 19.0104 8.09147 18.3919 8.09147 17.526C8.09147 16.722 8.70996 16.0417 9.57585 16.0417ZM25.4092 16.0417H30.3571H31.3467C32.1507 16.0417 32.8311 16.722 32.8311 17.526V20.4948C32.8311 21.3607 32.1507 21.9792 31.3467 21.9792H30.3571C29.4912 21.9792 28.8727 21.3607 28.8727 20.4948V19.0104H25.4092C24.5433 19.0104 23.9248 18.3919 23.9248 17.526C23.9248 16.722 24.5433 16.0417 25.4092 16.0417Z"
    ],
};
var phoneRegular = {
    prefix: 'pkd',
    iconName: 'phone-regular',
    icon: [
        48,
        48,
        [],
        null,
        "M33.873 23.9773L27.7995 21.3735C26.4753 20.7995 24.9148 21.1812 24.0143 22.2981L22.3172 24.3701C19.5476 22.7775 17.2276 20.4581 15.6375 17.746L17.7125 16.0464C18.8202 15.1422 19.2025 13.591 18.6415 12.2706L16.0234 6.1172C15.3951 4.68231 13.8377 3.8993 12.3205 4.24998L6.67682 5.55252C5.19987 5.89022 4.16699 7.18719 4.16699 8.7062C4.16699 23.6675 16.3327 35.8332 31.2939 35.8332C32.813 35.8332 34.1099 34.8009 34.3926 33.3264L35.6951 27.6771C36.1058 26.1544 35.3141 24.5958 33.873 23.9773ZM32.8587 27.0079L31.5543 32.6615C31.5253 32.7296 31.4239 32.8645 31.2934 32.8645C17.9711 32.8645 7.13519 22.0285 7.13519 8.70626C7.13519 8.57675 7.2154 8.47532 7.34201 8.44631L12.9913 7.14253C13.0106 7.1377 13.0309 7.13577 13.0502 7.13577C13.1575 7.13577 13.258 7.20052 13.3005 7.29713L15.9069 13.3787C15.9532 13.488 15.9223 13.6155 15.8295 13.6918L12.8135 16.1101C12.28 16.5469 12.1177 17.2939 12.4211 17.9124C14.4627 22.0705 17.8799 25.4889 22.0386 27.5299C22.6014 27.8334 23.4049 27.671 23.8421 27.1376L26.3167 24.115C26.3853 24.028 26.5138 23.9951 26.6211 24.0454L32.6984 26.6486C32.8154 26.7543 32.8835 26.8842 32.8587 27.0079Z"
    ],
};
var thumbUpRegular = {
    prefix: 'pkd',
    iconName: 'thumb-up-regular',
    icon: [
        48,
        48,
        [],
        null,
        "M10.1097 16.0417H7.14095C5.77656 16.0417 4.66699 17.1488 4.66699 18.5157V31.3803C4.66699 32.7471 5.77656 33.8542 7.14095 33.8542H10.1097C11.4766 33.8542 12.5837 32.7471 12.5837 31.3803V18.5157C12.5837 17.1488 11.4766 16.0417 10.1097 16.0417ZM10.6045 31.3803C10.6045 31.6524 10.3825 31.875 10.1097 31.875H7.14095C6.8682 31.875 6.64616 31.6524 6.64616 31.3803V18.5157C6.64616 18.2435 6.8682 18.0209 7.14095 18.0209H10.1097C10.3825 18.0209 10.6045 18.2435 10.6045 18.5157V31.3803ZM36.3337 17.8663C36.3337 15.7696 34.6266 14.0625 32.5299 14.0625H26.1904C26.9314 12.3889 27.355 10.7549 27.355 9.89825C27.3594 8.0533 25.9183 6.14587 23.5247 6.14587C20.9753 6.14587 20.389 7.9463 19.8682 9.53582C18.1562 14.7491 14.5628 15.5964 14.5628 17.0313C14.5628 17.5941 15.0267 18.0209 15.5524 18.0209C15.806 18.0209 16.0596 17.9219 16.2513 17.7302C19.5095 14.4497 20.3964 14.2598 21.7441 10.1537C22.3125 8.43119 22.4919 8.12504 23.5247 8.12504C24.8025 8.12504 25.3765 9.16225 25.3765 9.89825C25.3765 10.5223 24.8266 12.6165 23.7301 14.5666C23.6456 14.717 23.6035 14.884 23.6035 15.0509C23.6051 15.6397 24.0876 16.0417 24.5947 16.0417H32.5299C33.5381 16.0417 34.3545 16.8581 34.3545 17.8663C34.3545 18.8144 33.6074 19.6079 32.6549 19.6722C32.1315 19.7082 31.7327 20.1453 31.7327 20.6594C31.7327 21.4133 32.4786 21.4547 32.4786 22.5538C32.4786 23.4283 31.8533 24.1804 30.9924 24.3406C30.635 24.4075 30.1846 24.7375 30.1846 25.3147C30.1846 25.8643 30.603 25.9777 30.603 26.7719C30.603 28.6904 28.6542 28.14 28.6542 29.4345C28.6542 29.6526 28.7275 29.7518 28.7275 30.0536C28.7262 31.0586 27.9098 31.875 26.9017 31.875H23.4629C18.3907 31.875 16.7646 27.9155 15.5524 27.9155C15.0036 27.9155 14.5628 28.3604 14.5628 28.9026C14.5072 29.9144 18.5583 33.8542 23.4629 33.8542H26.9035C29.0008 33.8542 30.7073 32.1484 30.7073 30.0524C31.8546 29.3807 32.5831 28.135 32.5831 26.7706C32.5831 26.474 32.5464 26.1773 32.4759 25.8881C33.6702 25.2374 34.4588 23.9726 34.4588 22.5519C34.4588 22.1054 34.3795 21.6669 34.2268 21.2543C35.4801 20.6309 36.3337 19.3383 36.3337 17.8663Z"
    ],
};

library.add(
//PKD Custom
clockOneRegular, calendarClockLight, checkLight, euroSignLight, laptopFileLight, helmSafetyLight, shieldCheckLight, headsetRegular, calendarRegular, gaugeSimpleHighRegular, houseSignalRegular, laptopMobileRegular, handshakeAngleRegular, questionRegular, mailBoxRegular, phoneRegular, thumbUpRegular, 
// D3X Custom
aolLight, aolRegular, aolOverviewLight, aolOverviewRegular, clicksLight, clicksRegular, deviceCheckLight, deviceCheckRegular, eedLight, eedRegular, energyCertificateLight, energyCertificateRegular, fileContractPenLight, fileContractPenRegular, fileInvoiceDollarMagnifyingGlassLight, fileInvoiceDollarMagnifyingGlassRegular, legionelaLight, legionelaRegular, manualDeliveryLight, manualDeliveryRegular, smokeAlarmLight, smokeAlarmRegular, userArrowRotateRightLight, userArrowRotateRightRegular, userSwitchLight, userSwitchRegular, 
// Light
faArrowRightArrowLeftLight, faCalendarClockLight, faDoorClosedLight, faEqualsLight, faEuroSignLight, faFileCircleInfoLight, faFileExportLight, faFolderMagnifyingGlassLight, faHouseBuildingLight, faLocationPinLight, faMagnifyingGlassLight, faMinusLight, faPersonWalkingArrowLoopLeftLight, faPlusLight, faScrewdriverWrenchLight, faSquareInfoLight, faTriangleExclamationLight, faUserGearLight, faUserGroupLight, 
// Regular
faAngleDown, faApartment, faArrowDown, faArrowRight, faArrowRightLong, faArrowRotateLeft, faArrowRotateRight, faArrowTurnDownRight, faArrowUp, faArrowUpArrowDown, faArrowUpFromGroundWater, faArrowUpRightFromSquare, faBacterium, faBallotCheck, faBars, faBell, faBlockBrick, faBlockBrickFire, faBoltLightning, faBroom, faBroomBall, faBug, faBuildingColumns, faBuildingShield, faBullhorn, faCalendarDay, faCameraCctv, faChalkboardUser, faChartBar, faChartLineUp, faChartMixed, faChartPie, faCheck, faChevronDown, faChevronLeft, faChevronRight, faChevronUp, faChevronsLeft, faChevronsRight, faChimney, faCircleCheck, faCircleExclamation, faCircleInfo, faCirclePlus, faClock, faCoins, faCommentLines, faCreditCardBlank, faDesktop, faDroplet, faElevator, faEllipsisVertical, faEnvelope, faEnvelopeCircleCheck, faEnvelopeOpen, faEnvelopeOpenText, faEnvelopesBulk, faEuroSign, faEye, faFileContract, faFileInvoice, faFileLines, faFilePdf, faFilePlus, faFireBurner, faFireExtinguisher, faFireFlame, faFireFlameSimple, faFireSmoke, faFlask, faFolderPlus, faForward, faGear, faGrid2, faGrid2Plus, faHammer, faHandWave, faHorizontalRule, faHouseBlank, faHouseBuilding, faIdCard, faInfinity, faInputNumeric, faKey, faLandmark, faLanguage, faLeaf, faLeafMaple, faLightbulb, faLightbulbOn, faListCheck, faListTimeline, faListTree, faLock, faMagnifyingGlass, faMemoCircleCheck, faMemoCircleInfo, faMessageQuestion, faMeterFire, faMobileRetro, faMoneyBillTrendUp, faMoneyBillWave, faNewspaper, faPanelFire, faPen, faPencil, faPenSlash, faPeopleRoof, faPersonShelter, faPhone, faPiggyBank, faPlugCircleBolt, faPlus, faPlusLarge, faPowerOff, faPumpSoap, faRecycle, faScrewdriverWrench, faShieldCheck, faShieldCross, faShieldKeyhole, faShieldQuartered, faShoePrints, faShovelSnow, faSignalStreamSlash, faSlidersSimple, faSnowplow, faSprayCan, faSprayCanSparkles, faStairs, faStamp, faTable, faTankWater, faTelescope, faThumbsDown, faThumbsUp, faToggleOn, faToilet, faToolbox, faTowerBroadcast, faTrashCan, faTriangleExclamation, faTruckArrowRight, faTruckContainer, faTruckDroplet, faTruckFast, faTv, faTvRetro, faUser, faUserCheck, faUserGear, faUserGroup, faUserLarge, faUserLargeSlash, faUserPen, faUserVneckHair, faUserXmark, faUsers, faVacuum, faWashingMachine, faWater, faWaterArrowDown, faWindowFrame, faXmark, faXmarkLarge);

config.autoAddCss = false;
function SetupClient() {
    return null;
}

export { Accordion, Advice, Button, ButtonActionsContainer, CardGrid, Container, ContinuousText, FeatureCard, GlobalFooter, Icon, IconWithText, ImageCard, InputWithLabel, Link, Navigation, Section, SetupClient, SideSheet, Spinner, TechemLogo, TechemLogoMonochrome, Typography };
//# sourceMappingURL=index.esm.js.map
